/**
 * KOPS-463: Agent Client Hub
 * aisearchportal.com/agent-portal/clients
 * Navy + Gold split-panel layout for agent's buyer/seller leads
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Button, Form, Badge, Spinner, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faEnvelope, faPhone, faUsers, faHome, faUser,
  faCalendarAlt, faMapMarkerAlt, faDollarSign, faChartLine,
  faStickyNote, faArrowRight, faUserPlus, faExternalLinkAlt,
  faBed, faBath, faRuler, faCheck, faCheckCircle, faClock, faComments, faEdit, faRobot, faFolderOpen, faPlus,
  faPaperPlane, faBolt, faCopy, faExclamationTriangle, faLightbulb, faSave, faSyncAlt, faBrain,
  faHeart, faEye, faGlobe, faUniversity, faFileContract, faTrophy, faRocket
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import agentPortalService from '../../services/agentPortal.service';
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';
import PropertyDetailModal from '../../components/chat/PropertyDetailModal';
import AISearchTab from '../../components/modules/crm/AISearchTab';
import KanbanDealBoard from '../lender/KanbanDealBoard';
import AgentDealBoard from './AgentDealBoard';

// KOPS-520: Real transaction components migrated from TransactionDetails
import DocumentChecklistWithUploads from '../../components/agent-portal/DocumentChecklistWithUploads';
import TransactionOffersPanel from '../../components/agent-portal/TransactionOffersPanel'; // KOPS-711
import OfferIntakeLinkButton from '../../components/agent-portal/OfferIntakeLinkButton'; // KOPS-711
import './deal-theme.css'; // KOPS-654: re-skins the bootstrap doc components to the navy/gold look
import BrokerReviewStatus from '../../components/agent-portal/BrokerReviewStatus';
import TransactionPartyAssignment from '../../components/agent-portal/TransactionPartyAssignment';
import CommissionDisbursementAuthorization from '../../components/agent-portal/CommissionDisbursementAuthorization';
import CommissionPane from '../../components/broker-portal/CommissionPane';
import AIComplianceDisplay from '../../components/agent-portal/AIComplianceDisplay';
import TransactionTemplateManager from '../../components/agent-portal/TransactionTemplateManager';
import LandlordInvoiceGenerator from '../../components/agent-portal/LandlordInvoiceGenerator';

console.log('🏗️ [CLIENT HUB] Component loaded at:', window.location.href);

const colors = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
  white: '#FFFFFF', off: '#E8EEF6', offBg: '#eef1f6', muted: '#8FA3BF',
  green: '#27ae60', red: '#e74c3c', orange: '#f39c12', blue: '#3498db'
};

const AgentClientHub: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState<any[]>([]);
  const [sponsoredAgents, setSponsoredAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [clientTotals, setClientTotals] = useState({ total: 0, buyers: 0, sellers: 0 });
  const [selected, setSelected] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [clientDetail, setClientDetail] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'ai-search' | 'email' | 'buyer' | 'seller' | 'offer-analyzer' | 'activity' | 'ai-coach' | 'automations' | 'property'>('overview');

  // KOPS-576: Property Details tab state
  const [propertyData, setPropertyData] = useState<any[]>([]);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [propertyLoaded, setPropertyLoaded] = useState('');

  // KOPS-463 Phase 10 — top-level page mode: "clients" (CRM) or "deals" (pipeline)
  const [pageMode, setPageMode] = useState<'clients' | 'deals' | 'pipeline' | 'automations' | 'deal-board' | 'sellers'>('clients');
  const [deals, setDeals] = useState<any[]>([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<any>(null);
  const [dealDetail, setDealDetail] = useState<any>(null);
  const [dealParties, setDealParties] = useState<any[]>([]);
  const [dealDocuments, setDealDocuments] = useState<any[]>([]);
  const [dealActivity, setDealActivity] = useState<any[]>([]);
  const [dealDetailLoading, setDealDetailLoading] = useState(false);
  const [dealStageFilter, setDealStageFilter] = useState('all');
  const [dealSearch, setDealSearch] = useState('');
  const [dealTab, setDealTab] = useState<'overview' | 'email' | 'documents' | 'templates' | 'compliance' | 'parties' | 'commission' | 'invoice' | 'ai' | 'notes' | 'automations'>('overview');
  const [dealEmails, setDealEmails] = useState<any[]>([]);
  const [dealEmailsLoading, setDealEmailsLoading] = useState(false);

  // Automations state
  const [automationsData, setAutomationsData] = useState<any>(null);
  const [automationsLoading, setAutomationsLoading] = useState(false);
  const [queueData, setQueueData] = useState<any>(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [dealActionsData, setDealActionsData] = useState<any>(null);
  const [dealActionsLoading, setDealActionsLoading] = useState(false);
  const [automationModal, setAutomationModal] = useState<any>(null);
  const [dealModal, setDealModal] = useState<any>(null);
  const [dealModalDetail, setDealModalDetail] = useState<any>(null);
  const [dealModalLoading, setDealModalLoading] = useState(false);
  const [automationEditing, setAutomationEditing] = useState(false);
  const [automationEditBody, setAutomationEditBody] = useState('');
  const [automationEditSubject, setAutomationEditSubject] = useState('');
  const [automationScheduleDate, setAutomationScheduleDate] = useState('');
  const [automationSending, setAutomationSending] = useState(false);
  const [automationNoteText, setAutomationNoteText] = useState('');
  const [automationNoteSaving, setAutomationNoteSaving] = useState(false);
  const [regenFeedback, setRegenFeedback] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);

  // KOPS-645: Seller Dashboard state
  const [sellersList, setSellersList] = useState<any[]>([]);
  const [sellersLoading, setSellersLoading] = useState(false);
  const [sellersTotal, setSellersTotal] = useState(0);
  const [sellersStats, setSellersStats] = useState<any>({ total: 0, readyToList: 0, needsCma: 0, avgValue: 0 });
  const [sellersPage, setSellersPage] = useState(1);
  const [sellersStageFilter, setSellersStageFilter] = useState('all');
  const [sellersSearch, setSellersSearch] = useState('');
  const [selectedSellers, setSelectedSellers] = useState<Set<string>>(new Set());
  const [sellerCmaStatus, setSellerCmaStatus] = useState<Record<string, string>>({});
  const [bulkCmaRunning, setBulkCmaRunning] = useState(false);
  const [sellersLoaded, setSellersLoaded] = useState(false);
  // KOPS-539: Seller Scoring
  const [scoringRunning, setScoringRunning] = useState(false);
  const [scoringResults, setScoringResults] = useState<any>(null);
  const [sellersGradeFilter, setSellersGradeFilter] = useState('all');
  // KOPS-684: Active Sellers (from transactions) state
  const [activeSellersList, setActiveSellersList] = useState<any[]>([]);
  const [activeSellersLoading, setActiveSellersLoading] = useState(false);
  const [activeSellersStats, setActiveSellersStats] = useState<any>({ total: 0, pre_contract: 0, under_contract: 0, avg_price: 0 });
  const [activeSellersLoaded, setActiveSellersLoaded] = useState(false);
  const [sellerSubTab, setSellerSubTab] = useState<'active' | 'pipeline' | 'past'>('active');
  // KOPS-645: Recurring CMA schedule state
  const [showRecurringCmaModal, setShowRecurringCmaModal] = useState(false);
  const [recurringCmaFrequency, setRecurringCmaFrequency] = useState<'monthly' | 'quarterly' | 'manual'>('monthly');
  const [recurringCmaTarget, setRecurringCmaTarget] = useState<{ type: 'single', leadId: string } | { type: 'bulk', leadIds: string[] } | null>(null);
  const [recurringCmaLoading, setRecurringCmaLoading] = useState(false);

  // Add party to deal (legacy — now handled by TransactionPartyAssignment component)

  // KOPS-520: Real component state
  const [checklistRefresh, setChecklistRefresh] = useState(0);
  const [brokerReviewRefresh, setBrokerReviewRefresh] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [bulkFiles, setBulkFiles] = useState<File[]>([]);
  const [bulkUploading, setBulkUploading] = useState(false);

  // KOPS-520: Overview edit mode
  const [editingDeal, setEditingDeal] = useState(false);
  const [dealEditForm, setDealEditForm] = useState<any>({});
  const [savingDealEdit, setSavingDealEdit] = useState(false);
  const [changingStage, setChangingStage] = useState(false);

  // KOPS-520: Notes tab (replaces Activity)
  const [dealNoteText, setDealNoteText] = useState('');
  const [savingDealNote, setSavingDealNote] = useState(false);
  const [dealNotes, setDealNotes] = useState<any[]>([]);
  const [propertyVerifying, setPropertyVerifying] = useState(false);
  const [propertyVerifyResult, setPropertyVerifyResult] = useState<any>(null);

  const fetchDeals = useCallback(async () => {
    setDealsLoading(true);
    try {
      const params: any = { limit: 100 };
      if (dealStageFilter !== 'all') params.stage = dealStageFilter;
      const res = await axiosInstance.get('/transactions', { params });
      setDeals(res.data?.transactions || res.data?.data || []);
    } catch { setDeals([]); }
    finally { setDealsLoading(false); }
  }, [dealStageFilter]);

  const fetchDealDetail = useCallback(async (dealId: string) => {
    setDealDetailLoading(true);
    try {
      const [dRes, pRes, docRes, aRes] = await Promise.all([
        axiosInstance.get(`/transactions/${dealId}`),
        axiosInstance.get(`/transactions/${dealId}/parties`).catch(() => ({ data: { parties: [] } })),
        axiosInstance.get(`/transactions/${dealId}/documents`).catch(() => ({ data: { documents: [] } })),
        axiosInstance.get(`/transactions/${dealId}/activity`).catch(() => ({ data: { activities: [] } })),
      ]);
      setDealDetail(dRes.data?.transaction || dRes.data?.data || null);
      setDealParties(pRes.data?.parties || []);
      setDealDocuments(docRes.data?.documents || []);
      setDealActivity(aRes.data?.activities || aRes.data?.activity || []);
      // Extract notes from activity
      const allActivities = aRes.data?.activities || aRes.data?.activity || [];
      setDealNotes(allActivities.filter((a: any) => a.activity_type === 'note' || a.activity_type === 'admin_note'));
    } catch { setDealDetail(null); }
    finally { setDealDetailLoading(false); }
  }, []);

  useEffect(() => { if (pageMode === 'deals') fetchDeals(); }, [pageMode, fetchDeals]);

  // KOPS-520: Helpers migrated from TransactionDetails
  const getAgentRole = (deal: any): string => {
    const type = deal?.type?.toLowerCase();
    const clientType = deal?.client_type?.toLowerCase();
    if (type === 'purchase') return clientType === 'seller' ? 'seller_agent' : 'buyer_agent';
    if (type === 'listing' || type === 'sale') return clientType === 'buyer' ? 'buyer_agent' : 'listing_agent';
    if (type === 'referral') return 'referring_agent';
    return clientType === 'buyer' ? 'buyer_agent' : 'listing_agent';
  };

  const handleChecklistDocumentUpload = async (document: any, file: File, existingDocumentId?: string) => {
    if (!dealDetail?.id) return;
    try {
      // Step 1: Upload file to document storage
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', document.type || 'transaction_document');
      formData.append('description', document.name || file.name);
      formData.append('checklist_name', document.name || '');
      formData.append('owner_type', 'user');
      formData.append('transaction_id', dealDetail.id);
      if (dealDetail.brokerage_id) formData.append('brokerage_id', dealDetail.brokerage_id);
      if (existingDocumentId) formData.append('replaces_document_id', existingDocumentId);

      const uploadRes = await axiosInstance.post('/transaction-documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });

      if (!uploadRes.data?.success || !uploadRes.data?.document) {
        throw new Error(uploadRes.data?.error || 'Upload failed');
      }

      // Step 2: Link document to transaction (skip for replacements)
      const documentId = uploadRes.data.document.id || uploadRes.data.document.document_id;
      if (!existingDocumentId && documentId) {
        await axiosInstance.post(`/transactions/${dealDetail.id}/documents`, {
          document_id: documentId,
          document_role: 'required',
        });
      }

      toast.success(`${document.name || file.name} uploaded`);
      // Clear brief cache so the next Overview tab load fetches the refreshed brief
      try { localStorage.removeItem(`deal_brief_${dealDetail.id}`); localStorage.removeItem(`deal_brief_ack_${dealDetail.id}`); } catch {}
      setChecklistRefresh(prev => prev + 1);
      fetchDealDetail(dealDetail.id);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Upload failed');
    }
  };

  // KOPS-654: "Upload Multiple Documents" — the button fired onBulkUpload -> setShowUploadModal(true)
  // but NO modal was ever rendered, so the click did nothing. This uploads each selected file via the
  // same (working) single-doc path used by the checklist.
  const handleBulkUploadSubmit = async () => {
    if (!bulkFiles.length || !dealDetail?.id) return;
    setBulkUploading(true);
    for (const file of bulkFiles) {
      await handleChecklistDocumentUpload({ type: 'additional_document', name: file.name }, file);
    }
    setBulkUploading(false);
    setBulkFiles([]);
    setShowUploadModal(false);
  };

  const handlePartiesUpdated = useCallback((updatedParties: any[]) => {
    setDealParties(updatedParties);
    if (dealDetail?.id) fetchDealDetail(dealDetail.id);
  }, [dealDetail?.id, fetchDealDetail]);

  // Build party prefill data for templates
  const partyPrefillData = React.useMemo(() => {
    const data: Record<string, string> = {};
    const parties = dealParties || [];
    const buyers = parties.filter((p: any) => p.party_type === 'buyer');
    const sellers = parties.filter((p: any) => p.party_type === 'seller');
    if (buyers[0]) { data.buyer_name = buyers[0].name || ''; data.buyer_email = buyers[0].email || ''; data.buyer_phone = buyers[0].phone || ''; }
    if (sellers[0]) { data.seller_name = sellers[0].name || ''; data.seller_email = sellers[0].email || ''; data.seller_phone = sellers[0].phone || ''; }
    const buyerAgents = parties.filter((p: any) => (p.role_description || '').toLowerCase() === 'buyer_agent');
    const sellerAgents = parties.filter((p: any) => (p.role_description || '').toLowerCase() === 'seller_agent');
    if (buyerAgents[0]) { data.buyer_agent_name = buyerAgents[0].name || ''; data.buyer_agent_email = buyerAgents[0].email || ''; }
    if (sellerAgents[0]) { data.seller_agent_name = sellerAgents[0].name || ''; data.seller_agent_email = sellerAgents[0].email || ''; }
    return data;
  }, [dealParties]);

  const [activityData, setActivityData] = useState<any>(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [editingBuyer, setEditingBuyer] = useState(false);
  const [buyerEditData, setBuyerEditData] = useState<any>({});
  const [savingBuyer, setSavingBuyer] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [alert, setAlert] = useState<{ type: string; message: string } | null>(null);

  // Property Ownership (county lookup)
  const [ownedProperties, setOwnedProperties] = useState<any[]>([]);
  const [propsLoading, setPropsLoading] = useState(false);
  const [propsLoaded, setPropsLoaded] = useState('');
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [addPropForm, setAddPropForm] = useState({ address: '', city: '', zip: '', propertyType: '', ownership: '', bedrooms: '', bathrooms: '', sqft: '', yearBuilt: '', estimatedValue: '' });
  const [addPropSaving, setAddPropSaving] = useState(false);

  // Linked Contacts (relationships)
  const [linkedContacts, setLinkedContacts] = useState<any[]>([]);
  const [linkedLoaded, setLinkedLoaded] = useState('');
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkSearchResults, setLinkSearchResults] = useState<any[]>([]);
  const [linkType, setLinkType] = useState('spouse');

  const loadLinkedContacts = async (leadId: string) => {
    if (linkedLoaded === leadId) return;
    try {
      const { data } = await axiosInstance.get(`/agents/clients/${leadId}/relationships`);
      if (data.success) { setLinkedContacts(data.relationships); setLinkedLoaded(leadId); }
    } catch { }
  };

  const loadOwnedProperties = async (leadId: string) => {
    if (propsLoaded === leadId || propsLoading) return;
    setPropsLoading(true);
    try {
      // First try stored snapshots (from batch county scan — instant)
      const { data: snapData } = await axiosInstance.get(`/county-deals/property-snapshots/${leadId}`);
      if (snapData.success && snapData.properties?.length > 0) {
        setOwnedProperties(snapData.properties);
        setPropsLoaded(leadId);
        setPropsLoading(false);
        return;
      }
      // Fallback to live county API lookup (slower, for leads not yet scanned)
      const { data } = await axiosInstance.get(`/county-deals/property-lookup/${leadId}`);
      if (data.success) {
        setOwnedProperties(data.properties || []);
        setPropsLoaded(leadId);
      }
    } catch { setOwnedProperties([]); }
    setPropsLoading(false);
  };

  // Email state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailTo, setEmailTo] = useState<string[]>([]);
  const [emailToInput, setEmailToInput] = useState('');
  const [emailCc, setEmailCc] = useState<string[]>([]);
  const [emailCcInput, setEmailCcInput] = useState('');
  const [emailBcc, setEmailBcc] = useState<string[]>([]);
  const [emailBccInput, setEmailBccInput] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [emailAttachments, setEmailAttachments] = useState<File[]>([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailHistory, setEmailHistory] = useState<any[]>([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [expandedEmailId, setExpandedEmailId] = useState<string | null>(null);
  const [replyToEmail, setReplyToEmail] = useState<any>(null);
  const [connectedAccounts, setConnectedAccounts] = useState<any[]>([]);
  const [selectedFromAccount, setSelectedFromAccount] = useState<string>(() => localStorage.getItem('defaultFromAccount') || '');
  const [generatingAIDraft, setGeneratingAIDraft] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState<any>(null);
  const [aiDraft, setAiDraft] = useState<any>(null);
  const [loadingRecommendation, setLoadingRecommendation] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [sendingAIDraft, setSendingAIDraft] = useState(false);
  const [editingAIDraft, setEditingAIDraft] = useState(false);
  const [aiEditSubject, setAiEditSubject] = useState('');
  const [aiEditBody, setAiEditBody] = useState('');
  const [aiHistory, setAiHistory] = useState<any[]>([]);
  const [upcomingTasks, setUpcomingTasks] = useState<any[]>([]);
  const [allAutomations, setAllAutomations] = useState<any[]>([]);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [completeNotes, setCompleteNotes] = useState('');
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [reschedulingId, setReschedulingId] = useState<string | null>(null);

  // Add client form
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ firstName: '', lastName: '', email: '', phone: '', source: 'manual' });
  const [addingClient, setAddingClient] = useState(false);

  // Edit lead
  const [editingLead, setEditingLead] = useState(false);
  const [editLeadData, setEditLeadData] = useState<any>({});
  const [savingLead, setSavingLead] = useState(false);

  // Plan gating
  const [userPlan, setUserPlan] = useState<string>('trial');
  const [demoMode, setDemoMode] = useState(() => sessionStorage.getItem('demoMode') === 'true');
  const blur = demoMode ? { filter: 'blur(5px)', userSelect: 'none' as const } : {};
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [userFeatures, setUserFeatures] = useState<string[]>([]);
  const hasFeature = (feature: string) => userFeatures.includes(feature);

  const startEditLead = () => {
    if (!selected) return;
    setEditLeadData({
      lead_first_name: selected.lead_first_name || '',
      lead_last_name: selected.lead_last_name || '',
      lead_email: selected.lead_email || '',
      lead_phone: selected.lead_phone || '',
      lead_source: selected.lead_source || ''
    });
    setEditingLead(true);
  };

  const saveLeadEdit = async () => {
    if (!selected) return;
    setSavingLead(true);
    try {
      await axiosInstance.patch(`/leads/${selected.id}`, {
        first_name: editLeadData.lead_first_name,
        last_name: editLeadData.lead_last_name,
        email: editLeadData.lead_email,
        phone: editLeadData.lead_phone,
        lead_source: editLeadData.lead_source
      });
      setAlert({ type: 'success', message: 'Lead updated!' });
      setEditingLead(false);
      // Update selected immediately with new data
      setSelected((prev: any) => prev ? {
        ...prev,
        lead_first_name: editLeadData.lead_first_name,
        lead_last_name: editLeadData.lead_last_name,
        lead_email: editLeadData.lead_email,
        lead_phone: editLeadData.lead_phone,
        lead_source: editLeadData.lead_source
      } : prev);
      // Refresh client list
      fetchClients();
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to update lead' });
    }
    setSavingLead(false);
  };

  // KOPS-704: Archive (soft delete) — record retained for audit, hidden from lists
  const archiveClient = async () => {
    if (!selected) return;
    const name = getClientName(selected);
    if (!window.confirm(`Archive ${name}?\n\nThey will be hidden from your client lists, but all history is kept for audit. You can restore them anytime from the Archived filter.`)) return;
    try {
      await axiosInstance.post(`/agents/clients/${selected.id}/archive`, {});
      setAlert({ type: 'success', message: `${name} archived. Find them under the Archived filter.` });
      setSelected(null);
      setSearchParams({});
      fetchClients();
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to archive contact' });
    }
  };

  const restoreClient = async () => {
    if (!selected) return;
    try {
      await axiosInstance.post(`/agents/clients/${selected.id}/unarchive`, {});
      setAlert({ type: 'success', message: `${getClientName(selected)} restored to your active clients.` });
      setSelected(null);
      setSearchParams({});
      fetchClients();
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to restore contact' });
    }
  };

  const handleAddClient = async () => {
    if (!newClient.email && !newClient.phone) {
      setAlert({ type: 'danger', message: 'Email or phone required' });
      return;
    }
    setAddingClient(true);
    try {
      const response = await axiosInstance.post('/leads', {
        first_name: newClient.firstName,
        last_name: newClient.lastName,
        email: newClient.email,
        phone: newClient.phone,
        lead_source: newClient.source || 'manual'
      });
      setAlert({ type: 'success', message: `${newClient.firstName || 'Client'} added successfully!` });
      setNewClient({ firstName: '', lastName: '', email: '', phone: '', source: 'manual' });
      setShowAddClient(false);
      fetchClients();
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to add client' });
    }
    setAddingClient(false);
  };
  const isSponsorPlan = ['sponsor-pro', 'sponsor-enterprise', 'sponsor-demo', 'sponsor-admin', 'demo-lender'].includes(userPlan);
  const hasBrokerage = (() => { try { const u = JSON.parse(localStorage.getItem('user_info') || '{}'); return !!u.brokerage_id; } catch { return false; } })();
  const isLender = isSponsorPlan;
  // Non-paying broker agents (have brokerage but no $185 AI Pro subscription) only see Deals
  const isBrokerComplianceOnly = hasBrokerage && !isSponsorPlan && !['ai-pro', 'internal', 'Enterprise', 'enterprise', 'Professional', 'professional', 'growth', 'premium', 'Essential', 'essential', 'Broker Plan', 'BrokerPlan', 'BrokerTeamPlan', 'AgentUnderBroker', 'LenderSponsored', 'TeamLead', 'TeamMemberDiscounted', 'agent-crm', 'ai-search'].includes(userPlan);
  // Default compliance-only agents to deals view. MUST run after isBrokerComplianceOnly is declared —
  // referencing it from an earlier useEffect caused a temporal-dead-zone crash ("Cannot access
  // 'isBrokerComplianceOnly' before initialization").
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isBrokerComplianceOnly && pageMode === 'clients' && !new URLSearchParams(window.location.search).get('mode')) setPageMode('deals'); }, [isBrokerComplianceOnly]);
  const portalBase = window.location.pathname.includes('lender-portal') ? '/lender-portal' : '/agent-portal';
  const hasAISearch = ['ai-search', 'ai-pro', 'agent-crm', 'internal'].includes(userPlan) || isSponsorPlan;
  const hasAIPro = ['ai-pro', 'internal', 'Enterprise', 'enterprise', 'Professional', 'professional', 'growth', 'premium', 'Essential', 'essential', 'Broker Plan', 'BrokerPlan', 'BrokerTeamPlan', 'AgentUnderBroker', 'LenderSponsored', 'TeamLead', 'TeamMemberDiscounted'].includes(userPlan) || isSponsorPlan;
  // MLS data (CMA, search, offers) restricted to licensed agents only per ARMLS CLA
  // MLS access requires ARMLS API credentials configured in AWS Secrets Manager
  // This is checked at login and stored as mls_configured in user_info
  // 'internal' bypasses for admin testing. No credentials = no MLS data. CLA compliance.
  const mlsConfigured = (() => { try { const u = JSON.parse(localStorage.getItem('user_info') || '{}'); return !!u.mls_configured; } catch { return false; } })();
  const hasMLSAccess = (mlsConfigured || userPlan === 'internal') && !isSponsorPlan;
  const [rescheduleDays, setRescheduleDays] = useState('3');
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryText, setSummaryText] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);
  const [editingRelType, setEditingRelType] = useState('');

  // Invite form
  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState({ firstName: '', lastName: '', email: '' });
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    // Fetch sponsored agents for agent assignment dropdown
    axiosInstance.get('/lender/agents').then(res => {
      if (res.data?.success && res.data?.agents) setSponsoredAgents(res.data.agents);
    }).catch(() => {});

    // Fetch agent's portal slug for aisearchportal.com links
    axiosInstance.get('/portal-settings/my-slug').then(res => {
      if (res.data?.slug) (window as any).__agentPortalSlug = res.data.slug;
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const init = async () => {
      // Fetch user plan
      axiosInstance.get('/agents/plan').then(res => {
        setUserPlan(res.data?.plan || 'trial');
        setUserFeatures(res.data?.features || []);
      }).catch(() => {});
      // Fetch connected emails
      axiosInstance.get('/agents/connected-emails').then(res => {
        const accounts = res.data?.accounts || [];
        setConnectedAccounts(accounts);
        if (accounts.length > 0) {
          const saved = localStorage.getItem('defaultFromAccount');
          const validSaved = saved && accounts.some((a: any) => String(a.id) === saved);
          setSelectedFromAccount(validSaved ? saved : String(accounts[0].id));
        }
      }).catch(() => {});

      // Check for add mode
      if (searchParams.get('add') === 'true') {
        setShowAddClient(true);
        searchParams.delete('add');
        window.history.replaceState({}, '', window.location.pathname);
      }

      // KOPS-520: Restore deals mode + deal selection from URL
      const urlMode = searchParams.get('mode');
      const urlDealId = searchParams.get('dealId');
      const isLenderPortal = window.location.pathname.includes('lender-portal');
      const portalBase = isLenderPortal ? '/lender-portal' : '/agent-portal';
      const urlLeadId = searchParams.get('id');
      if (isLenderPortal && !urlMode && !urlLeadId) {
        // Default lender-portal to pipeline mode — but only if no specific lead selected
        setPageMode(window.location.pathname.includes('/pipeline') ? 'pipeline' : 'clients');
      } else if (urlLeadId) {
        // Specific lead selected — stay in clients mode to show detail
        setPageMode('clients');
      } else if (urlMode === 'pipeline') {
        setPageMode('pipeline');
      } else if (urlMode === 'automations') {
        setPageMode('automations');
      } else if (urlMode === 'deal-board') {
        setPageMode('deal-board');
      } else if (urlMode === 'sellers') {
        setPageMode('sellers');
      } else if (urlMode === 'deals') {
        setPageMode('deals');
        // Fetch deals and auto-select if dealId in URL
        try {
          const dRes = await axiosInstance.get('/transactions', { params: { limit: 100 } });
          const allDeals = dRes.data?.transactions || dRes.data?.data || [];
          setDeals(allDeals);
          if (urlDealId) {
            const match = allDeals.find((d: any) => d.id === urlDealId);
            if (match) {
              setSelectedDeal(match);
              setDealTab('overview');
              fetchDealDetail(match.id);
            } else {
              // Deal not in list — try fetching directly
              try {
                const directRes = await axiosInstance.get(`/transactions/${urlDealId}`);
                const directDeal = directRes.data?.transaction || directRes.data?.data || null;
                if (directDeal) {
                  setDeals(prev => [directDeal, ...prev]);
                  setSelectedDeal(directDeal);
                  setDealTab('overview');
                  fetchDealDetail(directDeal.id);
                }
              } catch {}
            }
          }
        } catch { setDeals([]); }
        setLoading(false);
        return; // Skip client fetch when in deals mode
      }

      // Fetch clients and restore selection from URL
      try {
        const params: any = { limit: clientFilter !== 'all' ? 2000 : 500 };
        const response = await axiosInstance.get('/agents/crm-leads', { params });
        if (response.data.success) {
          const leads = response.data.data || [];
          setClients(leads);
          // KOPS-632: Set total from pagination (backend returns COUNT(*) not just results length)
          if (response.data.pagination?.total) {
            setClientTotals(prev => ({ ...prev, total: response.data.pagination.total }));
          }

          const urlId = searchParams.get('id');
          const urlTab = searchParams.get('tab');
          console.log('🔍 [CLIENT HUB] URL restore:', { urlId, urlTab, leadsCount: leads.length });
          if (urlId) {
            const match = leads.find((c: any) => c.id === urlId);
            console.log('🔍 [CLIENT HUB] Match found:', !!match, match?.lead_email);
            if (match) {
              selectClient(match);
              if (urlTab) setActiveTab(urlTab as any);
            } else {
              // Lead not in first 500 — fetch it directly
              console.log('🔍 [CLIENT HUB] Lead not in list, fetching directly...');
              try {
                const directRes = await axiosInstance.get('/agents/crm-leads', { params: { search: urlId, limit: 1 } });
                const directLeads = directRes.data?.data || [];
                if (directLeads.length > 0) {
                  setClients(prev => [...prev, directLeads[0]]);
                  selectClient(directLeads[0]);
                  if (urlTab) setActiveTab(urlTab as any);
                }
              } catch {}
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch clients:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Auto-dismiss alerts after 3 seconds
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  const fetchClients = async (filter?: string) => {
    try {
      const params: any = { limit: (filter || clientFilter) !== 'all' ? 2000 : 500 };
      if (search) params.search = search;
      const f = filter || clientFilter;
      if (f && f !== 'all') params.filter = f;
      const response = await axiosInstance.get('/agents/crm-leads', { params });
      if (response.data.success) {
        setClients(response.data.data || []);
        if (response.data.pagination?.total && f === 'all') {
          setClientTotals(prev => ({ ...prev, total: response.data.pagination.total }));
        }
      }
      // Fetch buyer/seller counts (lightweight)
      if (!f || f === 'all') {
        try {
          const [bRes, sRes] = await Promise.all([
            axiosInstance.get('/agents/crm-leads', { params: { limit: 1, filter: 'buyers' } }),
            axiosInstance.get('/agents/crm-leads', { params: { limit: 1, filter: 'sellers' } })
          ]);
          setClientTotals(prev => ({
            ...prev,
            total: response.data.pagination?.total || prev.total,
            buyers: bRes.data.pagination?.total || 0,
            sellers: sRes.data.pagination?.total || 0
          }));
        } catch { }
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => { fetchClients(); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch email history when email tab is opened
  useEffect(() => {
    if (activeTab === 'email' && selected) {
      fetchEmailHistory(selected.id);
    }
  }, [activeTab, selected?.id]);

  const selectClient = async (client: any) => {
    setSelected(client);
    setDetailLoading(true);
    setActiveTab('overview');
    setSearchParams({ id: client.id, tab: 'overview' });
    setEmailHistory([]);
    setEmailSubject('');
    setEmailBody('');
    setEmailTo([]);
    setEmailToInput('');
    setReplyToEmail(null);
    setEmailCc([]);
    setEmailCcInput('');
    setEmailBcc([]);
    setEmailBccInput('');
    setShowCcBcc(false);
    setEmailAttachments([]);
    setActivityData(null);
    setShowSellerForm(false);
    setShowBuyerCMA(false);
    setOwnedProperties([]);
    setPropsLoaded('');
    setLinkedContacts([]);
    setLinkedLoaded('');
    // If URL has offer-analyzer tab, load reports after client data arrives
    const urlTab = searchParams.get('tab');
    if (urlTab === 'offer-analyzer') {
      setActiveTab('offer-analyzer');
    }
    setBuyerCMAResult(null);
    setBuyerCMAAddress('');
    setBuyerCMAMlsId('');
    try {
      const response = await agentPortalService.getClientProfile(client.id);
      if (response.success) {
        // For sponsor/lender accounts, also fetch deal-board detail for agent assignment
        if (isSponsorPlan) {
          try {
            const dealDetail = await axiosInstance.get(`/sponsor/deal-board/${client.id}/detail`);
            console.log('🔧 [AGENT-ASSIGN] Deal detail response:', dealDetail.data?.detail?.assignedAgent);
            if (dealDetail.data?.detail) {
              // Merge deal-board detail (has assignedAgent) into the client detail
              if (!response.data.detail) response.data.detail = {};
              response.data.detail = { ...response.data.detail, ...dealDetail.data.detail };
            }
          } catch (e) {
            console.log('🔧 [AGENT-ASSIGN] Deal detail fetch failed, trying lead_tags directly');
            // Fallback: fetch lead_tags directly for agent assignment
            try {
              const leadRes = await axiosInstance.get(`/agents/crm-leads`, { params: { search: client.id, limit: 1 } });
              const leadData = leadRes.data?.data?.[0];
              if (leadData?.lead_tags) {
                const tags = typeof leadData.lead_tags === 'string' ? JSON.parse(leadData.lead_tags) : leadData.lead_tags;
                if (tags.assigned_agent_name) {
                  if (!response.data.detail) response.data.detail = {};
                  response.data.detail.assignedAgent = { name: tags.assigned_agent_name, email: tags.assigned_agent_email };
                  response.data.detail.tags = tags;
                }
              }
            } catch {}
          }
        }
        setClientDetail(response.data);
      }
      // Load property ownership from county (non-blocking)
      loadOwnedProperties(client.id);
      // Load linked contacts (non-blocking)
      loadLinkedContacts(client.id);
    } catch (err) {
      console.error('Failed to fetch client detail:', err);
      setClientDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchEmailHistory = async (leadId: string) => {
    setEmailsLoading(true);
    try {
      const response = await axiosInstance.get(`/agents/clients/${leadId}/emails`);
      if (response.data.success) {
        setEmailHistory(response.data.emails || []);
      }
    } catch (err) {
      console.error('Failed to fetch email history:', err);
    } finally {
      setEmailsLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!selected || !noteText.trim()) return;
    try {
      await axiosInstance.post(`/agents/clients/${selected.id}/note`, { note: noteText });
      setNoteText('');
      setAlert({ type: 'success', message: 'Note added' });
      setActivityData(null); // Force reload on next Activity tab visit
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to add note' });
    }
  };

  const fetchAiHistory = async (leadId: string) => {
    try {
      const response = await axiosInstance.get(`/ai-coach/queue?lead_id=${leadId}&status=all`);
      setAiHistory(response.data?.queue || []);
    } catch (err) {
      console.log('Could not fetch AI history:', err);
    }
    // Also fetch upcoming scheduled tasks for this lead
    try {
      const response = await axiosInstance.get('/ai-coach/upcoming');
      const allTasks = response.data?.tasks || [];
      // Filter to this lead
      const leadTasks = allTasks.filter((t: any) => t.lead_id === leadId || t.lead_id === leadId?.substring(0, 26));
      setUpcomingTasks(leadTasks);
    } catch (err) {
      console.log('Could not fetch upcoming tasks:', err);
    }
  };

  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [sellerCMA, setSellerCMA] = useState<any>(null);
  const [allCMAReports, setAllCMAReports] = useState<any[]>([]);
  const [loadingCMA, setLoadingCMA] = useState(false);
  const [runningCMA, setRunningCMA] = useState(false);

  // Buyer CMA / Offer Analyzer state
  const [showBuyerCMA, setShowBuyerCMA] = useState(false);
  const [buyerCMAAddress, setBuyerCMAAddress] = useState('');
  const [buyerCMAMlsId, setBuyerCMAMlsId] = useState('');
  const [buyerCMALoading, setBuyerCMALoading] = useState(false);
  const [buyerCMAResult, setBuyerCMAResult] = useState<any>(null);
  const [buyerCMAReports, setBuyerCMAReports] = useState<any[]>([]);
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const addressTimerRef = useRef<any>(null);

  // Comp images fetched from Theta (keyed by comp index)
  const [compImages, setCompImages] = useState<{ [key: number]: string[] }>({});

  // CMA Narration — fetched after report generates (async, ~8s delay)
  const [cmaNarration, setCmaNarration] = useState<any>(null);

  // Fetch narration after CMA report is created
  useEffect(() => {
    const reportId = buyerCMAResult?.report_id;
    if (!reportId) { setCmaNarration(null); return; }
    // Narration fires async on the backend — poll after a delay
    const timer = setTimeout(async () => {
      try {
        const userId = (() => { try { return JSON.parse(atob((localStorage.getItem('auth_token') || '').split('.')[1])).id; } catch { return ''; } })();
        const res = await axiosInstance.get(`/sellers/${selected?.lead_id || selected?.id}/cma-reports`, {
          headers: { 'x-user-id': userId }
        });
        const reports = res.data?.reports || (Array.isArray(res.data) ? res.data : []);
        const report = reports.find((r: any) => (r.id || r.report_id) === reportId) || reports[0];
        if (report) {
          const pd = typeof report.predictionData === 'string' ? JSON.parse(report.predictionData) : report.predictionData;
          const narr = pd?.narrative;
          if (narr?.full || narr?.summary) {
            setCmaNarration(narr);
          }
        }
      } catch { /* narration is supplementary */ }
    }, 10000); // wait 10s for narration to complete
    return () => clearTimeout(timer);
  }, [buyerCMAResult?.report_id]);

  // Fetch comp images from MLS via Theta (uses MLS number for direct lookup)
  useEffect(() => {
    const comps = buyerCMAResult?.analysis_results?.comparables;
    if (!comps || comps.length === 0) return;
    setCompImages({}); // reset on new result
    comps.slice(0, 15).forEach((comp: any, i: number) => {
      // Prefer MLS number lookup (instant, reliable) over address search
      const mlsNum = comp.mls_number || comp.listing_id || '';
      const query = mlsNum ? `MLS ${mlsNum}` : [comp.unparsed_address || comp.address || '', comp.city || '', comp.state || 'AZ'].filter(Boolean).join(' ');
      if (!query.trim()) return;
      axiosInstance.post('/theta-ai/search', { query }).then(res => {
        const props = res.data?.properties || [];
        if (props.length > 0) {
          const imgs = props[0].images || props[0].thumbnails || [];
          if (imgs.length > 0) {
            setCompImages(prev => ({ ...prev, [i]: imgs }));
          }
        }
      }).catch(() => { /* no photo — fine */ });
    });
  }, [buyerCMAResult]);

  // AI Coach - Offer Strategy (chat + draft modes)
  const [offerCoachMessage, setOfferCoachMessage] = useState('');
  const [offerCoachChat, setOfferCoachChat] = useState<Array<{ role: string; text: string }>>([]);
  const [offerCoachLoading, setOfferCoachLoading] = useState(false);
  const [offerCoachDraft, setOfferCoachDraft] = useState<any>(null);
  const [offerDraftSubject, setOfferDraftSubject] = useState('');
  const [offerDraftBody, setOfferDraftBody] = useState('');

  const buildOfferData = () => {
    if (!buyerCMAResult?.analysis_results) return null;
    const ar = buyerCMAResult.analysis_results;
    return {
      address: buyerCMAAddress,
      predictedValue: ar.xgboost_prediction?.predicted_price || ar.statistics?.median_price || 0,
      offerRange: {
        opening: Math.round((ar.xgboost_prediction?.predicted_price || ar.statistics?.median_price || 0) * 0.93),
        target: Math.round((ar.xgboost_prediction?.predicted_price || ar.statistics?.median_price || 0) * 0.96),
        max: Math.round((ar.xgboost_prediction?.predicted_price || ar.statistics?.median_price || 0) * 0.99),
      },
      comps: ar.comparables || [],
      compCount: (ar.comparables || []).length,
    };
  };

  const handleOfferCoachAsk = async (presetMessage?: string, mode: 'chat' | 'draft' = 'chat') => {
    if (!buyerCMAResult?.analysis_results || !selected) return;
    const offerData = buildOfferData();
    if (!offerData) return;

    const msg = presetMessage || offerCoachMessage;
    if (!msg.trim() && mode === 'chat') return;

    setOfferCoachLoading(true);
    if (mode === 'chat') {
      setOfferCoachChat(prev => [...prev, { role: 'agent', text: msg }]);
    }

    try {
      const leadId = selected.lead_id || selected.id;
      // Pass chat history so draft mode knows the strategy discussion
      const chatHistory = offerCoachChat.map(m => `${m.role === 'agent' ? 'Agent' : 'AI Coach'}: ${m.text}`).join('\n');
      const { data } = await axiosInstance.post('/ai-coach/offer-strategy', {
        leadId, mode, message: msg, offerData, chatHistory
      });

      if (data.success) {
        if (mode === 'draft' && data.draft) {
          setOfferCoachDraft(data.draft);
          setOfferDraftSubject(data.draft.subject || '');
          setOfferDraftBody(data.draft.body || '');
        } else if (mode === 'chat' && data.response) {
          setOfferCoachChat(prev => [...prev, { role: 'coach', text: data.response }]);
        }
      }
    } catch (err: any) {
      if (mode === 'chat') {
        setOfferCoachChat(prev => [...prev, { role: 'coach', text: 'Sorry, I couldn\'t process that. Try again.' }]);
      }
    }
    setOfferCoachLoading(false);
    setOfferCoachMessage('');
  };

  const handleAddressSearch = (value: string) => {
    setBuyerCMAAddress(value);
    if (addressTimerRef.current) clearTimeout(addressTimerRef.current);
    if (value.length < 4) { setAddressSuggestions([]); return; }
    addressTimerRef.current = setTimeout(async () => {
      try {
        // Use backend proxy to avoid exposing Mapbox token
        const res = await axiosInstance.get(`/sellers/geocode-suggestions?q=${encodeURIComponent(value)}`);
        setAddressSuggestions(res.data?.suggestions || []);
      } catch { setAddressSuggestions([]); }
    }, 300);
  };

  // Helper: extract report data into buyerCMAResult format
  const loadReportIntoBuyerCMA = (report: any) => {
    const pd = typeof report.propertyData === 'string' ? JSON.parse(report.propertyData) : (report.propertyData || {});
    const cd = typeof report.cmaData === 'string' ? JSON.parse(report.cmaData) : (report.cmaData || {});
    const sp = cd.subject_property || {};
    const rc = report.full_analysis?.comparable_properties?.comparables || cd.comparable_properties?.comparables || report.comparablesData?.comparables || [];
    const rs = report.full_analysis?.market_stats || report.analysisData?.comp_stats || report.timing_recommendations?.market_insights?.price_analysis || {};
    const ev = Number(report.pricing_recommendations?.estimated_value || report.predicted_value || report.predictionData?.predicted_value || pd.estimated_value || 0);
    const cf = Number(report.pricing_recommendations?.confidence_score || report.confidence_score || report.predictionData?.confidence_score || 0);
    const ad = pd.unparsed_address || pd.original_address || pd.mls_matched_address || sp.unparsed_address || `${sp.address || ''}, ${sp.city || ''}, ${sp.state || ''} ${pd.postal_code || sp.postal_code || ''}`.trim().replace(/^,\s*/, '') || report.title?.replace('Smart CMA - ', '') || '';
    const medianFromReport = Number(report.timing_recommendations?.market_insights?.price_analysis?.median_sale_price || rs.median_price || 0);
    setBuyerCMAAddress(ad);
    setBuyerCMAResult({
      report_id: report.report_id || report.id,
      analysis_results: {
        xgboost_prediction: { predicted_price: ev, confidence: cf }, enhanced_confidence: cf,
        comparables: rc.map((c: any) => ({ unparsed_address: c.address || c.unparsed_address || 'Comp', bedrooms: c.bedrooms, bathrooms: c.bathrooms, living_area: c.living_area, close_price: c.close_price, list_price: c.list_price, year_built: c.year_built, close_date: c.close_date, distance_miles: c.distance_miles, price_per_sqft: c.price_per_sqft, classification: c.classification, days_on_market: c.days_on_market, pool: c.pool })),
        statistics: { median_price: medianFromReport || rs.median_price || 0, median_ppsf: rs.median_ppsf || 0, total_comps: rc.length },
        property_address: ad,
      },
    });
  };

  // Load buyer offer report history when Offer Analyzer tab opens
  // Only loads previous buyer offer reports — NOT seller CMAs
  useEffect(() => {
    if (activeTab !== 'offer-analyzer' || !selected) return;
    const uid = (() => { try { return JSON.parse(atob((localStorage.getItem('auth_token') || '').split('.')[1])).id; } catch { return ''; } })();
    // Only fetch report history for the sidebar — don't auto-load into the analyzer
    if (buyerCMAReports.length === 0) {
      (async () => {
        try {
          const res = await axiosInstance.get(`/sellers/${selected.id}/cma-reports`, { headers: { 'x-user-id': uid } });
          const reports = res.data?.reports || (Array.isArray(res.data) ? res.data : []);
          if (reports.length > 0) setBuyerCMAReports(reports);
        } catch { /* */ }
      })();
    }
  }, [activeTab, selected]);

  const handleRunBuyerCMA = async () => {
    if (isSponsorPlan) { toast.error('MLS data access requires an active agent subscription with broker MLS authorization. Connect your MLS account in Settings to unlock CMA reports.'); return; }
    const address = buyerCMAAddress.trim();
    const mlsId = buyerCMAMlsId.trim();
    if (!selected || (!address && !mlsId)) {
      setAlert({ type: 'danger', message: 'Enter a property address or MLS ID' });
      return;
    }
    setBuyerCMALoading(true);
    setBuyerCMAResult(null);
    setAlert({ type: 'info', message: 'Running offer analysis... this may take up to 60 seconds' });
    try {
      let searchAddress = address;

      // If MLS ID provided, look up from our MLS database
      if (mlsId) {
        try {
          const mlsRes = await axiosInstance.get(`/sellers/mls-lookup/${mlsId}`);
          if (mlsRes.data?.success && mlsRes.data?.property) {
            const prop = mlsRes.data.property;
            searchAddress = prop.unparsed_address || `${prop.city}, AZ ${prop.postal_code}`;
            setBuyerCMAAddress(searchAddress);
          }
        } catch {
          if (!searchAddress) {
            setAlert({ type: 'danger', message: `MLS# ${mlsId} not found in our database. Try entering the address instead.` });
            setBuyerCMALoading(false);
            return;
          }
        }
      }

      if (!searchAddress) {
        setAlert({ type: 'danger', message: 'Could not determine property address' });
        setBuyerCMALoading(false);
        return;
      }

      const userId = (() => { try { return JSON.parse(atob((localStorage.getItem('auth_token') || '').split('.')[1])).id; } catch { return ''; } })();

      const { data } = await axiosInstance.post('/sellers/enhanced-cma/address-analysis', {
        address: searchAddress,
        lead_id: selected.lead_id || selected.id,
        user_id: userId,
        seller_profile_data: {},
        report_type: 'buyer_offer'
      }, { timeout: 120000 });

      // Normalize response — handle all response formats
      const normalized: any = { ...data };
      if (!normalized.analysis_results) {
        const xg = normalized.xgboost_prediction || {};
        const ui = normalized.ui_data || {};
        const comparables = ui.comparables || normalized.comparables || [];
        normalized.analysis_results = {
          xgboost_prediction: {
            predicted_price: xg.predicted_value || ui.valuation?.estimated_value || xg.predicted_price || 0,
            confidence: xg.confidence_score || ui.valuation?.confidence_score || normalized.enhanced_confidence || 0,
          },
          enhanced_confidence: xg.confidence_score || ui.valuation?.confidence_score || normalized.enhanced_confidence || 0,
          comparables: comparables,
          statistics: normalized.statistics || {
            median_price: ui.market_analysis?.average_sale_price || 0,
            median_ppsf: ui.valuation?.price_per_sqft || ui.market_analysis?.median_ppsf || 0,
            total_comps: ui.market_analysis?.total_comparables || comparables.length || 0,
          },
          property_address: normalized.analysis_summary?.address || normalized.property_address || searchAddress,
        };
      }
      // If a report was created, fetch the full report with comps
      const reportId = data.report_id;
      if (reportId && (!normalized.analysis_results?.comparables?.length)) {
        try {
          // Try multiple lead IDs — the report may be stored under id or lead_id
          const idsToTry = [selected.id, selected.lead_id].filter(Boolean);
          let reports: any[] = [];
          for (const lid of idsToTry) {
            const reportRes = await axiosInstance.get(`/sellers/${lid}/cma-reports`, {
                headers: { 'x-user-id': userId }
              });
            reports = reportRes.data?.reports || (Array.isArray(reportRes.data) ? reportRes.data : []);
            if (reports.length > 0) break;
          }
          const latestReport = reports.find((r: any) => r.id === reportId || r.report_id === reportId) || reports[0];
          if (latestReport) {
            const reportComps = latestReport.full_analysis?.comparable_properties?.comparables
              || latestReport.cmaData?.comparable_properties?.comparables
              || latestReport.comparablesData?.comparables
              || [];
            if (reportComps.length > 0) {
              normalized.analysis_results.comparables = reportComps.map((c: any) => ({
                unparsed_address: c.address || c.unparsed_address || 'Comp',
                bedrooms: c.bedrooms, bathrooms: c.bathrooms, living_area: c.living_area,
                close_price: c.close_price, list_price: c.list_price,
                year_built: c.year_built, close_date: c.close_date,
                distance_miles: c.distance_miles, price_per_sqft: c.price_per_sqft,
                mls_number: c.mls_number || c.listing_id || c.mlsNumber || '',
                city: c.city || '', state: c.state || '', zip_code: c.zip_code || '',
                days_on_market: c.days_on_market,
              }));
              normalized.analysis_results.statistics.total_comps = reportComps.length;
            }
            // Also get median price from report
            const reportStats = latestReport.full_analysis?.market_stats || latestReport.analysisData?.comp_stats || {};
            if (reportStats.median_price) normalized.analysis_results.statistics.median_price = reportStats.median_price;
            if (reportStats.median_ppsf) normalized.analysis_results.statistics.median_ppsf = reportStats.median_ppsf;

            // Store report ID for links
            normalized.report_id = reportId;
          }
        } catch { /* report fetch is supplementary */ }
      }

      setBuyerCMAResult(normalized);
      setAlert({ type: 'success', message: 'Offer analysis complete!' });

      // Activity is logged server-side by the address-analysis endpoint when it creates the CMA report
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Offer analysis failed' });
    }
    setBuyerCMALoading(false);
  };

  // Seller profile inline form
  const [showSellerForm, setShowSellerForm] = useState(false);
  const [editingSellerProfile, setEditingSellerProfile] = useState(false);
  const [spEdit, setSpEdit] = useState<any>({});
  const [savingSellerProfile, setSavingSellerProfile] = useState(false);
  const [sellerFormData, setSellerFormData] = useState({
    property_address: '', property_city: '', property_state: 'AZ', property_zip: '',
    selling_timeframe: '', reason_for_selling: '', estimated_value: '', desired_price: ''
  });

  const handleCreateSellerProfile = async () => {
    if (!selected || !sellerFormData.property_address) return;
    setSavingSellerProfile(true);
    try {
      const leadId = selected.lead_id || selected.id;
      await axiosInstance.post(`/sellers/${leadId}/profile`, {
        ...sellerFormData,
        estimated_value: sellerFormData.estimated_value ? Number(sellerFormData.estimated_value) : null,
        desired_price: sellerFormData.desired_price ? Number(sellerFormData.desired_price) : null,
      });
      setAlert({ type: 'success', message: `Seller profile created for ${getClientName(selected)}` });
      setShowSellerForm(false);
      setSellerFormData({ property_address: '', property_city: '', property_state: 'AZ', property_zip: '', selling_timeframe: '', reason_for_selling: '', estimated_value: '', desired_price: '' });
      // Refresh client detail
      selectClient(selected);
    } catch (e: any) {
      setAlert({ type: 'danger', message: e.response?.data?.error || 'Failed to create seller profile' });
    } finally { setSavingSellerProfile(false); }
  };

  const fetchSellerCMA = async (leadId: string) => {
    if (isSponsorPlan) { toast.error('MLS data access requires an active agent subscription with broker MLS authorization.'); return; }
    setLoadingCMA(true);
    try {
      // Get user ID from JWT for x-user-id header
      let userId = '';
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        if (token) userId = JSON.parse(atob(token.split('.')[1])).id;
      } catch { /* */ }

      const response = await axiosInstance.get(`/sellers/${leadId}/cma-reports`, {
        headers: { 'x-user-id': userId }
      });
      const reports = response.data?.reports || (Array.isArray(response.data) ? response.data : []);
      if (Array.isArray(reports) && reports.length > 0) {
        // Sort newest first
        const sorted = reports.sort((a: any, b: any) =>
          new Date(b.created_on || b.createdAt || 0).getTime() - new Date(a.created_on || a.createdAt || 0).getTime()
        );
        setAllCMAReports(sorted);
        setSellerCMA(sorted[0]); // Latest as default
      }
    } catch (err) {
      console.log('Could not fetch CMA reports:', err);
    }
    setLoadingCMA(false);
  };

  const handleRunCMA = async () => {
    if (isSponsorPlan) { toast.error('MLS data access requires an active agent subscription with broker MLS authorization. Connect your MLS account in Settings to unlock CMA reports.'); return; }
    if (!selected || !clientDetail?.seller_profile) return;
    const sp = clientDetail.seller_profile;
    const address = `${sp.property_address || ''}, ${sp.property_city || ''} ${sp.property_state || ''} ${sp.property_zip || ''}`.trim();
    if (!address || address === ',') {
      setAlert({ type: 'danger', message: 'No property address on seller profile' });
      return;
    }
    setRunningCMA(true);
    setAlert({ type: 'info', message: 'Running CMA analysis... this may take up to 60 seconds' });
    try {
      let userId = '';
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        if (token) userId = JSON.parse(atob(token.split('.')[1])).id;
      } catch { /* */ }

      await axiosInstance.post('/sellers/enhanced-cma/address-analysis', {
        address,
        lead_id: selected.id,
        user_id: userId,
        report_type: 'seller_cma'
      });
      setAlert({ type: 'success', message: 'CMA report generated successfully!' });
      fetchSellerCMA(selected.id);
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'CMA analysis failed' });
    }
    setRunningCMA(false);
  };

  // KOPS-645: Fetch sellers for Seller Dashboard
  const fetchSellers = async (page = 1, append = false) => {
    setSellersLoading(true);
    try {
      const params: any = { page, limit: 50 };
      if (sellersSearch) params.search = sellersSearch;
      if (sellersStageFilter && sellersStageFilter !== 'all') params.stage = sellersStageFilter;
      const response = await axiosInstance.get('/agents/sellers-dashboard', { params });
      const data = response.data?.data || [];
      if (append) {
        setSellersList(prev => [...prev, ...data]);
      } else {
        setSellersList(data);
      }
      setSellersTotal(response.data?.pagination?.total || 0);
      setSellersStats(response.data?.stats || { total: 0, readyToList: 0, needsCma: 0, avgValue: 0 });
      setSellersPage(page);
    } catch (err) {
      console.error('Failed to fetch sellers:', err);
    }
    setSellersLoading(false);
    setSellersLoaded(true);
  };

  // KOPS-684: Fetch active sellers from transactions
  const fetchActiveSellers = async () => {
    setActiveSellersLoading(true);
    try {
      const response = await axiosInstance.get('/agents/active-sellers');
      setActiveSellersList(response.data?.data || []);
      setActiveSellersStats(response.data?.stats || { total: 0, pre_contract: 0, under_contract: 0, avg_price: 0 });
    } catch (err) {
      console.error('Failed to fetch active sellers:', err);
    }
    setActiveSellersLoading(false);
    setActiveSellersLoaded(true);
  };

  // KOPS-645: Load sellers when entering sellers mode
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (pageMode === 'sellers' && !sellersLoaded && !sellersLoading) fetchSellers(); }, [pageMode, sellersLoaded]);
  // KOPS-684: Load active sellers when entering sellers mode
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (pageMode === 'sellers' && !activeSellersLoaded && !activeSellersLoading) fetchActiveSellers(); }, [pageMode, activeSellersLoaded]);

  // KOPS-662: CMA Send Modal state
  const [cmaSendModal, setCmaSendModal] = useState<any>(null); // seller object
  const [cmaSendTo, setCmaSendTo] = useState('');
  const [cmaSendCc, setCmaSendCc] = useState('');
  const [cmaSendSubject, setCmaSendSubject] = useState('');
  const [cmaSendBody, setCmaSendBody] = useState('');
  const [cmaSendFrequency, setCmaSendFrequency] = useState<'send_now' | 'monthly' | 'quarterly' | 'manual'>('send_now');
  const [cmaSendLoading, setCmaSendLoading] = useState(false);
  const [cmaSendSending, setCmaSendSending] = useState(false);
  const [cmaSendValuation, setCmaSendValuation] = useState<any>(null);

  // Open CMA Send Modal — fetch valuation + pre-fill email
  const handleSellerCma = async (seller: any) => {
    const address = `${seller.property_address || ''}, ${seller.property_city || ''} ${seller.property_state || ''} ${seller.property_zip || ''}`.trim();
    if (!address || address === ',') {
      toast.error('No property address for this seller');
      return;
    }

    // Resolve agent info
    let agentName = 'Your Agent';
    let agentSlug = '';
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        agentName = payload.name || agentName;
      }
      const slugRes = await axiosInstance.get('/portal-settings/my-slug').catch(() => ({ data: {} }));
      agentSlug = slugRes.data?.slug || '';
    } catch {}

    const clientName = (seller.lead_first_name && seller.lead_first_name !== 'Unknown')
      ? `${seller.lead_first_name} ${seller.lead_last_name || ''}`.trim()
      : '[Client Name]';
    const portalUrl = agentSlug ? `https://www.aisearchportal.com/${agentSlug}/home-value` : 'https://www.aisearchportal.com';
    const value = Number(seller.estimated_value) || 0;

    // Pre-fill modal
    setCmaSendModal(seller);
    setCmaSendTo(seller.lead_email && seller.lead_email !== 'unknown@example.com' ? seller.lead_email : '');
    setCmaSendCc('');
    setCmaSendSubject(`Your Home Value Update — ${seller.property_city || 'Market'} ${new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })}`);
    setCmaSendBody(
      `Hi ${clientName},\n\n` +
      `Here's your monthly home value update for ${seller.property_address || 'your property'}${seller.property_city ? `, ${seller.property_city}` : ''}.\n\n` +
      (value ? `Your home is currently estimated at $${value.toLocaleString()}.\n\n` : '') +
      `Market highlights for your area:\n` +
      `• Median home price: [pending valuation]\n` +
      `• Homes sold (30 days): [pending valuation]\n` +
      `• Average days on market: [pending valuation]\n\n` +
      `View your full report with comparable sales map:\n${portalUrl}\n\n` +
      `If you're thinking about selling or just curious about your options, I'm always happy to chat.\n\n` +
      `Best,\n${agentName}`
    );
    setCmaSendFrequency(seller.cma_frequency || 'send_now');
    setCmaSendValuation(null);
    setCmaSendLoading(true);

    // Fetch fresh valuation in background
    try {
      let userId = '';
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        if (token) userId = JSON.parse(atob(token.split('.')[1])).id;
      } catch {}

      const cmaRes = await axiosInstance.post('/sellers/enhanced-cma/address-analysis', {
        address,
        lead_id: seller.id,
        user_id: userId,
        report_type: 'seller_cma'
      }).catch(() => null);

      if (cmaRes?.data) {
        const d = cmaRes.data;
        const ui = d.ui_data || {};
        const val = ui.valuation || {};
        const mkt = ui.market_analysis || {};
        const estValue = val.estimated_value || d.analysis_summary?.estimated_value || value;
        const median = mkt.median_price || 0;
        const comps = mkt.total_comparables || d.analysis_summary?.comparables_found || 0;
        const ppsf = mkt.median_ppsf || 0;
        const rangeLow = val.price_range?.min || Math.round(estValue * 0.95);
        const rangeHigh = val.price_range?.max || Math.round(estValue * 1.05);
        const confidence = val.confidence_score ? Math.round(val.confidence_score * 100) : 0;
        const insights = ui.ai_insights?.recommendations || [];

        setCmaSendValuation({
          estimated_value: estValue,
          range_low: rangeLow,
          range_high: rangeHigh,
          confidence,
          median_price: median,
          median_ppsf: ppsf,
          comparables_found: comps,
          insights,
        });

        // Build rich email body with real data
        setCmaSendBody(
          `Hi ${clientName},\n\n` +
          `Here's your monthly home value update for ${seller.property_address || 'your property'}${seller.property_city ? `, ${seller.property_city}` : ''}.\n\n` +
          `Your home is currently estimated at $${Number(estValue).toLocaleString()}.\n` +
          `Estimated range: $${Number(rangeLow).toLocaleString()} — $${Number(rangeHigh).toLocaleString()}\n\n` +
          `Market highlights for your area:\n` +
          (median ? `• Median home price: $${Number(median).toLocaleString()}\n` : '') +
          (ppsf ? `• Price per square foot: $${Number(ppsf).toLocaleString()}\n` : '') +
          (comps ? `• Based on ${comps} recent comparable sales\n` : '') +
          `\n` +
          `View your full report with comparable sales map:\n${portalUrl}\n\n` +
          `If you're thinking about selling or just curious about your options, I'm always happy to chat.\n\n` +
          `Best,\n${agentName}`
        );
      }
    } catch {}
    setCmaSendLoading(false);
  };

  // Send CMA email from modal
  const handleCmaSendApprove = async () => {
    if (!cmaSendModal) return;
    setCmaSendSending(true);
    try {
      // Send email
      const toEmails = cmaSendTo.split(',').map(e => e.trim()).filter(Boolean);
      if (toEmails.length > 0) {
        await axiosInstance.post('/ai-coach/send-deal-automation', {
          dealId: cmaSendModal.id,
          taskName: 'Monthly CMA Update',
          subject: cmaSendSubject,
          body: cmaSendBody,
          toEmails,
          ccEmails: cmaSendCc.split(',').map(e => e.trim()).filter(Boolean),
        }).catch(() => {});
      }

      // Set schedule if not "send_now"
      if (cmaSendFrequency !== 'send_now') {
        await axiosInstance.put(`/agents/sellers-dashboard/${cmaSendModal.id}/cma-schedule`, {
          frequency: cmaSendFrequency,
        }).catch(() => {});
      }

      setSellerCmaStatus(prev => ({ ...prev, [cmaSendModal.id]: 'done' }));
      toast.success(`CMA sent for ${cmaSendModal.property_address}`);
      setCmaSendModal(null);
      fetchSellers();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to send CMA');
    } finally {
      setCmaSendSending(false);
    }
  };

  // KOPS-645: Bulk CMA for selected sellers
  // KOPS-539: Score all sellers (A/B/C/D grading)
  const handleScoreAllSellers = async () => {
    setScoringRunning(true);
    setScoringResults(null);
    try {
      const resp = await axiosInstance.post('/agents/sellers-dashboard/score-all');
      if (resp.data?.success) {
        setScoringResults(resp.data);
        setAlert({ type: 'success', message: `Scored ${resp.data.total} sellers — A:${resp.data.gradeCount?.A || 0} B:${resp.data.gradeCount?.B || 0} C:${resp.data.gradeCount?.C || 0} D:${resp.data.gradeCount?.D || 0}` });
        // Refresh the seller list to show updated scores
        fetchSellers(1);
      }
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.error || 'Scoring failed' });
    } finally {
      setScoringRunning(false);
    }
  };

  const handleBulkCma = async () => {
    if (selectedSellers.size === 0) return;
    setBulkCmaRunning(true);
    const toRun = sellersList.filter(s => selectedSellers.has(s.id) && s.property_address);
    for (const seller of toRun) {
      await handleSellerCma(seller);
    }
    setBulkCmaRunning(false);
    setSelectedSellers(new Set());
    fetchSellers(); // Refresh
  };

  // KOPS-645: Save recurring CMA schedule
  const handleSaveRecurringCma = async () => {
    if (!recurringCmaTarget) return;
    setRecurringCmaLoading(true);
    try {
      if (recurringCmaTarget.type === 'single') {
        await axiosInstance.put(`/agents/sellers-dashboard/${recurringCmaTarget.leadId}/cma-schedule`, {
          frequency: recurringCmaFrequency,
        });
      } else {
        await axiosInstance.put('/agents/sellers-dashboard/bulk-cma-schedule', {
          seller_ids: recurringCmaTarget.leadIds,
          frequency: recurringCmaFrequency,
        });
      }
      toast.success(recurringCmaFrequency === 'manual'
        ? 'Recurring CMA cancelled'
        : `${recurringCmaFrequency.charAt(0).toUpperCase() + recurringCmaFrequency.slice(1)} CMA scheduled`);
      setSellersLoaded(false); // trigger refetch
      setShowRecurringCmaModal(false);
      setRecurringCmaTarget(null);
      setSelectedSellers(new Set());
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update schedule');
    }
    setRecurringCmaLoading(false);
  };

  const fetchAllAutomations = async () => {
    if (!selected) return;
    try {
      const response = await axiosInstance.get(`/ai-coach/lead/${selected.id}/automations`);
      setAllAutomations(response.data?.tasks || []);
    } catch (err) {
      console.log('Could not fetch automations:', err);
    }
  };

  const handleGenerateActionPlan = async () => {
    if (!selected) return;
    setGeneratingPlan(true);
    try {
      const response = await axiosInstance.post('/ai-coach/action-plan', { leadId: selected.id });
      const plan = response.data?.plan;
      setAlert({ type: 'success', message: `Action plan created: ${plan?.totalSteps || 0} steps scheduled for ${plan?.leadName}` });
      fetchAllAutomations();
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to generate action plan' });
    }
    setGeneratingPlan(false);
  };

  const handleApproveTask = async (taskId: string) => {
    try {
      await axiosInstance.post(`/ai-coach/upcoming/${taskId}/approve`);
      setAlert({ type: 'success', message: 'Task approved — will execute shortly' });
      if (selected) fetchAiHistory(selected.id);
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to approve task' });
    }
  };

  const handleCancelTask = async (taskId: string) => {
    try {
      await axiosInstance.post(`/ai-coach/upcoming/${taskId}/cancel`, { reason: cancelReason });
      setAlert({ type: 'success', message: 'Task cancelled' });
      setCancellingId(null);
      setCancelReason('');
      if (selected) fetchAiHistory(selected.id);
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to cancel task' });
    }
  };

  const handleRescheduleTask = async (taskId: string) => {
    const scheduledFor = new Date(Date.now() + parseInt(rescheduleDays || '3') * 24 * 60 * 60 * 1000).toISOString();
    try {
      await axiosInstance.post(`/ai-coach/upcoming/${taskId}/reschedule`, { scheduledFor });
      setAlert({ type: 'success', message: `Rescheduled to ${rescheduleDays} days from now` });
      setReschedulingId(null);
      setRescheduleDays('3');
      if (selected) fetchAiHistory(selected.id);
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to reschedule' });
    }
  };

  const handleDismissDraft = async (queueId: string) => {
    try {
      await axiosInstance.post(`/ai-coach/queue/${queueId}/dismiss`);
    } catch (err) { /* non-critical */ }
  };

  const handleCompleteRecommendation = async (queueId: string) => {
    try {
      await axiosInstance.post(`/ai-coach/queue/${queueId}/complete`, { notes: completeNotes });
      setAlert({ type: 'success', message: 'Marked as completed' });
      setCompletingId(null);
      setCompleteNotes('');
      if (selected) fetchAiHistory(selected.id);
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to update' });
    }
  };

  const handleGetRecommendation = async () => {
    if (!selected) return;
    setLoadingRecommendation(true);
    setAiDraft(null);
    try {
      const response = await axiosInstance.get(`/ai-coach/recommend/${selected.id}`);
      setAiRecommendation(response.data?.recommendation || null);
      // Refresh history to include the new recommendation
      fetchAiHistory(selected.id);
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to get AI recommendation' });
    }
    setLoadingRecommendation(false);
  };

  const handleGenerateCoachDraft = async (instructionOrReason?: string) => {
    if (!selected) return;
    setLoadingDraft(true);
    try {
      const response = await axiosInstance.post('/ai-coach/draft-followup', {
        leadId: selected.id,
        reason: aiRecommendation?.reason,
        instruction: instructionOrReason || undefined,
      });
      const draft = response.data?.draft;
      if (draft) {
        setAiDraft(draft);
        setAiEditSubject(draft.subject);
        setAiEditBody(draft.body);
      }
      // Refresh activity history to show the new draft + audit
      if (selected) fetchAiHistory(selected.id);
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to generate draft' });
    }
    setLoadingDraft(false);
  };

  const handleGenerateSellerUpdate = async () => {
    if (!selected) return;
    setLoadingDraft(true);
    try {
      const response = await axiosInstance.post('/ai-coach/draft-seller-update', {
        leadId: selected.id
      });
      const draft = response.data?.draft;
      if (draft) {
        setAiDraft(draft);
        setAiEditSubject(draft.subject);
        setAiEditBody(draft.body);
      }
      if (selected) fetchAiHistory(selected.id);
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to generate seller update' });
    }
    setLoadingDraft(false);
  };

  const handleSendCoachDraft = async () => {
    if (!aiDraft?.queueId) return;
    setSendingAIDraft(true);
    try {
      await axiosInstance.post(`/ai-coach/queue/${aiDraft.queueId}/send`, {
        fromAccountId: selectedFromAccount || undefined,
        editedSubject: editingAIDraft ? aiEditSubject : undefined,
        editedBody: editingAIDraft ? aiEditBody : undefined
      });
      setAlert({ type: 'success', message: `Email sent to ${getClientName(selected)}` });
      setAiDraft(null);
      setEditingAIDraft(false);
      fetchEmailHistory(selected!.id);
      if (selected) fetchAiHistory(selected.id);
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to send' });
    }
    setSendingAIDraft(false);
  };

  const handleAIDraft = async () => {
    if (!selected) return;
    setGeneratingAIDraft(true);
    try {
      const response = await axiosInstance.post('/ai-coach/draft-followup', {
        leadId: selected.id
      });
      const draft = response.data?.draft;
      if (draft) {
        setEmailSubject(draft.subject);
        setEmailBody(draft.body);
        setAlert({ type: 'success', message: 'Jasper drafted a follow-up — review and send below' });
      }
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to generate AI draft' });
    }
    setGeneratingAIDraft(false);
  };

  const handleSendEmail = async () => {
    if (!selected || !emailSubject || !emailBody) return;
    setSendingEmail(true);
    try {
      const toList = emailTo.length > 0 ? emailTo : [selected.lead_email];
      const toAddr = toList.join(',');

      // Use FormData if attachments exist
      if (emailAttachments.length > 0) {
        const formData = new FormData();
        formData.append('subject', emailSubject);
        formData.append('message', emailBody);
        formData.append('to', toAddr);
        if (emailCc.length > 0) formData.append('cc', emailCc.join(','));
        if (emailBcc.length > 0) formData.append('bcc', emailBcc.join(','));
        if (selectedFromAccount) formData.append('fromAccountId', selectedFromAccount);
        if (replyToEmail?.gmail_thread_id) formData.append('threadId', replyToEmail.gmail_thread_id);
        if (replyToEmail?.gmail_message_id) formData.append('inReplyTo', replyToEmail.gmail_message_id);
        emailAttachments.forEach(file => formData.append('attachments', file));

        await axiosInstance.post(`/agents/clients/${selected.id}/email`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axiosInstance.post(`/agents/clients/${selected.id}/email`, {
          subject: emailSubject,
          message: emailBody,
          to: toAddr,
          cc: emailCc.length > 0 ? emailCc.join(',') : undefined,
          bcc: emailBcc.length > 0 ? emailBcc.join(',') : undefined,
          fromAccountId: selectedFromAccount || undefined,
          threadId: replyToEmail?.gmail_thread_id || undefined,
          inReplyTo: replyToEmail?.gmail_message_id || undefined
        });
      }

      setAlert({ type: 'success', message: `Email sent to ${toAddr}` });
      setEmailSubject('');
      setEmailBody('');
      setEmailTo([]);
    setEmailToInput('');
      setEmailCc([]);
      setEmailCcInput('');
      setEmailBcc([]);
      setEmailBccInput('');
      setShowCcBcc(false);
      setEmailAttachments([]);
      setReplyToEmail(null);
      fetchEmailHistory(selected.id);
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to send email' });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);
    try {
      await agentPortalService.inviteClient(inviteData);
      setAlert({ type: 'success', message: `Invitation sent to ${inviteData.email}` });
      setInviteData({ firstName: '', lastName: '', email: '' });
      setShowInvite(false);
      fetchClients();
    } catch (err: any) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to invite' });
    } finally {
      setInviting(false);
    }
  };

  const getClientName = (c: any) => `${c.lead_first_name || c.first_name || ''} ${c.lead_last_name || c.last_name || ''}`.trim() || c.lead_email || c.email || 'Unknown';
  const getClientEmail = (c: any) => c.lead_email || c.email || '';
  const getClientPhone = (c: any) => c.lead_phone || c.phone || '';
  const getProfileType = (c: any) => {
    if (c.has_buyer_profile && c.has_seller_profile) return 'Both';
    if (c.has_buyer_profile) return 'Buyer';
    if (c.has_seller_profile) return 'Seller';
    return 'Lead';
  };
  const getProfileColor = (type: string) => type === 'Buyer' ? colors.blue : type === 'Seller' ? colors.green : type === 'Both' ? colors.gold : colors.muted;

  const formatPrice = (val: any) => {
    if (!val) return '—';
    const num = typeof val === 'object' && val.d ? val.d[0] : Number(val);
    return num ? `$${num.toLocaleString()}` : '—';
  };

  return (
    <div style={{ background: colors.navy, minHeight: '100vh', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Nav */}
      <nav style={{ background: colors.navy3, borderBottom: `2px solid ${colors.gold}`, padding: '12px 0' }}>
        <Container fluid className="px-4 d-flex justify-content-between align-items-center">
          <div>
            <a href="/agent-portal/dashboard" style={{ textDecoration: 'none' }}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px', color: colors.white }}>
                {isSponsorPlan || window.location.pathname.includes('lender-portal') ? <>AI <span style={{ color: colors.gold2 }}>Broker</span> Portal</> : <>AI<span style={{ color: colors.gold2 }}>Agent</span>Portal</>}
              </span>
            </a>
            <div style={{ display: 'inline-flex', marginLeft: '16px', background: colors.navy, borderRadius: '6px', overflow: 'hidden', border: `1px solid ${colors.navy3}` }}>
              {!isBrokerComplianceOnly && (
              <button onClick={() => { setPageMode('clients'); window.history.replaceState({}, '', window.location.pathname); }}
                style={{ background: pageMode === 'clients' ? colors.gold : 'transparent', color: pageMode === 'clients' ? colors.navy : colors.muted, border: 'none', padding: '5px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.5px' }}>
                Clients
              </button>
              )}
              {!isSponsorPlan && !isBrokerComplianceOnly && (
              <button onClick={() => { setPageMode('deal-board'); window.history.replaceState({}, '', `${window.location.pathname}?mode=deal-board`); }}
                style={{ background: pageMode === 'deal-board' ? colors.gold : 'transparent', color: pageMode === 'deal-board' ? colors.navy : colors.muted, border: 'none', padding: '5px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.5px' }}>
                Deal Board
              </button>
              )}
              {hasBrokerage && (
              <button onClick={() => { setPageMode('deals'); window.history.replaceState({}, '', `${window.location.pathname}?mode=deals`); }}
                style={{ background: pageMode === 'deals' ? colors.gold : 'transparent', color: pageMode === 'deals' ? colors.navy : colors.muted, border: 'none', padding: '5px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.5px' }}>
                Deals
              </button>
              )}
              {isSponsorPlan && (
              <button onClick={() => { setPageMode('pipeline'); window.history.replaceState({}, '', `${window.location.pathname}?mode=pipeline`); }}
                style={{ background: pageMode === 'pipeline' ? colors.gold : 'transparent', color: pageMode === 'pipeline' ? colors.navy : colors.muted, border: 'none', padding: '5px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.5px' }}>
                Pipeline
              </button>
              )}
              {!isBrokerComplianceOnly && (
              <button onClick={() => { setPageMode('automations'); window.history.replaceState({}, '', `${window.location.pathname}?mode=automations`); }}
                style={{ background: pageMode === 'automations' ? colors.gold : 'transparent', color: pageMode === 'automations' ? colors.navy : colors.muted, border: 'none', padding: '5px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.5px' }}>
                Automations
              </button>
              )}
              {!isSponsorPlan && (
              <button onClick={() => { setPageMode('sellers'); window.history.replaceState({}, '', `${window.location.pathname}?mode=sellers`); }}
                style={{ background: pageMode === 'sellers' ? colors.gold : 'transparent', color: pageMode === 'sellers' ? colors.navy : colors.muted, border: 'none', padding: '5px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.5px' }}>
                Sellers
              </button>
              )}
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <a href="/agent-portal/dashboard" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Dashboard</a>
            <button
              onClick={() => {
                const token = localStorage.getItem('auth_token') || '';
                const slug = (window as any).__agentPortalSlug || localStorage.getItem('agent_portal_slug') || '';
                const isDevEnv = window.location.hostname.includes('dev.') || window.location.hostname === 'localhost';
                const portalBase = isDevEnv ? 'https://dev.aisearchportal.com' : 'https://www.aisearchportal.com';
                if (!slug) {
                  fetch('/api/portal-settings/my-slug', { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.ok ? r.json() : null)
                    .then(d => {
                      const s = d?.slug || '';
                      if (s) localStorage.setItem('agent_portal_slug', s);
                      window.open(`${portalBase}/${s}/agent-dashboard?token=${token}`, '_blank');
                    });
                  return;
                }
                window.open(`${portalBase}/${slug}/agent-dashboard?token=${token}`, '_blank');
              }}
              style={{ background: `linear-gradient(135deg, #1F3864 0%, #2a4a7f 100%)`, border: 'none', color: 'white', fontSize: '12px', fontWeight: 600, padding: '5px 12px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <FontAwesomeIcon icon={faRocket} /> AI Search Portal
            </button>
            {!isLender && (
              <Button size="sm" onClick={() => setShowInvite(!showInvite)}
                style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontSize: '12px', fontWeight: 600 }}>
                <FontAwesomeIcon icon={faUserPlus} className="me-1" /> Invite Client
              </Button>
            )}
          </div>
        </Container>
      </nav>

      {alert && (
        <div style={{ padding: '0 24px', paddingTop: '12px' }}>
          <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>{alert.message}</Alert>
        </div>
      )}

      {/* Invite Form */}
      {showInvite && (
        <div style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '16px 24px' }}>
          <form onSubmit={handleInvite} className="d-flex gap-2 align-items-center">
            <input required placeholder="First Name" value={inviteData.firstName} onChange={e => setInviteData({ ...inviteData, firstName: e.target.value })}
              style={{ background: colors.navy3, border: 'none', borderRadius: '6px', padding: '8px 12px', color: colors.white, fontSize: '13px', outline: 'none', width: '150px' }} />
            <input required placeholder="Last Name" value={inviteData.lastName} onChange={e => setInviteData({ ...inviteData, lastName: e.target.value })}
              style={{ background: colors.navy3, border: 'none', borderRadius: '6px', padding: '8px 12px', color: colors.white, fontSize: '13px', outline: 'none', width: '150px' }} />
            <input required type="email" placeholder="Email" value={inviteData.email} onChange={e => setInviteData({ ...inviteData, email: e.target.value })}
              style={{ background: colors.navy3, border: 'none', borderRadius: '6px', padding: '8px 12px', color: colors.white, fontSize: '13px', outline: 'none', width: '250px' }} />
            <Button type="submit" size="sm" disabled={inviting} style={{ background: colors.gold, border: 'none', fontWeight: 600 }}>
              {inviting ? <Spinner size="sm" /> : 'Send Invite'}
            </Button>
            <Button size="sm" variant="outline-light" onClick={() => setShowInvite(false)} style={{ borderColor: colors.muted, color: colors.muted }}>Cancel</Button>
          </form>
        </div>
      )}

      {/* ======== DEALS MODE ======== */}
      {pageMode === 'deals' && (
        <>
          <div style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '12px 24px', display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faHome} style={{ color: colors.gold }} />
              <span style={{ color: colors.white, fontWeight: 700, fontSize: '18px' }}>{deals.length}</span>
              <span style={{ color: colors.muted, fontSize: '12px' }}>Active Deals</span>
            </div>
            <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
              {['all', 'pre_contract', 'showing', 'under_contract', 'closing', 'completed'].map((s) => (
                <button key={s} onClick={() => setDealStageFilter(s)}
                  style={{ background: dealStageFilter === s ? colors.gold : colors.navy3, color: dealStageFilter === s ? colors.navy : colors.muted, border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '10px', fontWeight: 600, cursor: 'pointer', textTransform: 'uppercase' }}>
                  {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', height: 'calc(100vh - 145px)' }}>
            {/* Deals left panel */}
            <div style={{ width: panelCollapsed ? '0px' : '400px', minWidth: panelCollapsed ? '0px' : '400px', background: colors.navy2, borderRight: panelCollapsed ? 'none' : `1px solid ${colors.navy3}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'width 0.2s ease, min-width 0.2s ease' }}>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${colors.navy3}`, display: 'flex', gap: '8px' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '10px', top: '9px', fontSize: '11px', color: colors.muted }} />
                  <input value={dealSearch} onChange={(e) => setDealSearch(e.target.value)} placeholder="Search deals…"
                    style={{ width: '100%', background: colors.navy3, color: colors.white, border: 'none', borderRadius: '6px', padding: '8px 10px 8px 30px', fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <Button size="sm" style={{ background: colors.gold, border: 'none', fontWeight: 700, fontSize: '11px', whiteSpace: 'nowrap' }}
                  onClick={async () => {
                    try {
                      const res = await axiosInstance.post('/transactions', { type: 'purchase', client_type: 'buyer', client_name: 'New Deal' });
                      if (res.data?.success) { setAlert({ type: 'success', message: 'Deal created!' }); fetchDeals(); }
                    } catch (err: any) { setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed' }); }
                  }}>
                  + New Deal
                </Button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {dealsLoading ? (
                  <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>Loading deals…</div>
                ) : deals.filter(d => !dealSearch || (d.client_name || '').toLowerCase().includes(dealSearch.toLowerCase()) || (d.file_number || '').includes(dealSearch)).length === 0 ? (
                  <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>No deals found</div>
                ) : (
                  deals.filter(d => !dealSearch || (d.client_name || '').toLowerCase().includes(dealSearch.toLowerCase()) || (d.file_number || '').includes(dealSearch)).map((d: any) => {
                    const isSel = selectedDeal?.id === d.id;
                    const pd = typeof d.property_data === 'string' ? (() => { try { return JSON.parse(d.property_data); } catch { return null; } })() : d.property_data;
                    const addr = pd?.address || '';
                    const stageColors: Record<string, string> = { pre_contract: colors.muted, showing: colors.blue, under_contract: colors.gold, closing: colors.green, completed: colors.green, cancelled: colors.red };
                    return (
                      <div key={d.id} onClick={() => { setSelectedDeal(d); setDealTab('overview'); fetchDealDetail(d.id); window.history.replaceState({}, '', `${window.location.pathname}?mode=deals&dealId=${d.id}`); }}
                        style={{ padding: '14px 16px', cursor: 'pointer', borderBottom: `1px solid ${colors.navy3}`, borderLeft: isSel ? `3px solid ${colors.gold}` : '3px solid transparent', background: isSel ? colors.navy3 : 'transparent' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: colors.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
                            {addr || d.client_name || d.file_number || 'Untitled'}
                          </div>
                          <span style={{ fontSize: '9px', fontWeight: 700, padding: '3px 8px', borderRadius: '4px', background: `${stageColors[d.stage] || colors.muted}22`, color: stageColors[d.stage] || colors.muted, textTransform: 'uppercase' }}>
                            {(d.stage || 'pre_contract').replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div style={{ fontSize: '11px', color: colors.muted, marginTop: '3px' }}>
                          {d.client_name || '—'} · {d.type?.replace(/_/g, ' ')} {d.file_number ? `· #${d.file_number}` : ''}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            {/* Collapse toggle for the deals list panel */}
            <div
              onClick={() => setPanelCollapsed(!panelCollapsed)}
              style={{ width: '24px', minWidth: '24px', background: colors.navy2, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRight: `1px solid ${colors.navy3}`, transition: 'background 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = colors.navy3; }}
              onMouseLeave={e => { e.currentTarget.style.background = colors.navy2; }}
              title={panelCollapsed ? 'Show deals list' : 'Hide deals list'}
            >
              <span style={{ color: colors.muted, fontSize: '14px', fontWeight: 700, userSelect: 'none' }}>
                {panelCollapsed ? '›' : '‹'}
              </span>
            </div>

            {/* Deals right panel */}
            <div className="deal-theme" style={{ flex: 1, overflowY: 'auto', background: colors.offBg }}>
              {selectedDeal && dealDetail ? (
                /* KOPS-654: center the deal detail in a ~1150px column so it doesn't stretch
                   edge-to-edge on wide screens (document rows were too spread out). */
                <div style={{ padding: '24px', maxWidth: 1150, margin: '0 auto' }}>
                  <div style={{ background: '#f8f9fa', borderLeft: `4px solid ${colors.gold}`, borderRadius: '8px', padding: '20px 24px', marginBottom: '14px' }}>
                    <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '26px', color: colors.navy, margin: '0 0 6px' }}>
                      {(() => { const pd = typeof dealDetail.property_data === 'string' ? (() => { try { return JSON.parse(dealDetail.property_data); } catch { return null; } })() : dealDetail.property_data; return pd?.address || dealDetail.client_name || 'Deal'; })()}
                    </h2>
                    <div style={{ fontSize: '12px', color: colors.muted, display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {dealDetail.client_name && <span><FontAwesomeIcon icon={faUser} style={{ marginRight: '4px' }} />{dealDetail.client_name}</span>}
                      {dealDetail.file_number && <span>#{dealDetail.file_number}</span>}
                      <span style={{ textTransform: 'capitalize' }}>{dealDetail.type?.replace(/_/g, ' ')} · {dealDetail.client_type}</span>
                    </div>
                    {/* Stage workflow buttons */}
                    <div style={{ marginTop: '12px', display: 'flex', gap: 0, borderRadius: '6px', overflow: 'hidden', border: '1px solid #dee2e6' }}>
                      {(['pre_contract', 'showing', 'under_contract', 'closing', 'completed'] as const).map((s, i, arr) => {
                        const stageLabelsMap: Record<string, string> = { pre_contract: 'Pre-Contract', showing: 'Showing', under_contract: 'Under Contract', closing: 'Closing', completed: 'Completed' };
                        const currentIdx = arr.indexOf(dealDetail.stage || 'pre_contract');
                        const isActive = dealDetail.stage === s;
                        const isPast = i < currentIdx;
                        return (
                          <button key={s} disabled={changingStage}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (s === dealDetail.stage) return;
                              setChangingStage(true);
                              try {
                                await axiosInstance.put(`/transactions/${dealDetail.id}/stage`, { stage: s });
                                toast.success(`Stage → ${stageLabelsMap[s]}`);
                                fetchDeals();
                                fetchDealDetail(dealDetail.id);
                              } catch (err: any) {
                                toast.error(err?.response?.data?.error || 'Failed to change stage');
                              } finally { setChangingStage(false); }
                            }}
                            style={{
                              flex: 1, padding: '8px 4px', border: 'none', cursor: changingStage ? 'wait' : 'pointer',
                              background: isActive ? colors.gold : isPast ? `${colors.green}15` : '#f8f9fa',
                              color: isActive ? 'white' : isPast ? colors.green : colors.muted,
                              fontWeight: isActive ? 700 : 500, fontSize: '10px',
                              borderRight: i < arr.length - 1 ? '1px solid #dee2e6' : 'none',
                              transition: 'all 0.15s ease', textTransform: 'uppercase', letterSpacing: '0.3px',
                            }}>
                            {isPast && <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '3px', fontSize: '9px' }} />}
                            {stageLabelsMap[s]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Detail tabs */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0, borderBottom: '2px solid #dee2e6', marginBottom: '14px' }}>
                    {([
                      { key: 'overview', label: 'Overview' },
                      { key: 'documents', label: `Documents (${dealDocuments.length})` },
                      { key: 'compliance', label: 'Compliance' },
                      { key: 'parties', label: `Parties (${dealParties.length})` },
                      ...(dealDetail?.type === 'rental' || dealDetail?.type === 'lease' ? [{ key: 'invoice' as const, label: 'Invoice' }] : []),
                      { key: 'commission', label: 'Commission' },
                      { key: 'email', label: 'Email' },
                      { key: 'ai', label: 'AI Coach' },
                      { key: 'automations', label: 'Automations' },
                      { key: 'notes', label: 'Notes' },
                    ] as { key: string; label: string }[]).map((t) => (
                      <button key={t.key} onClick={() => {
                        setDealTab(t.key as any);
                        // Fetch emails when email tab is clicked
                        if (t.key === 'email' && dealDetail?.id) {
                          setDealEmailsLoading(true);
                          // Get party emails to filter by
                          const partyEmails = dealParties.map((p: any) => p.email).filter(Boolean);
                          axiosInstance.get(`/transactions/${dealDetail.id}/activity`)
                            .then(res => {
                              // Also try to fetch emails from connected accounts filtered by deal parties
                              const activities = res.data?.activities || res.data?.activity || [];
                              const emailActivities = activities.filter((a: any) => a.activity_type?.includes('email') || a.description?.includes('email'));
                              setDealEmails(emailActivities);
                            })
                            .catch(() => setDealEmails([]))
                            .finally(() => setDealEmailsLoading(false));
                        }
                      }}
                        style={{ background: dealTab === t.key ? 'rgba(201,149,42,0.10)' : 'none', border: 'none', borderBottom: `3px solid ${dealTab === t.key ? colors.gold : 'transparent'}`, color: dealTab === t.key ? colors.navy : '#5b6b82', fontWeight: dealTab === t.key ? 800 : 600, fontSize: '15px', padding: '12px 18px', cursor: 'pointer', marginBottom: '-2px', fontFamily: 'inherit', letterSpacing: '0.3px', whiteSpace: 'nowrap', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  {/* ---- OVERVIEW TAB ---- */}
                  {dealTab === 'overview' && <DealBrief dealId={dealDetail.id} />}
                  {dealTab === 'overview' && dealDetail.type === 'listing' && (
                    /* KOPS-711: public offer link — LISTINGS only (buyer's agents submit offers onto this deal) */
                    <div className="d-flex align-items-center justify-content-between p-3 mb-3 rounded"
                      style={{ background: '#fff', border: '1px solid #dee2e6', borderLeft: '4px solid #C9952A' }}>
                      <div>
                        <strong style={{ fontSize: 14 }}>Public Offer Link</strong>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Share with buyer's agents — their offer packages land on this deal automatically, time-stamped, with the listing agent notified.
                        </div>
                      </div>
                      <OfferIntakeLinkButton transactionId={dealDetail.id} />
                    </div>
                  )}
                  {dealTab === 'overview' && <ClientPortalInvite dealId={dealDetail.id} parties={dealParties} clientName={dealDetail.client_name} dealType={dealDetail.type} />}
                  {dealTab === 'overview' && (() => {
                    const propData = dealDetail.property_data || dealDetail.property || {};
                    const propertyAddress = dealDetail.property_address || propData.address || propData.street_address || '';
                    const keyDates: { label: string; date: string | null; color: string; field: string }[] = [
                      { label: 'Inspection', date: dealDetail.inspection_date || propData.inspection_date || null, color: colors.blue, field: 'inspection_date' },
                      { label: 'Appraisal', date: dealDetail.appraisal_date || propData.appraisal_date || null, color: colors.orange, field: 'appraisal_date' },
                      { label: 'Loan Contingency', date: dealDetail.loan_contingency_date || propData.loan_contingency_date || null, color: '#9b59b6', field: 'loan_contingency_date' },
                      { label: 'Closing', date: dealDetail.closing_date || propData.closing_date || dealDetail.close_date || null, color: colors.green, field: 'estimated_closing_date' },
                    ];
                    const getDaysLeft = (d: string | null) => {
                      if (!d) return null;
                      return Math.ceil((new Date(d).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                    };
                    const nextDeadline = keyDates.filter(kd => kd.date && (getDaysLeft(kd.date) ?? 999) > 0).sort((a, b) => (getDaysLeft(a.date) ?? 999) - (getDaysLeft(b.date) ?? 999))[0];
                    const stages = ['pre_contract', 'showing', 'under_contract', 'closing', 'completed'];
                    const stageLabels: Record<string, string> = { pre_contract: 'Pre-Contract', showing: 'Showing', under_contract: 'Under Contract', closing: 'Closing', completed: 'Completed' };
                    const stageIdx = stages.indexOf(dealDetail.stage || 'pre_contract');

                    const startEdit = () => {
                      const pd = dealDetail.property_data || {};
                      setDealEditForm({
                        type: dealDetail.type || 'purchase',
                        agent_role: dealDetail.agent_role || 'buyer_agent',
                        stage: dealDetail.stage || 'pre_contract',
                        client_type: dealDetail.client_type || 'buyer',
                        client_name: dealDetail.client_name || '',
                        client_email: dealDetail.client_email || '',
                        property_type: (dealDetail.property_data || {}).property_type || 'residential',
                        loan_type: (dealDetail.property_data || {}).loan_type || 'conventional',
                        water_source: (dealDetail.property_data || {}).water_source || 'city',
                        flood_zone: (dealDetail.property_data || {}).flood_zone || '',
                        flood_insurance: (dealDetail.property_data || {}).flood_insurance || false,
                        notes: dealDetail.notes || '',
                        address: pd.address || '',
                        city: pd.city || '',
                        state: pd.state || '',
                        zip: pd.zip || '',
                        price: pd.price || '',
                        inspection_date: pd.inspection_date || dealDetail.inspection_date || '',
                        appraisal_date: pd.appraisal_date || dealDetail.appraisal_date || '',
                        loan_contingency_date: pd.loan_contingency_date || dealDetail.loan_contingency_date || '',
                        estimated_closing_date: pd.estimated_closing_date || dealDetail.closing_date || dealDetail.close_date || '',
                        // Property characteristics
                        year_built: pd.year_built || '',
                        pool: pd.pool || false,
                        hoa: pd.hoa || false,
                        solar: pd.solar || false,
                        septic: pd.septic || false,
                        well: pd.well || false,
                        short_sale: pd.short_sale || false,
                        investment_property: pd.investment_property || false,
                      });
                      setEditingDeal(true);
                    };

                    const saveDealEdit = async () => {
                      setSavingDealEdit(true);
                      try {
                        await axiosInstance.put(`/transactions/${dealDetail.id}`, {
                          type: dealEditForm.type,
                          agent_role: dealEditForm.agent_role,
                          stage: dealEditForm.stage,
                          client_name: dealEditForm.client_name,
                          notes: dealEditForm.notes,
                          client_email: dealEditForm.client_email || null,
                          property_data: {
                            ...(dealDetail.property_data || {}),
                            address: dealEditForm.address,
                            city: dealEditForm.city,
                            state: dealEditForm.state,
                            zip: dealEditForm.zip,
                            price: dealEditForm.price,
                            inspection_date: dealEditForm.inspection_date || null,
                            appraisal_date: dealEditForm.appraisal_date || null,
                            loan_contingency_date: dealEditForm.loan_contingency_date || null,
                            estimated_closing_date: dealEditForm.estimated_closing_date || null,
                            // Property characteristics
                            property_type: dealEditForm.property_type || 'residential',
                            loan_type: dealEditForm.loan_type || 'conventional',
                            water_source: dealEditForm.water_source || 'city',
                            flood_zone: dealEditForm.flood_zone || '',
                            flood_insurance: dealEditForm.flood_insurance || false,
                            year_built: dealEditForm.year_built ? parseInt(dealEditForm.year_built) : null,
                            pool: dealEditForm.pool || false,
                            hoa: dealEditForm.hoa || false,
                            solar: dealEditForm.solar || false,
                            septic: dealEditForm.septic || false,
                            well: dealEditForm.well || false,
                            short_sale: dealEditForm.short_sale || false,
                            investment_property: dealEditForm.investment_property || false,
                          },
                        });
                        toast.success('Deal updated');
                        setEditingDeal(false);
                        fetchDealDetail(dealDetail.id);
                        fetchDeals();
                      } catch (err: any) {
                        toast.error(err?.response?.data?.error || 'Failed to update deal');
                      } finally { setSavingDealEdit(false); }
                    };

                    const changeStage = async (newStage: string) => {
                      if (newStage === dealDetail.stage) return;
                      setChangingStage(true);
                      try {
                        await axiosInstance.put(`/transactions/${dealDetail.id}/stage`, { stage: newStage });
                        toast.success(`Stage changed to ${stageLabels[newStage]}`);
                        fetchDealDetail(dealDetail.id);
                        fetchDeals();
                      } catch (err: any) {
                        toast.error(err?.response?.data?.error || 'Failed to change stage');
                      } finally { setChangingStage(false); }
                    };

                    const inputStyle = { background: 'white', border: '1px solid #ddd', borderRadius: '6px', padding: '8px 10px', fontSize: '12px', outline: 'none', width: '100%', fontFamily: 'inherit' };

                    return (
                    <div style={{ background: 'white', border: '1px solid #dee2e6', borderLeft: `4px solid ${colors.gold}`, borderRadius: '10px', padding: '18px' }}>

                      {/* Property address card + Edit button */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                        {propertyAddress ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.navy2} 100%)`, borderRadius: '8px', flex: 1 }}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: colors.gold, fontSize: '18px' }} />
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: 700, color: 'white' }}>{propertyAddress}</div>
                              {(dealDetail.price || propData.price) && (
                                <div style={{ fontSize: '12px', color: colors.gold2, marginTop: '2px' }}>
                                  <FontAwesomeIcon icon={faDollarSign} style={{ marginRight: '3px', fontSize: '10px' }} />
                                  {Number(dealDetail.price || propData.price).toLocaleString()}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div style={{ flex: 1, padding: '12px 14px', background: '#f8f9fa', borderRadius: '8px', color: '#999', fontSize: '12px' }}>
                            No property address set — click Edit to add
                          </div>
                        )}
                        <Button size="sm" style={{ background: editingDeal ? colors.red : colors.navy, color: '#FFFFFF', border: 'none', fontSize: '11px', fontWeight: 600, marginLeft: '8px', padding: '8px 14px' }}
                          onClick={() => editingDeal ? setEditingDeal(false) : startEdit()}>
                          <FontAwesomeIcon icon={faEdit} style={{ marginRight: '4px' }} />
                          {editingDeal ? 'Cancel' : 'Edit'}
                        </Button>
                      </div>

                      {/* Edit Form */}
                      {editingDeal ? (
                        <div style={{ padding: '16px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '14px', border: '1px solid #eee' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', marginBottom: '12px' }}>Edit Deal Details</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                            <div>
                              <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Client Name</label>
                              <input value={dealEditForm.client_name} onChange={e => setDealEditForm({ ...dealEditForm, client_name: e.target.value })} style={inputStyle} />
                            </div>
                            <div>
                              <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Client Email</label>
                              <input type="email" placeholder="client@email.com" value={dealEditForm.client_email} onChange={e => setDealEditForm({ ...dealEditForm, client_email: e.target.value })} style={inputStyle} />
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                            <div>
                              <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Type</label>
                              <select value={dealEditForm.type} onChange={e => setDealEditForm({ ...dealEditForm, type: e.target.value })} style={inputStyle}>
                                <option value="purchase">Purchase</option>
                                <option value="listing">Listing</option>
                                <option value="rental">Rental</option>
                                <option value="referral">Referral</option>
                              </select>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                            <div>
                              <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Your Role</label>
                              <select value={dealEditForm.agent_role} onChange={e => setDealEditForm({ ...dealEditForm, agent_role: e.target.value })} style={inputStyle}>
                                <option value="buyer_agent">Buyer&apos;s Agent</option>
                                <option value="seller_agent">Seller&apos;s Agent</option>
                                <option value="dual_agent">Dual Agent</option>
                                <option value="listing_agent">Listing Agent</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Stage</label>
                              <select value={dealEditForm.stage} onChange={e => setDealEditForm({ ...dealEditForm, stage: e.target.value })} style={inputStyle}>
                                <option value="pre_contract">Pre-Contract</option>
                                <option value="showing">Showing</option>
                                <option value="under_contract">Under Contract</option>
                                <option value="closing">Closing</option>
                                <option value="completed">Completed</option>
                              </select>
                            </div>
                          </div>
                          <div style={{ fontSize: '10px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', marginBottom: '6px', marginTop: '8px' }}>Property</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                            <input placeholder="Address" value={dealEditForm.address} onChange={e => setDealEditForm({ ...dealEditForm, address: e.target.value })} style={inputStyle} />
                            <input placeholder="City" value={dealEditForm.city} onChange={e => setDealEditForm({ ...dealEditForm, city: e.target.value })} style={inputStyle} />
                            <input placeholder="State" value={dealEditForm.state} onChange={e => setDealEditForm({ ...dealEditForm, state: e.target.value })} style={inputStyle} />
                            <input placeholder="Zip" value={dealEditForm.zip} onChange={e => setDealEditForm({ ...dealEditForm, zip: e.target.value })} style={inputStyle} />
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', marginBottom: '10px' }}>
                            <div>
                              <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Price</label>
                              <input type="number" placeholder="450000" value={dealEditForm.price} onChange={e => setDealEditForm({ ...dealEditForm, price: e.target.value })} style={inputStyle} />
                            </div>
                          </div>
                          <div style={{ fontSize: '10px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', marginBottom: '6px', marginTop: '8px' }}>Key Dates</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                            <div>
                              <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Inspection Date</label>
                              <input type="date" value={dealEditForm.inspection_date?.split('T')[0] || ''} onChange={e => setDealEditForm({ ...dealEditForm, inspection_date: e.target.value })} style={inputStyle} />
                            </div>
                            <div>
                              <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Appraisal Date</label>
                              <input type="date" value={dealEditForm.appraisal_date?.split('T')[0] || ''} onChange={e => setDealEditForm({ ...dealEditForm, appraisal_date: e.target.value })} style={inputStyle} />
                            </div>
                            <div>
                              <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Loan Contingency</label>
                              <input type="date" value={dealEditForm.loan_contingency_date?.split('T')[0] || ''} onChange={e => setDealEditForm({ ...dealEditForm, loan_contingency_date: e.target.value })} style={inputStyle} />
                            </div>
                            <div>
                              <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Closing Date</label>
                              <input type="date" value={dealEditForm.estimated_closing_date?.split('T')[0] || ''} onChange={e => setDealEditForm({ ...dealEditForm, estimated_closing_date: e.target.value })} style={inputStyle} />
                            </div>
                          </div>
                          <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Notes</label>
                            <textarea value={dealEditForm.notes} onChange={e => setDealEditForm({ ...dealEditForm, notes: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                          </div>

                          {/* KOPS-691: Property Characteristics — triggers conditional documents */}
                          <div style={{ marginBottom: '12px', padding: '12px 14px', background: '#f8f9fb', borderRadius: '8px', border: '1px solid #e8eef6' }}>
                            <div style={{ fontSize: '10px', color: colors.navy, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '10px' }}>
                              <FontAwesomeIcon icon={faHome} style={{ marginRight: 6, color: colors.gold }} />
                              Property Characteristics
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                              <div>
                                <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Property Type</label>
                                <select value={dealEditForm.property_type} onChange={e => setDealEditForm({ ...dealEditForm, property_type: e.target.value })} style={inputStyle}>
                                  <option value="residential">Residential</option>
                                  <option value="vacant_land">Vacant Land</option>
                                  <option value="commercial">Commercial</option>
                                  <option value="income_1-4">Income (1-4 units)</option>
                                  <option value="multi_family">Multi-Family (5+)</option>
                                </select>
                              </div>
                              <div>
                                <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Loan Type</label>
                                <select value={dealEditForm.loan_type} onChange={e => setDealEditForm({ ...dealEditForm, loan_type: e.target.value })} style={inputStyle}>
                                  <option value="conventional">Conventional</option>
                                  <option value="fha">FHA</option>
                                  <option value="va">VA</option>
                                  <option value="usda">USDA</option>
                                  <option value="cash">Cash</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                              <div>
                                <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Water Source</label>
                                <select value={dealEditForm.water_source} onChange={e => setDealEditForm({ ...dealEditForm, water_source: e.target.value })} style={inputStyle}>
                                  <option value="city">City Water</option>
                                  <option value="well">Well Water</option>
                                  <option value="shared_well">Shared Well</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                              <div>
                                <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Flood Zone</label>
                                <input placeholder="e.g. X, A, AE" value={dealEditForm.flood_zone || ''} onChange={e => setDealEditForm({ ...dealEditForm, flood_zone: e.target.value })} style={inputStyle} />
                              </div>
                              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                  <input type="checkbox" checked={dealEditForm.flood_insurance || false} onChange={e => setDealEditForm({ ...dealEditForm, flood_insurance: e.target.checked })} />
                                  <span style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Flood Insurance Required</span>
                                </label>
                              </div>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                              <label style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Year Built</label>
                              <input type="number" placeholder="e.g. 1973" value={dealEditForm.year_built || ''} onChange={e => setDealEditForm({ ...dealEditForm, year_built: e.target.value })} style={{ ...inputStyle, width: '120px' }} />
                              {dealEditForm.year_built && parseInt(dealEditForm.year_built) < 1978 && (
                                <span style={{ fontSize: 10, color: '#e74c3c', fontWeight: 600, marginLeft: 8 }}>
                                  Pre-1978 → Lead Paint Disclosure required
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                              {[
                                { key: 'pool', label: 'Pool / Spa', trigger: 'Pool Safety Advisory' },
                                { key: 'hoa', label: 'HOA / Planned Community', trigger: 'HOA Addendum' },
                                { key: 'solar', label: 'Solar Panels', trigger: 'Solar Addendum' },
                                { key: 'septic', label: 'Septic System', trigger: 'Wastewater Addendum' },
                                { key: 'well', label: 'Well Water', trigger: 'Water Well Addendum' },
                                { key: 'short_sale', label: 'Short Sale', trigger: 'Short Sale Addendum' },
                                { key: 'investment_property', label: 'Investment Property (1-4 units)', trigger: 'Income Property Addendum' },
                              ].map(item => (
                                <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, cursor: 'pointer', background: dealEditForm[item.key] ? '#fef2f220' : 'white', border: `1px solid ${dealEditForm[item.key] ? colors.orange : '#eee'}` }}>
                                  <input
                                    type="checkbox"
                                    checked={!!dealEditForm[item.key]}
                                    onChange={e => setDealEditForm({ ...dealEditForm, [item.key]: e.target.checked })}
                                    style={{ width: 14, height: 14, cursor: 'pointer' }}
                                  />
                                  <div>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: colors.navy }}>{item.label}</div>
                                    {dealEditForm[item.key] && (
                                      <div style={{ fontSize: 9, color: colors.orange }}>→ {item.trigger}</div>
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                            <div style={{ fontSize: 9, color: '#bbb', marginTop: 6 }}>
                              Checked items trigger conditional document requirements. County data auto-fills when available.
                            </div>
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: '10px', color: '#bbb' }}>File # {dealDetail.file_number || '—'} (auto-generated, not editable)</div>
                            <Button size="sm" disabled={savingDealEdit} style={{ background: colors.gold, border: 'none', fontSize: '11px', fontWeight: 600, padding: '6px 20px' }} onClick={saveDealEdit}>
                              {savingDealEdit ? 'Saving…' : <><FontAwesomeIcon icon={faSave} style={{ marginRight: '4px' }} />Save Changes</>}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Deal info KPI cards (read-only) */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                            {[
                              { l: 'Type', v: dealDetail.type?.replace(/_/g, ' ') || '—' },
                              { l: 'Client', v: dealDetail.client_name || dealDetail.client_type || '—' },
                              { l: 'Compliance', v: dealDetail.compliance_status || 'pending' },
                              { l: 'File #', v: dealDetail.file_number || '—' },
                              { l: 'Created', v: dealDetail.created_at ? new Date(dealDetail.created_at).toLocaleDateString() : '—' },
                              { l: 'Updated', v: dealDetail.updated_at ? new Date(dealDetail.updated_at).toLocaleDateString() : '—' },
                            ].map((s, i) => (
                              <div key={i} style={{ background: '#f8f9fa', borderRadius: '6px', padding: '10px 12px' }}>
                                <div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.l}</div>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: colors.navy, marginTop: '2px', textTransform: 'capitalize' }}>{s.v}</div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      {/* Key Dates with Countdown Timers */}
                      {!editingDeal && (
                        <div style={{ marginBottom: '14px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                            <FontAwesomeIcon icon={faClock} style={{ marginRight: '6px', color: colors.gold }} />Key Dates
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            {keyDates.map((kd, i) => {
                              const daysLeft = getDaysLeft(kd.date);
                              const isOverdue = daysLeft !== null && daysLeft < 0;
                              const isUrgent = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7;
                              return (
                                <div key={i} style={{ padding: '10px 12px', background: kd.date ? (isOverdue ? '#fdf0f0' : isUrgent ? '#fff8f0' : '#f8f9fa') : '#f8f9fa', borderRadius: '6px', border: `1px solid ${kd.date ? (isOverdue ? '#f5c6cb' : isUrgent ? '#fde8cd' : '#eee') : '#eee'}` }}>
                                  <div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{kd.label}</div>
                                  {kd.date ? (
                                    <>
                                      <div style={{ fontSize: '13px', fontWeight: 600, color: colors.navy, marginTop: '2px' }}>{new Date(kd.date).toLocaleDateString()}</div>
                                      <div style={{ fontSize: '10px', fontWeight: 700, marginTop: '2px', color: isOverdue ? colors.red : isUrgent ? colors.orange : colors.green }}>
                                        {isOverdue ? `${Math.abs(daysLeft!)} days overdue` : daysLeft === 0 ? 'Today!' : `${daysLeft} days left`}
                                      </div>
                                    </>
                                  ) : (
                                    <div style={{ fontSize: '12px', color: '#ccc', marginTop: '2px' }}>Not set</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Quick stats row */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                        <div style={{ textAlign: 'center', padding: '12px', background: '#f0f7ff', borderRadius: '8px', border: '1px solid #d0e4f7', cursor: 'pointer' }} onClick={() => setDealTab('parties')}>
                          <div style={{ fontSize: '20px', fontWeight: 700, color: colors.navy }}>{dealParties.length}</div>
                          <div style={{ fontSize: '10px', color: '#3498db', textTransform: 'uppercase', fontWeight: 600 }}>Parties</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '12px', background: '#f0faf0', borderRadius: '8px', border: '1px solid #d0e8d0', cursor: 'pointer' }} onClick={() => setDealTab('documents')}>
                          <div style={{ fontSize: '20px', fontWeight: 700, color: colors.navy }}>{dealDocuments.length}</div>
                          <div style={{ fontSize: '10px', color: colors.green, textTransform: 'uppercase', fontWeight: 600 }}>Documents</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '12px', background: '#fff8f0', borderRadius: '8px', border: '1px solid #f0e0c8', cursor: 'pointer' }} onClick={() => setDealTab('notes')}>
                          <div style={{ fontSize: '20px', fontWeight: 700, color: colors.navy }}>{dealActivity.length}</div>
                          <div style={{ fontSize: '10px', color: colors.orange, textTransform: 'uppercase', fontWeight: 600 }}>Activities</div>
                        </div>
                      </div>

                      {/* AI Insight Card */}
                      {!editingDeal && nextDeadline && (
                        <div style={{ padding: '14px', background: `linear-gradient(135deg, ${colors.gold}11 0%, ${colors.gold}08 100%)`, border: `1px solid ${colors.gold}33`, borderRadius: '8px', marginBottom: '14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <FontAwesomeIcon icon={faLightbulb} style={{ color: colors.gold, fontSize: '14px' }} />
                            <span style={{ fontSize: '11px', fontWeight: 700, color: colors.gold, textTransform: 'uppercase', letterSpacing: '0.5px' }}>AI Insight</span>
                          </div>
                          <div style={{ fontSize: '13px', color: colors.navy, lineHeight: 1.6 }}>
                            <strong>{nextDeadline.label}</strong> {getDaysLeft(nextDeadline.date) === 0 ? 'is today' : `expires in ${getDaysLeft(nextDeadline.date)} days`} ({new Date(nextDeadline.date!).toLocaleDateString()}).
                            {nextDeadline.label === 'Inspection' && ' Consider drafting a repair request if the inspection report has been received.'}
                            {nextDeadline.label === 'Appraisal' && ' Ensure the appraiser has access and all comparable data is ready.'}
                            {nextDeadline.label === 'Loan Contingency' && ' Check with the lender on loan approval status.'}
                            {nextDeadline.label === 'Closing' && ' Verify all documents are submitted and broker has approved the file.'}
                          </div>
                        </div>
                      )}

                      {!editingDeal && dealDetail.notes && <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '6px', fontSize: '13px', color: '#555', lineHeight: 1.6 }}>{dealDetail.notes}</div>}

                      {/* KOPS-691: Property Details from County Assessor */}
                      {(() => {
                        const pd = dealDetail?.property_data || dealDetail?.property || {};
                        const propData = typeof pd === 'string' ? (() => { try { return JSON.parse(pd); } catch { return {}; } })() : pd;
                        const hasCountyData = propData.characteristics_source || propData.year_built || propData.pool !== undefined;
                        const handleVerify = async () => {
                          setPropertyVerifying(true);
                          try {
                            const res = await axiosInstance.post(`/transactions/${dealDetail.id}/verify-property`);
                            if (res.data.success && res.data.found) {
                              setPropertyVerifyResult(res.data);
                              toast.success(res.data.message);
                              fetchDealDetail(dealDetail.id);
                            } else {
                              toast.error(res.data.message || 'No county records found');
                            }
                          } catch { toast.error('Failed to verify property'); }
                          setPropertyVerifying(false);
                        };

                        const displayData = propertyVerifyResult?.property || (hasCountyData ? propData : null);

                        return (
                          <div style={{
                            background: 'white', border: '1px solid #dee2e6',
                            borderLeft: `4px solid ${hasCountyData ? colors.green : colors.orange}`,
                            borderRadius: '10px', padding: '14px 18px', marginBottom: '12px',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FontAwesomeIcon icon={faHome} style={{ color: hasCountyData ? colors.green : colors.orange, fontSize: 13 }} />
                                <span style={{ fontSize: 11, fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  Property Details
                                </span>
                                {hasCountyData && (
                                  <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, background: '#27ae6020', color: '#27ae60', fontWeight: 600 }}>
                                    County Verified
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={handleVerify}
                                disabled={propertyVerifying}
                                style={{
                                  padding: '4px 12px', borderRadius: 5, border: `1px solid ${colors.gold}`,
                                  background: 'transparent', color: colors.gold, fontSize: 10, fontWeight: 600,
                                  cursor: propertyVerifying ? 'wait' : 'pointer', opacity: propertyVerifying ? 0.6 : 1,
                                }}
                              >
                                {propertyVerifying ? 'Verifying...' : hasCountyData ? 'Re-Verify' : 'Verify Property'}
                              </button>
                            </div>

                            {displayData ? (
                              <>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6, marginBottom: 10 }}>
                                  {[
                                    { label: 'Year Built', value: displayData.year_built || '—', highlight: displayData.year_built && displayData.year_built < 1978 },
                                    { label: 'Sqft', value: displayData.sqft ? displayData.sqft.toLocaleString() : '—' },
                                    { label: 'Lot', value: displayData.lot_sqft ? (displayData.lot_sqft.toLocaleString() + ' sqft') : '—' },
                                    { label: 'Pool', value: displayData.pool ? 'Yes' : 'No', highlight: displayData.pool },
                                    { label: 'Garage', value: displayData.garage ? displayData.garage + ' car' : '—' },
                                    { label: 'Owner', value: displayData.ownership ? displayData.ownership.substring(0, 18) : '—' },
                                    { label: 'Since', value: displayData.deed_date || '—' },
                                    { label: 'Ownership', value: displayData.ownership_years ? displayData.ownership_years.toFixed(1) + ' yrs' : '—' },
                                    { label: 'Purchase Price', value: displayData.sale_price ? '$' + displayData.sale_price.toLocaleString() : '—' },
                                  ].map((item, i) => (
                                    <div key={i} style={{ padding: '6px 8px', background: item.highlight ? '#fef2f220' : '#f8f9fa', borderRadius: 6, border: item.highlight ? '1px solid #fecaca' : '1px solid #eee' }}>
                                      <div style={{ fontSize: 9, color: colors.muted, textTransform: 'uppercase', fontWeight: 600 }}>{item.label}</div>
                                      <div style={{ fontSize: 13, fontWeight: 700, color: item.highlight ? '#e74c3c' : colors.navy }}>{item.value}</div>
                                    </div>
                                  ))}
                                </div>

                                {displayData.county_valuation && (
                                  <div style={{ fontSize: 11, color: colors.muted, marginBottom: 6 }}>
                                    County Valuation: <strong style={{ color: colors.navy }}>${displayData.county_valuation.toLocaleString()}</strong>
                                    {displayData.characteristics_verified_at && (
                                      <span> · Verified {new Date(displayData.characteristics_verified_at).toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                    )}
                                  </div>
                                )}

                                {/* Conditional docs triggered */}
                                {(propertyVerifyResult?.conditionalDocsTriggered?.length > 0 || (displayData.year_built && displayData.year_built < 1978) || displayData.pool) && (
                                  <div style={{ marginTop: 8, padding: '8px 10px', background: '#fef2f2', borderRadius: 6, border: '1px solid #fecaca' }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: '#e74c3c', textTransform: 'uppercase', marginBottom: 4 }}>
                                      <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 4 }} />
                                      Conditional Documents Required
                                    </div>
                                    {displayData.year_built && displayData.year_built < 1978 && (
                                      <div style={{ fontSize: 11, color: '#555', padding: '2px 0' }}>
                                        • <strong>Lead-Based Paint Disclosure</strong> — home built {displayData.year_built} (pre-1978, federal requirement)
                                      </div>
                                    )}
                                    {displayData.pool && (
                                      <div style={{ fontSize: 11, color: '#555', padding: '2px 0' }}>
                                        • <strong>Pool Safety Advisory</strong> — property has pool/spa
                                      </div>
                                    )}
                                    {propertyVerifyResult?.conditionalDocsTriggered?.filter((d: any) =>
                                      !d.doc.includes('Lead') && !d.doc.includes('Pool')
                                    ).map((d: any, i: number) => (
                                      <div key={i} style={{ fontSize: 11, color: '#555', padding: '2px 0' }}>
                                        • <strong>{d.doc}</strong> — {d.reason}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div style={{ fontSize: 12, color: colors.muted, textAlign: 'center', padding: '12px 0' }}>
                                <FontAwesomeIcon icon={faHome} style={{ fontSize: 20, marginBottom: 6, display: 'block', color: '#ddd' }} />
                                Click "Verify Property" to look up county assessor data for this address.
                                <br />
                                <span style={{ fontSize: 10 }}>Auto-detects year built, pool, sqft, ownership history, and triggers conditional documents.</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* KOPS-685: Client Activity — portal visits, questions, AI responses */}
                      {(() => {
                        const clientTypes = ['client_portal_visit', 'client_question', 'ai_client_response', 'client_brief_view'];
                        const clientActivity = dealActivity.filter((a: any) => clientTypes.includes(a.activity_type));
                        if (clientActivity.length === 0) return null;

                        const iconMap: Record<string, any> = {
                          client_portal_visit: { icon: faUser, color: '#3498db', label: 'Portal Visit' },
                          client_brief_view: { icon: faFileContract, color: '#27ae60', label: 'Viewed Update' },
                          client_question: { icon: faComments, color: '#e67e22', label: 'Client Question' },
                          ai_client_response: { icon: faRobot, color: '#8e44ad', label: 'AI Response' },
                        };

                        return (
                          <div style={{
                            background: 'white', border: '1px solid #dee2e6', borderLeft: '4px solid #3498db',
                            borderRadius: '10px', padding: '14px 18px', marginBottom: '12px',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                              <FontAwesomeIcon icon={faComments} style={{ color: '#3498db', fontSize: 13 }} />
                              <span style={{ fontSize: 11, fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Client Activity
                              </span>
                              <span style={{
                                fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
                                background: '#3498db20', color: '#3498db',
                              }}>
                                {clientActivity.length}
                              </span>
                            </div>
                            <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                              {clientActivity.slice(0, 20).map((a: any, i: number) => {
                                const cfg = iconMap[a.activity_type] || iconMap.client_portal_visit;
                                const isQuestion = a.activity_type === 'client_question';
                                const isResponse = a.activity_type === 'ai_client_response';
                                return (
                                  <div key={a.id || i} style={{
                                    padding: '8px 10px', marginBottom: 4, borderRadius: 6,
                                    background: isResponse ? '#f5f0fa' : isQuestion ? '#fff8f0' : '#f8f9fa',
                                    border: `1px solid ${isResponse ? '#8e44ad15' : isQuestion ? '#e67e2215' : '#eee'}`,
                                    borderLeft: `3px solid ${cfg.color}`,
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                      <FontAwesomeIcon icon={cfg.icon} style={{ color: cfg.color, fontSize: 10 }} />
                                      <span style={{ fontSize: 10, fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
                                      <span style={{ fontSize: 9, color: '#999', marginLeft: 'auto' }}>
                                        {new Date(a.created_at).toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                      </span>
                                    </div>
                                    <div style={{ fontSize: 11, color: '#444', lineHeight: 1.5 }}>
                                      {a.description}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {/* KOPS-654: Documents checklist moved to Documents tab only — removed from Overview
                          to avoid duplication. Agents access documents via the Documents tab. */}
                    </div>
                  );})()}

                  {/* ---- EMAIL TAB ---- */}
                  {dealTab === 'email' && (
                    <div style={{ background: 'white', border: '1px solid #dee2e6', borderLeft: `4px solid ${colors.gold}`, borderRadius: '10px', padding: '18px' }}>
                      <div style={{ fontSize: '11px', color: '#888', marginBottom: '12px', lineHeight: 1.5 }}>
                        Emails related to this deal are matched by party email addresses.
                        Add parties to the deal to see their email threads here.
                      </div>
                      {dealParties.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                          {dealParties.filter((p: any) => p.email).map((p: any, i: number) => (
                            <span key={i} style={{ background: `${colors.gold}22`, color: colors.gold, fontSize: '10px', fontWeight: 600, padding: '3px 8px', borderRadius: '4px' }}>
                              {p.name || p.full_name}: {p.email}
                            </span>
                          ))}
                        </div>
                      )}
                      {dealEmailsLoading ? (
                        <div style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '12px' }}>Loading emails…</div>
                      ) : dealEmails.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '32px 0', color: '#999' }}>
                          <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '28px', color: '#ddd', marginBottom: '8px' }} />
                          <p style={{ fontSize: '13px' }}>No email activity for this deal yet.</p>
                          <p style={{ fontSize: '11px', color: '#bbb' }}>Emails will appear here as you communicate with deal parties.</p>
                        </div>
                      ) : (
                        dealEmails.map((e: any, i: number) => (
                          <div key={e.id || i} style={{ padding: '10px 0', borderBottom: '1px solid #eee', fontSize: '12px' }}>
                            <div style={{ fontWeight: 600, color: colors.navy }}>{e.description || e.subject || e.activity_type || '—'}</div>
                            <div style={{ color: '#999', fontSize: '11px', marginTop: '2px' }}>{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* ---- DOCUMENTS TAB (real component from TransactionDetails) ---- */}
                  {dealTab === 'documents' && (
                    <div>
                      {(!dealParties || dealParties.length === 0) && (
                        <Alert variant="warning" className="mb-3" style={{ fontSize: '12px' }}>
                          <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '6px' }} />
                          <strong>Action Required:</strong> Add transaction parties (buyer, seller) in the "Parties" tab before using templates.
                          <Button variant="warning" size="sm" className="ms-3" onClick={() => setDealTab('parties')}>Go to Parties</Button>
                        </Alert>
                      )}
                      <DocumentChecklistWithUploads
                        transactionId={dealDetail.id}
                        transactionType={dealDetail.type}
                        transactionRole={getAgentRole(dealDetail)}
                        transactionStage={dealDetail.stage}
                        existingDocuments={dealDocuments}
                        onDocumentUpload={handleChecklistDocumentUpload}
                        brokerageId={dealDetail.brokerage_id}
                        refreshTrigger={checklistRefresh}
                        onBulkUpload={() => setShowUploadModal(true)}
                      />
                      {/* KOPS-711: Offers — each offer is its own document section on the deal */}
                      <TransactionOffersPanel
                        transactionId={dealDetail.id}
                        brokerageId={dealDetail.brokerage_id}
                        transactionType={dealDetail.type}
                        clientName={dealDetail.client_name}
                        onOffersChanged={() => fetchDealDetail(dealDetail.id)}
                      />
                      <BrokerReviewStatus
                        transactionId={dealDetail.id}
                        refreshTrigger={brokerReviewRefresh}
                        portal="agent"
                        onDocumentReviewed={() => {
                          setBrokerReviewRefresh(prev => prev + 1);
                          toast.success('Document reviewed successfully!');
                        }}
                      />
                    </div>
                  )}

                  {/* ---- TEMPLATES TAB ---- */}
                  {dealTab === 'templates' && (
                    <TransactionTemplateManager
                      transactionId={dealDetail.id}
                      brokerageId={dealDetail.brokerage_id}
                      onDocumentsCopied={() => { setChecklistRefresh(prev => prev + 1); fetchDealDetail(dealDetail.id); }}
                      transactionData={{
                        ...partyPrefillData,
                        property_address: dealDetail.property_data?.address || dealDetail.property_address || '',
                        property_city: dealDetail.property_data?.city || '',
                        property_state: dealDetail.property_data?.state || '',
                        property_zip: dealDetail.property_data?.zip || '',
                        purchase_price: (dealDetail.property_data?.price || dealDetail.price || '').toString(),
                        closing_date: dealDetail.closing_date || dealDetail.property_data?.estimated_closing_date || '',
                        buyer_name: partyPrefillData.buyer_name || dealDetail.client_name || '',
                        buyer_email: partyPrefillData.buyer_email || '',
                        buyer_phone: partyPrefillData.buyer_phone || '',
                        seller_name: partyPrefillData.seller_name || '',
                        seller_email: partyPrefillData.seller_email || '',
                        seller_phone: partyPrefillData.seller_phone || '',
                        buyer_agent_name: partyPrefillData.buyer_agent_name || '',
                        buyer_agent_email: partyPrefillData.buyer_agent_email || '',
                        seller_agent_name: partyPrefillData.seller_agent_name || '',
                        seller_agent_email: partyPrefillData.seller_agent_email || '',
                      }}
                    />
                  )}

                  {/* ---- COMPLIANCE TAB ---- */}
                  {dealTab === 'compliance' && (
                    <div>
                      <AIComplianceDisplay
                        transaction={{ ...dealDetail, documents: dealDocuments, parties: dealParties }}
                        onNavigateToDocuments={() => setDealTab('documents')}
                      />
                    </div>
                  )}

                  {/* ---- PARTIES TAB (real component from TransactionDetails) ---- */}
                  {dealTab === 'parties' && (
                    <TransactionPartyAssignment
                      transactionId={dealDetail.id}
                      transactionType={dealDetail.type}
                      clientType={dealDetail.client_type}
                      transactionStage={dealDetail.stage}
                      isReadOnly={dealDetail.stage === 'completed' || !!dealDetail.closed_at}
                      existingParties={(dealParties || []).map((party: any) => ({
                        ...party,
                        name: party.name || '',
                        email: party.email || '',
                        phone: party.phone || ''
                      }))}
                      onPartiesUpdated={handlePartiesUpdated}
                    />
                  )}

                  {/* ---- INVOICE TAB (rental/lease only) ---- */}
                  {dealTab === 'invoice' && (dealDetail.type === 'rental' || dealDetail.type === 'lease') && (
                    <LandlordInvoiceGenerator
                      property={{
                        address: dealDetail.property_data?.address || dealDetail.property_address || 'Property Address'
                      }}
                      landlord={{
                        name: dealParties.find((p: any) => p.party_type === 'seller' || p.party_type === 'landlord')?.name,
                        email: dealParties.find((p: any) => p.party_type === 'seller' || p.party_type === 'landlord')?.email,
                        phone: dealParties.find((p: any) => p.party_type === 'seller' || p.party_type === 'landlord')?.phone,
                      }}
                      transactionId={dealDetail.id}
                      onSave={() => fetchDealDetail(dealDetail.id)}
                    />
                  )}

                  {/* ---- COMMISSION TAB (CDA) ---- */}
                  {dealTab === 'commission' && (
                    <CommissionPane
                      transaction={{ ...dealDetail, documents: dealDocuments, parties: dealParties }}
                      onReload={() => fetchDealDetail(dealDetail.id)}
                    />
                  )}

                  {/* ---- AI COACH TAB ---- */}
                  {dealTab === 'ai' && (
                    <DealCoachTab dealId={dealDetail.id} transaction={{ ...dealDetail, documents: dealDocuments, parties: dealParties }} />
                  )}

                  {/* ---- NOTES TAB ---- */}
                  {dealTab === 'notes' && (
                    <div style={{ background: 'white', border: '1px solid #dee2e6', borderLeft: `4px solid ${colors.gold}`, borderRadius: '10px', padding: '18px' }}>
                      {/* Add note input */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                          <FontAwesomeIcon icon={faStickyNote} style={{ marginRight: '6px', color: colors.gold }} />Add a Note
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <textarea
                            value={dealNoteText}
                            onChange={e => setDealNoteText(e.target.value)}
                            placeholder="Type a private note about this deal… (only you can see this)"
                            rows={3}
                            style={{ flex: 1, border: '1px solid #ddd', borderRadius: '6px', padding: '10px 12px', fontSize: '12px', resize: 'vertical', outline: 'none', fontFamily: 'inherit' }}
                          />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                          <span style={{ fontSize: '10px', color: '#bbb' }}>
                            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '4px', fontSize: '9px' }} />
                            Private — never visible to broker
                          </span>
                          <Button size="sm" disabled={savingDealNote || !dealNoteText.trim()}
                            style={{ background: colors.gold, border: 'none', fontSize: '11px', fontWeight: 600, padding: '5px 16px' }}
                            onClick={async () => {
                              if (!dealNoteText.trim() || !dealDetail?.id) return;
                              setSavingDealNote(true);
                              try {
                                // Try transaction-level note first, fallback to client note
                                await axiosInstance.post(`/transactions/${dealDetail.id}/activity`, {
                                  activity_type: 'note',
                                  description: dealNoteText.trim(),
                                }).catch(() =>
                                  axiosInstance.post(`/agents/clients/${dealDetail.client_id || dealDetail.lead_id}/note`, {
                                    note: dealNoteText.trim(),
                                  })
                                );
                                toast.success('Note saved');
                                setDealNoteText('');
                                // Clear brief cache so next Overview shows updated brief with this note
                                try { localStorage.removeItem(`deal_brief_${dealDetail.id}`); } catch {}
                                fetchDealDetail(dealDetail.id);
                              } catch (err: any) {
                                toast.error(err?.response?.data?.error || 'Failed to save note');
                              } finally { setSavingDealNote(false); }
                            }}>
                            {savingDealNote ? 'Saving…' : <><FontAwesomeIcon icon={faSave} style={{ marginRight: '4px' }} />Save Note</>}
                          </Button>
                        </div>
                      </div>

                      {/* Notes list */}
                      {dealNotes.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Notes</div>
                          {dealNotes.map((n: any, i: number) => (
                            <div key={n.id || i} style={{ padding: '10px 12px', marginBottom: '6px', background: '#fffdf5', borderRadius: '6px', border: `1px solid ${colors.gold}22`, borderLeft: `3px solid ${colors.gold}` }}>
                              <div style={{ fontSize: '12px', color: colors.navy, lineHeight: 1.6 }}>{n.description || n.note || n.content || '—'}</div>
                              <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                                {n.created_by_name || n.author || ''}{n.created_at ? ` · ${new Date(n.created_at).toLocaleString()}` : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Activity timeline */}
                      <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                        <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '6px', color: colors.gold }} />Activity Timeline
                      </div>
                      {dealActivity.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '24px 0', color: '#999' }}>
                          <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: '24px', color: '#ddd', marginBottom: '8px' }} />
                          <p style={{ fontSize: '12px' }}>No activity recorded yet.</p>
                        </div>
                      ) : (
                        dealActivity.filter((a: any) => a.activity_type !== 'note' && a.activity_type !== 'admin_note').map((a: any, i: number) => (
                          <div key={a.id || i} style={{ display: 'flex', gap: '12px', padding: '8px 0', borderBottom: '1px solid #f5f5f5', fontSize: '12px' }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: `${colors.gold}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <FontAwesomeIcon icon={faCalendarAlt} style={{ color: colors.gold, fontSize: '10px' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: colors.navy, fontWeight: 500 }}>{a.description || a.activity_type?.replace(/_/g, ' ') || '—'}</div>
                              <div style={{ color: '#999', fontSize: '10px', marginTop: '2px' }}>{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* ── KOPS-662: Automations Tab ── */}
                  {dealTab === 'automations' && (
                    <DealAutomationsTab
                      dealId={dealDetail?.id}
                      dealType={dealDetail?.type}
                      stage={dealDetail?.stage}
                      parties={dealParties}
                      propertyAddress={dealDetail?.property_address || dealDetail?.address || ''}
                      brokerageId={dealDetail?.brokerage_id}
                      uploadedDocs={dealDocuments}
                    />
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '48px', color: colors.muted }}>
                  <FontAwesomeIcon icon={faHome} style={{ fontSize: '48px', marginBottom: '12px', color: colors.gold }} />
                  <div style={{ fontSize: '15px', fontWeight: 600, color: colors.navy }}>Deals Pipeline</div>
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>Select a deal or create a new one to manage your escrow files</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ======== AGENT DEAL BOARD MODE (KOPS-577: Agent Kanban + Lender Linking) ======== */}
      {pageMode === 'deal-board' && (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <AgentDealBoard />
        </div>
      )}

      {/* ======== PIPELINE MODE (KOPS-555: Kanban Deal Board) ======== */}
      {pageMode === 'pipeline' && (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <KanbanDealBoard />
        </div>
      )}

      {/* ======== AUTOMATIONS MODE ======== */}
      {pageMode === 'automations' && (() => {
        // Lazy-load automations data
        if (!automationsData && !automationsLoading) {
          setAutomationsLoading(true);
          axiosInstance.get('/ai-coach/upcoming').then(({ data }) => { if (data.success || data.tasks) setAutomationsData(data); }).catch(() => {}).finally(() => setAutomationsLoading(false));
        }
        if (!queueData && !queueLoading) {
          setQueueLoading(true);
          axiosInstance.get('/ai-coach/queue').then(({ data }) => { if (data.success || data.queue) setQueueData(data); }).catch(() => {}).finally(() => setQueueLoading(false));
        }
        if (!dealActionsData && !dealActionsLoading) {
          setDealActionsLoading(true);
          axiosInstance.get('/sponsor/deal-board/actions').then(({ data }) => { if (data.success) setDealActionsData(data); }).catch(() => {}).finally(() => setDealActionsLoading(false));
        }
        if (automationsLoading || queueLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flex: 1 }}><Spinner animation="border" style={{ color: colors.gold }} /></div>;

        const allTasks = automationsData?.tasks || [];
        const dealActions = dealActionsData?.actions || [];
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        const next7 = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

        // Today's Queue = EVERYTHING waiting on lender action
        // 1. Overdue workflow tasks (missed from past days)
        const overdueItems = allTasks.filter((t: any) => t.status === 'pending' && new Date(t.scheduled_for) < startOfToday);
        overdueItems.forEach((t: any) => { t._overdue = true; t._source = 'workflow'; });
        // 2. Today's scheduled workflow tasks
        const todayWorkflow = allTasks.filter((t: any) => t.status === 'pending' && new Date(t.scheduled_for) >= startOfToday && new Date(t.scheduled_for) <= endOfToday);
        todayWorkflow.forEach((t: any) => { t._source = 'workflow'; });
        // 3. ALL pending AI coach items waiting for approval (drafts, calls, CMAs)
        // Only filter out no_action items — everything else is actionable
        const pendingDrafts = (queueData?.queue || []).filter((d: any) =>
          d.status === 'pending' &&
          d.action_type !== 'no_action'
        );
        const pendingAsQueue = pendingDrafts.map((d: any) => {
          // Resolve display name — use lead name if available, otherwise extract from context/subject
          let displayFirst = d.lead_first_name;
          let displayLast = d.lead_last_name;
          if (!displayFirst || displayFirst === 'Unknown') {
            const ctx = d.context || {};
            const bodyMatch = (d.body || '').match(/^(?:Hi|Hello|Dear)\s+([A-Z][a-z]+)/);
            if (bodyMatch) { displayFirst = bodyMatch[1]; displayLast = ''; }
            else if (ctx.leadType) { displayFirst = ctx.leadType.replace(/_/g, ' '); displayLast = '(unlinked)'; }
            else { displayFirst = 'Contact'; displayLast = '(review needed)'; }
          }
          return {
            ...d,
            _source: 'coach',
            task_id: d.id,
            lead_first_name: displayFirst,
            lead_last_name: displayLast,
            action: d.action_type,
            priority: d.context?.recommendation?.urgency || 'medium',
            scheduled_for: d.created_at,
          };
        });
        // 4. Merge all into one queue — overdue first, then coach items, then today's workflow
        const todayQueue = [...overdueItems, ...pendingAsQueue, ...todayWorkflow];
        // Upcoming = future workflow tasks only (not coach items — those are already in today's queue)
        const upcomingTasks = allTasks.filter((t: any) => t.status === 'pending' && new Date(t.scheduled_for) > endOfToday && new Date(t.scheduled_for) <= next7);

        const upcomingByDay: Record<string, any[]> = {};
        upcomingTasks.forEach((t: any) => {
          const day = new Date(t.scheduled_for).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
          if (!upcomingByDay[day]) upcomingByDay[day] = [];
          upcomingByDay[day].push(t);
        });

        const priorityColor = (p: string) => (p || '').toLowerCase() === 'urgent' ? '#c62828' : (p || '').toLowerCase() === 'high' ? '#e65100' : (p || '').toLowerCase() === 'medium' ? colors.gold : '#888';
        const priorityBg = (p: string) => (p || '').toLowerCase() === 'urgent' ? '#ffebee' : (p || '').toLowerCase() === 'high' ? '#fff3e0' : (p || '').toLowerCase() === 'medium' ? '#FBF4DA' : '#f5f5f5';

        const handleApproveTask = async (taskId: string) => {
          try { await axiosInstance.post(`/ai-coach/upcoming/${taskId}/approve`); toast.success('Task approved'); setAutomationsData(null); } catch { toast.error('Failed to approve'); }
        };
        const handleCancelTask = async (taskId: string) => {
          try { await axiosInstance.post(`/ai-coach/upcoming/${taskId}/cancel`); toast.success('Task cancelled'); setAutomationsData(null); } catch { toast.error('Failed to cancel'); }
        };
        const handleSendDraft = async (id: string, editedSubject?: string, editedBody?: string) => {
          setAutomationSending(true);
          try {
            await axiosInstance.post(`/ai-coach/queue/${id}/send`, { subject: editedSubject, body: editedBody });
            toast.success('Email sent!'); setQueueData(null); setAutomationModal(null); setAutomationEditing(false);
          } catch { toast.error('Failed to send'); }
          setAutomationSending(false);
        };
        const handleDismissDraft = async (id: string, reason?: string) => {
          try { await axiosInstance.post(`/ai-coach/queue/${id}/complete`, { reason }); toast.success('Dismissed'); setQueueData(null); setAutomationModal(null); } catch { toast.error('Failed to dismiss'); }
        };
        const handleScheduleSend = async (id: string, scheduledFor: string) => {
          setAutomationSending(true);
          try {
            await axiosInstance.post(`/ai-coach/upcoming/${id}/reschedule`, { scheduled_for: scheduledFor });
            toast.success(`Scheduled for ${new Date(scheduledFor).toLocaleDateString()}`); setQueueData(null); setAutomationModal(null);
          } catch { toast.error('Failed to schedule'); }
          setAutomationSending(false);
        };
        const openModal = (item: any, type: 'draft' | 'task') => {
          setAutomationModal({ ...item, _type: type });
          setAutomationEditSubject(item.subject || '');
          setAutomationEditBody(item.body || '');
          setAutomationEditing(false);
          setAutomationScheduleDate('');
          setAutomationNoteText('');
          setRegenFeedback('');
        };

        return (
          <div style={{ flex: 1, overflowY: 'auto', padding: 24, background: colors.offBg }}>
            {/* Hero Banner */}
            <div style={{ background: `linear-gradient(135deg, ${colors.navy}, #1a2d4a)`, borderRadius: 12, padding: 24, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: colors.gold, letterSpacing: 1, textTransform: 'uppercase' }}>Pipeline Automations</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>AI Workflow Dashboard</div>
                <div style={{ fontSize: 13, color: '#8FA3BF', marginTop: 4 }}>Review, approve, and manage AI-generated actions for your pipeline</div>
              </div>
              <div style={{ display: 'flex', gap: 20, textAlign: 'center' }}>
                <div><div style={{ fontSize: 28, fontWeight: 700, color: todayQueue.length > 0 ? '#ef5350' : colors.gold }}>{todayQueue.length}</div><div style={{ fontSize: 10, color: '#8FA3BF' }}>Due Today</div></div>
                <div><div style={{ fontSize: 28, fontWeight: 700, color: '#e65100' }}>{dealActions.length}</div><div style={{ fontSize: 10, color: '#8FA3BF' }}>Active Deals</div></div>
                <div><div style={{ fontSize: 28, fontWeight: 700, color: '#2980b9' }}>{upcomingTasks.length}</div><div style={{ fontSize: 10, color: '#8FA3BF' }}>This Week</div></div>
                <div><div style={{ fontSize: 28, fontWeight: 700, color: '#27ae60' }}>{pendingDrafts.length}</div><div style={{ fontSize: 10, color: '#8FA3BF' }}>Drafts Ready</div></div>
              </div>
            </div>

            {/* Stats Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Needs Action', value: todayQueue.length, color: '#c62828' },
                { label: 'Overdue', value: overdueItems.length, color: '#e65100' },
                { label: 'Emails to Send', value: pendingDrafts.filter((d: any) => d.action_type === 'draft_email').length, color: '#2980b9' },
                { label: 'Calls to Schedule', value: pendingDrafts.filter((d: any) => d.action_type === 'schedule_call').length + todayQueue.filter((t: any) => (t.action || '').includes('call')).length, color: '#27ae60' },
              ].map((s, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 10, padding: 16, borderTop: `3px solid ${s.color}` }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: colors.navy }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* TODAY'S QUEUE */}
              <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0' }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FontAwesomeIcon icon={faBolt} style={{ color: colors.gold }} /> Today's Queue ({todayQueue.length})
                  {overdueItems.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#ffebee', color: '#c62828', marginLeft: 4 }}>{overdueItems.length} OVERDUE</span>}
                </h4>
                {todayQueue.length === 0 && <div style={{ fontSize: 12, color: '#888', padding: '20px 0', textAlign: 'center' }}>No items due today. You're all caught up!</div>}
                {todayQueue.slice(0, 15).map((t: any, i: number) => (
                  <div key={t.task_id || t.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < Math.min(todayQueue.length, 15) - 1 ? '1px solid #f0f0f0' : 'none' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {t._overdue && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#ffebee', color: '#c62828' }}>OVERDUE</span>}
                        {t._source === 'coach' && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#e3f2fd', color: '#1565c0' }}>{(t.action_type || t.action || '').replace(/_/g, ' ').toUpperCase()}</span>}
                        {t._source !== 'coach' && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: priorityBg(t.priority), color: priorityColor(t.priority) }}>{(t.priority || 'MEDIUM').toUpperCase()}</span>}
                        <span style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>{t.lead_first_name || ''} {t.lead_last_name || ''}</span>
                      </div>
                      <div style={{ fontSize: 11, color: t._overdue ? '#c62828' : '#888', marginTop: 2 }}>
                        {t._overdue ? `Missed — was due ${new Date(t.scheduled_for).toLocaleDateString()}` :
                         t._source === 'coach' ? (t.subject || t.body || '').substring(0, 80) :
                         (t.action || t.agent_type || 'Workflow task').replace(/_/g, ' ')}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {t._source === 'coach' ? (
                        <>
                          <button onClick={() => openModal(t, 'draft')} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: 'none', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy, fontWeight: 700, cursor: 'pointer' }}>Review</button>
                          <button onClick={() => handleDismissDraft(t.id)} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', color: '#888', cursor: 'pointer' }}>Dismiss</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => handleApproveTask(t.task_id)} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: 'none', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy, fontWeight: 700, cursor: 'pointer' }}>Approve</button>
                          <button onClick={() => handleCancelTask(t.task_id)} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', color: '#888', cursor: 'pointer' }}>Skip</button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* APPROVE & SEND */}
              <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0' }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FontAwesomeIcon icon={faEnvelope} style={{ color: colors.gold }} /> Approve & Send ({pendingDrafts.length})
                </h4>
                {pendingDrafts.length === 0 && <div style={{ fontSize: 12, color: '#888', padding: '20px 0', textAlign: 'center' }}>No drafts waiting for approval.</div>}
                {pendingDrafts.slice(0, 10).map((d: any, i: number) => (
                  <div key={d.id || i} style={{ padding: '10px 0', borderBottom: i < Math.min(pendingDrafts.length, 10) - 1 ? '1px solid #f0f0f0' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: d.action_type === 'draft_email' ? '#e3f2fd' : d.action_type === 'schedule_call' ? '#e8f5e9' : '#f3e5f5', color: d.action_type === 'draft_email' ? '#1565c0' : d.action_type === 'schedule_call' ? '#2e7d32' : '#6a1b9a' }}>{(d.action_type || '').replace(/_/g, ' ').toUpperCase()}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>{d.subject || 'No subject'}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 4, maxHeight: 36, overflow: 'hidden' }}>{(d.body || '').substring(0, 120)}{(d.body || '').length > 120 ? '...' : ''}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginLeft: 12, flexShrink: 0 }}>
                        <button onClick={() => openModal(d, 'draft')} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: 'none', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><FontAwesomeIcon icon={faRobot} style={{ fontSize: 9 }} /> Review</button>
                        <button onClick={() => handleDismissDraft(d.id)} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', color: '#888', cursor: 'pointer' }}>Dismiss</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ACTIVE DEALS - Full width */}
              <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0', gridColumn: '1 / -1' }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FontAwesomeIcon icon={faHome} style={{ color: colors.gold }} /> Active Deals Needing Attention ({dealActions.length})
                </h4>
                {dealActions.length === 0 && <div style={{ fontSize: 12, color: '#888', padding: '20px 0', textAlign: 'center' }}>No deals need attention right now.</div>}
                {dealActions.map((d: any, i: number) => (
                  <div key={d.lead_id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < dealActions.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: d.priority === 'urgent' ? '#ffebee' : d.priority === 'high' ? '#fff3e0' : '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: d.priority === 'urgent' ? '#c62828' : d.priority === 'high' ? '#e65100' : '#1565c0' }}>
                        {(d.borrower_name || 'U').split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: priorityBg(d.priority), color: priorityColor(d.priority) }}>{(d.priority || 'MEDIUM').toUpperCase()}</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>{d.borrower_name}</span>
                          <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 8, background: '#e8eaf6', color: '#3949ab' }}>{d.stageLabel}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{d.action}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => { setDealModal(d); setDealModalDetail(null); setDealModalLoading(true); axiosInstance.get(`/sponsor/deal-board/${d.lead_id}/detail`).then(({ data }) => setDealModalDetail(data)).catch(() => {}).finally(() => setDealModalLoading(false)); }} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: 'none', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy, fontWeight: 700, cursor: 'pointer' }}>
                        View Deal
                      </button>
                      <button onClick={() => { setPageMode('pipeline'); window.history.replaceState({}, '', `${portalBase}/clients?mode=pipeline`); }} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', color: '#888', cursor: 'pointer' }}>
                        Pipeline
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* UPCOMING WORKFLOW - Full width */}
              <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0', gridColumn: '1 / -1' }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FontAwesomeIcon icon={faClock} style={{ color: colors.gold }} /> Upcoming This Week ({upcomingTasks.length})
                </h4>
                {upcomingTasks.length === 0 && <div style={{ fontSize: 12, color: '#888', padding: '20px 0', textAlign: 'center' }}>No tasks scheduled for the next 7 days.</div>}
                {Object.entries(upcomingByDay).map(([day, tasks]: [string, any[]]) => (
                  <div key={day} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: colors.gold, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8, paddingBottom: 4, borderBottom: '2px solid rgba(201,149,42,0.15)' }}>{day}</div>
                    {tasks.map((t: any, i: number) => (
                      <div key={t.task_id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < tasks.length - 1 ? '1px solid #f8f8f8' : 'none' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: priorityBg(t.priority), color: priorityColor(t.priority) }}>{(t.priority || 'MEDIUM').toUpperCase()}</span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>{t.lead_first_name || ''} {t.lead_last_name || ''}</span>
                            <span style={{ fontSize: 11, color: '#888' }}>— {(t.action || t.agent_type || 'task').replace(/_/g, ' ')}</span>
                          </div>
                          {t.task_data?.reason && <div style={{ fontSize: 11, color: '#888', marginTop: 3, marginLeft: 28 }}>{t.task_data.reason.substring(0, 100)}{t.task_data.reason.length > 100 ? '...' : ''}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button onClick={() => {
                            setAutomationModal({
                              ...t,
                              _type: 'task',
                              id: t.task_id,
                              subject: (t.action || '').replace(/_/g, ' '),
                              body: t.task_data?.reason || '',
                              context: { recommendation: { action: t.action, reason: t.task_data?.reason, urgency: t.priority }, leadType: t.task_data?.leadType, metrics: {} },
                              action_type: t.action,
                            });
                            setAutomationEditSubject((t.action || '').replace(/_/g, ' '));
                            setAutomationEditBody(t.task_data?.reason || '');
                            setAutomationEditing(false);
                            setAutomationScheduleDate('');
                          }} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6, border: 'none', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy, fontWeight: 700, cursor: 'pointer' }}>Review</button>
                          <button onClick={() => handleCancelTask(t.task_id)} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', color: '#888', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* ══ Deal Detail Modal ══ */}
            {dealModal && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setDealModal(null)}>
                <div style={{ background: '#fff', borderRadius: 16, width: 680, maxHeight: '90vh', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>

                  {/* Header */}
                  <div style={{ background: colors.navy, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16, borderBottom: `3px solid ${dealModal.priority === 'urgent' ? '#c62828' : dealModal.priority === 'high' ? '#e65100' : colors.gold}` }}>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: dealModal.priority === 'urgent' ? '#c62828' : dealModal.priority === 'high' ? '#e65100' : colors.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 18 }}>
                      {(dealModal.borrower_name || 'U').split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h2 style={{ margin: 0, fontSize: 20, color: '#fff', fontWeight: 700 }}>{dealModal.borrower_name}</h2>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.15)', color: '#fff' }}>{dealModal.stageLabel}</span>
                        {dealModal.loanProgram && <span style={{ fontSize: 10, color: '#8FA3BF' }}>{dealModal.loanProgram}</span>}
                        <span style={{ fontSize: 10, color: '#8FA3BF' }}>{dealModal.daysInStage}d in stage</span>
                      </div>
                    </div>
                    <button onClick={() => setDealModal(null)} style={{ background: 'none', border: 'none', color: '#8FA3BF', fontSize: 20, cursor: 'pointer' }}>✕</button>
                  </div>

                  {dealModalLoading ? (
                    <div style={{ padding: 60, textAlign: 'center' }}><Spinner animation="border" style={{ color: colors.gold }} /></div>
                  ) : (
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                      {/* Deal Snapshot */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, borderBottom: '1px solid #eee' }}>
                        <div style={{ padding: '16px 20px', textAlign: 'center', borderRight: '1px solid #eee' }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: dealModal.daysInStage > 7 ? '#c62828' : colors.navy }}>{dealModal.daysInStage}</div>
                          <div style={{ fontSize: 10, color: '#888' }}>Days in Stage</div>
                        </div>
                        <div style={{ padding: '16px 20px', textAlign: 'center', borderRight: '1px solid #eee' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: colors.navy }}>{dealModal.loanType || dealModal.loanProgram || '—'}</div>
                          <div style={{ fontSize: 10, color: '#888' }}>Loan Program</div>
                        </div>
                        <div style={{ padding: '16px 20px', textAlign: 'center' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: dealModal.coeDate ? colors.navy : '#888' }}>{dealModal.coeDate || 'TBD'}</div>
                          <div style={{ fontSize: 10, color: '#888' }}>Close of Escrow</div>
                        </div>
                      </div>

                      {/* Contact Info */}
                      <div style={{ padding: '16px 24px', borderBottom: '1px solid #eee', display: 'flex', gap: 20 }}>
                        {dealModal.phone && <div style={{ fontSize: 13, color: colors.navy }}><FontAwesomeIcon icon={faPhone} style={{ color: '#27ae60', marginRight: 6 }} />{dealModal.phone}</div>}
                        {dealModal.email && <div style={{ fontSize: 13, color: colors.navy }}><FontAwesomeIcon icon={faEnvelope} style={{ color: '#3498db', marginRight: 6 }} />{dealModal.email}</div>}
                      </div>

                      {/* Next Deliverable */}
                      <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: colors.gold, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
                          <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 6 }} /> Next Deliverable
                        </div>
                        <div style={{ background: dealModal.priority === 'urgent' ? '#fff5f5' : '#f8f9fa', borderRadius: 10, padding: 16, border: `1px solid ${dealModal.priority === 'urgent' ? '#ffcdd2' : '#eee'}` }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: dealModal.priority === 'urgent' ? '#c62828' : colors.navy }}>{dealModal.action}</div>
                          {dealModal.lastNote && <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Last note: {dealModal.lastNote}</div>}
                        </div>
                      </div>

                      {/* Activity Timeline from detail */}
                      {dealModalDetail?.activities && dealModalDetail.activities.length > 0 && (
                        <div style={{ padding: '20px 24px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
                            Recent Activity
                          </div>
                          {dealModalDetail.activities.slice(0, 5).map((a: any, i: number) => (
                            <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: i < Math.min(dealModalDetail.activities.length, 5) - 1 ? '1px solid #f5f5f5' : 'none' }}>
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors.gold, marginTop: 5, flexShrink: 0 }} />
                              <div>
                                <div style={{ fontSize: 12, color: colors.navy, fontWeight: 500 }}>{a.description || a.subject || a.activity_type}</div>
                                <div style={{ fontSize: 10, color: '#888' }}>{a.activity_date ? new Date(a.activity_date).toLocaleDateString() : ''}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <div style={{ padding: '16px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa' }}>
                    <button onClick={() => setDealModal(null)} style={{ fontSize: 12, padding: '8px 20px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', color: '#888', cursor: 'pointer' }}>Close</button>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button onClick={() => { setDealModal(null); setPageMode('clients'); window.location.href = `${portalBase}/clients?id=${dealModal.lead_id}&tab=overview`; }} style={{ fontSize: 12, padding: '8px 20px', borderRadius: 8, border: `1px solid ${colors.navy}`, background: '#fff', color: colors.navy, cursor: 'pointer', fontWeight: 600 }}>
                        Open Full Deal
                      </button>
                      <button onClick={() => { setDealModal(null); setPageMode('pipeline'); window.history.replaceState({}, '', `${portalBase}/clients?mode=pipeline`); }} style={{ fontSize: 12, padding: '8px 20px', borderRadius: 8, border: 'none', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy, cursor: 'pointer', fontWeight: 700 }}>
                        Go to Pipeline
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ══ Explainable AI Modal ══ */}
            {automationModal && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setAutomationModal(null)}>
                <div style={{ background: '#fff', borderRadius: 16, width: 700, maxHeight: '90vh', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>

                  {/* Header */}
                  <div style={{ background: colors.navy, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16, borderBottom: `3px solid ${colors.gold}` }}>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: colors.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.navy, fontWeight: 700, fontSize: 18 }}>
                      <FontAwesomeIcon icon={faRobot} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h2 style={{ margin: 0, fontSize: 18, color: '#fff', fontWeight: 700 }}>AI Recommendation</h2>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 10, background: 'rgba(201,149,42,0.25)', color: colors.gold2 }}>
                          {(automationModal.action_type || automationModal.action || '').replace(/_/g, ' ').toUpperCase()}
                        </span>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setAutomationModal(null);
                            setPageMode('clients');
                            window.history.replaceState({}, '', `${window.location.pathname}?mode=clients`);
                            // Find and select the lead by ID
                            if (automationModal.lead_id) {
                              const lead = clients.find((l: any) => l.id === automationModal.lead_id || l.id === automationModal.lead_id?.substring(0, 26));
                              if (lead) {
                                setSelected(lead);
                                setActiveTab('overview');
                              }
                            }
                          }}
                          style={{ fontSize: 10, color: colors.gold2, textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
                        >
                          {automationModal.lead_first_name} {automationModal.lead_last_name}
                        </a>
                      </div>
                    </div>
                    <button onClick={() => setAutomationModal(null)} style={{ background: 'none', border: 'none', color: '#8FA3BF', fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
                  </div>

                  <div style={{ flex: 1, overflowY: 'auto' }}>
                    {/* WHY Section */}
                    <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: colors.gold, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
                        <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: 6 }} /> Why This Was Recommended
                      </div>
                      <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 16, border: '1px solid #eee' }}>
                        {(() => {
                          const ctx = automationModal.context && typeof automationModal.context === 'object' ? automationModal.context : null;
                          const rec = ctx?.recommendation || {};
                          const metrics = ctx?.metrics || {};
                          const leadType = ctx?.leadType || rec?.leadType || '';
                          const reason = rec?.reason || ctx?.reason || '';
                          const confidence = rec?.confidence || ctx?.confidence;

                          return ctx ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                              {/* Reason */}
                              {reason && (
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                  <span style={{ fontSize: 16, marginTop: 1 }}>💡</span>
                                  <div style={{ fontSize: 13, color: colors.navy, fontWeight: 600 }}>{reason}</div>
                                </div>
                              )}

                              {/* Lead Type + Confidence */}
                              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                {leadType && (
                                  <div style={{ padding: '6px 14px', borderRadius: 8, background: '#e3f2fd', fontSize: 12, fontWeight: 600, color: '#1565c0' }}>
                                    👤 {leadType.replace(/_/g, ' ')}
                                  </div>
                                )}
                                {confidence && (
                                  <div style={{ padding: '6px 14px', borderRadius: 8, background: confidence > 0.7 ? '#e8f5e9' : '#fff3e0', fontSize: 12, fontWeight: 600, color: confidence > 0.7 ? '#2e7d32' : '#e65100' }}>
                                    🎯 {Math.round(confidence * 100)}% confidence
                                  </div>
                                )}
                              </div>

                              {/* Metrics */}
                              {(metrics.totalActivities || metrics.inboundCount || metrics.outboundCount || metrics.daysSinceCreated) && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 4 }}>
                                  {metrics.totalActivities !== undefined && (
                                    <div style={{ background: '#fff', borderRadius: 8, padding: '8px 10px', textAlign: 'center', border: '1px solid #eee' }}>
                                      <div style={{ fontSize: 16, fontWeight: 700, color: colors.navy }}>{metrics.totalActivities}</div>
                                      <div style={{ fontSize: 9, color: '#888' }}>Activities</div>
                                    </div>
                                  )}
                                  {metrics.inboundCount !== undefined && (
                                    <div style={{ background: '#fff', borderRadius: 8, padding: '8px 10px', textAlign: 'center', border: '1px solid #eee' }}>
                                      <div style={{ fontSize: 16, fontWeight: 700, color: '#27ae60' }}>{metrics.inboundCount}</div>
                                      <div style={{ fontSize: 9, color: '#888' }}>Inbound</div>
                                    </div>
                                  )}
                                  {metrics.outboundCount !== undefined && (
                                    <div style={{ background: '#fff', borderRadius: 8, padding: '8px 10px', textAlign: 'center', border: '1px solid #eee' }}>
                                      <div style={{ fontSize: 16, fontWeight: 700, color: '#2980b9' }}>{metrics.outboundCount}</div>
                                      <div style={{ fontSize: 9, color: '#888' }}>Outbound</div>
                                    </div>
                                  )}
                                  {metrics.daysSinceLastContact !== undefined && (
                                    <div style={{ background: '#fff', borderRadius: 8, padding: '8px 10px', textAlign: 'center', border: '1px solid #eee' }}>
                                      <div style={{ fontSize: 16, fontWeight: 700, color: metrics.daysSinceLastContact > 7 ? '#c62828' : colors.gold }}>{metrics.daysSinceLastContact}</div>
                                      <div style={{ fontSize: 9, color: '#888' }}>Days Silent</div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Compliance note if present */}
                              {ctx.compliance && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: '#fff3e0', border: '1px solid #ffe0b2' }}>
                                  <span>⚠️</span>
                                  <span style={{ fontSize: 12, color: '#e65100' }}>{typeof ctx.compliance === 'string' ? ctx.compliance : 'Compliance review applied'}</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{ fontSize: 13, color: '#888' }}>AI analyzed this contact's email history, pipeline stage, and engagement patterns to generate this recommendation.</div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* DRAFT Section */}
                    {(automationModal.subject || automationModal.body) && (
                      <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: 2 }}>
                            <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 6 }} /> {automationEditing ? 'Edit Draft' : 'Draft Preview'}
                          </div>
                          {!automationEditing && (
                            <button onClick={() => setAutomationEditing(true)} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: `1px solid ${colors.gold}`, background: 'transparent', color: colors.gold, cursor: 'pointer', fontWeight: 600 }}>
                              <FontAwesomeIcon icon={faEdit} style={{ marginRight: 4 }} /> Edit
                            </button>
                          )}
                        </div>
                        {automationEditing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 600, color: '#888', display: 'block', marginBottom: 4 }}>Subject</label>
                              <input value={automationEditSubject} onChange={e => setAutomationEditSubject(e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, color: colors.navy }} />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 600, color: '#888', display: 'block', marginBottom: 4 }}>Message</label>
                              <textarea value={automationEditBody} onChange={e => setAutomationEditBody(e.target.value)} rows={8} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, color: colors.navy, resize: 'vertical', lineHeight: 1.5 }} />
                            </div>
                          </div>
                        ) : (
                          <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 16, border: '1px solid #eee' }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: colors.navy, marginBottom: 8 }}>{automationModal.subject}</div>
                            <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{automationModal.body}</div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* REGENERATE DRAFT Section */}
                    {(automationModal.subject || automationModal.body) && (
                      <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: colors.gold, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
                          <FontAwesomeIcon icon={faRobot} style={{ marginRight: 6 }} /> Regenerate Draft with Feedback
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <textarea
                            value={regenFeedback}
                            onChange={e => setRegenFeedback(e.target.value)}
                            placeholder={"Tell the AI what to change, e.g.:\n• \"This email is wrong — he already sold the property\"\n• \"Make it shorter and mention our upcoming event\""}
                            rows={3}
                            style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, color: colors.navy, resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit' }}
                          />
                          <button
                            disabled={!regenFeedback.trim() || regenLoading}
                            onClick={async () => {
                              if (!regenFeedback.trim()) return;
                              setRegenLoading(true);
                              try {
                                const res = await axiosInstance.post(`/ai-coach/queue/${automationModal.id}/regenerate`, {
                                  feedback: regenFeedback,
                                  currentSubject: automationEditing ? automationEditSubject : automationModal.subject,
                                  currentBody: automationEditing ? automationEditBody : automationModal.body,
                                });
                                if (res.data?.success) {
                                  setAutomationEditSubject(res.data.subject || automationModal.subject);
                                  setAutomationEditBody(res.data.body || automationModal.body);
                                  setAutomationEditing(true);
                                  setRegenFeedback('');
                                  toast.success('Draft regenerated with your feedback');
                                }
                              } catch (err) {
                                toast.error('Failed to regenerate draft');
                              }
                              setRegenLoading(false);
                            }}
                            style={{
                              alignSelf: 'flex-end',
                              fontSize: 12, padding: '8px 16px', borderRadius: 8,
                              border: 'none',
                              background: regenFeedback.trim() && !regenLoading
                                ? `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`
                                : '#f0f0f0',
                              color: regenFeedback.trim() ? colors.navy : '#aaa',
                              cursor: regenFeedback.trim() && !regenLoading ? 'pointer' : 'not-allowed',
                              fontWeight: 700, whiteSpace: 'nowrap',
                              display: 'flex', alignItems: 'center', gap: 6
                            }}
                          >
                            <FontAwesomeIcon icon={faRobot} />
                            {regenLoading ? 'Rewriting...' : 'Regenerate'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* SCHEDULE Section */}
                    <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
                        <FontAwesomeIcon icon={faClock} style={{ marginRight: 6 }} /> Schedule Send (Optional)
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <input type="datetime-local" value={automationScheduleDate} onChange={e => setAutomationScheduleDate(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, color: colors.navy }} />
                        <span style={{ fontSize: 12, color: '#888' }}>Leave blank to send immediately</span>
                      </div>
                    </div>

                    {/* Add Note Section */}
                    <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
                        <FontAwesomeIcon icon={faStickyNote} style={{ marginRight: 6 }} /> Add Note to Customer Record
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <textarea
                          value={automationNoteText}
                          onChange={e => setAutomationNoteText(e.target.value)}
                          placeholder="Add a note about this customer..."
                          rows={2}
                          style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, color: colors.navy, resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit' }}
                        />
                        <button
                          disabled={!automationNoteText.trim() || automationNoteSaving}
                          onClick={async () => {
                            if (!automationModal.lead_id || !automationNoteText.trim()) return;
                            setAutomationNoteSaving(true);
                            try {
                              await axiosInstance.post(`/agents/clients/${automationModal.lead_id}/note`, { note: automationNoteText });
                              toast.success('Note added to customer record');
                              setAutomationNoteText('');
                              setActivityData(null);
                            } catch (err) {
                              toast.error('Failed to add note');
                            }
                            setAutomationNoteSaving(false);
                          }}
                          style={{
                            alignSelf: 'flex-end',
                            fontSize: 12, padding: '8px 16px', borderRadius: 8,
                            border: `1px solid ${colors.gold}`,
                            background: automationNoteText.trim() ? `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})` : '#f0f0f0',
                            color: automationNoteText.trim() ? colors.navy : '#aaa',
                            cursor: automationNoteText.trim() && !automationNoteSaving ? 'pointer' : 'not-allowed',
                            fontWeight: 700, whiteSpace: 'nowrap'
                          }}
                        >
                          {automationNoteSaving ? 'Saving...' : 'Save Note'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Footer Actions */}
                  <div style={{ padding: '16px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa' }}>
                    <button onClick={() => handleDismissDraft(automationModal.id, 'dismissed_from_modal')} style={{ fontSize: 12, padding: '8px 20px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', color: '#888', cursor: 'pointer' }}>
                      Dismiss
                    </button>
                    <div style={{ display: 'flex', gap: 10 }}>
                      {automationScheduleDate && (
                        <button onClick={() => handleScheduleSend(automationModal.id, automationScheduleDate)} disabled={automationSending} style={{ fontSize: 12, padding: '8px 20px', borderRadius: 8, border: `1px solid ${colors.navy}`, background: '#fff', color: colors.navy, cursor: 'pointer', fontWeight: 600 }}>
                          <FontAwesomeIcon icon={faClock} style={{ marginRight: 6 }} />
                          {automationSending ? 'Scheduling...' : `Schedule for ${new Date(automationScheduleDate).toLocaleDateString()}`}
                        </button>
                      )}
                      <button onClick={() => handleSendDraft(automationModal.id, automationEditing ? automationEditSubject : undefined, automationEditing ? automationEditBody : undefined)} disabled={automationSending} style={{ fontSize: 12, padding: '8px 24px', borderRadius: 8, border: 'none', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy, cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FontAwesomeIcon icon={faPaperPlane} />
                        {automationSending ? 'Sending...' : automationEditing ? 'Send Edited' : 'Approve & Send'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ======== SELLERS DASHBOARD MODE (KOPS-645) ======== */}
      {pageMode === 'sellers' && (() => {
        const stageColors: Record<string, string> = {
          considering: '#6c757d', researching: '#17a2b8', preparing: '#ffc107',
          ready_to_list: colors.green, listing: colors.blue, showing: '#6f42c1',
          reviewing_offers: colors.orange, under_contract: '#20c997', closed: '#28a745',
          paused: '#adb5bd', lost: colors.red
        };
        const motivationColors: Record<string, string> = {
          low: '#6c757d', medium: colors.gold, high: colors.orange, urgent: colors.red
        };

        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Hero Banner */}
            <div style={{ background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.navy2} 100%)`, border: `1px solid ${colors.navy3}`, borderRadius: '12px', padding: '24px 32px', margin: '0 0 16px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ color: colors.gold, fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '4px' }}>Sellers</div>
                  <h2 style={{ color: colors.white, fontSize: '22px', fontWeight: 800, margin: 0 }}>Seller Dashboard</h2>
                  <p style={{ color: colors.muted, fontSize: '12px', margin: '4px 0 0' }}>Active listings, seller pipeline, and past clients</p>
                </div>
                <div style={{ display: 'flex', gap: '24px', textAlign: 'center' }}>
                  {[
                    { label: 'Active Listings', value: activeSellersStats.total || 0, color: colors.green },
                    { label: 'Pipeline', value: sellersStats.total || sellersTotal, color: colors.gold },
                    { label: 'Under Contract', value: activeSellersStats.under_contract || 0, color: '#20c997' },
                    { label: 'Avg Price', value: activeSellersStats.avg_price ? `$${Math.round(activeSellersStats.avg_price / 1000)}k` : '$0', color: colors.blue },
                  ].map((s, i) => (
                    <div key={i}>
                      <div style={{ color: s.color, fontSize: '22px', fontWeight: 800 }}>{s.value}</div>
                      <div style={{ color: colors.muted, fontSize: '10px', letterSpacing: '0.5px' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* KOPS-684: Sub-tab bar */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
              {[
                { key: 'active' as const, label: 'Active Sellers', count: activeSellersStats.total || 0 },
                { key: 'pipeline' as const, label: 'Seller Pipeline', count: sellersStats.total || sellersTotal },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setSellerSubTab(tab.key)}
                  style={{
                    background: sellerSubTab === tab.key ? colors.gold : colors.navy2,
                    color: sellerSubTab === tab.key ? colors.navy : colors.muted,
                    border: sellerSubTab === tab.key ? 'none' : `1px solid ${colors.navy3}`,
                    borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: 700,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                  }}
                >
                  {tab.label}
                  <span style={{
                    background: sellerSubTab === tab.key ? 'rgba(15,30,53,0.2)' : colors.navy3,
                    color: sellerSubTab === tab.key ? colors.navy : colors.gold,
                    borderRadius: '10px', padding: '1px 8px', fontSize: '11px', fontWeight: 800,
                  }}>{tab.count}</span>
                </button>
              ))}
              <button
                onClick={() => {
                  // + Add Seller — navigate to deal creation or open modal
                  navigate('?mode=deals');
                  setPageMode('deals');
                }}
                style={{
                  background: 'transparent', border: `1px dashed ${colors.gold}`, color: colors.gold,
                  borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontWeight: 700,
                  cursor: 'pointer', marginLeft: 'auto',
                }}
              >+ Add Seller</button>
            </div>

            {/* KOPS-684: Active Sellers Tab */}
            {sellerSubTab === 'active' && (
              <div style={{ flex: 1, overflow: 'auto' }}>
                {activeSellersLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', color: colors.muted }}>
                    <Spinner animation="border" size="sm" style={{ color: colors.gold, marginRight: '8px' }} /> Loading active sellers...
                  </div>
                ) : activeSellersList.length === 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px', color: colors.muted, background: colors.navy2, borderRadius: '12px', border: `1px solid ${colors.navy3}` }}>
                    <FontAwesomeIcon icon={faHome} style={{ fontSize: '36px', color: colors.gold, marginBottom: '12px' }} />
                    <div style={{ fontSize: '14px', fontWeight: 600, color: colors.white }}>No active listings</div>
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>Create a listing deal to see your active sellers here</div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '12px' }}>
                    {activeSellersList.map((seller: any) => {
                      const address = seller.address || 'No address';
                      const cityState = [seller.city, seller.state].filter(Boolean).join(', ');
                      const price = seller.price ? `$${Number(seller.price).toLocaleString()}` : 'No price';
                      const stageLabel = (seller.stage || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
                      const stageColor = seller.stage === 'under_contract' ? '#20c997' : seller.stage === 'pre_contract' ? colors.gold : colors.blue;

                      return (
                        <div key={seller.deal_id} style={{
                          background: colors.navy2, border: `1px solid ${colors.navy3}`, borderRadius: '12px',
                          padding: '20px', transition: 'border-color 0.2s', cursor: 'pointer',
                          borderLeft: `4px solid ${stageColor}`,
                        }}
                          onMouseEnter={(e) => (e.currentTarget.style.borderColor = colors.gold)}
                          onMouseLeave={(e) => (e.currentTarget.style.borderColor = colors.navy3)}
                          onClick={() => { navigate(`?mode=deals`); setPageMode('deals'); setSelectedDeal({ id: seller.deal_id }); }}
                        >
                          {/* Address + Price */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                            <div>
                              <div style={{ color: colors.white, fontSize: '15px', fontWeight: 700 }}>{address}</div>
                              <div style={{ color: colors.muted, fontSize: '12px', marginTop: '2px' }}>{cityState} {seller.zip || ''}</div>
                            </div>
                            <div style={{ color: colors.green, fontSize: '16px', fontWeight: 800 }}>{price}</div>
                          </div>

                          {/* Client Name */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                            <FontAwesomeIcon icon={faUser} style={{ color: colors.muted, fontSize: '11px' }} />
                            <span style={{ color: colors.white, fontSize: '13px', fontWeight: 600 }}>{seller.client_name}</span>
                            {seller.seller_email && (
                              <span style={{ color: colors.muted, fontSize: '11px' }}>{seller.seller_email}</span>
                            )}
                          </div>

                          {/* Stage + Compliance */}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                            <span style={{
                              background: stageColor, color: '#fff', fontSize: '10px', fontWeight: 700,
                              padding: '3px 10px', borderRadius: '10px', textTransform: 'uppercase', letterSpacing: '0.5px',
                            }}>{stageLabel}</span>
                            <span style={{
                              background: colors.navy3, color: seller.compliance > 50 ? colors.green : colors.muted,
                              fontSize: '10px', fontWeight: 700, padding: '3px 10px', borderRadius: '10px',
                            }}>{seller.compliance}% Compliance</span>
                          </div>

                          {/* Action Buttons */}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`?mode=deals`); setPageMode('deals'); setSelectedDeal({ id: seller.deal_id }); }}
                              style={{ flex: 1, background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', color: colors.navy, borderRadius: '6px', padding: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                            >Deal Portal</button>
                            {seller.seller_email && (
                              <button
                                onClick={(e) => { e.stopPropagation(); /* TODO: Send invite */ toast.success('Invite flow coming soon'); }}
                                style={{ background: colors.navy3, border: `1px solid ${colors.gold}`, color: colors.gold, borderRadius: '6px', padding: '6px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                              >Send Invite</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Seller Pipeline Tab - Filter Bar */}
            {sellerSubTab === 'pipeline' && <>
            {/* Filter Bar */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search by address, name, or email..."
                value={sellersSearch}
                onChange={(e) => setSellersSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setSellersPage(1); fetchSellers(1); } }}
                style={{ background: colors.navy2, border: `1px solid ${colors.navy3}`, color: colors.white, borderRadius: '6px', padding: '6px 12px', fontSize: '12px', width: '260px', outline: 'none' }}
              />
              <select
                value={sellersStageFilter}
                onChange={(e) => { setSellersStageFilter(e.target.value); setSellersPage(1); setTimeout(() => fetchSellers(1), 0); }}
                style={{ background: colors.navy2, border: `1px solid ${colors.navy3}`, color: colors.white, borderRadius: '6px', padding: '6px 10px', fontSize: '12px', outline: 'none' }}
              >
                <option value="all">All Stages</option>
                <option value="considering">Considering</option>
                <option value="researching">Researching</option>
                <option value="preparing">Preparing</option>
                <option value="ready_to_list">Ready to List</option>
                <option value="listing">Listing</option>
                <option value="under_contract">Under Contract</option>
                <option value="closed">Closed</option>
              </select>
              {/* KOPS-539: Grade filter */}
              <select
                value={sellersGradeFilter}
                onChange={(e) => setSellersGradeFilter(e.target.value)}
                style={{ background: colors.navy2, border: `1px solid ${colors.navy3}`, color: colors.white, borderRadius: '6px', padding: '6px 10px', fontSize: '12px', outline: 'none' }}
              >
                <option value="all">All Grades</option>
                <option value="A">Grade A — Call Now</option>
                <option value="B">Grade B — Priority</option>
                <option value="C">Grade C — Nurture</option>
                <option value="D">Grade D — Automate</option>
              </select>
              <button
                onClick={() => { setSellersPage(1); fetchSellers(1); }}
                style={{ background: colors.navy3, border: 'none', color: colors.white, borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >Search</button>
              {/* KOPS-539: Score All Sellers button */}
              <button
                onClick={handleScoreAllSellers}
                disabled={scoringRunning}
                style={{ background: `linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)`, border: 'none', color: 'white', borderRadius: '6px', padding: '6px 16px', fontSize: '12px', fontWeight: 700, cursor: scoringRunning ? 'not-allowed' : 'pointer', opacity: scoringRunning ? 0.7 : 1, marginLeft: '4px' }}
              >{scoringRunning ? 'Scoring...' : 'Score All Sellers'}</button>

              {/* Bulk Action Bar */}
              {selectedSellers.size > 0 && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ color: colors.muted, fontSize: '12px' }}>{selectedSellers.size} selected</span>
                  <button
                    onClick={handleBulkCma}
                    disabled={bulkCmaRunning}
                    style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', color: colors.navy, borderRadius: '6px', padding: '6px 16px', fontSize: '12px', fontWeight: 700, cursor: bulkCmaRunning ? 'not-allowed' : 'pointer', opacity: bulkCmaRunning ? 0.7 : 1 }}
                  >{bulkCmaRunning ? 'Running CMAs...' : `Send CMA (${selectedSellers.size})`}</button>
                  <button
                    onClick={() => { setRecurringCmaTarget({ type: 'bulk', leadIds: Array.from(selectedSellers) }); setRecurringCmaFrequency('monthly'); setShowRecurringCmaModal(true); }}
                    style={{ background: colors.navy3, border: `1px solid ${colors.gold}`, color: colors.gold, borderRadius: '6px', padding: '6px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                  >Schedule Recurring</button>
                  <button
                    onClick={() => setSelectedSellers(new Set())}
                    style={{ background: 'transparent', border: `1px solid ${colors.navy3}`, color: colors.muted, borderRadius: '6px', padding: '5px 10px', fontSize: '11px', cursor: 'pointer' }}
                  >Clear</button>
                </div>
              )}
            </div>

            {/* Seller Table */}
            <div style={{ flex: 1, overflow: 'auto', borderRadius: '8px', border: `1px solid ${colors.navy3}` }}>
              {/* Header Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '32px 1.5fr 1fr 100px 80px 100px 100px 110px 120px', gap: '8px', padding: '10px 16px', background: colors.navy, borderBottom: `1px solid ${colors.navy3}`, fontSize: '10px', fontWeight: 700, color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.8px', position: 'sticky', top: 0, zIndex: 1 }}>
                <div><input type="checkbox" onChange={(e) => {
                  if (e.target.checked) setSelectedSellers(new Set(sellersList.map(s => s.id)));
                  else setSelectedSellers(new Set());
                }} checked={selectedSellers.size === sellersList.length && sellersList.length > 0} /></div>
                <div>Property / Contact</div>
                <div>Location</div>
                <div>Value</div>
                <div style={{ textAlign: 'center' }}>Grade</div>
                <div>Stage</div>
                <div>Motivation</div>
                <div>CMA Schedule</div>
                <div style={{ textAlign: 'center' }}>Action</div>
              </div>

              {sellersLoading && !sellersList.length ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', color: colors.muted }}>
                  <Spinner animation="border" size="sm" style={{ color: colors.gold, marginRight: '8px' }} /> Loading sellers...
                </div>
              ) : sellersList.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px', color: colors.muted }}>
                  <FontAwesomeIcon icon={faDollarSign} style={{ fontSize: '36px', color: colors.gold, marginBottom: '12px' }} />
                  <div style={{ fontSize: '14px', fontWeight: 600, color: colors.white }}>No sellers found</div>
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>Sellers are created when leads are classified with property data</div>
                </div>
              ) : (
                <>
                  {sellersList.filter((seller: any) => {
                    if (sellersGradeFilter === 'all') return true;
                    const score = Number(seller.seller_score) || 0;
                    const grade = score >= 80 ? 'A' : score >= 60 ? 'B' : score >= 40 ? 'C' : 'D';
                    return grade === sellersGradeFilter;
                  }).map((seller: any) => {
                    const hasName = seller.lead_first_name && seller.lead_first_name !== 'Unknown' && seller.lead_first_name !== '';
                    const displayAddress = seller.property_address || 'No address';
                    const cityState = [seller.property_city, seller.property_state].filter(Boolean).join(', ');
                    const fullLocation = [cityState, seller.property_zip].filter(Boolean).join(' ');
                    const value = Number(seller.estimated_value) || 0;
                    const cmaDate = seller.last_cma_date ? new Date(seller.last_cma_date) : null;
                    const cmaDaysAgo = cmaDate ? Math.floor((Date.now() - cmaDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
                    const cmaStatus = sellerCmaStatus[seller.id];
                    const hasEmail = seller.lead_email && seller.lead_email !== 'unknown@example.com' && seller.lead_email !== '';

                    return (
                      <div key={seller.id} style={{
                        display: 'grid', gridTemplateColumns: '32px 1.5fr 1fr 100px 80px 100px 100px 110px 120px', gap: '8px',
                        padding: '10px 16px', borderBottom: `1px solid ${colors.navy3}`, background: colors.navy2,
                        alignItems: 'center', fontSize: '12px', transition: 'background 0.15s',
                        borderLeft: Number(seller.seller_score) >= 80 ? '3px solid #27ae60' : Number(seller.seller_score) >= 60 ? '3px solid #3498db' : '3px solid transparent',
                      }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = colors.navy3)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = colors.navy2)}
                      >
                        {/* Checkbox */}
                        <div><input type="checkbox" checked={selectedSellers.has(seller.id)}
                          onChange={(e) => {
                            const next = new Set(selectedSellers);
                            e.target.checked ? next.add(seller.id) : next.delete(seller.id);
                            setSelectedSellers(next);
                          }} /></div>

                        {/* Property / Contact */}
                        <div>
                          <div style={{ color: colors.white, fontWeight: 600, fontSize: '13px' }}>{displayAddress}</div>
                          {hasName && <div style={{ color: colors.muted, fontSize: '11px', marginTop: '2px' }}>{seller.lead_first_name} {seller.lead_last_name}</div>}
                          {hasEmail && <div style={{ color: colors.muted, fontSize: '10px', marginTop: '1px' }}>{seller.lead_email}</div>}
                        </div>

                        {/* Location */}
                        <div style={{ color: colors.muted }}>{fullLocation || '—'}</div>

                        {/* Value */}
                        <div style={{ color: value ? colors.green : colors.muted, fontWeight: 600 }}>
                          {value ? `$${Math.round(value).toLocaleString()}` : '—'}
                        </div>

                        {/* Grade Badge (KOPS-539) */}
                        <div style={{ textAlign: 'center' }}>
                          {(() => {
                            const score = Number(seller.seller_score) || 0;
                            const grade = score >= 80 ? 'A' : score >= 60 ? 'B' : score >= 40 ? 'C' : score > 0 ? 'D' : '—';
                            const gradeConfig: Record<string, { bg: string; color: string; glow: string; icon: string; label: string }> = {
                              A: { bg: 'linear-gradient(135deg, #27ae60, #2ecc71)', color: '#fff', glow: '0 0 8px rgba(39,174,96,0.5)', icon: '', label: 'Call Now' },
                              B: { bg: 'linear-gradient(135deg, #2980b9, #3498db)', color: '#fff', glow: '0 0 8px rgba(52,152,219,0.4)', icon: '', label: 'Priority' },
                              C: { bg: `${colors.navy3}`, color: '#f39c12', glow: 'none', icon: '', label: 'Nurture' },
                              D: { bg: `${colors.navy3}`, color: '#e74c3c', glow: 'none', icon: '', label: 'Auto' },
                            };
                            const cfg = gradeConfig[grade];
                            return cfg ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                <span style={{
                                  background: cfg.bg, color: cfg.color, padding: '4px 12px', borderRadius: '8px',
                                  fontSize: '13px', fontWeight: 900, letterSpacing: '1px', boxShadow: cfg.glow,
                                  display: 'inline-flex', alignItems: 'center', gap: '3px',
                                }} title={`Score: ${score}/100 — ${cfg.label}`}>
                                  {cfg.icon && <span style={{ fontSize: '10px' }}>{cfg.icon}</span>}{grade}
                                </span>
                                <span style={{ fontSize: '8px', color: grade === 'A' ? '#2ecc71' : grade === 'B' ? '#3498db' : colors.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  {cfg.label}
                                </span>
                              </div>
                            ) : (
                              <span style={{ color: colors.muted, fontSize: '10px' }}>—</span>
                            );
                          })()}
                        </div>

                        {/* Stage Badge */}
                        <div>
                          <span style={{
                            background: `${stageColors[seller.seller_stage] || '#6c757d'}20`,
                            color: stageColors[seller.seller_stage] || '#6c757d',
                            padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap'
                          }}>
                            {(seller.seller_stage || 'unknown').replace(/_/g, ' ')}
                          </span>
                        </div>

                        {/* Motivation Badge */}
                        <div>
                          <span style={{
                            background: `${motivationColors[seller.motivation_level] || '#6c757d'}20`,
                            color: motivationColors[seller.motivation_level] || '#6c757d',
                            padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600
                          }}>
                            {seller.motivation_level || '—'}
                          </span>
                        </div>

                        {/* CMA Schedule */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                          {seller.cma_frequency === 'monthly' ? (
                            <span style={{ background: '#3498db20', color: '#3498db', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600 }}>Monthly</span>
                          ) : seller.cma_frequency === 'quarterly' ? (
                            <span style={{ background: '#27ae6020', color: '#27ae60', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600 }}>Quarterly</span>
                          ) : cmaDate ? (
                            <span style={{ color: cmaDaysAgo !== null && cmaDaysAgo > 30 ? colors.orange : colors.muted, fontSize: '10px' }}>{cmaDaysAgo}d ago</span>
                          ) : (
                            <span style={{ color: colors.muted, fontSize: '10px' }}>Manual</span>
                          )}
                          {seller.next_cma_scheduled_at && (
                            <span style={{ color: colors.muted, fontSize: '9px' }}>
                              Next: {new Date(seller.next_cma_scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          {cmaStatus === 'running' ? (
                            <span style={{ color: colors.gold, fontSize: '11px' }}><Spinner animation="border" size="sm" style={{ width: '12px', height: '12px', marginRight: '4px' }} />Running...</span>
                          ) : cmaStatus === 'done' ? (
                            <span style={{ color: colors.green, fontSize: '11px', fontWeight: 600 }}>✓ Sent</span>
                          ) : cmaStatus === 'error' ? (
                            <button onClick={() => handleSellerCma(seller)}
                              style={{ background: colors.red, border: 'none', color: 'white', borderRadius: '6px', padding: '4px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                              Retry
                            </button>
                          ) : (
                            <button onClick={() => handleSellerCma(seller)}
                              style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', color: colors.navy, borderRadius: '6px', padding: '4px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                              View
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); setRecurringCmaTarget({ type: 'single', leadId: seller.id }); setRecurringCmaFrequency(seller.cma_frequency || 'monthly'); setShowRecurringCmaModal(true); }}
                            title="Set recurring CMA schedule"
                            style={{ background: 'transparent', border: `1px solid ${colors.navy3}`, color: colors.muted, borderRadius: '6px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}
                          >
                            <FontAwesomeIcon icon={faCalendarAlt} />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Load More / Pagination */}
                  {sellersList.length < sellersTotal && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                      <button
                        onClick={() => fetchSellers(sellersPage + 1, true)}
                        disabled={sellersLoading}
                        style={{ background: colors.navy3, border: 'none', color: colors.white, borderRadius: '6px', padding: '8px 24px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                      >{sellersLoading ? 'Loading...' : `Load More (${sellersList.length} of ${sellersTotal})`}</button>
                    </div>
                  )}
                </>
              )}
            </div>
            </>}
          </div>
        );
      })()}

      {/* KOPS-645: Recurring CMA Schedule Modal */}
      {showRecurringCmaModal && (
        <Modal show={showRecurringCmaModal} onHide={() => setShowRecurringCmaModal(false)} centered>
          <Modal.Header closeButton style={{ background: colors.navy, borderBottom: `1px solid ${colors.navy3}` }}>
            <Modal.Title style={{ color: colors.white, fontSize: '16px', fontWeight: 700 }}>
              <FontAwesomeIcon icon={faCalendarAlt} className="me-2" style={{ color: colors.gold }} />
              Set Up Recurring CMA
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: colors.navy2, padding: '24px' }}>
            <p style={{ color: colors.muted, fontSize: '13px', marginBottom: '20px' }}>
              CMA reports will be generated automatically and queued for your review before sending.
              {recurringCmaTarget?.type === 'bulk' && ` (${(recurringCmaTarget as any).leadIds.length} sellers selected)`}
            </p>
            {(['monthly', 'quarterly', 'manual'] as const).map((freq) => (
              <label key={freq} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', marginBottom: '8px',
                background: recurringCmaFrequency === freq ? `${colors.gold}15` : 'transparent',
                border: `1px solid ${recurringCmaFrequency === freq ? colors.gold : colors.navy3}` }}>
                <input type="radio" name="cmaFrequency" value={freq}
                  checked={recurringCmaFrequency === freq}
                  onChange={() => setRecurringCmaFrequency(freq)} />
                <div>
                  <div style={{ color: colors.white, fontSize: '13px', fontWeight: 600 }}>
                    {freq === 'monthly' ? 'Monthly' : freq === 'quarterly' ? 'Quarterly' : 'Manual (Cancel Recurring)'}
                  </div>
                  <div style={{ color: colors.muted, fontSize: '11px' }}>
                    {freq === 'monthly' ? 'Generate CMA every 30 days — $0.0004/email' : freq === 'quarterly' ? 'Generate CMA every 90 days' : 'Only send when you click "Send CMA"'}
                  </div>
                </div>
              </label>
            ))}
          </Modal.Body>
          <Modal.Footer style={{ background: colors.navy, borderTop: `1px solid ${colors.navy3}` }}>
            <Button variant="secondary" onClick={() => setShowRecurringCmaModal(false)} style={{ background: colors.navy3, border: 'none', color: colors.muted }}>
              Cancel
            </Button>
            <Button onClick={handleSaveRecurringCma} disabled={recurringCmaLoading}
              style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', color: colors.navy, fontWeight: 700 }}>
              {recurringCmaLoading ? 'Saving...' : 'Save Schedule'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* KOPS-662: CMA Send Modal — valuation + email composer + schedule */}
      {cmaSendModal && (
        <Modal show={!!cmaSendModal} onHide={() => setCmaSendModal(null)} centered size="lg">
          <Modal.Header closeButton style={{ background: colors.navy, borderBottom: `2px solid ${colors.gold}` }}>
            <Modal.Title style={{ color: colors.white, fontSize: '16px', fontWeight: 700 }}>
              <FontAwesomeIcon icon={faChartLine} className="me-2" style={{ color: colors.gold }} />
              CMA Report — {cmaSendModal.property_address || 'Property'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: '#f8f9fa', padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Valuation Summary */}
            <div style={{ background: colors.navy, borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
              {cmaSendLoading ? (
                <div style={{ textAlign: 'center', padding: '16px', color: colors.muted }}>
                  <Spinner animation="border" size="sm" style={{ color: colors.gold, marginRight: '8px' }} />
                  Running valuation...
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: colors.muted, fontSize: '10px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Estimated Value</div>
                      <div style={{ color: colors.gold, fontSize: '26px', fontWeight: 800 }}>
                        {cmaSendValuation?.estimated_value ? `$${Number(cmaSendValuation.estimated_value).toLocaleString()}`
                          : Number(cmaSendModal.estimated_value) ? `$${Number(cmaSendModal.estimated_value).toLocaleString()}` : '—'}
                      </div>
                      {cmaSendValuation?.confidence ? (
                        <div style={{ color: colors.muted, fontSize: '10px', marginTop: '2px' }}>{cmaSendValuation.confidence}% confidence</div>
                      ) : null}
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: colors.muted, fontSize: '10px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Price Range</div>
                      <div style={{ color: colors.white, fontSize: '16px', fontWeight: 700 }}>
                        {cmaSendValuation?.range_low ? `$${Number(cmaSendValuation.range_low).toLocaleString()} — $${Number(cmaSendValuation.range_high).toLocaleString()}` : '—'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: colors.muted, fontSize: '10px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Median in Area</div>
                      <div style={{ color: colors.white, fontSize: '16px', fontWeight: 700 }}>
                        {cmaSendValuation?.median_price ? `$${Number(cmaSendValuation.median_price).toLocaleString()}` : '—'}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: colors.muted, fontSize: '10px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Price/SqFt</div>
                      <div style={{ color: colors.white, fontSize: '16px', fontWeight: 700 }}>
                        {cmaSendValuation?.median_ppsf ? `$${Number(cmaSendValuation.median_ppsf).toLocaleString()}` : '—'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: colors.muted, fontSize: '10px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Comparables</div>
                      <div style={{ color: colors.white, fontSize: '16px', fontWeight: 700 }}>
                        {cmaSendValuation?.comparables_found || '—'}
                      </div>
                    </div>
                  </div>
                  {cmaSendValuation?.insights?.length > 0 && (
                    <div style={{ marginTop: '12px', borderTop: `1px solid ${colors.navy3}`, paddingTop: '12px' }}>
                      {cmaSendValuation.insights.map((insight: string, i: number) => (
                        <div key={i} style={{ fontSize: '11px', color: colors.muted, padding: '2px 0' }}>• {insight}</div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Email Composer */}
            <div style={{ background: '#fff', borderRadius: '10px', padding: '20px', border: '1px solid #dee2e6', borderLeft: `4px solid ${colors.gold}` }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '12px' }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '6px', color: colors.gold }} />
                Email Preview
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '10px', color: colors.navy, fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>To</label>
                <input value={cmaSendTo} onChange={e => setCmaSendTo(e.target.value)} placeholder="client@email.com"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #dee2e6', background: '#fff', color: colors.navy, fontSize: '13px', outline: 'none' }}
                  onFocus={e => { e.currentTarget.style.borderColor = colors.gold; }} onBlur={e => { e.currentTarget.style.borderColor = '#dee2e6'; }} />
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '10px', color: colors.navy, fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Cc <span style={{ fontWeight: 400, color: '#999' }}>(optional)</span></label>
                <input value={cmaSendCc} onChange={e => setCmaSendCc(e.target.value)} placeholder="cc@email.com"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #dee2e6', background: '#fff', color: colors.navy, fontSize: '13px', outline: 'none' }}
                  onFocus={e => { e.currentTarget.style.borderColor = colors.gold; }} onBlur={e => { e.currentTarget.style.borderColor = '#dee2e6'; }} />
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '10px', color: colors.navy, fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Subject</label>
                <input value={cmaSendSubject} onChange={e => setCmaSendSubject(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #dee2e6', background: '#fff', color: colors.navy, fontSize: '13px', fontWeight: 600, outline: 'none' }}
                  onFocus={e => { e.currentTarget.style.borderColor = colors.gold; }} onBlur={e => { e.currentTarget.style.borderColor = '#dee2e6'; }} />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '10px', color: colors.navy, fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Message</label>
                <textarea value={cmaSendBody} onChange={e => setCmaSendBody(e.target.value)} rows={12}
                  style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #dee2e6', background: '#fff', color: '#333', fontSize: '13px', lineHeight: 1.7, outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                  onFocus={e => { e.currentTarget.style.borderColor = colors.gold; }} onBlur={e => { e.currentTarget.style.borderColor = '#dee2e6'; }} />
              </div>
            </div>

            {/* Schedule Options */}
            <div style={{ background: '#fff', borderRadius: '10px', padding: '16px', marginTop: '12px', border: '1px solid #dee2e6' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: colors.navy, marginBottom: '10px' }}>
                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '6px', color: colors.gold }} />
                Recurring Schedule
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[
                  { key: 'send_now', label: 'Send Now Only', desc: 'One-time send' },
                  { key: 'monthly', label: 'Monthly', desc: 'Auto-send every 30 days' },
                  { key: 'quarterly', label: 'Quarterly', desc: 'Auto-send every 90 days' },
                  { key: 'manual', label: 'Manual', desc: 'No auto-send' },
                ].map(opt => (
                  <label key={opt.key} style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '8px',
                    border: `1px solid ${cmaSendFrequency === opt.key ? colors.gold : '#dee2e6'}`,
                    background: cmaSendFrequency === opt.key ? `${colors.gold}10` : '#fff',
                    cursor: 'pointer', flex: '1 1 auto',
                  }}>
                    <input type="radio" name="cmaSchedule" checked={cmaSendFrequency === opt.key as any}
                      onChange={() => setCmaSendFrequency(opt.key as any)} />
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: colors.navy }}>{opt.label}</div>
                      <div style={{ fontSize: '10px', color: '#999' }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer style={{ background: '#f8f9fa', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '10px', color: '#999' }}>Review and edit before sending — agent owns the relationship</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="secondary" onClick={() => setCmaSendModal(null)} style={{ fontSize: '13px' }}>Cancel</Button>
              <Button onClick={handleCmaSendApprove} disabled={cmaSendSending || !cmaSendTo.trim()}
                style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', color: colors.navy, fontWeight: 700, fontSize: '13px', padding: '8px 24px' }}>
                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                {cmaSendSending ? 'Sending...' : 'Approve & Send'}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}

      {/* ======== CLIENTS MODE ======== */}
      {pageMode === 'clients' && (
      <>
      {/* Stats Strip */}
      <div style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '16px 24px' }}>
        <div className="d-flex gap-4 align-items-center">
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faUsers} style={{ color: colors.blue }} />
            <span style={{ color: colors.white, fontWeight: 700, fontSize: '18px' }}>{clientTotals.total || clients.length}</span>
            <span style={{ color: colors.muted, fontSize: '12px' }}>{isSponsorPlan ? 'Total Contacts' : 'Total Clients'}</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faHome} style={{ color: colors.green }} />
            <span style={{ color: colors.white, fontWeight: 700, fontSize: '18px' }}>{isSponsorPlan ? clients.filter(c => c.classification === 'past_buyer').length : (clientTotals.buyers || clients.filter(c => c.has_buyer_profile).length)}</span>
            <span style={{ color: colors.muted, fontSize: '12px' }}>{isSponsorPlan ? 'Past Borrowers' : 'Buyers'}</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faDollarSign} style={{ color: colors.orange }} />
            <span style={{ color: colors.white, fontWeight: 700, fontSize: '18px' }}>{isSponsorPlan ? clients.filter(c => ['active_buyer', 'new_contact'].includes(c.classification)).length : (clientTotals.sellers || clients.filter(c => c.has_seller_profile).length)}</span>
            <span style={{ color: colors.muted, fontSize: '12px' }}>{isSponsorPlan ? 'Active Pipeline' : 'Sellers'}</span>
          </div>
        </div>
      </div>

      {/* Split Panel */}
      <div style={{ display: 'flex', height: 'calc(100vh - 145px)' }}>

        {/* Left Panel - Client List */}
        <div style={{ width: panelCollapsed ? '0px' : '350px', minWidth: panelCollapsed ? '0px' : '350px', background: colors.navy2, borderRight: panelCollapsed ? 'none' : `1px solid ${colors.navy3}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'width 0.2s ease, min-width 0.2s ease' }}>
          {/* Search + Add Button */}
          <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
            <div className="d-flex gap-2 mb-2">
              <div style={{ position: 'relative', flex: 1 }}>
                <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '10px', top: '10px', color: colors.muted, fontSize: '12px' }} />
                <input type="text" placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)}
                  style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '8px 8px 8px 30px', color: colors.white, fontSize: '13px', outline: 'none' }} />
              </div>
              {userPlan === 'internal' && (
                <Button size="sm" title={demoMode ? 'Exit Demo Mode' : 'Demo Mode (blur PII)'}
                  onClick={() => { const next = !demoMode; setDemoMode(next); sessionStorage.setItem('demoMode', String(next)); }}
                  style={{ background: demoMode ? '#f44336' : colors.navy3, border: 'none', fontSize: '10px', padding: '4px 8px', color: 'white', borderRadius: '4px' }}
                >
                  {demoMode ? 'DEMO' : 'Demo'}
                </Button>
              )}
              <Button size="sm"
                onClick={() => setShowAddClient(!showAddClient)}
                style={{ background: showAddClient ? colors.navy3 : `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap', color: showAddClient ? colors.muted : colors.navy }}
              >
                {showAddClient ? '✕' : '+ Add'}
              </Button>
            </div>

            {/* Inline Add Client Form */}
            {showAddClient && (
              <div style={{ background: colors.navy3, borderRadius: '8px', padding: '10px', marginTop: '4px' }}>
                <div className="d-flex gap-2 mb-2">
                  <input placeholder="First name" value={newClient.firstName} onChange={e => setNewClient({ ...newClient, firstName: e.target.value })}
                    style={{ flex: 1, background: colors.navy2, border: '1px solid rgba(201,149,42,0.2)', borderRadius: '4px', padding: '6px 8px', color: colors.white, fontSize: '12px', outline: 'none' }} />
                  <input placeholder="Last name" value={newClient.lastName} onChange={e => setNewClient({ ...newClient, lastName: e.target.value })}
                    style={{ flex: 1, background: colors.navy2, border: '1px solid rgba(201,149,42,0.2)', borderRadius: '4px', padding: '6px 8px', color: colors.white, fontSize: '12px', outline: 'none' }} />
                </div>
                <input placeholder="Email" value={newClient.email} onChange={e => setNewClient({ ...newClient, email: e.target.value })}
                  style={{ width: '100%', background: colors.navy2, border: '1px solid rgba(201,149,42,0.2)', borderRadius: '4px', padding: '6px 8px', color: colors.white, fontSize: '12px', outline: 'none', marginBottom: '6px' }} />
                <input placeholder="Phone" value={newClient.phone} onChange={e => setNewClient({ ...newClient, phone: e.target.value })}
                  style={{ width: '100%', background: colors.navy2, border: '1px solid rgba(201,149,42,0.2)', borderRadius: '4px', padding: '6px 8px', color: colors.white, fontSize: '12px', outline: 'none', marginBottom: '6px' }} />
                <select value={newClient.source} onChange={e => setNewClient({ ...newClient, source: e.target.value })}
                  style={{ width: '100%', background: colors.navy2, border: '1px solid rgba(201,149,42,0.2)', borderRadius: '4px', padding: '6px 8px', color: colors.white, fontSize: '12px', outline: 'none', marginBottom: '8px' }}>
                  <option value="manual">Manual Entry</option>
                  <option value="zillow">Zillow</option>
                  <option value="facebook">Facebook</option>
                  <option value="realtor.com">Realtor.com</option>
                  <option value="referral">Referral</option>
                  <option value="open_house">Open House</option>
                  <option value="website">Website</option>
                  <option value="other">Other</option>
                </select>
                <Button size="sm" className="w-100"
                  onClick={handleAddClient}
                  disabled={addingClient || (!newClient.email && !newClient.phone)}
                  style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, fontSize: '12px', color: colors.navy }}
                >
                  {addingClient ? <Spinner size="sm" /> : 'Add Client'}
                </Button>
              </div>
            )}
          </div>

          {/* Filter Pills */}
          <div style={{ padding: '6px 12px', borderBottom: `1px solid ${colors.navy3}`, display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {(isSponsorPlan ? [
              { key: 'all', label: 'All' },
              { key: 'past_buyer', label: 'Past Borrowers', color: '#27ae60' },
              { key: 'active_buyer', label: 'Active Pipeline', color: '#2980b9' },
              { key: 'cold_reactivation', label: 'Cold/Lost', color: '#e67e22' },
              { key: 'new_contact', label: 'Unconverted', color: '#8e44ad' },
              { key: 'foreclosure', label: '🚨 Foreclosure', color: '#c62828' },
              { key: 'death_deed', label: '⚠️ Death Deed', color: '#e65100' },
              { key: 'archived', label: 'Archived', color: '#78909c' },
            ] : [
              { key: 'all', label: 'All' },
              { key: 'sellers', label: 'Sellers' },
              { key: 'buyers', label: 'Buyers' },
              { key: 'foreclosure', label: '🚨 Foreclosure', color: '#c62828' },
              { key: 'death_deed', label: '⚠️ Death Deed', color: '#e65100' },
              { key: 'absentee', label: '✈️ Absentee', color: '#1565c0' },
              { key: 'new', label: 'New' },
              { key: 'archived', label: 'Archived', color: '#78909c' },
            ]).map(f => (
              <button key={f.key} onClick={() => { setClientFilter(f.key); setLoading(true); fetchClients(f.key); }}
                style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
                  background: clientFilter === f.key ? (f.color || colors.gold) : colors.navy3, color: clientFilter === f.key ? 'white' : colors.muted
                }}
              >{f.label}</button>
            ))}
          </div>

          {/* Client List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.gold }} /></div>
            ) : clients.length === 0 ? (
              <div className="text-center py-5" style={{ color: colors.muted }}>
                <FontAwesomeIcon icon={faUsers} style={{ fontSize: '32px', marginBottom: '8px' }} /><br />
                {search ? 'No clients found' : 'No clients yet. Invite your first client!'}
              </div>
            ) : (
              clients.map(client => (
                <div key={client.id} onClick={() => selectClient(client)}
                  ref={el => { if (el && selected?.id === client.id && !el.dataset.scrolled) { el.dataset.scrolled = 'true'; setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300); } }}
                  style={{
                    padding: '12px 16px', cursor: 'pointer', borderBottom: `1px solid ${colors.navy3}`,
                    background: selected?.id === client.id ? colors.navy3 : 'transparent',
                    borderLeft: selected?.id === client.id ? `3px solid ${colors.gold}` : '3px solid transparent'
                  }}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div style={{ color: colors.white, fontWeight: 600, fontSize: '14px', ...blur }}>{getClientName(client)}</div>
                      <div style={{ color: colors.muted, fontSize: '12px', ...blur }}>{getClientEmail(client)}</div>
                      {getClientPhone(client) && <div style={{ color: colors.muted, fontSize: '11px', ...blur }}>{getClientPhone(client)}</div>}
                      {isSponsorPlan && client.lead_street_name && <div style={{ color: colors.gold, fontSize: '10px', ...blur }}><FontAwesomeIcon icon={faHome} style={{ marginRight: 3, fontSize: 9 }} />{client.lead_street_name}{client.lead_city ? `, ${client.lead_city}` : ''}</div>}
                    </div>
                    <div className="text-end">
                      {client.classification && client.classification !== 'imported' ? (
                        <span style={{
                          fontSize: '9px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px',
                          background: client.classification === 'past_buyer' ? '#e8f5e920' :
                            client.classification === 'past_seller' ? '#fff3e020' :
                            client.classification === 'investor' ? '#e3f2fd20' :
                            client.classification === 'vendor' ? '#f3e5f520' :
                            client.classification === 'hot_buyer' ? '#ffebee20' : '#f5f5f520',
                          color: client.classification === 'past_buyer' ? '#4caf50' :
                            client.classification === 'past_seller' ? '#ff9800' :
                            client.classification === 'investor' ? '#2196f3' :
                            client.classification === 'vendor' ? '#9c27b0' :
                            client.classification === 'hot_buyer' ? '#f44336' : '#999'
                        }}>
                          {client.classification.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      ) : (
                        <span style={{
                          fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px',
                          background: getProfileColor(getProfileType(client)) + '20',
                          color: getProfileColor(getProfileType(client))
                        }}>
                          {getProfileType(client)}
                        </span>
                      )}
                      {/* lead_tags can arrive as string[], JSON string of
                          string[], or JSON string of [{tags: string}] —
                          normalize to a clean string[] before rendering
                          or React blows up with "Objects are not valid
                          as a React child (found: object with keys {tags})". */}
                      {client.lead_tags && (() => {
                        try {
                          const raw = typeof client.lead_tags === 'string'
                            ? JSON.parse(client.lead_tags)
                            : client.lead_tags;
                          if (!Array.isArray(raw)) return null;
                          const tags: string[] = raw
                            .map((item: any): string | null => {
                              if (item == null) return null;
                              if (typeof item === 'string') return item;
                              if (typeof item === 'object') {
                                return typeof item.tags === 'string' ? item.tags
                                     : typeof item.tag === 'string' ? item.tag
                                     : typeof item.name === 'string' ? item.name
                                     : null;
                              }
                              return String(item);
                            })
                            .filter((t: string | null): t is string => !!t);
                          if (tags.length === 0) return null;
                          return (
                            <div style={{ marginTop: '3px' }}>
                              {tags.slice(0, 2).map((t: string) => (
                                <span key={t} style={{ fontSize: '8px', background: '#C9952A', color: 'white', padding: '1px 5px', borderRadius: '8px', marginLeft: '3px' }}>{t}</span>
                              ))}
                            </div>
                          );
                        } catch {
                          return null;
                        }
                      })()}
                      <div style={{ color: colors.muted, fontSize: '10px', marginTop: '3px' }}>
                        {(client.created_on || client.created_at) ? new Date(client.created_on || client.created_at).toLocaleDateString() : ''}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Panel Toggle + Right Panel */}
        <div
          onClick={() => setPanelCollapsed(!panelCollapsed)}
          style={{ width: '24px', minWidth: '24px', background: colors.navy2, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRight: `1px solid ${colors.navy3}`, transition: 'background 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.background = colors.navy3; }}
          onMouseLeave={e => { e.currentTarget.style.background = colors.navy2; }}
          title={panelCollapsed ? 'Show client list' : 'Hide client list'}
        >
          <span style={{ color: colors.muted, fontSize: '14px', fontWeight: 700, userSelect: 'none' }}>
            {panelCollapsed ? '›' : '‹'}
          </span>
        </div>

        {/* Right Panel - Client Detail */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#eef1f6' }}>
          {!selected ? (
            <div style={{ padding: '24px' }}>
              <h4 style={{ color: colors.navy, fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', marginBottom: '20px' }}>
                Client <span style={{ color: colors.gold }}>Dashboard</span>
              </h4>

              {/* Quick Stats */}
              <Row className="g-3 mb-4">
                {(isSponsorPlan ? [
                  { label: 'Total Contacts', value: clientTotals.total || clients.length, color: colors.blue, icon: faUsers },
                  { label: 'Past Borrowers', value: clients.filter(c => c.classification === 'past_buyer').length, color: colors.green, icon: faHome },
                  { label: 'Active Pipeline', value: clients.filter(c => ['active_buyer'].includes(c.classification)).length, color: '#2980b9', icon: faChartLine },
                  { label: 'Unconverted Leads', value: clients.filter(c => ['new_contact', 'cold_reactivation'].includes(c.classification)).length, color: '#e67e22', icon: faExclamationTriangle },
                ] : [
                  { label: 'Total Clients', value: clientTotals.total || clients.length, color: colors.blue, icon: faUsers },
                  { label: 'Buyers', value: clientTotals.buyers || clients.filter(c => c.has_buyer_profile).length, color: colors.green, icon: faHome },
                  { label: 'Sellers', value: clientTotals.sellers || clients.filter(c => c.has_seller_profile).length, color: colors.orange, icon: faDollarSign },
                  { label: 'New This Month', value: clients.filter(c => { const d = new Date(c.created_on || c.created_at); const now = new Date(); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); }).length, color: '#8e44ad', icon: faUserPlus },
                ]).map((s, i) => (
                  <Col md={3} key={i}>
                    <div style={{ background: 'white', borderRadius: '10px', padding: '16px', border: '1px solid #e9ecef', borderTop: `3px solid ${s.color}` }}>
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={s.icon} style={{ color: s.color, fontSize: '16px' }} />
                        <span style={{ fontSize: '24px', fontWeight: 700, color: colors.navy }}>{s.value}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>{s.label}</div>
                    </div>
                  </Col>
                ))}
              </Row>

              {/* Property Intelligence Scanner — Lender accounts */}
              {isSponsorPlan && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px 20px', border: '1px solid #e9ecef', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: colors.navy }}>
                      <FontAwesomeIcon icon={faHome} className="me-2" style={{ color: colors.gold }} />Property Intelligence
                    </div>
                    <div style={{ fontSize: '12px', color: '#888' }}>
                      Scan county records for your borrowers — find property values, ownership changes, foreclosures, and triggers.
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      toast.success('Property scan started for past borrowers. This runs in the background — results appear on each contact.');
                      try {
                        await axiosInstance.post('/agents/scan-properties', { filter: 'past_buyer' });
                      } catch { }
                    }}
                    style={{ padding: '8px 20px', fontSize: '12px', fontWeight: 700, borderRadius: '6px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                      background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy }}
                  >
                    Scan Past Borrowers
                  </button>
                </div>
              )}

              {/* Recent Clients */}
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginBottom: '20px' }}>
                <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '16px' }}>
                  <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: colors.gold }} />Recent Clients
                </h6>
                {clients.length === 0 ? (
                  <p style={{ color: '#999', textAlign: 'center', padding: '10px 0' }}>No clients yet. Invite your first client!</p>
                ) : (
                  clients.slice(0, 8).map((client: any) => (
                    <div key={client.id} onClick={() => selectClient(client)}
                      style={{ padding: '10px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', borderRadius: '6px' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div style={{ fontWeight: 600, color: colors.navy, fontSize: '14px' }}>{getClientName(client)}</div>
                          <div style={{ fontSize: '12px', color: '#888' }}>{getClientEmail(client)}</div>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                          <span style={{
                            fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px',
                            background: getProfileColor(getProfileType(client)) + '20',
                            color: getProfileColor(getProfileType(client))
                          }}>
                            {getProfileType(client)}
                          </span>
                          <span style={{ fontSize: '11px', color: '#999' }}>
                            {(client.created_on || client.created_at) ? new Date(client.created_on || client.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Quick Actions — agent only */}
              {!isSponsorPlan && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>
                <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px' }}>
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" style={{ color: colors.green }} />Quick Actions
                </h6>
                <div className="d-flex gap-2 flex-wrap">
                  <Button size="sm" onClick={() => setShowInvite(true)}
                    style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}>
                    <FontAwesomeIcon icon={faUserPlus} className="me-1" /> Invite New Client
                  </Button>
                  <Button size="sm" variant="outline-dark" onClick={() => navigate('/agent-portal/dashboard')}>
                    <FontAwesomeIcon icon={faChartLine} className="me-1" /> View Dashboard
                  </Button>
                  <Button size="sm" variant="outline-dark" onClick={() => navigate('/smart-cma-workflow')}>
                    <FontAwesomeIcon icon={faDollarSign} className="me-1" /> New Seller CMA
                  </Button>
                </div>
              </div>
              )}
            </div>
          ) : detailLoading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.gold }} /></div>
          ) : (
            <div style={{ padding: '24px' }}>
              {/* Profile Header */}
              <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '24px', marginBottom: '20px', borderLeft: `4px solid ${colors.gold}` }}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 style={{ color: colors.navy, fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', letterSpacing: '1px', margin: 0, ...blur }}>
                      {getClientName(selected)}
                    </h3>
                    <div className="d-flex gap-3 mt-2" style={{ fontSize: '14px' }}>
                      <span style={{ color: '#555', ...blur }}><FontAwesomeIcon icon={faEnvelope} className="me-1" style={{ color: colors.gold }} />{getClientEmail(selected)}</span>
                      {getClientPhone(selected) && <span style={{ color: '#555', ...blur }}><FontAwesomeIcon icon={faPhone} className="me-1" style={{ color: colors.gold }} />{getClientPhone(selected)}</span>}
                    </div>
                    {/* Pipeline Stage + Address */}
                    <div className="d-flex gap-2 mt-2 align-items-center flex-wrap">
                      {clientDetail?.ai_context?.journeyStage && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12,
                          background: ['past_buyer', 'past_seller'].includes(clientDetail.ai_context.journeyStage) ? '#e8f5e9' :
                            ['active_buyer', 'active_seller', 'under_contract'].includes(clientDetail.ai_context.journeyStage) ? '#e3f2fd' :
                            ['cold_reactivation'].includes(clientDetail.ai_context.journeyStage) ? '#fff3e0' : '#f5f5f5',
                          color: ['past_buyer', 'past_seller'].includes(clientDetail.ai_context.journeyStage) ? '#2e7d32' :
                            ['active_buyer', 'active_seller', 'under_contract'].includes(clientDetail.ai_context.journeyStage) ? '#1565c0' :
                            ['cold_reactivation'].includes(clientDetail.ai_context.journeyStage) ? '#e65100' : '#666',
                        }}>
                          {(isSponsorPlan ? {
                            new_contact: 'New Lead', hot_lead: 'Hot Lead', active_buyer: 'Application',
                            pre_qualified: 'Pre-Qualified', under_contract: 'Under Contract',
                            past_buyer: 'Funded', cold_reactivation: 'Cold/Lost', nurture: 'Nurture'
                          } : {
                            new_contact: 'New', active_buyer: 'Active Buyer', active_seller: 'Active Seller',
                            past_buyer: 'Past Buyer', past_seller: 'Past Seller',
                            cold_reactivation: 'Cold', sphere_of_influence: 'Sphere', investor: 'Investor'
                          } as any)[clientDetail.ai_context.journeyStage] || clientDetail.ai_context.journeyStage.replace(/_/g, ' ')}
                        </span>
                      )}
                      {isSponsorPlan && selected?.lead_street_name && (
                        <span style={{ fontSize: 12, color: '#555' }}>
                          <FontAwesomeIcon icon={faHome} className="me-1" style={{ color: colors.gold, fontSize: 10 }} />
                          {selected.lead_street_name}{selected.lead_city ? `, ${selected.lead_city}` : ''}
                        </span>
                      )}
                    </div>
                    {/* Agent Assignment (Lender view) */}
                    {isSponsorPlan && (
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <FontAwesomeIcon icon={faUser} style={{ color: colors.gold, fontSize: 11 }} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#888' }}>Agent:</span>
                        {(() => {
                          const assignedName = clientDetail?.detail?.assignedAgent?.name || clientDetail?.detail?.tags?.assigned_agent_name || '';
                          const assignedEmail = clientDetail?.detail?.assignedAgent?.email || clientDetail?.detail?.tags?.assigned_agent_email || '';
                          const currentValue = assignedName ? `${assignedName}|${assignedEmail}` : '';
                          return (
                            <select
                              value={currentValue}
                              onChange={async (e) => {
                                const selectedOpt = e.target.value;
                                if (!selected?.id) return;
                                const [name, email] = selectedOpt ? selectedOpt.split('|') : ['', ''];
                                try {
                                  await axiosInstance.patch(`/sponsor/deal-board/${selected.id}/assign-agent`, {
                                    agentName: name || null,
                                    agentEmail: email || null,
                                  });
                                  toast.success(name ? `Assigned to ${name}` : 'Agent unassigned');
                                  const { data } = await axiosInstance.get(`/sponsor/deal-board/${selected.id}/detail`);
                                  setClientDetail((prev: any) => ({
                                    ...prev,
                                    detail: { ...(prev?.detail || {}), ...(data?.detail || {}), assignedAgent: data?.detail?.assignedAgent },
                                  }));
                                } catch { toast.error('Failed to assign agent'); }
                              }}
                              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: assignedName ? `1px solid ${colors.gold}` : '1px solid #ddd', color: colors.navy, maxWidth: 220, background: assignedName ? '#FBF4DA' : '#fff' }}
                            >
                              <option value="">— Select Agent —</option>
                              {(sponsoredAgents || []).map((a: any, i: number) => {
                                const agentName = a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim();
                                const agentEmail = a.email || '';
                                return (
                                  <option key={i} value={`${agentName}|${agentEmail}`}>
                                    {agentName}
                                  </option>
                                );
                              })}
                            </select>
                          );
                        })()}
                      </div>
                    )}
                    {/* KOPS-577: View Deal link */}
                    {(() => {
                      const leadId = selected?.id;
                      if (!leadId) return null;
                      return (
                        <ViewDealLink
                          leadId={leadId}
                          isSponsor={isSponsorPlan}
                          onNavigate={(mode) => {
                            setPageMode(mode as any);
                            window.history.replaceState({}, '', `${window.location.pathname}?mode=${mode}`);
                          }}
                        />
                      );
                    })()}
                  </div>
                  <div className="d-flex gap-2">
                    <span style={{
                      fontSize: '12px', fontWeight: 700, padding: '4px 12px', borderRadius: '6px',
                      background: getProfileColor(getProfileType(selected)) + '20',
                      color: getProfileColor(getProfileType(selected))
                    }}>
                      {getProfileType(selected)}
                    </span>
                    <Button size="sm" onClick={startEditLead} variant="outline-dark" style={{ fontSize: '12px' }}>
                      <FontAwesomeIcon icon={faEdit} className="me-1" /> Edit
                    </Button>
                    {clientFilter === 'archived' ? (
                      <Button size="sm" onClick={restoreClient} variant="outline-success" style={{ fontSize: '12px' }}>
                        Restore
                      </Button>
                    ) : (
                      <Button size="sm" onClick={archiveClient} variant="outline-secondary" style={{ fontSize: '12px' }} title="Hide from lists — all history kept for audit">
                        Archive
                      </Button>
                    )}
                  </div>
                </div>

                {/* Inline Lead Edit Form */}
                {editingLead && (
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
                    <Row className="g-2">
                      <Col md={3}>
                        <input placeholder="First name" value={editLeadData.lead_first_name} onChange={e => setEditLeadData({ ...editLeadData, lead_first_name: e.target.value })}
                          style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', fontSize: '13px' }} />
                      </Col>
                      <Col md={3}>
                        <input placeholder="Last name" value={editLeadData.lead_last_name} onChange={e => setEditLeadData({ ...editLeadData, lead_last_name: e.target.value })}
                          style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', fontSize: '13px' }} />
                      </Col>
                      <Col md={3}>
                        <input placeholder="Email" value={editLeadData.lead_email} onChange={e => setEditLeadData({ ...editLeadData, lead_email: e.target.value })}
                          style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', fontSize: '13px' }} />
                      </Col>
                      <Col md={3}>
                        <input placeholder="Phone" value={editLeadData.lead_phone} onChange={e => setEditLeadData({ ...editLeadData, lead_phone: e.target.value })}
                          style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', fontSize: '13px' }} />
                      </Col>
                    </Row>
                    <div className="d-flex gap-2 mt-2">
                      <Button size="sm" onClick={saveLeadEdit} disabled={savingLead}
                        style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, fontSize: '12px' }}>
                        {savingLead ? <Spinner size="sm" /> : 'Save'}
                      </Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => setEditingLead(false)} style={{ fontSize: '12px' }}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div className="d-flex gap-1 mb-3">
                {([
                  { key: 'overview', label: 'Overview' },
                  ...(!isSponsorPlan && !isBrokerComplianceOnly ? [{ key: 'ai-search' as const, label: 'AI Search' }] : []),
                  ...(!isBrokerComplianceOnly ? [{ key: 'email', label: 'Email' }] : []),
                  ...(!isBrokerComplianceOnly ? [{ key: 'buyer', label: isSponsorPlan ? 'Loan Details' : 'Buyer Profile' }] : []),
                  ...(!isSponsorPlan && !isBrokerComplianceOnly ? [{ key: 'seller' as const, label: 'Seller Profile' }] : []),
                  ...(!isSponsorPlan && !isBrokerComplianceOnly ? [{ key: 'offer-analyzer' as const, label: 'Offer Analyzer' }] : []),
                  { key: 'activity', label: 'Activity' },
                  ...(!isBrokerComplianceOnly ? [{ key: 'property', label: 'Property Details' }] : []),
                  ...(!isBrokerComplianceOnly ? [{ key: 'ai-coach', label: 'AI Coach' }] : []),
                  ...(!isBrokerComplianceOnly ? [{ key: 'automations', label: 'Automations' }] : [])
                ] as const).map(tab => (
                  <button key={tab.key} onClick={() => {
                    setActiveTab(tab.key as any);
                    if (selected) setSearchParams({ id: selected.id, tab: tab.key });
                    if (tab.key === 'activity' && selected) {
                      setActivityLoading(true);
                      axiosInstance.get(`/agents/clients/${selected.id}/activities`)
                        .then(res => { if (res.data.success) setActivityData(res.data); })
                        .catch(() => {})
                        .finally(() => setActivityLoading(false));
                    }
                    if (tab.key === 'ai-coach' && selected) {
                      fetchAiHistory(selected.id);
                    }
                    if (tab.key === 'automations') {
                      fetchAllAutomations();
                    }
                    if (tab.key === 'seller' && selected) {
                      fetchSellerCMA(selected.id);
                    }
                    if (tab.key === 'offer-analyzer' && selected && !buyerCMAResult) {
                      // Load latest CMA report for this client
                      (async () => {
                        try {
                          const uid = (() => { try { return JSON.parse(atob((localStorage.getItem('auth_token') || '').split('.')[1])).id; } catch { return ''; } })();
                          const idsToTry = [selected.id, selected.lead_id].filter(Boolean);
                          for (const lid of idsToTry) {
                            const res = await axiosInstance.get(`/sellers/${lid}/cma-reports`, { headers: { 'x-user-id': uid } });
                            const reports = res.data?.reports || (Array.isArray(res.data) ? res.data : []);
                            if (reports.length > 0) {
                              const latest = reports[0];
                              const reportComps = latest.full_analysis?.comparable_properties?.comparables
                                || latest.cmaData?.comparable_properties?.comparables
                                || latest.comparablesData?.comparables || [];
                              const reportStats = latest.full_analysis?.market_stats || latest.analysisData?.comp_stats || {};
                              const estValue = Number(latest.pricing_recommendations?.estimated_value || latest.predicted_value || latest.predictionData?.predicted_value || 0);
                              const conf = Number(latest.pricing_recommendations?.confidence_score || latest.confidence_score || latest.predictionData?.confidence_score || 0);
                              const addr = latest.propertyData?.unparsed_address || latest.propertyData?.address || latest.title?.replace('Smart CMA - ', '') || '';
                              setBuyerCMAAddress(addr);
                              setBuyerCMAResult({
                                report_id: latest.report_id || latest.id,
                                analysis_results: {
                                  xgboost_prediction: { predicted_price: estValue, confidence: conf },
                                  enhanced_confidence: conf,
                                  comparables: reportComps.map((c: any) => ({
                                    unparsed_address: c.address || c.unparsed_address || 'Comp',
                                    bedrooms: c.bedrooms, bathrooms: c.bathrooms, living_area: c.living_area,
                                    close_price: c.close_price, list_price: c.list_price,
                                    year_built: c.year_built, close_date: c.close_date,
                                    distance_miles: c.distance_miles, price_per_sqft: c.price_per_sqft,
                                    classification: c.classification, days_on_market: c.days_on_market,
                                    pool: c.pool,
                                  })),
                                  statistics: {
                                    median_price: reportStats.median_price || 0,
                                    median_ppsf: reportStats.median_ppsf || 0,
                                    total_comps: reportComps.length,
                                  },
                                  property_address: addr,
                                },
                              });
                              break;
                            }
                          }
                        } catch { /* no previous reports */ }
                      })();
                    }
                  }}
                    style={{ background: activeTab === tab.key ? colors.gold : '#f0f0f0', color: activeTab === tab.key ? colors.navy : '#666', border: 'none', borderRadius: '6px', padding: '6px 16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Overview Tab */}
              {activeTab === 'overview' && clientDetail && (
                <div>
                  {/* Lead Source Tracking (LO Pipeline) */}
                  {(() => {
                    // Parse lead_tags — could be array or object depending on source
                    let tagsObj: any = {};
                    try {
                      const rawTags = clientDetail.lead?.lead_tags || selected?.lead_tags;
                      if (rawTags) {
                        const parsed = typeof rawTags === 'string' ? JSON.parse(rawTags) : rawTags;
                        tagsObj = Array.isArray(parsed) ? {} : parsed;
                      }
                    } catch {}
                    const currentSource = tagsObj.source_tag || '';
                    const currentAgent = tagsObj.referring_agent || '';

                    const SOURCE_OPTS = [
                      { key: 'ccr', label: 'Current Client Referral', color: '#27ae60', short: 'CCR' },
                      { key: 'past_client_referral', label: 'Past Client Referral', color: '#2980b9', short: 'PCR' },
                      { key: 'past_client', label: 'Past Client', color: '#3498db', short: 'PC' },
                      { key: 'personal_friend', label: 'Personal Friend', color: '#8e44ad', short: 'Friend' },
                      { key: 'realtor', label: 'Realtor', color: '#e67e22', short: 'Realtor' },
                      { key: 'ad', label: 'Advertisement', color: '#c0392b', short: 'Ad' },
                      { key: 'vip', label: 'VIP Client', color: '#f1c40f', short: 'VIP' },
                      { key: 'builder', label: 'Builder', color: '#16a085', short: 'Builder' },
                    ];
                    const activeTag = SOURCE_OPTS.find(s => s.key === currentSource);

                    return (
                      <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginBottom: '16px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 style={{ color: colors.navy, fontWeight: 700, margin: 0 }}>
                            <FontAwesomeIcon icon={faFolderOpen} className="me-2" style={{ color: colors.gold }} />Lead Source
                          </h6>
                          {activeTag && (
                            <span style={{
                              background: `${activeTag.color}18`, color: activeTag.color,
                              padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
                            }}>
                              {activeTag.label}
                              {currentSource === 'realtor' && currentAgent ? ` — ${currentAgent}` : ''}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {SOURCE_OPTS.map(tag => {
                            const isActive = currentSource === tag.key;
                            return (
                              <button
                                key={tag.key}
                                onClick={async () => {
                                  try {
                                    const newTag = isActive ? '' : tag.key;
                                    await axiosInstance.patch(`/sponsor/deal-board/${selected?.id}/source`, {
                                      sourceTag: newTag,
                                      referringAgent: newTag === 'realtor' ? currentAgent : '',
                                    });
                                    toast.success(newTag ? `Source: ${tag.label}` : 'Source cleared');
                                    // Refresh profile
                                    if (selected) {
                                      const r = await agentPortalService.getClientProfile(selected.id);
                                      if (r.success) setClientDetail(r.data);
                                    }
                                  } catch { toast.error('Failed to update source'); }
                                }}
                                style={{
                                  padding: '5px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '6px',
                                  border: isActive ? `2px solid ${tag.color}` : '1px solid #ddd',
                                  background: isActive ? `${tag.color}18` : '#fff',
                                  color: isActive ? tag.color : '#666',
                                  cursor: 'pointer',
                                }}
                              >
                                {tag.label}
                              </button>
                            );
                          })}
                        </div>
                        {/* Realtor name input — show when Realtor is selected */}
                        {currentSource === 'realtor' && (
                          <div className="mt-2 d-flex gap-2 align-items-center">
                            <span style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>Referring Agent:</span>
                            <input
                              defaultValue={currentAgent}
                              placeholder="Type agent name..."
                              onBlur={async (e) => {
                                const val = e.target.value.trim();
                                if (val !== currentAgent) {
                                  try {
                                    await axiosInstance.patch(`/sponsor/deal-board/${selected?.id}/source`, {
                                      sourceTag: 'realtor',
                                      referringAgent: val,
                                    });
                                    toast.success('Referring agent updated');
                                    if (selected) {
                                      const r = await agentPortalService.getClientProfile(selected.id);
                                      if (r.success) setClientDetail(r.data);
                                    }
                                  } catch { toast.error('Failed to update'); }
                                }
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                              style={{
                                flex: 1, padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px',
                                fontSize: '13px', outline: 'none', maxWidth: '300px',
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Buyer Summary */}
                  {clientDetail.buyer_profile && (() => {
                    const bp = clientDetail.buyer_profile;
                    const pp = typeof bp.property_preferences === 'string' ? JSON.parse(bp.property_preferences) : bp.property_preferences || {};
                    const lp = typeof bp.location_preferences === 'string' ? JSON.parse(bp.location_preferences) : bp.location_preferences || {};
                    const ls = typeof bp.lifestyle_preferences === 'string' ? JSON.parse(bp.lifestyle_preferences) : bp.lifestyle_preferences || {};
                    return (
                      <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginBottom: '16px' }}>
                        <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px' }}>
                          <FontAwesomeIcon icon={faHome} className="me-2" style={{ color: colors.blue }} />Buyer Search Criteria
                        </h6>
                        <Row>
                          <Col md={3}>
                            <div style={{ fontSize: '12px', color: '#888' }}>Price Range</div>
                            <div style={{ fontWeight: 600, color: colors.navy }}>
                              {formatPrice(pp.budget_min || bp.price_min)} - {formatPrice(pp.budget_max || bp.price_max)}
                            </div>
                          </Col>
                          <Col md={2}>
                            <div style={{ fontSize: '12px', color: '#888' }}>Beds</div>
                            <div style={{ fontWeight: 600, color: colors.navy }}>
                              <FontAwesomeIcon icon={faBed} className="me-1" style={{ color: colors.gold }} />
                              {pp.bedrooms || bp.bedrooms_min || '—'}+
                            </div>
                          </Col>
                          <Col md={2}>
                            <div style={{ fontSize: '12px', color: '#888' }}>Baths</div>
                            <div style={{ fontWeight: 600, color: colors.navy }}>
                              <FontAwesomeIcon icon={faBath} className="me-1" style={{ color: colors.gold }} />
                              {pp.bathrooms || bp.bathrooms_min || '—'}+
                            </div>
                          </Col>
                          <Col md={2}>
                            <div style={{ fontSize: '12px', color: '#888' }}>Sq Ft</div>
                            <div style={{ fontWeight: 600, color: colors.navy }}>
                              <FontAwesomeIcon icon={faRuler} className="me-1" style={{ color: colors.gold }} />
                              {pp.square_feet_min ? `${pp.square_feet_min.toLocaleString()}+` : '—'}
                            </div>
                          </Col>
                          <Col md={3}>
                            <div style={{ fontSize: '12px', color: '#888' }}>Areas</div>
                            <div style={{ fontWeight: 600, color: colors.navy, fontSize: '13px' }}>
                              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" style={{ color: colors.gold }} />
                              {Array.isArray(lp.preferred_cities) ? lp.preferred_cities.join(', ') : (lp.preferred_cities || '—')}
                            </div>
                          </Col>
                        </Row>
                        {ls.must_haves && ls.must_haves.length > 0 && (
                          <div className="mt-2">
                            <span style={{ fontSize: '12px', color: '#888' }}>Must Haves: </span>
                            {ls.must_haves.map((item: string, i: number) => (
                              <span key={i} style={{ fontSize: '11px', background: '#e8f5e9', color: colors.green, padding: '2px 8px', borderRadius: '4px', marginRight: '4px' }}>{item}</span>
                            ))}
                          </div>
                        )}
                        {ls.deal_breakers && ls.deal_breakers.length > 0 && (
                          <div className="mt-1">
                            <span style={{ fontSize: '12px', color: '#888' }}>Deal Breakers: </span>
                            {ls.deal_breakers.map((item: string, i: number) => (
                              <span key={i} style={{ fontSize: '11px', background: '#fde8e8', color: colors.red, padding: '2px 8px', borderRadius: '4px', marginRight: '4px' }}>{item}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Seller Summary */}
                  {clientDetail.seller_profile && (
                    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginBottom: '16px' }}>
                      <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px' }}>
                        <FontAwesomeIcon icon={faDollarSign} className="me-2" style={{ color: colors.green }} />Seller Property
                      </h6>
                      <Row>
                        <Col md={6}>
                          <div style={{ fontSize: '12px', color: '#888' }}>Property Address</div>
                          <div style={{ fontWeight: 600, color: colors.navy }}>
                            {clientDetail.seller_profile.property_address || '—'}
                          </div>
                        </Col>
                        <Col md={3}>
                          <div style={{ fontSize: '12px', color: '#888' }}>Estimated Value</div>
                          <div style={{ fontWeight: 600, color: colors.green }}>
                            {formatPrice(clientDetail.seller_profile.estimated_value || clientDetail.seller_profile.listing_price)}
                          </div>
                        </Col>
                        <Col md={3}>
                          <div style={{ fontSize: '12px', color: '#888' }}>Timeline</div>
                          <div style={{ fontWeight: 600, color: colors.navy }}>
                            {clientDetail.seller_profile.selling_timeframe || '—'}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {/* Property Address (Lender accounts — from lead record) */}
                  {isSponsorPlan && (selected?.lead_street_name || clientDetail?.lead?.lead_street_name) && (
                    <div style={{ background: 'white', borderRadius: '12px', padding: '16px 20px', border: '1px solid #e9ecef', marginBottom: '16px' }}>
                      <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '8px', fontSize: '13px' }}>
                        <FontAwesomeIcon icon={faHome} className="me-2" style={{ color: colors.gold }} />Financed Property
                      </h6>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: colors.navy }}>
                        {selected?.lead_street_name || clientDetail?.lead?.lead_street_name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {[selected?.lead_city || clientDetail?.lead?.lead_city, selected?.lead_state || clientDetail?.lead?.lead_state, selected?.lead_zip_code || clientDetail?.lead?.lead_zip_code].filter(Boolean).join(', ')}
                      </div>
                    </div>
                  )}

                  {/* AI Classification + Intelligence */}
                  {(clientDetail.ai_context || selected) && (
                    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginBottom: '16px' }}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 style={{ color: colors.navy, fontWeight: 700, margin: 0 }}>
                          <FontAwesomeIcon icon={faRobot} className="me-2" style={{ color: colors.gold }} />AI Intelligence
                        </h6>
                        <Button size="sm" variant="outline-secondary" style={{ fontSize: '11px', padding: '2px 10px' }}
                          onClick={async () => {
                            toast.success('Re-analyzing lead...');
                            try {
                              await axiosInstance.post(`/agents/reanalyze-lead`, { leadId: selected?.id });
                              toast.success('Analysis started — refresh in a moment');
                              setTimeout(async () => {
                                if (selected) { const r = await agentPortalService.getClientProfile(selected.id); if (r.success) setClientDetail(r.data); }
                              }, 5000);
                            } catch { toast.error('Analysis failed'); }
                          }}>
                          <FontAwesomeIcon icon={faBolt} className="me-1" />Re-analyze
                        </Button>
                        {clientDetail.ai_context?.classification?.relationshipType && (
                          <span style={{
                            background: clientDetail.ai_context?.classification.relationshipType === 'past_buyer' ? '#e8f5e9' :
                              clientDetail.ai_context?.classification.relationshipType === 'past_seller' ? '#fff3e0' :
                              clientDetail.ai_context?.classification.relationshipType === 'investor' ? '#e3f2fd' :
                              clientDetail.ai_context?.classification.relationshipType === 'vendor' ? '#f3e5f5' :
                              clientDetail.ai_context?.classification.relationshipType === 'hot_buyer' ? '#ffebee' : '#f5f5f5',
                            color: clientDetail.ai_context?.classification.relationshipType === 'past_buyer' ? '#2e7d32' :
                              clientDetail.ai_context?.classification.relationshipType === 'past_seller' ? '#e65100' :
                              clientDetail.ai_context?.classification.relationshipType === 'investor' ? '#1565c0' :
                              clientDetail.ai_context?.classification.relationshipType === 'vendor' ? '#7b1fa2' :
                              clientDetail.ai_context?.classification.relationshipType === 'hot_buyer' ? '#c62828' : '#616161',
                            padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase'
                          }}>
                            {clientDetail.ai_context?.classification.relationshipType.replace(/_/g, ' ')}
                            {clientDetail.ai_context?.classification.confidence ? ` (${Math.round(clientDetail.ai_context?.classification.confidence * 100)}%)` : ''}
                          </span>
                        )}
                      </div>

                      {/* Relationship Summary — Editable by agent */}
                      {clientDetail.ai_context?.classification?.summary && (
                        <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', marginBottom: '12px', borderLeft: '3px solid #C9952A' }}>
                          {editingSummary ? (
                            <>
                              <div style={{ marginBottom: '8px' }}>
                                <label style={{ fontSize: '11px', color: '#888', fontWeight: 600 }}>Classification Type</label>
                                <select
                                  value={editingRelType || clientDetail.ai_context?.classification?.relationshipType || ''}
                                  onChange={(e) => setEditingRelType(e.target.value)}
                                  style={{ fontSize: '12px', fontWeight: 600, border: '1px solid #ddd', borderRadius: '6px', padding: '4px 8px', width: '100%', color: colors.navy }}
                                >
                                  <option value="past_buyer">Past Client - Buyer</option>
                                  <option value="past_seller">Past Client - Seller</option>
                                  <option value="active_buyer">Active Buyer</option>
                                  <option value="active_seller">Active Seller</option>
                                  <option value="investor">Investor</option>
                                  <option value="sphere_of_influence">Sphere of Influence</option>
                                  <option value="referral_source">Referral Source</option>
                                  <option value="vendor">Vendor / Partner</option>
                                  <option value="cold_reactivation">Cold Reactivation</option>
                                  <option value="nurture">Nurture</option>
                                  <option value="unknown">Unknown</option>
                                </select>
                              </div>
                              <textarea
                                value={summaryText}
                                onChange={(e) => setSummaryText(e.target.value)}
                                style={{ width: '100%', minHeight: '80px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', padding: '8px', color: '#444', lineHeight: 1.5, resize: 'vertical' }}
                              />
                              <div className="d-flex gap-2 mt-2">
                                <Button size="sm" variant="outline-secondary" onClick={() => { setEditingSummary(false); setEditingRelType(''); }}>Cancel</Button>
                                <Button size="sm" style={{ background: '#C9952A', border: 'none' }} disabled={savingSummary} onClick={async () => {
                                  setSavingSummary(true);
                                  try {
                                    await axiosInstance.patch(`/agents/clients/${selected?.id}/ai-summary`, { summary: summaryText, relationshipType: editingRelType || undefined });
                                    toast.success('AI classification updated — this correction will be preserved on re-analyze');
                                    setEditingSummary(false);
                                    setEditingRelType('');
                                    if (selected) { const r = await agentPortalService.getClientProfile(selected.id); if (r.success) setClientDetail(r.data); }
                                  } catch { toast.error('Failed to update'); }
                                  setSavingSummary(false);
                                }}>{savingSummary ? 'Saving...' : 'Save Correction'}</Button>
                              </div>
                            </>
                          ) : (
                            <div className="d-flex justify-content-between align-items-start">
                              <div style={{ fontSize: '13px', color: '#444', lineHeight: 1.5, flex: 1 }}>
                                {clientDetail.ai_context?.classification.summary}
                                {clientDetail.ai_context?.classification.agentCorrected && (
                                  <span style={{ fontSize: '10px', color: '#C9952A', marginLeft: '8px' }}>(Agent corrected)</span>
                                )}
                              </div>
                              <span onClick={() => { setSummaryText(clientDetail.ai_context?.classification.summary); setEditingSummary(true); }} style={{ cursor: 'pointer', fontSize: '11px', color: '#C9952A', fontWeight: 600, marginLeft: '8px', whiteSpace: 'nowrap' }}>
                                <FontAwesomeIcon icon={faEdit} className="me-1" />Edit
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Recommended Action */}
                      {clientDetail.ai_context?.classification?.recommendedApproach && (
                        <div style={{ background: '#fff8e1', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontSize: '12px', color: '#5d4037' }}>
                          <strong>Recommended:</strong> {clientDetail.ai_context?.classification.recommendedApproach}
                        </div>
                      )}

                      {/* Metrics Row */}
                      <Row>
                        <Col md={3}>
                          <div style={{ fontSize: '11px', color: '#888' }}>{isSponsorPlan ? 'Pipeline Stage' : 'Lead Stage'}</div>
                          <select
                            value={clientDetail.ai_context?.journeyStage || ''}
                            onChange={async (e) => {
                              try {
                                await axiosInstance.patch(`/agents/clients/${selected?.id}/stage`, { stage: e.target.value });
                                toast.success('Stage updated');
                                if (selected) { const r = await agentPortalService.getClientProfile(selected.id); if (r.success) setClientDetail(r.data); }
                              } catch { toast.error('Failed to update stage'); }
                            }}
                            style={{ fontSize: '12px', fontWeight: 600, border: '1px solid #ddd', borderRadius: '6px', padding: '4px 8px', width: '100%', color: colors.navy }}
                          >
                            {isSponsorPlan ? (<>
                              <option value="">— Select Stage —</option>
                              <option value="hot_lead">New Lead</option>
                              <option value="active_buyer">Loan Application</option>
                              <option value="pre_qualified">Pre-Qualified</option>
                              <option value="under_contract">Under Contract / Active Refi</option>
                              <option value="disclosures">Disclosures</option>
                              <option value="initial_approval">Initial Approval / Conditions</option>
                              <option value="closing_docs">Closing Docs</option>
                              <option value="past_buyer">Funded / Completed</option>
                              <option value="cold_reactivation">Cold / Lost</option>
                              <option value="nurture">Credit Repair / Long-term Nurture</option>
                            </>) : (<>
                              <option value="new">New Lead</option>
                              <option value="new_contact">New Contact</option>
                              <option value="active_buyer">Active Buyer</option>
                              <option value="active_seller">Active Seller (Listed)</option>
                              <option value="past_buyer">Past Client - Buyer</option>
                              <option value="past_seller">Past Client - Seller</option>
                              <option value="buyer_prospect">Buyer Prospect</option>
                              <option value="seller_prospect">Seller Prospect</option>
                              <option value="past_client_buyer">Past Client - Buyer (Closed)</option>
                              <option value="past_client_seller">Past Client - Seller (Closed)</option>
                              <option value="investor">Investor</option>
                              <option value="sphere_of_influence">Sphere of Influence</option>
                              <option value="sphere_nurture">Sphere - Monthly CMA Updates</option>
                              <option value="sphere_referral">Sphere - Referral Source</option>
                              <option value="cold_reactivation">Cold Reactivation</option>
                              <option value="vendor">Vendor / Partner</option>
                              <option value="long_term_nurture">Long-term Nurture</option>
                              <option value="not_ready">Not Ready Now (Stay in Touch)</option>
                            </>)}
                          </select>
                          {/* Multi-type chips — allows selecting multiple types (e.g. Past Buyer + Past Seller) */}
                          {!isSponsorPlan && (() => {
                            const savedTypes: string[] = (clientDetail as any)?.custom_fields?.lead_types || [clientDetail.ai_context?.journeyStage].filter(Boolean);
                            const multiTypeOptions = [
                              { value: 'past_buyer', label: 'Past Buyer' },
                              { value: 'past_seller', label: 'Past Seller' },
                              { value: 'investor', label: 'Investor' },
                              { value: 'sphere_of_influence', label: 'Sphere' },
                              { value: 'sphere_referral', label: 'Referral Source' },
                            ];
                            const hasMultiple = savedTypes.length > 1;
                            return (
                              <div style={{ marginTop: '4px' }}>
                                <div style={{ fontSize: '10px', color: '#888', marginBottom: '2px' }}>Also applies:</div>
                                <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                                  {multiTypeOptions.map(opt => {
                                    const isActive = savedTypes.includes(opt.value);
                                    const isPrimary = savedTypes[0] === opt.value;
                                    return (
                                      <button
                                        key={opt.value}
                                        onClick={async () => {
                                          let newTypes: string[];
                                          if (isActive && !isPrimary) {
                                            newTypes = savedTypes.filter(t => t !== opt.value);
                                          } else if (!isActive) {
                                            newTypes = [...savedTypes, opt.value];
                                          } else {
                                            return; // Don't remove primary via chips
                                          }
                                          try {
                                            await axiosInstance.patch(`/agents/clients/${selected?.id}/lead-types`, { leadTypes: newTypes });
                                            toast.success('Types updated');
                                            if (selected) { const r = await agentPortalService.getClientProfile(selected.id); if (r.success) setClientDetail(r.data); }
                                          } catch { toast.error('Failed to update'); }
                                        }}
                                        style={{
                                          padding: '1px 6px', fontSize: '9px', fontWeight: 600, borderRadius: '10px', cursor: isPrimary ? 'default' : 'pointer',
                                          border: `1px solid ${isActive ? colors.gold : '#ddd'}`,
                                          background: isActive ? '#fff8e1' : 'white',
                                          color: isActive ? '#8d6e0e' : '#999',
                                        }}
                                      >
                                        {isActive && !isPrimary ? '× ' : ''}{opt.label}
                                      </button>
                                    );
                                  })}
                                </div>
                                {hasMultiple && (
                                  <div style={{ fontSize: '9px', color: colors.gold, fontWeight: 600, marginTop: '2px' }}>
                                    {savedTypes.length} types assigned
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </Col>
                        <Col md={3}>
                          <div style={{ fontSize: '11px', color: '#888' }}>Confidence</div>
                          <div style={{ fontWeight: 600, color: colors.navy }}>{clientDetail.ai_context?.confidenceScore || 0}/100</div>
                        </Col>
                        <Col md={3}>
                          <div style={{ fontSize: '11px', color: '#888' }}>Engagement</div>
                          <div style={{ fontWeight: 600, color: colors.navy }}>{clientDetail.ai_context?.engagementScore || 0}/100</div>
                        </Col>
                        <Col md={3}>
                          <div style={{ fontSize: '11px', color: '#888' }}>Last Analyzed</div>
                          <div style={{ fontWeight: 600, color: colors.navy, fontSize: '12px' }}>
                            {clientDetail.ai_context?.lastAnalyzed ? new Date(clientDetail.ai_context?.lastAnalyzed).toLocaleDateString() : '—'}
                          </div>
                        </Col>
                      </Row>

                      {/* Topics */}
                      {clientDetail.ai_context?.classification?.keyTopics?.length > 0 && (
                        <div style={{ marginTop: '10px' }}>
                          {clientDetail.ai_context?.classification.keyTopics.map((topic: string) => (
                            <span key={topic} style={{ background: '#e3f2fd', color: '#1565c0', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', marginRight: '6px', display: 'inline-block', marginBottom: '4px' }}>
                              {topic.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tags — defensive normalization: the backend has returned
                      three historical shapes here (string[], JSON string of
                      string[], JSON string of [{tags: string}]), so we
                      flatten everything to a clean string[] before rendering.
                      Without the extractTag step below, the object-wrapper
                      shape blows up React with "Objects are not valid as a
                      React child (found: object with keys {tags})". */}
                  <div style={{ background: 'white', borderRadius: '12px', padding: '16px 20px', border: '1px solid #e9ecef', marginBottom: '16px' }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span style={{ fontSize: '12px', fontWeight: 700, color: colors.navy }}>Tags:</span>
                      {((() => {
                        try {
                          const raw = clientDetail.tags;
                          const arr = Array.isArray(raw)
                            ? raw
                            : typeof raw === 'string'
                            ? JSON.parse(raw)
                            : [];
                          if (!Array.isArray(arr)) return [];
                          const extractTag = (item: any): string | null => {
                            if (item == null) return null;
                            if (typeof item === 'string') return item;
                            if (typeof item === 'object') {
                              return typeof item.tags === 'string' ? item.tags
                                   : typeof item.tag === 'string' ? item.tag
                                   : typeof item.name === 'string' ? item.name
                                   : null;
                            }
                            return String(item);
                          };
                          return arr.map(extractTag).filter((t): t is string => !!t);
                        } catch {
                          return [];
                        }
                      })()).map((tag: string) => (
                        <span key={tag} style={{ background: '#C9952A', color: 'white', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          {tag}
                          <span style={{ cursor: 'pointer', opacity: 0.7 }} onClick={async () => {
                            try {
                              await axiosInstance.post(`/agents/clients/${selected?.id}/tags`, { action: 'remove', tag });
                              if (selected) { const r = await agentPortalService.getClientProfile(selected.id); if (r.success) setClientDetail(r.data); }
                            } catch {}
                          }}>x</span>
                        </span>
                      ))}
                      <input
                        placeholder="Add tag..."
                        style={{ border: 'none', outline: 'none', fontSize: '12px', width: '100px' }}
                        onKeyDown={async (e: any) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            const tag = e.target.value.trim();
                            try {
                              await axiosInstance.post(`/agents/clients/${selected?.id}/tags`, { action: 'add', tag });
                              e.target.value = '';
                              if (selected) { const r = await agentPortalService.getClientProfile(selected.id); if (r.success) setClientDetail(r.data); }
                            } catch {}
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Linked Contacts (Spouses, Co-owners, etc.) */}
                  <div style={{ background: 'white', borderRadius: '12px', padding: '16px 20px', border: '1px solid #e9ecef', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: linkedContacts.length > 0 || showLinkForm ? '10px' : '0' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: colors.navy }}>
                        <FontAwesomeIcon icon={faUsers} className="me-2" style={{ color: colors.gold }} />
                        Linked Contacts
                        {linkedContacts.length > 0 && <span style={{ fontWeight: 400, color: '#888', marginLeft: '4px' }}>({linkedContacts.length})</span>}
                      </span>
                      <button
                        onClick={() => { setShowLinkForm(!showLinkForm); setLinkSearch(''); setLinkSearchResults([]); }}
                        style={{ padding: '2px 8px', fontSize: '10px', fontWeight: 600, borderRadius: '4px', border: `1px solid ${colors.gold}`, background: showLinkForm ? colors.gold : 'white', color: showLinkForm ? 'white' : colors.gold, cursor: 'pointer' }}
                      >
                        {showLinkForm ? 'Cancel' : '+ Link'}
                      </button>
                    </div>

                    {/* Link form */}
                    {showLinkForm && (
                      <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                          <input
                            placeholder="Search by name or email..."
                            value={linkSearch}
                            onChange={async (e) => {
                              setLinkSearch(e.target.value);
                              if (e.target.value.length >= 2) {
                                try {
                                  const { data } = await axiosInstance.get(`/agents/crm-leads?search=${encodeURIComponent(e.target.value)}&limit=5`);
                                  const results = (data.clients || data.data || []).filter((c: any) => (c.lead_id || c.id) !== (selected?.lead_id || selected?.id));
                                  setLinkSearchResults(results);
                                } catch { setLinkSearchResults([]); }
                              } else { setLinkSearchResults([]); }
                            }}
                            style={{ flex: 1, padding: '6px 10px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }}
                          />
                          <select value={linkType} onChange={e => setLinkType(e.target.value)} style={{ padding: '6px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', background: 'white' }}>
                            <option value="spouse">Spouse</option>
                            <option value="co_owner">Co-owner</option>
                            <option value="family">Family</option>
                            <option value="business_partner">Business Partner</option>
                            <option value="referral">Referral</option>
                          </select>
                        </div>
                        {linkSearchResults.length > 0 && (
                          <div style={{ border: '1px solid #e9ecef', borderRadius: '6px', maxHeight: '120px', overflowY: 'auto' }}>
                            {linkSearchResults.map((r: any) => (
                              <div
                                key={r.lead_id || r.id}
                                onClick={async () => {
                                  try {
                                    const leadId = selected?.lead_id || selected?.id;
                                    await axiosInstance.post(`/agents/clients/${leadId}/relationships`, {
                                      linkedLeadId: r.lead_id || r.id,
                                      relationshipType: linkType,
                                    });
                                    toast.success(`Linked ${r.lead_first_name || r.first_name} as ${linkType.replace(/_/g, ' ')}`);
                                    setShowLinkForm(false);
                                    setLinkedLoaded(''); // force reload
                                    loadLinkedContacts(leadId);
                                  } catch { toast.error('Failed to link contacts'); }
                                }}
                                style={{ padding: '6px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f0f4ff')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                              >
                                <span style={{ fontWeight: 600, color: colors.navy }}>
                                  {r.lead_first_name || r.first_name} {r.lead_last_name || r.last_name}
                                </span>
                                <span style={{ color: '#888', fontSize: '11px' }}>{r.lead_email || r.email}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Existing linked contacts */}
                    {linkedContacts.map((rel: any) => (
                      <div key={rel.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <div>
                          <span
                            style={{ fontSize: '13px', fontWeight: 600, color: colors.navy, cursor: 'pointer' }}
                            onClick={() => {
                              // Navigate to linked contact
                              const linkedId = rel.linked_lead_id;
                              if (linkedId) navigate(`/agent-portal/clients?select=${linkedId}`);
                            }}
                          >
                            {rel.linked_first_name} {rel.linked_last_name}
                          </span>
                          <span style={{ fontSize: '10px', color: colors.gold, fontWeight: 600, marginLeft: '6px', textTransform: 'capitalize' }}>
                            {(rel.relationship_type || '').replace(/_/g, ' ')}
                          </span>
                          {rel.linked_email && <div style={{ fontSize: '11px', color: '#888' }}>{rel.linked_email}</div>}
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              const leadId = selected?.lead_id || selected?.id;
                              await axiosInstance.delete(`/agents/clients/${leadId}/relationships/${rel.id}`);
                              setLinkedContacts(prev => prev.filter(r => r.id !== rel.id));
                              toast.success('Unlinked');
                            } catch { toast.error('Failed to unlink'); }
                          }}
                          style={{ padding: '2px 6px', fontSize: '9px', fontWeight: 600, borderRadius: '4px', border: '1px solid #ddd', background: 'white', color: '#999', cursor: 'pointer' }}
                        >
                          Unlink
                        </button>
                      </div>
                    ))}

                    {linkedContacts.length === 0 && !showLinkForm && (
                      <div style={{ fontSize: '11px', color: '#aaa', textAlign: 'center', padding: '4px 0' }}>No linked contacts</div>
                    )}
                  </div>

                  {/* Quick Actions — agent only */}
                  {!isSponsorPlan && (
                  <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>
                    <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px' }}>Quick Actions</h6>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button size="sm" onClick={() => navigate(`/agent-portal/client/${selected.lead_id || selected.id}#ai-search`)}
                        style={{ background: colors.navy, border: 'none', fontSize: '12px' }}>
                        <FontAwesomeIcon icon={faComments} className="me-1" /> AI Search Chat
                      </Button>
                      <Button size="sm" onClick={() => navigate(`/agent-portal/client/${selected.lead_id || selected.id}#seller-ai`)}
                        style={{ background: colors.green, border: 'none', fontSize: '12px' }}>
                        <FontAwesomeIcon icon={faDollarSign} className="me-1" /> Run Seller CMA
                      </Button>
                      <Button size="sm" onClick={() => { setShowBuyerCMA(!showBuyerCMA); setBuyerCMAResult(null); }}
                        style={{ background: '#3498db', border: 'none', fontSize: '12px' }}>
                        <FontAwesomeIcon icon={faChartLine} className="me-1" /> Run Buyer CMA
                      </Button>
                      <Button size="sm" style={{ background: '#C9A84C', border: 'none', color: '#0F1E35', fontSize: '12px', fontWeight: 700 }}
                        onClick={async () => {
                          try {
                            const isStaging = window.location.hostname.includes('dev.') || window.location.hostname === 'localhost';
                            const portalBase = isStaging ? 'https://dev.aisearchportal.com' : 'https://www.aisearchportal.com';
                            const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
                            const agentId = token ? JSON.parse(atob(token.split('.')[1])).id : '';
                            const portalSlug = (window as any).__agentPortalSlug || agentId;
                            const leadId = selected.lead_id || selected.id;
                            const portalLink = `${portalBase}/${portalSlug}?client=${leadId}`;

                            // Send email invite if client has email
                            if (selected.lead_email) {
                              await axiosInstance.post(`/customer-portal/invite-lead/invite-from-lead`, { leadId, agentId });
                            }

                            // Copy link to clipboard
                            navigator.clipboard.writeText(portalLink);

                            setAlert({
                              type: 'success',
                              message: selected.lead_email
                                ? `Portal invite sent to ${selected.lead_email} and link copied!`
                                : `Portal link copied! Share it with your client.`
                            });
                          } catch (e: any) {
                            setAlert({ type: 'danger', message: e.response?.data?.message || 'Could not send invite' });
                          }
                        }}>
                        <FontAwesomeIcon icon={faEnvelope} className="me-1" /> Invite to Portal
                      </Button>
                    </div>
                  </div>
                  )}

                  {/* Buyer CMA — Offer Analysis */}
                  {showBuyerCMA && (
                    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #3498db', marginTop: '16px' }}>
                      <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px' }}>
                        <FontAwesomeIcon icon={faChartLine} className="me-2" style={{ color: '#3498db' }} />Buyer Offer Analysis
                      </h6>
                      <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                        Enter the property address your buyer wants to make an offer on. We'll run comps, price prediction, and give you an offer range.
                      </p>
                      <div style={{ position: 'relative', marginBottom: '12px' }}>
                        <div className="d-flex gap-2">
                          <input
                            placeholder="Start typing an address..."
                            value={buyerCMAAddress}
                            onChange={e => handleAddressSearch(e.target.value)}
                            style={{ flex: 1, border: '1px solid #dee2e6', borderRadius: '6px', padding: '8px 12px', fontSize: '13px' }}
                            onKeyDown={e => { if (e.key === 'Enter' && !buyerCMALoading) { setAddressSuggestions([]); handleRunBuyerCMA(); } }}
                          />
                          <Button size="sm" onClick={() => { setAddressSuggestions([]); handleRunBuyerCMA(); }} disabled={buyerCMALoading || !buyerCMAAddress.trim()}
                            style={{ background: '#3498db', border: 'none', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {buyerCMALoading ? <><Spinner size="sm" className="me-1" />Analyzing...</> : <><FontAwesomeIcon icon={faChartLine} className="me-1" />Analyze</>}
                          </Button>
                        </div>
                        {addressSuggestions.length > 0 && (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 60, zIndex: 10,
                            background: '#fff', border: '1px solid #dee2e6', borderRadius: '0 0 6px 6px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto',
                          }}>
                            {addressSuggestions.map((s: any, i: number) => (
                              <div key={i} onClick={() => { setBuyerCMAAddress(s.place_name); setAddressSuggestions([]); }}
                                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: i < addressSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                {s.place_name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Results */}
                      {buyerCMAResult?.analysis_results && (() => {
                        const ar = buyerCMAResult.analysis_results;
                        const predicted = ar.xgboost_prediction?.predicted_price;
                        const confidence = ar.enhanced_confidence || ar.xgboost_prediction?.confidence || 0;
                        const comps = ar.comparables || [];
                        const stats = ar.statistics || {};
                        const medianPrice = stats.median_price;
                        const medianPpsf = stats.median_ppsf || stats.avg_price_per_sqft;

                        // Check if this is a property the client already owns
                        const searchAddrClean = (buyerCMAAddress || '').toUpperCase().replace(/[,.\s]+/g, ' ').trim();
                        const isOwnedProperty = ownedProperties.some((p: any) => {
                          const propAddr = (p.fullAddress || p.address || '').toUpperCase().replace(/[,.\s]+/g, ' ').trim();
                          return propAddr && searchAddrClean && (searchAddrClean.includes(propAddr.split(' ').slice(0, 3).join(' ')) || propAddr.includes(searchAddrClean.split(' ').slice(0, 3).join(' ')));
                        }) || (clientDetail?.all_seller_profiles || []).some((sp: any) => {
                          const spAddr = (sp.property_address || '').toUpperCase();
                          return spAddr && searchAddrClean.includes(spAddr.split(' ').slice(0, 3).join(' '));
                        });

                        // Offer range: 93-99% of estimated value
                        // Use XGBoost prediction if available, fall back to median comp price
                        const estimatedValue = predicted || medianPrice || null;
                        const offerLow = estimatedValue ? Math.round(estimatedValue * 0.93) : null;
                        const offerMid = estimatedValue ? Math.round(estimatedValue * 0.96) : null;
                        const offerHigh = estimatedValue ? Math.round(estimatedValue * 0.99) : null;

                        const fmt = (v: any) => v ? `$${Number(v).toLocaleString()}` : '—';

                        return (
                          <div>
                            {/* Show different header based on owned vs buyer offer */}
                            {isOwnedProperty ? (
                              <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #bbf7d0' }}>
                                <div style={{ fontSize: '12px', color: '#166534', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
                                  Home Value Analysis — Property Owned by Client
                                </div>
                                <div className="d-flex gap-3 justify-content-around text-center">
                                  <div>
                                    <div style={{ fontSize: '10px', color: '#666' }}>Estimated Market Value</div>
                                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#166534' }}>{fmt(predicted)}</div>
                                    <div style={{ fontSize: '10px', color: '#999' }}>Based on {comps.length} comparable sales</div>
                                  </div>
                                  <div>
                                    <div style={{ fontSize: '10px', color: '#666' }}>Listing Price Range</div>
                                    <div style={{ fontSize: '16px', fontWeight: 700, color: colors.navy }}>{fmt(offerMid)} — {fmt(predicted)}</div>
                                    <div style={{ fontSize: '10px', color: '#999' }}>96-100% of market value</div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                            <div style={{ background: '#e8f4fd', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #b8daff' }}>
                              <div style={{ fontSize: '12px', color: '#1a5276', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
                                Recommended Offer Range
                              </div>
                              <div className="d-flex gap-3 justify-content-around text-center">
                                <div>
                                  <div style={{ fontSize: '10px', color: '#666' }}>Opening Offer</div>
                                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#27ae60' }}>{fmt(offerLow)}</div>
                                  <div style={{ fontSize: '10px', color: '#999' }}>93% of market</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '10px', color: '#666' }}>Target Price</div>
                                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#2980b9' }}>{fmt(offerMid)}</div>
                                  <div style={{ fontSize: '10px', color: '#999' }}>96% of market</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '10px', color: '#666' }}>Max Offer</div>
                                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#e74c3c' }}>{fmt(offerHigh)}</div>
                                  <div style={{ fontSize: '10px', color: '#999' }}>99% of market</div>
                                </div>
                              </div>
                            </div>
                            )}

                            {/* Market Stats */}
                            <div className="d-flex gap-2 mb-3">
                              <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                                <div style={{ fontSize: '10px', color: '#666' }}>AI Predicted Value</div>
                                <div style={{ fontSize: '16px', fontWeight: 700, color: colors.navy }}>{fmt(predicted)}</div>
                                <div style={{ fontSize: '10px', color: '#999' }}>Based on {comps.length} comparable sales</div>
                              </div>
                              <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                                <div style={{ fontSize: '10px', color: '#666' }}>Median Comp Price</div>
                                <div style={{ fontSize: '16px', fontWeight: 700, color: colors.navy }}>{fmt(medianPrice)}</div>
                              </div>
                              <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                                <div style={{ fontSize: '10px', color: '#666' }}>$/SqFt</div>
                                <div style={{ fontSize: '16px', fontWeight: 700, color: colors.navy }}>{medianPpsf ? `$${Math.round(medianPpsf)}` : '—'}</div>
                              </div>
                              <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                                <div style={{ fontSize: '10px', color: '#666' }}>Comps Found</div>
                                <div style={{ fontSize: '16px', fontWeight: 700, color: colors.navy }}>{comps.length}</div>
                              </div>
                            </div>

                            {/* Comparable Sales */}
                            {comps.length > 0 && (
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: colors.navy, marginBottom: '8px' }}>
                                  Comparable Sales ({comps.length})
                                </div>
                                {comps.slice(0, 8).map((comp: any, i: number) => (
                                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < Math.min(comps.length, 8) - 1 ? '1px solid #eee' : 'none', fontSize: '12px' }}>
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#333' }}>{comp.unparsed_address || comp.address}</div>
                                      <div style={{ color: '#8mon88' }}>
                                        {comp.bedrooms}bd/{comp.bathrooms}ba/{comp.living_area?.toLocaleString()}sf
                                        {comp.year_built ? ` • Built ${comp.year_built}` : ''}
                                        {comp.distance_miles != null ? ` • ${comp.distance_miles.toFixed(2)}mi` : ''}
                                      </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <div style={{ fontWeight: 700, color: '#27ae60' }}>{fmt(comp.close_price || comp.list_price)}</div>
                                      {comp.close_date && <div style={{ fontSize: '10px', color: '#999' }}>{new Date(comp.close_date).toLocaleDateString()}</div>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {buyerCMAResult?.error && (
                        <div style={{ color: '#e74c3c', fontSize: '13px' }}>{buyerCMAResult.error}</div>
                      )}
                    </div>
                  )}

                  {/* Property Ownership Card */}
                  {(ownedProperties.length > 0 || propsLoading || showAddProperty) && (
                    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginTop: '16px' }}>
                      <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>
                          <FontAwesomeIcon icon={faHome} className="me-2" style={{ color: colors.gold }} />
                          Property Ownership
                          {ownedProperties.length > 0 && <span style={{ fontSize: '11px', fontWeight: 400, color: '#888', marginLeft: '8px' }}>({ownedProperties.length} propert{ownedProperties.length === 1 ? 'y' : 'ies'})</span>}
                        </span>
                        <button
                          onClick={() => { setShowAddProperty(!showAddProperty); setAddPropForm({ address: '', city: '', zip: '', propertyType: '', ownership: '', bedrooms: '', bathrooms: '', sqft: '', yearBuilt: '', estimatedValue: '' }); }}
                          style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', border: `1px solid ${colors.gold}`, background: showAddProperty ? colors.gold : 'white', color: showAddProperty ? 'white' : colors.gold, cursor: 'pointer' }}
                        >
                          {showAddProperty ? 'Cancel' : '+ Add Property'}
                        </button>
                      </h6>

                      {/* Add Property Form */}
                      {showAddProperty && (
                        <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px', marginBottom: '12px', border: '1px solid #e9ecef' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                            <input placeholder="Street Address *" value={addPropForm.address} onChange={e => setAddPropForm(p => ({ ...p, address: e.target.value }))} style={{ gridColumn: '1 / -1', padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }} />
                            <input placeholder="City" value={addPropForm.city} onChange={e => setAddPropForm(p => ({ ...p, city: e.target.value }))} style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }} />
                            <input placeholder="ZIP" value={addPropForm.zip} onChange={e => setAddPropForm(p => ({ ...p, zip: e.target.value }))} style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }} />
                            <select value={addPropForm.propertyType} onChange={e => setAddPropForm(p => ({ ...p, propertyType: e.target.value }))} style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none', background: 'white' }}>
                              <option value="">Property Type</option>
                              <option value="Single Family">Single Family</option>
                              <option value="Condo/Townhouse">Condo/Townhouse</option>
                              <option value="Multi-Family">Multi-Family</option>
                              <option value="Land">Land</option>
                              <option value="Commercial">Commercial</option>
                            </select>
                            <input placeholder="Owner Name" value={addPropForm.ownership} onChange={e => setAddPropForm(p => ({ ...p, ownership: e.target.value }))} style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }} />
                            <input placeholder="Beds" type="number" value={addPropForm.bedrooms} onChange={e => setAddPropForm(p => ({ ...p, bedrooms: e.target.value }))} style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }} />
                            <input placeholder="Baths" type="number" value={addPropForm.bathrooms} onChange={e => setAddPropForm(p => ({ ...p, bathrooms: e.target.value }))} style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }} />
                            <input placeholder="Sq Ft" type="number" value={addPropForm.sqft} onChange={e => setAddPropForm(p => ({ ...p, sqft: e.target.value }))} style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }} />
                            <input placeholder="Year Built" type="number" value={addPropForm.yearBuilt} onChange={e => setAddPropForm(p => ({ ...p, yearBuilt: e.target.value }))} style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }} />
                            <input placeholder="Estimated Value ($)" type="number" value={addPropForm.estimatedValue} onChange={e => setAddPropForm(p => ({ ...p, estimatedValue: e.target.value }))} style={{ padding: '8px 10px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', outline: 'none' }} />
                          </div>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button
                              disabled={!addPropForm.address.trim() || addPropSaving}
                              onClick={async () => {
                                setAddPropSaving(true);
                                try {
                                  const leadId = selected?.lead_id || selected?.id;
                                  const { data } = await axiosInstance.post('/county-deals/add-property', {
                                    leadId,
                                    address: addPropForm.address.trim(),
                                    city: addPropForm.city.trim(),
                                    zip: addPropForm.zip.trim(),
                                    propertyType: addPropForm.propertyType,
                                    ownership: addPropForm.ownership.trim() || `${selected?.lead_first_name || ''} ${selected?.lead_last_name || ''}`.trim(),
                                    bedrooms: addPropForm.bedrooms ? parseInt(addPropForm.bedrooms) : null,
                                    bathrooms: addPropForm.bathrooms ? parseInt(addPropForm.bathrooms) : null,
                                    sqft: addPropForm.sqft ? parseInt(addPropForm.sqft) : null,
                                    yearBuilt: addPropForm.yearBuilt ? parseInt(addPropForm.yearBuilt) : null,
                                    estimatedValue: addPropForm.estimatedValue ? parseFloat(addPropForm.estimatedValue) : null,
                                  });
                                  if (data.success) {
                                    setOwnedProperties(prev => [...prev, data.property]);
                                    setShowAddProperty(false);
                                    setAlert({ type: 'success', message: `Added property: ${addPropForm.address}` });
                                  }
                                } catch { setAlert({ type: 'danger', message: 'Failed to add property' }); }
                                setAddPropSaving(false);
                              }}
                              style={{ padding: '6px 16px', fontSize: '12px', fontWeight: 700, borderRadius: '6px', border: 'none', background: addPropForm.address.trim() ? colors.gold : '#ccc', color: 'white', cursor: addPropForm.address.trim() ? 'pointer' : 'not-allowed' }}
                            >
                              {addPropSaving ? 'Saving...' : 'Add Property'}
                            </button>
                          </div>
                        </div>
                      )}
                      {propsLoading && <div style={{ fontSize: '12px', color: '#888' }}><Spinner animation="border" size="sm" /> Searching county records...</div>}
                      {ownedProperties.map((prop: any, i: number) => {
                        const leadLast = (selected?.lead_last_name || selected?.last_name || '').toUpperCase().trim();
                        const countyOwner = (prop.ownership || '').toUpperCase();
                        const ownershipMatch = leadLast && leadLast.length > 2 && countyOwner.includes(leadLast);
                        const propertySold = !ownershipMatch && countyOwner.length > 2 && leadLast.length > 2;

                        return (
                        <div key={i} style={{ padding: '12px 14px', marginBottom: '8px', borderRadius: '8px', border: propertySold ? '2px solid #e74c3c' : '1px solid #e9ecef', background: propertySold ? '#fff5f5' : '#fafbfc' }}>

                          {/* Property Sold Warning */}
                          {propertySold && (
                            <div style={{ background: '#fce4ec', borderRadius: '6px', padding: '10px 12px', marginBottom: '10px', border: '1px solid #f8bbd0' }}>
                              <div style={{ fontSize: '12px', fontWeight: 700, color: '#c62828', marginBottom: '4px' }}>
                                Property Sold — Owner Changed
                              </div>
                              <div style={{ fontSize: '11px', color: '#555' }}>
                                County records show this property is now owned by: <strong>{prop.ownership}</strong>.
                                {selected?.lead_first_name} {selected?.lead_last_name || ''} is no longer the owner on record.
                              </div>
                              <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                                <button
                                  onClick={async () => {
                                    const leadId = selected?.lead_id || selected?.id;
                                    try {
                                      // Update classification
                                      await axiosInstance.post(`/ai-coach/classifications/${leadId}`, { stage: 'past_seller', notes: `Property sold: ${prop.fullAddress || prop.address}. Now owned by ${prop.ownership}. Updated from active_seller.` });
                                      // Update the stage dropdown too
                                      await axiosInstance.patch(`/agents/clients/${leadId}/stage`, { stage: 'past_seller' });
                                      // Remove this property from the view
                                      setOwnedProperties(prev => prev.filter((_, idx) => idx !== i));
                                      setAlert({ type: 'success', message: 'Updated to Past Seller — property marked as sold' });
                                      // Refresh client detail
                                      if (selected) { const r = await agentPortalService.getClientProfile(selected.id); if (r.success) setClientDetail(r.data); }
                                    } catch { setAlert({ type: 'danger', message: 'Update failed' }); }
                                  }}
                                  style={{ padding: '4px 10px', fontSize: '10px', fontWeight: 600, borderRadius: '4px', border: '1px solid #c62828', background: 'white', color: '#c62828', cursor: 'pointer' }}
                                >
                                  Mark as Past Seller
                                </button>
                                <button
                                  onClick={() => setOwnedProperties(prev => prev.filter((_, idx) => idx !== i))}
                                  style={{ padding: '4px 10px', fontSize: '10px', fontWeight: 600, borderRadius: '4px', border: '1px solid #999', background: 'white', color: '#666', cursor: 'pointer' }}
                                >
                                  Dismiss
                                </button>
                              </div>
                            </div>
                          )}

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: 700, color: propertySold ? '#999' : colors.navy, textDecoration: propertySold ? 'line-through' : 'none' }}>{prop.fullAddress || prop.address}</div>
                              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                {prop.propertyType} {prop.sqft ? `· ${prop.sqft.toLocaleString()} sqft` : ''} {prop.bedrooms ? `· ${prop.bedrooms}bd/${prop.bathrooms || '?'}ba` : ''} {prop.yearBuilt ? `· Built ${prop.yearBuilt}` : ''}
                              </div>
                              <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
                                {prop.ownership} · {prop.deedTypeLabel || prop.deedType || ''} {prop.deedDate ? `(${prop.deedDate})` : ''} {prop.ownershipYears ? `· Owned ${prop.ownershipYears} yrs` : ''}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              {prop.fullCashValue && <div style={{ fontSize: '15px', fontWeight: 700, color: colors.navy }}>${prop.fullCashValue.toLocaleString()}</div>}
                              <div style={{ fontSize: '10px', color: '#999' }}>County Value</div>
                            </div>
                          </div>

                          {/* Alert badges */}
                          {(prop.alerts || []).length > 0 && (
                            <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {prop.alerts.map((alert: any, j: number) => {
                                const bgMap: any = { danger: '#fce4ec', warning: '#fff3e0', info: '#e3f2fd', neutral: '#f5f5f5' };
                                const colorMap: any = { danger: '#c62828', warning: '#e65100', info: '#1565c0', neutral: '#555' };
                                return (
                                  <span key={j} style={{ fontSize: '10px', fontWeight: 600, padding: '3px 8px', borderRadius: '4px', background: bgMap[alert.type] || '#f5f5f5', color: colorMap[alert.type] || '#555' }}>
                                    {alert.label}
                                  </span>
                                );
                              })}
                            </div>
                          )}

                          {/* Absentee detail */}
                          {prop.isAbsentee && (
                            <div style={{ marginTop: '6px', fontSize: '11px', color: '#e65100', fontWeight: 600 }}>
                              Mailing to: {prop.mailingAddress}, {prop.absenteeLocation}
                            </div>
                          )}

                          {/* Agent of Record */}
                          {prop.buyerAgentName && (
                            <div style={{ marginTop: '8px', background: prop.agentConfirmed ? '#e8f5e9' : '#e3f2fd', borderRadius: '6px', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontSize: '10px', color: '#888', marginBottom: '2px' }}>Buyer's Agent (MLS Record)</div>
                                <div style={{ fontSize: '13px', fontWeight: 700, color: prop.agentConfirmed ? '#2e7d32' : '#1565c0' }}>
                                  {prop.agentConfirmed && <FontAwesomeIcon icon={faCheckCircle} className="me-1" />}
                                  {prop.buyerAgentName} {prop.buyerAgentMlsId ? `(${prop.buyerAgentMlsId})` : ''}
                                </div>
                                {prop.closePrice && <div style={{ fontSize: '11px', color: '#666' }}>Closed: ${Number(prop.closePrice).toLocaleString()}</div>}
                              </div>
                              {!prop.agentConfirmed ? (
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button
                                    onClick={async () => {
                                      try {
                                        const leadId = selected?.lead_id || selected?.id;
                                        const { data } = await axiosInstance.post('/county-deals/confirm-agent', {
                                          leadId, snapshotId: prop.id || prop.apn,
                                          agentName: prop.buyerAgentName, agentMlsId: prop.buyerAgentMlsId
                                        });
                                        setOwnedProperties(prev => prev.map(p => p.apn === prop.apn ? { ...p, agentConfirmed: true } : p));
                                        if (data.agentAdded) toast.success(`${prop.buyerAgentName} added to your agent network!`);
                                        else toast.success('Agent confirmed');
                                      } catch { toast.error('Failed to confirm agent'); }
                                    }}
                                    style={{ padding: '4px 10px', fontSize: '10px', fontWeight: 700, borderRadius: '4px', border: '1px solid #27ae60', background: '#e8f5e9', color: '#2e7d32', cursor: 'pointer' }}
                                  >
                                    <FontAwesomeIcon icon={faCheckCircle} className="me-1" />Confirm Agent
                                  </button>
                                  <button
                                    onClick={() => setOwnedProperties(prev => prev.map(p => p.apn === prop.apn ? { ...p, buyerAgentName: null } : p))}
                                    style={{ padding: '4px 8px', fontSize: '10px', fontWeight: 600, borderRadius: '4px', border: '1px solid #ccc', background: 'white', color: '#888', cursor: 'pointer' }}
                                  >
                                    Wrong Agent
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={async () => {
                                    try {
                                      const leadId = selected?.lead_id || selected?.id;
                                      await axiosInstance.post('/county-deals/confirm-property', { leadId, snapshotId: prop.id || prop.apn, confirmed: false });
                                      setOwnedProperties(prev => prev.map(p => p.apn === prop.apn ? { ...p, agentConfirmed: false } : p));
                                    } catch { }
                                  }}
                                  style={{ padding: '2px 8px', fontSize: '9px', fontWeight: 600, borderRadius: '4px', border: '1px solid #ccc', background: 'white', color: '#888', cursor: 'pointer' }}
                                >
                                  Undo
                                </button>
                              )}
                            </div>
                          )}

                          {/* Property Confirmation */}
                          {!prop.confirmed && !propertySold && (
                            <div style={{ marginTop: '8px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <button
                                onClick={async () => {
                                  try {
                                    const leadId = selected?.lead_id || selected?.id;
                                    await axiosInstance.post(`/county-deals/confirm-property`, { leadId, snapshotId: prop.id || prop.apn, confirmed: true });
                                    setOwnedProperties(prev => prev.map(p => p.apn === prop.apn ? { ...p, confirmed: true } : p));
                                    setAlert({ type: 'success', message: `Confirmed: ${prop.address} belongs to ${selected?.lead_first_name}` });
                                  } catch { setAlert({ type: 'danger', message: 'Failed to confirm' }); }
                                }}
                                style={{ padding: '4px 10px', fontSize: '10px', fontWeight: 700, borderRadius: '4px', border: '1px solid #27ae60', background: '#e8f5e9', color: '#2e7d32', cursor: 'pointer' }}
                              >
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />This is their property
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    const leadId = selected?.lead_id || selected?.id;
                                    await axiosInstance.post(`/county-deals/dismiss-property`, { leadId, snapshotId: prop.id || prop.apn });
                                    setOwnedProperties(prev => prev.filter((_, idx) => idx !== i));
                                    setAlert({ type: 'info', message: `Dismissed: ${prop.address}` });
                                  } catch { setAlert({ type: 'danger', message: 'Failed to dismiss property' }); }
                                }}
                                style={{ padding: '4px 10px', fontSize: '10px', fontWeight: 600, borderRadius: '4px', border: '1px solid #ccc', background: 'white', color: '#888', cursor: 'pointer' }}
                              >
                                Not my client
                              </button>
                            </div>
                          )}
                          {prop.confirmed && (
                            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: '10px', fontWeight: 700, color: '#2e7d32' }}>
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />Confirmed property
                              </span>
                              <button
                                onClick={async () => {
                                  try {
                                    const leadId = selected?.lead_id || selected?.id;
                                    await axiosInstance.post(`/county-deals/confirm-property`, { leadId, snapshotId: prop.id || prop.apn, confirmed: false });
                                    setOwnedProperties(prev => prev.map(p => p.apn === prop.apn ? { ...p, confirmed: false } : p));
                                  } catch { }
                                }}
                                style={{ padding: '2px 8px', fontSize: '9px', fontWeight: 600, borderRadius: '4px', border: '1px solid #ccc', background: 'white', color: '#888', cursor: 'pointer' }}
                              >
                                Unconfirm
                              </button>
                            </div>
                          )}

                          {/* Action buttons */}
                          <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={async () => {
                                if (isSponsorPlan) { toast.error('MLS data access requires an active agent subscription with broker MLS authorization.'); return; }
                                const leadId = selected?.lead_id || selected?.id;
                                try {
                                  await axiosInstance.post(`/sellers/${leadId}/profile`, {
                                    property_address: prop.address || '', property_city: prop.city || '',
                                    property_state: prop.mailingState || 'AZ', property_zip: prop.zip || '',
                                  }).catch(() => {});
                                  const r = await agentPortalService.getClientProfile(selected.id);
                                  if (r.success) setClientDetail(r.data);
                                  setActiveTab('seller');
                                } catch {
                                  setActiveTab('seller');
                                }
                              }}
                              style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', border: '1px solid ' + colors.navy, background: 'white', color: colors.navy, cursor: 'pointer' }}
                            >
                              Run CMA
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const leadId = selected?.lead_id || selected?.id;
                                  await axiosInstance.post('/ai-coach/offer-strategy', {
                                    leadId,
                                    mode: 'draft',
                                    message: `Send a monthly home value update for ${prop.fullAddress}. Their property is valued at $${(prop.fullCashValue || 0).toLocaleString()} by the county. Include recent comparable sales activity in their neighborhood.`,
                                    offerData: { address: prop.fullAddress, predictedValue: prop.fullCashValue || 0, comps: [], compCount: 0 },
                                  });
                                  setAlert({ type: 'success', message: `Market update queued for ${prop.address}` });
                                } catch { setAlert({ type: 'danger', message: 'Failed to generate update' }); }
                              }}
                              style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', border: '1px solid #27ae60', background: 'white', color: '#27ae60', cursor: 'pointer' }}
                            >
                              Send Market Update
                            </button>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#555', cursor: 'pointer', marginLeft: '8px' }}>
                              <input
                                type="checkbox"
                                checked={!!prop.monthly_update_enabled}
                                onChange={async (e) => {
                                  const enabled = e.target.checked;
                                  try {
                                    const leadId = selected?.lead_id || selected?.id;
                                    await axiosInstance.post(`/county-deals/property-toggle-monthly/${prop.apn}`, { enabled, leadId });
                                    setOwnedProperties(prev => prev.map(p => p.apn === prop.apn ? { ...p, monthly_update_enabled: enabled ? 1 : 0 } : p));
                                    setAlert({ type: 'success', message: enabled ? `Monthly updates enabled for ${prop.address}` : `Monthly updates disabled` });
                                  } catch { setAlert({ type: 'danger', message: 'Failed to update' }); }
                                }}
                                style={{ width: '14px', height: '14px', accentColor: colors.navy }}
                              />
                              <span style={{ fontWeight: prop.monthly_update_enabled ? 600 : 400 }}>Monthly Auto-Update</span>
                            </label>
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  )}

                  {/* Add Property link when card is hidden */}
                  {ownedProperties.length === 0 && !propsLoading && !showAddProperty && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <button
                        onClick={() => setShowAddProperty(true)}
                        style={{ padding: '8px 16px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: `1px dashed ${colors.gold}`, background: 'white', color: colors.gold, cursor: 'pointer' }}
                      >
                        <FontAwesomeIcon icon={faHome} className="me-2" />Add a Property Manually
                      </button>
                    </div>
                  )}

                  {/* Notes */}
                  <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginTop: '16px' }}>
                    <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px' }}>
                      <FontAwesomeIcon icon={faStickyNote} className="me-2" style={{ color: colors.orange }} />Notes
                    </h6>
                    <div className="d-flex gap-2">
                      <input
                        placeholder="Add a note about this client..."
                        value={noteText}
                        onChange={e => setNoteText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && noteText.trim()) handleAddNote(); }}
                        style={{ flex: 1, border: '1px solid #ddd', borderRadius: '8px', padding: '10px', fontSize: '13px', outline: 'none' }}
                      />
                      <Button onClick={handleAddNote} disabled={!noteText.trim()} style={{ background: colors.gold, border: 'none', fontWeight: 600 }}>
                        Add
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Buyer Profile Tab */}
              {/* AI Search Tab */}
              {activeTab === 'ai-search' && !hasAISearch && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '40px', border: '1px solid #e9ecef', textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faSearch} size="3x" style={{ color: colors.gold, opacity: 0.4, marginBottom: '16px' }} />
                  <h5 style={{ fontWeight: 700, color: colors.navy }}>Upgrade to AI Search</h5>
                  <p style={{ color: '#666', fontSize: '14px', maxWidth: '400px', margin: '0 auto 20px' }}>
                    Give your buyers their own AI-powered search portal. Smart CMA reports with AI analysis. Shareable seller reports.
                  </p>
                  <Button onClick={() => navigate('/pricing')} style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, padding: '12px 32px' }}>
                    Upgrade to AI Search — $135/mo
                  </Button>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>Current plan: {userPlan.replace(/-/g, ' ')}</div>
                </div>
              )}
              {activeTab === 'ai-search' && hasAISearch && isSponsorPlan && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '40px', border: '1px solid #e9ecef', textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faSearch} size="3x" style={{ color: colors.gold, opacity: 0.4, marginBottom: '16px' }} />
                  <h5 style={{ fontWeight: 700, color: colors.navy }}>MLS Authorization Required</h5>
                  <p style={{ color: '#666', fontSize: '14px', maxWidth: '450px', margin: '0 auto 12px' }}>
                    AI Search uses live MLS data which requires an active agent subscription with broker MLS authorization per ARMLS Content License Agreement.
                  </p>
                  <p style={{ color: '#888', fontSize: '12px' }}>Your sponsored agents will have full access to AI Search when they connect their MLS account.</p>
                </div>
              )}
              {activeTab === 'ai-search' && hasAISearch && !isSponsorPlan && !hasMLSAccess && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '40px', border: '1px solid #e9ecef', textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faSearch} size="3x" style={{ color: colors.gold, opacity: 0.4, marginBottom: '16px' }} />
                  <h5 style={{ fontWeight: 700, color: colors.navy }}>Connect Your MLS for Access</h5>
                  <p style={{ color: '#666', fontSize: '14px', maxWidth: '450px', margin: '0 auto 16px' }}>
                    AI Search, Seller CMA Reports, and Offer Analyzer require an active MLS data feed. Connect your ARMLS credentials to unlock these features.
                  </p>
                  <p style={{ color: '#888', fontSize: '12px', maxWidth: '400px', margin: '0 auto 20px' }}>
                    Your broker receives 5 free IDX feeds from ARMLS. Contact your broker to request a feed slot, or configure your credentials in Settings.
                  </p>
                  <Button onClick={() => window.location.href = '/agent-portal/dashboard/settings'} style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, padding: '12px 32px' }}>
                    Connect MLS in Settings
                  </Button>
                </div>
              )}
              {activeTab === 'ai-search' && hasAISearch && !isSponsorPlan && hasMLSAccess && (
                <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e9ecef', overflow: 'hidden' }}>
                  <AISearchTab
                    leadId={selected.id}
                    clientName={getClientName(selected)}
                    buyerProfile={clientDetail?.buyer_profile}
                  />
                </div>
              )}

              {/* Email Tab */}
              {activeTab === 'email' && (() => {
                return (
                  <div>
                    {/* Compose */}
                    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginBottom: '16px' }}>
                      {replyToEmail && (
                        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px', padding: '8px 14px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', color: '#f57f17', fontWeight: 600 }}>
                            Replying to thread: {replyToEmail.subject || '(no subject)'}
                          </span>
                          <button onClick={() => { setReplyToEmail(null); setEmailSubject(''); setEmailBody(''); setEmailTo([]); }}
                            style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}>
                            &times;
                          </button>
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 style={{ color: colors.navy, fontWeight: 700, margin: 0 }}>
                          <FontAwesomeIcon icon={faEnvelope} className="me-2" style={{ color: colors.gold }} />
                          {replyToEmail ? 'Reply' : 'Compose Email'} to {getClientName(selected)}
                        </h6>
                        {hasAIPro && (
                          <Button
                            size="sm"
                            onClick={handleAIDraft}
                            disabled={generatingAIDraft}
                            style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600, fontSize: '12px' }}
                          >
                            {generatingAIDraft ? (
                              <><Spinner size="sm" className="me-1" /> Drafting...</>
                            ) : (
                              <><FontAwesomeIcon icon={faRobot} className="me-1" /> AI Draft</>
                            )}
                          </Button>
                        )}
                      </div>
                      {connectedAccounts.length > 0 && (
                        <div className="mb-2">
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <label style={{ fontSize: '12px', color: '#666', fontWeight: 600 }}>Send from:</label>
                            {localStorage.getItem('defaultFromAccount') === selectedFromAccount && (
                              <span style={{ fontSize: '10px', color: '#27ae60', fontWeight: 600 }}>Default</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <select
                              value={selectedFromAccount}
                              onChange={e => setSelectedFromAccount(e.target.value)}
                              style={{ flex: 1, border: '1px solid #ddd', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', outline: 'none', background: '#f8f9fa' }}
                            >
                              {connectedAccounts.map((acc: any) => (
                                <option key={acc.id} value={String(acc.id)}>
                                  {acc.email} ({acc.provider === 'gmail' || acc.provider === 'google' ? 'Gmail' : acc.provider === 'microsoft' ? 'Outlook' : acc.provider})
                                  {localStorage.getItem('defaultFromAccount') === String(acc.id) ? ' ★' : ''}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => { localStorage.setItem('defaultFromAccount', selectedFromAccount); setAlert({ type: 'success', message: 'Default email account saved' }); }}
                              title="Set as default send-from account"
                              style={{ padding: '8px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '6px', border: '1px solid #ddd', background: localStorage.getItem('defaultFromAccount') === selectedFromAccount ? '#e8f5e9' : 'white', color: localStorage.getItem('defaultFromAccount') === selectedFromAccount ? '#27ae60' : '#666', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              {localStorage.getItem('defaultFromAccount') === selectedFromAccount ? '★ Default' : 'Set Default'}
                            </button>
                          </div>
                        </div>
                      )}
                      {connectedAccounts.length === 0 && (
                        <div className="mb-2 p-2 rounded" style={{ background: '#fff3cd', fontSize: '12px' }}>
                          {isSponsorPlan
                            ? <>Connect your Gmail or Outlook account to send emails directly from the platform. <a href="/agent-portal/dashboard?tab=settings" style={{ color: colors.gold, fontWeight: 600 }}>Connect your email</a></>
                            : <>No email account connected. Emails will be sent from notifications@kairosiq.ai.{' '}<a href="/agent-portal/dashboard?tab=settings" style={{ color: colors.gold, fontWeight: 600 }}>Connect your email</a></>
                          }
                        </div>
                      )}
                      <div style={{ marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                          <label style={{ fontSize: '12px', fontWeight: 600, color: '#666', width: '30px', paddingTop: '8px' }}>To</label>
                          <div style={{ flex: 1, border: '1px solid #ddd', borderRadius: '6px', padding: '4px 8px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', minHeight: '36px' }}>
                            {(emailTo.length === 0 && selected?.lead_email ? [selected.lead_email] : emailTo).map((addr, i) => (
                              <span key={i} style={{ fontSize: '11px', padding: '2px 8px', background: '#e8f5e9', borderRadius: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                {addr}
                                {(emailTo.length > 0 || i > 0) && (
                                  <button onClick={() => setEmailTo(prev => { const list = prev.length === 0 ? [selected?.lead_email || ''] : [...prev]; return list.filter((_, j) => j !== i); })} style={{ background: 'none', border: 'none', color: '#2e7d32', cursor: 'pointer', fontSize: '13px', padding: 0, lineHeight: 1 }}>×</button>
                                )}
                              </span>
                            ))}
                            <input
                              value={emailToInput}
                              onChange={e => setEmailToInput(e.target.value)}
                              onKeyDown={e => {
                                if ((e.key === 'Enter' || e.key === ',' || e.key === 'Tab') && emailToInput.trim()) {
                                  e.preventDefault();
                                  const addr = emailToInput.replace(/,/g, '').trim();
                                  if (addr.includes('@')) {
                                    setEmailTo(prev => { const list = prev.length === 0 ? [selected?.lead_email || ''] : [...prev]; return [...list, addr]; });
                                    setEmailToInput('');
                                  }
                                }
                              }}
                              onBlur={() => { if (emailToInput.trim().includes('@')) { setEmailTo(prev => { const list = prev.length === 0 ? [selected?.lead_email || ''] : [...prev]; return [...list, emailToInput.trim()]; }); setEmailToInput(''); } }}
                              placeholder="Add recipient..."
                              style={{ border: 'none', outline: 'none', fontSize: '13px', flex: 1, minWidth: '120px', padding: '4px' }}
                            />
                          </div>
                        </div>
                        {showCcBcc && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                              <label style={{ fontSize: '12px', fontWeight: 600, color: '#666', width: '30px', paddingTop: '8px' }}>CC</label>
                              <div style={{ flex: 1, border: '1px solid #ddd', borderRadius: '6px', padding: '4px 8px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', minHeight: '36px' }}>
                                {emailCc.map((addr, i) => (
                                  <span key={i} style={{ fontSize: '11px', padding: '2px 8px', background: '#e3f2fd', borderRadius: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    {addr}
                                    <button onClick={() => setEmailCc(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#1565c0', cursor: 'pointer', fontSize: '13px', padding: 0, lineHeight: 1 }}>×</button>
                                  </span>
                                ))}
                                <input
                                  value={emailCcInput}
                                  onChange={e => setEmailCcInput(e.target.value)}
                                  onKeyDown={e => {
                                    if ((e.key === 'Enter' || e.key === ',' || e.key === 'Tab') && emailCcInput.trim()) {
                                      e.preventDefault();
                                      const addr = emailCcInput.replace(/,/g, '').trim();
                                      if (addr.includes('@')) { setEmailCc(prev => [...prev, addr]); setEmailCcInput(''); }
                                    }
                                    if (e.key === 'Backspace' && !emailCcInput && emailCc.length > 0) setEmailCc(prev => prev.slice(0, -1));
                                  }}
                                  onBlur={() => { if (emailCcInput.trim().includes('@')) { setEmailCc(prev => [...prev, emailCcInput.trim()]); setEmailCcInput(''); } }}
                                  placeholder={emailCc.length === 0 ? 'Type email, press Enter' : ''}
                                  style={{ border: 'none', outline: 'none', fontSize: '13px', flex: 1, minWidth: '120px', padding: '4px' }}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                              <label style={{ fontSize: '12px', fontWeight: 600, color: '#666', width: '30px', paddingTop: '8px' }}>BCC</label>
                              <div style={{ flex: 1, border: '1px solid #ddd', borderRadius: '6px', padding: '4px 8px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', minHeight: '36px' }}>
                                {emailBcc.map((addr, i) => (
                                  <span key={i} style={{ fontSize: '11px', padding: '2px 8px', background: '#f5f5f5', borderRadius: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    {addr}
                                    <button onClick={() => setEmailBcc(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '13px', padding: 0, lineHeight: 1 }}>×</button>
                                  </span>
                                ))}
                                <input
                                  value={emailBccInput}
                                  onChange={e => setEmailBccInput(e.target.value)}
                                  onKeyDown={e => {
                                    if ((e.key === 'Enter' || e.key === ',' || e.key === 'Tab') && emailBccInput.trim()) {
                                      e.preventDefault();
                                      const addr = emailBccInput.replace(/,/g, '').trim();
                                      if (addr.includes('@')) { setEmailBcc(prev => [...prev, addr]); setEmailBccInput(''); }
                                    }
                                    if (e.key === 'Backspace' && !emailBccInput && emailBcc.length > 0) setEmailBcc(prev => prev.slice(0, -1));
                                  }}
                                  onBlur={() => { if (emailBccInput.trim().includes('@')) { setEmailBcc(prev => [...prev, emailBccInput.trim()]); setEmailBccInput(''); } }}
                                  placeholder={emailBcc.length === 0 ? 'Type email, press Enter' : ''}
                                  style={{ border: 'none', outline: 'none', fontSize: '13px', flex: 1, minWidth: '120px', padding: '4px' }}
                                />
                              </div>
                            </div>
                          </>
                        )}
                        {!showCcBcc && (
                          <button onClick={() => setShowCcBcc(true)} style={{ fontSize: '11px', color: colors.navy, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                            + CC / BCC
                          </button>
                        )}
                      </div>
                      <input
                        placeholder="Subject"
                        value={emailSubject}
                        onChange={e => setEmailSubject(e.target.value)}
                        style={{ width: '100%', border: '1px solid #ddd', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', marginBottom: '8px', outline: 'none' }}
                      />
                      <textarea
                        placeholder={`Hi {{name}},\n\nGreat speaking with you today...`}
                        value={emailBody}
                        onChange={e => setEmailBody(e.target.value)}
                        rows={6}
                        style={{ width: '100%', border: '1px solid #ddd', borderRadius: '8px', padding: '12px', fontSize: '13px', marginBottom: '8px', outline: 'none', resize: 'vertical' }}
                      />
                      {/* Attachments */}
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#666', cursor: 'pointer', padding: '6px 12px', border: '1px solid #ddd', borderRadius: '6px', background: '#f8f9fa' }}>
                          <FontAwesomeIcon icon={faPlus} style={{ fontSize: '10px' }} /> Attach File
                          <input type="file" multiple style={{ display: 'none' }} onChange={e => {
                            const files = Array.from(e.target.files || []);
                            setEmailAttachments(prev => [...prev, ...files]);
                            e.target.value = '';
                          }} />
                        </label>
                        {emailAttachments.length > 0 && (
                          <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {emailAttachments.map((file, i) => (
                              <span key={i} style={{ fontSize: '11px', padding: '3px 8px', background: '#e3f2fd', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                {file.name} ({Math.round(file.size / 1024)}KB)
                                <button onClick={() => setEmailAttachments(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '12px', padding: 0 }}>×</button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <small style={{ color: '#999' }}>
                          Use <code style={{ color: colors.gold }}>{'{{name}}'}</code> to personalize.
                          {connectedAccounts.length > 0 && ' Sent directly from your email account.'}
                        </small>
                        <Button
                          onClick={handleSendEmail}
                          disabled={sendingEmail || !emailSubject || !emailBody}
                          style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}
                        >
                          {sendingEmail ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faArrowRight} className="me-1" /> Send</>}
                        </Button>
                      </div>
                    </div>

                    {/* Email History — Inbound + Outbound */}
                    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>
                      <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px' }}>
                        <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: colors.blue }} />
                        Email History ({emailHistory.length})
                      </h6>
                      {emailsLoading ? (
                        <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                      ) : emailHistory.length === 0 ? (
                        <p style={{ color: '#999', textAlign: 'center', padding: '20px 0', fontSize: '13px' }}>
                          No emails with this client yet. Use the compose form above to send your first email.
                        </p>
                      ) : (
                        emailHistory.map((em: any) => {
                          const isInbound = em.direction === 'inbound';
                          return (
                            <div key={em.id}
                              style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                              onClick={() => setExpandedEmailId(expandedEmailId === em.id ? null : em.id)}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <div style={{ flex: 1 }}>
                                  <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                    <span style={{
                                      display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700,
                                      background: isInbound ? '#e8f5e9' : '#e3f2fd',
                                      color: isInbound ? '#2e7d32' : '#1565c0'
                                    }}>
                                      {isInbound ? 'RECEIVED' : 'SENT'}
                                    </span>
                                    <div style={{ fontWeight: 600, color: '#333', fontSize: '14px' }}>{em.subject}</div>
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                                    {new Date(em.sent_at).toLocaleString()}
                                    {em.account && <span style={{ marginLeft: '8px' }}>via {em.account}</span>}
                                    {em.source === 'local' && <span style={{ marginLeft: '8px', color: colors.gold }}>SES</span>}
                                  </div>
                                </div>
                                <span style={{ color: '#ccc', fontSize: '12px' }}>{expandedEmailId === em.id ? '▲' : '▼'}</span>
                              </div>
                              {expandedEmailId === em.id && (em.body || em.snippet) && (() => {
                                // Clean up email body for display
                                let bodyText = (em.body || em.snippet || '');
                                // Decode HTML entities
                                const textarea = document.createElement('textarea');
                                textarea.innerHTML = bodyText;
                                bodyText = textarea.value;
                                // Strip quoted reply chain (From: ... Sent: ... To: ...)
                                bodyText = bodyText.replace(/\s*From:.*?Sent:.*?To:.*?(Subject:.*?)?$/s, '').trim();
                                // Also strip "On <date> <person> wrote:" style quotes
                                bodyText = bodyText.replace(/\s*On .+wrote:\s*$/s, '').trim();
                                // Clean up extra whitespace
                                bodyText = bodyText.replace(/\n{3,}/g, '\n\n').trim();

                                // Parse "Name <email>" from the from field
                                const fromName = em.from ? em.from.replace(/<[^>]+>/g, '').trim() : '';

                                return (
                                  <div style={{
                                    marginTop: '10px', padding: '14px', background: isInbound ? '#f6fdf6' : '#f8f9fa', borderRadius: '8px',
                                    borderLeft: `3px solid ${isInbound ? '#4caf50' : colors.gold}`, fontSize: '13px', color: '#444',
                                    lineHeight: 1.7
                                  }}>
                                    {isInbound && fromName && (
                                      <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px', fontWeight: 600, ...blur }}>
                                        {fromName}
                                      </div>
                                    )}
                                    <div style={{ whiteSpace: 'pre-wrap' }}>
                                      {bodyText.replace(/\{\{name\}\}/g, getClientName(selected))}
                                    </div>
                                    <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #e0e0e0', display: 'flex', gap: '8px' }}>
                                      <button onClick={(e) => {
                                        e.stopPropagation();
                                        const replyFrom = isInbound ? (em.from || '').replace(/.*</, '').replace(/>.*/, '').trim() || em.from : (em.to || '').split(',')[0].replace(/.*</, '').replace(/>.*/, '').trim() || em.to;
                                        setEmailTo([replyFrom]);
                                        setEmailSubject(em.subject?.startsWith('Re:') ? em.subject : `Re: ${em.subject || ''}`);
                                        setEmailBody(`\n\n--- Original Message ---\nFrom: ${em.from}\nDate: ${new Date(em.sent_at).toLocaleString()}\n\n${bodyText}`);
                                        setReplyToEmail(em);
                                        setExpandedEmailId(null);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }} style={{ padding: '4px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: `1px solid ${colors.gold}`, background: 'transparent', color: colors.gold, cursor: 'pointer' }}>
                                        Reply
                                      </button>
                                      <button onClick={(e) => {
                                        e.stopPropagation();
                                        setEmailTo([]);
                                        setEmailSubject(em.subject?.startsWith('Fwd:') ? em.subject : `Fwd: ${em.subject || ''}`);
                                        setEmailBody(`\n\n--- Forwarded Message ---\nFrom: ${em.from}\nTo: ${em.to}\nDate: ${new Date(em.sent_at).toLocaleString()}\nSubject: ${em.subject || ''}\n\n${bodyText}`);
                                        setReplyToEmail(null);
                                        setExpandedEmailId(null);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }} style={{ padding: '4px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: '1px solid #ccc', background: 'transparent', color: '#666', cursor: 'pointer' }}>
                                        Forward
                                      </button>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })()}

              {activeTab === 'buyer' && isSponsorPlan && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>
                  <h5 style={{ color: colors.navy, fontWeight: 700, marginBottom: '16px' }}>
                    <FontAwesomeIcon icon={faDollarSign} className="me-2" style={{ color: colors.gold }} />Loan Details
                  </h5>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <p style={{ fontSize: 12, color: '#888', margin: 0 }}>
                      Borrower loan information. Add details to enable AI refinance and equity analysis.
                    </p>
                    <button onClick={async () => {
                      // Auto-fill from MLS + County data
                      try {
                        const leadId = selected?.lead_id || selected?.id;
                        const { data } = await axiosInstance.get(`/agents/clients/${leadId}/auto-loan-data`);
                        if (data.success && data.loanData) {
                          await axiosInstance.put(`/agents/clients/${leadId}/buyer-profile`, data.loanData);
                          toast.success('Loan details auto-filled from MLS + county records');
                          if (selected) { const r = await agentPortalService.getClientProfile(selected.id); if (r.success) setClientDetail(r.data); }
                        } else { toast.error('No MLS/county data found for this address'); }
                      } catch { toast.error('Failed to auto-fill'); }
                    }} style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: 'none', cursor: 'pointer', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy, whiteSpace: 'nowrap' }}>
                      Auto-Fill from Records
                    </button>
                  </div>
                  {(() => {
                    const leadAddr = selected?.lead_street_name || clientDetail?.lead?.lead_street_name || '';
                    const leadCity = selected?.lead_city || clientDetail?.lead?.lead_city || '';
                    const leadState = selected?.lead_state || clientDetail?.lead?.lead_state || '';
                    const leadZip = selected?.lead_zip_code || clientDetail?.lead?.lead_zip_code || '';
                    return (
                      <div>
                        {/* Property Address */}
                        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Financed Property</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: colors.navy }}>
                            {leadAddr || '—'}
                          </div>
                          {leadCity && <div style={{ fontSize: 13, color: '#555' }}>{leadCity}{leadState ? `, ${leadState}` : ''} {leadZip}</div>}
                        </div>

                        {/* Loan Fields Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                          {[
                            { label: 'Loan Amount', key: 'loan_amount', placeholder: '$350,000', prefix: '$' },
                            { label: 'Interest Rate', key: 'interest_rate', placeholder: '6.5%', suffix: '%' },
                            { label: 'Loan Type', key: 'loan_type', type: 'select', options: ['', 'Conventional', 'FHA', 'VA', 'USDA', 'Jumbo', 'Other'] },
                            { label: 'Loan Term', key: 'loan_term', type: 'select', options: ['', '30-year fixed', '15-year fixed', '20-year fixed', '7/1 ARM', '5/1 ARM', 'Other'] },
                            { label: 'Close Date', key: 'close_date', type: 'date' },
                            { label: 'Loan Status', key: 'loan_status', type: 'select', options: ['', 'Active', 'Pre-Qualified', 'In Process', 'Funded', 'Paid Off', 'Refinanced', 'Default'] },
                            { label: 'Monthly Payment', key: 'monthly_payment', placeholder: '$2,100', prefix: '$' },
                            { label: 'Down Payment', key: 'down_payment', placeholder: '$35,000', prefix: '$' },
                          ].map((field, i) => (
                            <div key={i}>
                              <label style={{ fontSize: 11, fontWeight: 600, color: colors.navy, display: 'block', marginBottom: 4 }}>{field.label}</label>
                              {field.type === 'select' ? (
                                <select
                                  data-field={field.key}
                                  defaultValue={(clientDetail?.buyer_profile as any)?.[field.key] || ''}
                                  style={{ width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #ddd', outline: 'none', background: '#fff' }}
                                >
                                  {(field.options || []).map((opt, j) => <option key={j} value={opt}>{opt || '—'}</option>)}
                                </select>
                              ) : (
                                <input
                                  data-field={field.key}
                                  type={field.type || 'text'}
                                  placeholder={field.placeholder}
                                  defaultValue={(clientDetail?.buyer_profile as any)?.[field.key] || ''}
                                  onBlur={async (e) => {
                                    try {
                                      await axiosInstance.put(`/agents/clients/${selected?.id}/buyer-profile`, { [field.key]: e.target.value });
                                    } catch { }
                                  }}
                                  style={{ width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box' }}
                                />
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Loan Notes */}
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 600, color: colors.navy, display: 'block', marginBottom: 4 }}>Loan Notes</label>
                          <textarea
                            id="loan-notes-field"
                            rows={3}
                            placeholder="Add notes about this loan (rate lock, conditions, special circumstances...)"
                            defaultValue={(clientDetail?.buyer_profile as any)?.loan_notes || ''}
                            style={{ width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 6, border: '1px solid #ddd', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                          />
                        </div>

                        {/* Save Button */}
                        <button
                          onClick={async () => {
                            try {
                              const fieldKeys = ['loan_amount', 'interest_rate', 'loan_type', 'loan_term', 'close_date', 'loan_status', 'monthly_payment', 'down_payment'];
                              const fields: any = {};
                              fieldKeys.forEach(key => {
                                const el = document.querySelector(`[data-field="${key}"]`) as HTMLInputElement | HTMLSelectElement;
                                if (el && el.value) fields[key] = el.value;
                              });
                              const notes = (document.getElementById('loan-notes-field') as HTMLTextAreaElement)?.value;
                              if (notes !== undefined) fields.loan_notes = notes;
                              await axiosInstance.put('/agents/clients/' + (selected?.id || selected?.lead_id) + '/buyer-profile', fields);
                              toast.success('Loan details saved');
                              // Refresh to show saved data
                              if (selected) { const r = await agentPortalService.getClientProfile(selected.id); if (r.success) setClientDetail(r.data); }
                            } catch { toast.error('Failed to save'); }
                          }}
                          style={{ marginTop: 16, width: '100%', padding: '12px', fontSize: 14, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer',
                            background: 'linear-gradient(135deg, ' + colors.gold + ', ' + colors.gold2 + ')', color: colors.navy }}
                        >
                          Save Loan Details
                        </button>
                      </div>
                    );
                  })()}
                </div>
              )}

              {activeTab === 'buyer' && !isSponsorPlan && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>
                  {/* Unified flow — always render the buyer profile form,
                      whether or not a row exists in buyer_profiles yet.
                      When there's no row, `bp` defaults to {} so the
                      read-only grid shows "—" for everything and the
                      Edit button is still available. Clicking Edit →
                      typing → Save calls PUT which upserts on the
                      backend (see agents.routes.js buyer-profile PUT),
                      so the first Save both creates and populates the
                      row in one step. No more "Create Buyer Profile"
                      dead-end. */}
                  {(() => {
                    const bp = clientDetail?.buyer_profile || {};
                    const pp = typeof bp.property_preferences === 'string' ? JSON.parse(bp.property_preferences) : bp.property_preferences || {};
                    const lp = typeof bp.location_preferences === 'string' ? JSON.parse(bp.location_preferences) : bp.location_preferences || {};
                    const ls = typeof bp.lifestyle_preferences === 'string' ? JSON.parse(bp.lifestyle_preferences) : bp.lifestyle_preferences || {};
                    const fmtPrice = (v: any) => v ? `$${Number(v).toLocaleString()}` : '—';
                    const fmtDate = (v: any) => v ? new Date(v).toLocaleDateString() : '—';

                    const startEditing = () => {
                      setBuyerEditData({
                        bedrooms: pp.bedrooms || bp.bedrooms_min || '',
                        bathrooms: pp.bathrooms || bp.bathrooms_min || '',
                        budget_min: pp.budget_min || bp.price_min || '',
                        budget_max: pp.budget_max || bp.price_max || '',
                        property_type: pp.property_type || bp.property_type || 'single_family',
                        sqft_min: pp.square_feet_min || '',
                        sqft_max: pp.square_feet_max || '',
                        preferred_cities: Array.isArray(lp.preferred_cities) ? lp.preferred_cities.join(', ') : (lp.preferred_cities || ''),
                        pre_approved: bp.pre_approved ? '1' : '0',
                        pre_approval_amount: bp.pre_approval_amount || '',
                        first_time_buyer: bp.first_time_buyer ? '1' : '0',
                        buying_timeframe: bp.buying_timeframe || '',
                        desired_move_in_date: bp.desired_move_in_date ? bp.desired_move_in_date.substring(0, 10) : '',
                        motivation: bp.motivation || '',
                        work_location: bp.work_location || '',
                        max_commute_time: bp.max_commute_time || '',
                        buyer_stage: bp.buyer_stage || 'researching',
                        must_haves: ls.must_haves?.join(', ') || '',
                        deal_breakers: ls.deal_breakers?.join(', ') || ''
                      });
                      setEditingBuyer(true);
                    };

                    const saveBuyerProfile = async () => {
                      setSavingBuyer(true);
                      try {
                        const d = buyerEditData;
                        await axiosInstance.put(`/agents/clients/${selected.id}/buyer-profile`, {
                          bedrooms_min: parseInt(d.bedrooms) || null,
                          bathrooms_min: parseInt(d.bathrooms) || null,
                          price_min: parseInt(d.budget_min) || null,
                          price_max: parseInt(d.budget_max) || null,
                          property_type: d.property_type || null,
                          sqft_min: parseInt(d.sqft_min) || null,
                          sqft_max: parseInt(d.sqft_max) || null,
                          preferred_cities: d.preferred_cities.split(',').map((c: string) => c.trim()).filter(Boolean),
                          pre_approved: d.pre_approved === '1',
                          pre_approval_amount: parseInt(d.pre_approval_amount) || null,
                          first_time_buyer: d.first_time_buyer === '1',
                          buying_timeframe: d.buying_timeframe || null,
                          desired_move_in_date: d.desired_move_in_date || null,
                          motivation: d.motivation || null,
                          work_location: d.work_location || null,
                          max_commute_time: parseInt(d.max_commute_time) || null,
                          buyer_stage: d.buyer_stage || null,
                          must_haves: d.must_haves.split(',').map((c: string) => c.trim()).filter(Boolean),
                          deal_breakers: d.deal_breakers.split(',').map((c: string) => c.trim()).filter(Boolean)
                        });
                        setAlert({ type: 'success', message: 'Buyer profile updated!' });
                        setEditingBuyer(false);
                        selectClient(selected);
                      } catch (err: any) {
                        setAlert({ type: 'danger', message: err.response?.data?.message || 'Failed to update' });
                      } finally {
                        setSavingBuyer(false);
                      }
                    };

                    const inputStyle = { width: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', fontSize: '13px', outline: 'none' };
                    const labelStyle = { fontSize: '11px', color: '#888', marginBottom: '2px' };

                    return (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 style={{ color: colors.navy, fontWeight: 700, margin: 0 }}>
                            <FontAwesomeIcon icon={faHome} className="me-2" style={{ color: colors.blue }} />Buyer Profile
                          </h6>
                          {!editingBuyer ? (
                            <Button size="sm" variant="outline-dark" onClick={startEditing} style={{ fontSize: '12px' }}>
                              <FontAwesomeIcon icon={faEdit} className="me-1" /> Edit
                            </Button>
                          ) : (
                            <div className="d-flex gap-2">
                              <Button size="sm" onClick={saveBuyerProfile} disabled={savingBuyer}
                                style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, fontSize: '12px' }}>
                                {savingBuyer ? <Spinner size="sm" /> : 'Save'}
                              </Button>
                              <Button size="sm" variant="outline-secondary" onClick={() => setEditingBuyer(false)} style={{ fontSize: '12px' }}>Cancel</Button>
                            </div>
                          )}
                        </div>

                        {editingBuyer ? (
                          <Row className="g-3">
                            <Col md={4}><div style={labelStyle}>Bedrooms</div><input type="number" value={buyerEditData.bedrooms} onChange={e => setBuyerEditData({ ...buyerEditData, bedrooms: e.target.value })} style={inputStyle} /></Col>
                            <Col md={4}><div style={labelStyle}>Bathrooms</div><input type="number" value={buyerEditData.bathrooms} onChange={e => setBuyerEditData({ ...buyerEditData, bathrooms: e.target.value })} style={inputStyle} /></Col>
                            <Col md={4}><div style={labelStyle}>Property Type</div>
                              <select value={buyerEditData.property_type} onChange={e => setBuyerEditData({ ...buyerEditData, property_type: e.target.value })} style={inputStyle}>
                                <option value="">Any</option><option value="single_family">Single Family</option><option value="condo">Condo / Townhouse</option><option value="townhouse">Townhouse</option><option value="multi_family">Multi-Family</option><option value="rental">Rental</option><option value="land">Land</option><option value="commercial">Commercial</option>
                              </select>
                            </Col>
                            <Col md={4}><div style={labelStyle}>Budget Min ($)</div><input type="number" value={buyerEditData.budget_min} onChange={e => setBuyerEditData({ ...buyerEditData, budget_min: e.target.value })} style={inputStyle} /></Col>
                            <Col md={4}><div style={labelStyle}>Budget Max ($)</div><input type="number" value={buyerEditData.budget_max} onChange={e => setBuyerEditData({ ...buyerEditData, budget_max: e.target.value })} style={inputStyle} /></Col>
                            <Col md={4}><div style={labelStyle}>Pre-Approval Amount ($)</div><input type="number" value={buyerEditData.pre_approval_amount} onChange={e => setBuyerEditData({ ...buyerEditData, pre_approval_amount: e.target.value })} style={inputStyle} /></Col>
                            <Col md={4}><div style={labelStyle}>Sq Ft Min</div><input type="number" value={buyerEditData.sqft_min} onChange={e => setBuyerEditData({ ...buyerEditData, sqft_min: e.target.value })} style={inputStyle} /></Col>
                            <Col md={4}><div style={labelStyle}>Sq Ft Max</div><input type="number" value={buyerEditData.sqft_max} onChange={e => setBuyerEditData({ ...buyerEditData, sqft_max: e.target.value })} style={inputStyle} /></Col>
                            <Col md={4}><div style={labelStyle}>Preferred Cities</div><input value={buyerEditData.preferred_cities} onChange={e => setBuyerEditData({ ...buyerEditData, preferred_cities: e.target.value })} placeholder="Tempe, Chandler, Gilbert" style={inputStyle} /></Col>
                            <Col md={4}><div style={labelStyle}>Pre-Approved</div>
                              <select value={buyerEditData.pre_approved} onChange={e => setBuyerEditData({ ...buyerEditData, pre_approved: e.target.value })} style={inputStyle}>
                                <option value="1">Yes</option><option value="0">No</option>
                              </select>
                            </Col>
                            <Col md={4}><div style={labelStyle}>First Time Buyer</div>
                              <select value={buyerEditData.first_time_buyer} onChange={e => setBuyerEditData({ ...buyerEditData, first_time_buyer: e.target.value })} style={inputStyle}>
                                <option value="1">Yes</option><option value="0">No</option>
                              </select>
                            </Col>
                            <Col md={4}><div style={labelStyle}>Buyer Stage</div>
                              <select value={buyerEditData.buyer_stage} onChange={e => setBuyerEditData({ ...buyerEditData, buyer_stage: e.target.value })} style={inputStyle}>
                                <option value="researching">Researching</option><option value="viewing">Viewing</option><option value="offering">Offering</option><option value="under_contract">Under Contract</option><option value="closed">Closed</option><option value="paused">Paused</option><option value="lost">Lost</option>
                              </select>
                            </Col>
                            <Col md={4}><div style={labelStyle}>Buying Timeframe</div>
                              <select value={buyerEditData.buying_timeframe} onChange={e => setBuyerEditData({ ...buyerEditData, buying_timeframe: e.target.value })} style={inputStyle}>
                                <option value="Not specified">Not specified</option><option value="ASAP">ASAP</option><option value="30 days">30 days</option><option value="60 days">60 days</option><option value="0-3 months">0-3 months</option><option value="3-6 months">3-6 months</option><option value="6-12 months">6-12 months</option><option value="12+ months">12+ months</option>
                              </select>
                            </Col>
                            <Col md={4}><div style={labelStyle}>Desired Move-In</div><input type="date" value={buyerEditData.desired_move_in_date} onChange={e => setBuyerEditData({ ...buyerEditData, desired_move_in_date: e.target.value })} style={inputStyle} /></Col>
                            <Col md={4}><div style={labelStyle}>Max Commute (min)</div><input type="number" value={buyerEditData.max_commute_time} onChange={e => setBuyerEditData({ ...buyerEditData, max_commute_time: e.target.value })} style={inputStyle} /></Col>
                            <Col md={4}><div style={labelStyle}>Work Location</div><input value={buyerEditData.work_location} onChange={e => setBuyerEditData({ ...buyerEditData, work_location: e.target.value })} style={inputStyle} /></Col>
                            <Col md={8}><div style={labelStyle}>Motivation</div><input value={buyerEditData.motivation} onChange={e => setBuyerEditData({ ...buyerEditData, motivation: e.target.value })} style={inputStyle} /></Col>
                            <Col md={6}><div style={labelStyle}>Must Haves (comma separated)</div><input value={buyerEditData.must_haves} onChange={e => setBuyerEditData({ ...buyerEditData, must_haves: e.target.value })} placeholder="Pool, 3 car garage, modern kitchen" style={inputStyle} /></Col>
                            <Col md={6}><div style={labelStyle}>Deal Breakers (comma separated)</div><input value={buyerEditData.deal_breakers} onChange={e => setBuyerEditData({ ...buyerEditData, deal_breakers: e.target.value })} placeholder="No HOA, no flood zone, no fixer-upper" style={inputStyle} /></Col>
                          </Row>
                        ) : (
                          <>
                            <Row className="g-3">
                              {([
                                ['Bedrooms', pp.bedrooms ? `${pp.bedrooms}+` : bp.bedrooms_min ? `${bp.bedrooms_min}+` : '—'],
                                ['Bathrooms', pp.bathrooms ? `${pp.bathrooms}+` : bp.bathrooms_min ? `${bp.bathrooms_min}+` : '—'],
                                ['Budget', `${fmtPrice(pp.budget_min || bp.price_min)} — ${fmtPrice(pp.budget_max || bp.price_max)}`],
                                ['Property Type', (pp.property_type || bp.property_type || '—').replace(/_/g, ' ')],
                                ['Square Feet', pp.square_feet_min || pp.square_feet_max ? `${(pp.square_feet_min || 0).toLocaleString()} — ${(pp.square_feet_max || 0).toLocaleString()} sqft` : '—'],
                                ['Preferred Cities', Array.isArray(lp.preferred_cities) ? lp.preferred_cities.join(', ') : (lp.preferred_cities || '—')],
                                ['Pre-Approved', bp.pre_approved ? 'Yes' : bp.pre_approved === 0 ? 'No' : '—'],
                                ['Pre-Approval Amount', fmtPrice(bp.pre_approval_amount)],
                                ['First Time Buyer', bp.first_time_buyer ? 'Yes' : bp.first_time_buyer === 0 ? 'No' : '—'],
                                ['Buying Timeframe', bp.buying_timeframe || '—'],
                                ['Desired Move-In', fmtDate(bp.desired_move_in_date)],
                                ['Motivation', bp.motivation || '—'],
                                ['Work Location', bp.work_location || '—'],
                                ['Max Commute', bp.max_commute_time ? `${bp.max_commute_time} min` : '—'],
                                ['Buyer Stage', bp.buyer_stage || '—'],
                              ] as [string, string][]).map(([label, val]) => (
                                <Col md={4} key={label}>
                                  <div style={{ fontSize: '11px', color: '#888' }}>{label}</div>
                                  <div style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>{val}</div>
                                </Col>
                              ))}
                            </Row>

                            {ls.must_haves && ls.must_haves.length > 0 && (
                              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
                                <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>Must Haves</div>
                                <div className="d-flex flex-wrap gap-1">
                                  {ls.must_haves.map((item: string, i: number) => (
                                    <span key={i} style={{ fontSize: '12px', background: '#e8f5e9', color: colors.green, padding: '3px 10px', borderRadius: '4px', fontWeight: 500 }}>{item}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {ls.deal_breakers && ls.deal_breakers.length > 0 && (
                              <div style={{ marginTop: '12px' }}>
                                <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>Deal Breakers</div>
                                <div className="d-flex flex-wrap gap-1">
                                  {ls.deal_breakers.map((item: string, i: number) => (
                                    <span key={i} style={{ fontSize: '12px', background: '#fde8e8', color: colors.red, padding: '3px 10px', borderRadius: '4px', fontWeight: 500 }}>{item}</span>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee', fontSize: '11px', color: '#aaa' }}>
                              Created: {fmtDate(bp.created_on)} | Updated: {fmtDate(bp.updated_on)}
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {activeTab === 'seller' && !hasMLSAccess && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '40px', border: '1px solid #e9ecef', textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faDollarSign} size="3x" style={{ color: colors.gold, opacity: 0.4, marginBottom: '16px' }} />
                  <h5 style={{ fontWeight: 700, color: colors.navy }}>Connect Your MLS for Access</h5>
                  <p style={{ color: '#666', fontSize: '14px', maxWidth: '450px', margin: '0 auto 16px' }}>
                    Seller profiles and CMA reports use live MLS comparable sales data. Connect your ARMLS credentials to unlock seller intelligence.
                  </p>
                  <Button onClick={() => window.location.href = '/agent-portal/dashboard/settings'} style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, padding: '12px 32px' }}>
                    Connect MLS in Settings
                  </Button>
                </div>
              )}
              {activeTab === 'seller' && hasMLSAccess && (() => {
                const sp = clientDetail?.seller_profile;
                const address = sp ? `${sp.property_address || ''}, ${sp.property_city || ''} ${sp.property_state || ''} ${sp.property_zip || ''}`.trim() : '';
                const cma = sellerCMA;
                const pricing = cma?.pricing_recommendations || {};
                const marketActivity = cma?.timing_recommendations?.market_insights?.market_activity || {};
                const estValue = Number(pricing.estimated_value || pricing.final_estimated_value || cma?.predicted_value || sp?.estimated_value || 0);
                const rawConf = Number(pricing.confidence_score || cma?.confidence_score || 0);
                const confidence = rawConf > 0 && rawConf <= 1 ? Math.round(rawConf * 100) : Math.round(rawConf);
                const priceRange = pricing.price_range || {};
                const compCount = Number(marketActivity.recent_sales_count || cma?.cmaData?.total_found || 0);
                const cmaDate = cma?.created_on || cma?.createdAt;
                const cmaAge = cmaDate ? Math.floor((Date.now() - new Date(cmaDate).getTime()) / (1000 * 60 * 60 * 24)) : null;
                const avgDom = Number(marketActivity.avg_days_on_market || 0);
                const comps = cma?.full_analysis?.comparable_properties?.comparables || cma?.cmaData?.comparable_properties?.comparables || cma?.full_analysis?.comparable_properties || cma?.cmaData?.comparable_properties || [];
                const trend = avgDom < 30 ? 'hot' : avgDom < 60 ? 'stable' : 'cooling';

                const baseUrl = window.location.origin; // stay on the agent's current origin so the auth session carries (forcing aisearchportal.com caused a cross-domain login bounce)

                const statCard = (label: string, value: string, sub?: string) => (
                  <Col xs={6} md={3}>
                    <div style={{ textAlign: 'center', padding: '16px 8px', background: '#f8f9fa', borderRadius: '10px', border: '1px solid #e9ecef' }}>
                      <div style={{ fontSize: '22px', fontWeight: 700, color: colors.navy }}>{value}</div>
                      <div style={{ fontSize: '11px', color: '#888', fontWeight: 600 }}>{label}</div>
                      {sub && <div style={{ fontSize: '10px', color: '#aaa' }}>{sub}</div>}
                    </div>
                  </Col>
                );

                return (
                  <div>
                    {/* Multiple Properties — show cards for additional properties */}
                    {(clientDetail?.all_seller_profiles || []).length > 1 && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: colors.navy, marginBottom: '8px' }}>
                          {clientDetail.all_seller_profiles.length} Properties on File — Select to view
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {clientDetail.all_seller_profiles.map((profile: any, idx: number) => {
                            const isActive = sp?.id === profile.id;
                            const addr = `${profile.property_address || ''}, ${profile.property_city || ''} ${profile.property_zip || ''}`.trim();
                            return (
                              <div key={profile.id}
                                onClick={async () => {
                                  // Switch the active seller profile
                                  setClientDetail((prev: any) => ({ ...prev, seller_profile: profile }));
                                  setSellerCMA(null); // Reset CMA for new property
                                }}
                                style={{
                                  flex: '1', minWidth: '200px', padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                                  background: isActive ? colors.navy : 'white',
                                  color: isActive ? 'white' : colors.navy,
                                  border: isActive ? `2px solid ${colors.gold}` : '1px solid #e9ecef',
                                  transition: 'all 0.15s'
                                }}
                              >
                                <div style={{ fontSize: '13px', fontWeight: 700 }}>{addr}</div>
                                <div style={{ fontSize: '11px', opacity: 0.7 }}>
                                  {(profile.property_type || '').replace(/_/g, ' ')}
                                  {profile.bedrooms ? ` · ${profile.bedrooms}bd/${profile.bathrooms || '?'}ba` : ''}
                                  {profile.square_footage ? ` · ${Number(profile.square_footage).toLocaleString()} sqft` : ''}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {sp ? (
                      <>
                        {/* Address Bar */}
                        <div style={{ background: 'white', borderRadius: '12px', padding: '16px 20px', border: '1px solid #e9ecef', marginBottom: '12px' }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 style={{ color: colors.navy, fontWeight: 700, margin: 0 }}>
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" style={{ color: colors.gold }} />
                                {address || 'No address on file'}
                              </h6>
                              <small className="text-muted">
                                {sp.property_type ? sp.property_type.replace(/_/g, ' ') : ''}
                                {sp.bedrooms ? ` | ${sp.bedrooms}BR/${sp.bathrooms || '?'}BA` : ''}
                                {sp.square_footage ? ` | ${Number(sp.square_footage).toLocaleString()} sqft` : ''}
                                {sp.year_built ? ` | Built ${sp.year_built}` : ''}
                              </small>
                            </div>
                            <Button size="sm"
                              onClick={handleRunCMA}
                              disabled={runningCMA || !address}
                              style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                            >
                              {runningCMA ? <><Spinner size="sm" className="me-1" /> Running CMA...</> : <><FontAwesomeIcon icon={faChartLine} className="me-1" /> Run CMA</>}
                            </Button>
                          </div>
                        </div>

                        {/* CMA Stats Row */}
                        {(estValue > 0 || cma) && (
                          <Row className="g-2 mb-3">
                            {statCard('Estimated Value', estValue > 0 ? `$${Math.round(estValue).toLocaleString()}` : '—')}
                            {statCard('Based On', compCount > 0 ? `${compCount} Comparable Sales` : '—')}
                            {statCard('Analysis', compCount >= 10 ? 'Strong Data' : compCount >= 5 ? 'Good Data' : compCount > 0 ? 'Limited Data' : '—')}
                            {statCard('CMA Age', cmaAge !== null ? (cmaAge === 0 ? 'Today' : `${cmaAge}d ago`) : 'Never', cmaAge && cmaAge > 30 ? 'Update recommended' : undefined)}
                          </Row>
                        )}

                        {/* Seller Details + Market Snapshot */}
                        <Row className="g-3 mb-3">
                          <Col md={6}>
                            <div style={{ background: 'white', borderRadius: '10px', padding: '16px', border: '1px solid #e9ecef', height: '100%' }}>
                              <h6 style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '12px' }}>
                                <FontAwesomeIcon icon={faUser} className="me-2" style={{ color: colors.gold }} />Seller Details
                              </h6>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                                <div><span style={{ color: '#888', fontSize: '11px', display: 'block' }}>Stage</span><Badge bg={sp.seller_stage === 'ready_to_list' ? 'success' : sp.seller_stage === 'actively_selling' ? 'primary' : 'secondary'}>{(sp.seller_stage || 'unknown').replace(/_/g, ' ')}</Badge></div>
                                <div><span style={{ color: '#888', fontSize: '11px', display: 'block' }}>Motivation</span><Badge bg={sp.motivation_level === 'high' ? 'danger' : sp.motivation_level === 'medium' ? 'warning' : 'secondary'}>{sp.motivation_level || '—'}</Badge></div>
                                <div><span style={{ color: '#888', fontSize: '11px', display: 'block' }}>Timeframe</span><span style={{ fontWeight: 600 }}>{(sp.selling_timeframe || '—').replace(/_/g, ' ')}</span></div>
                                <div><span style={{ color: '#888', fontSize: '11px', display: 'block' }}>Condition</span><span style={{ fontWeight: 600 }}>{sp.property_condition || '—'}</span></div>
                                <div><span style={{ color: '#888', fontSize: '11px', display: 'block' }}>Desired Price</span><span style={{ fontWeight: 600, color: colors.navy }}>{sp.desired_price ? `$${Number(sp.desired_price).toLocaleString()}` : '—'}</span></div>
                                <div><span style={{ color: '#888', fontSize: '11px', display: 'block' }}>Minimum Price</span><span style={{ fontWeight: 600 }}>{sp.minimum_price ? `$${Number(sp.minimum_price).toLocaleString()}` : '—'}</span></div>
                                <div><span style={{ color: '#888', fontSize: '11px', display: 'block' }}>Pricing Strategy</span><span style={{ fontWeight: 600 }}>{(sp.pricing_strategy || '—').replace(/_/g, ' ')}</span></div>
                                <div><span style={{ color: '#888', fontSize: '11px', display: 'block' }}>Also Buying?</span><span style={{ fontWeight: 600 }}>{sp.is_also_buyer ? 'Yes' : 'No'}</span></div>
                              </div>

                              {/* Inline Edit Form — Full Seller Profile */}
                              {editingSellerProfile && (() => {
                                const saveField = async (fields: any) => {
                                  try { await axiosInstance.put(`/sellers/${selected?.id}/profile`, fields); } catch {}
                                };
                                const refreshProfile = async () => {
                                  const r = await agentPortalService.getClientProfile(selected.id);
                                  if (r.success) setClientDetail(r.data);
                                };
                                const inputStyle: any = { width: '100%', padding: '6px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', outline: 'none' };
                                const labelStyle: any = { fontSize: '10px', color: '#888', fontWeight: 600, display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.3px' };
                                const sectionStyle: any = { fontSize: '11px', fontWeight: 700, color: colors.navy, marginBottom: '8px', marginTop: '12px', borderBottom: '1px solid #e9ecef', paddingBottom: '4px' };

                                // Initialize edit data from seller profile (only once)
                                if (!spEdit.property_address && sp.property_address) {
                                  setSpEdit({
                                    property_address: sp.property_address || '', property_city: sp.property_city || '', property_state: sp.property_state || 'AZ', property_zip: sp.property_zip || '',
                                    bedrooms: sp.bedrooms || '', bathrooms: sp.bathrooms || '', square_footage: sp.square_footage || '', lot_size_sqft: sp.lot_size_sqft || '',
                                    year_built: sp.year_built || '', garage_spaces: sp.garage_spaces || '',
                                    selling_timeframe: sp.selling_timeframe || '', reason_for_selling: sp.reason_for_selling || '',
                                    estimated_value: sp.estimated_value || '', desired_price: sp.desired_price || '', minimum_price: sp.minimum_price || '', mortgage_balance: sp.mortgage_balance || '',
                                    recent_improvements: sp.recent_improvements || '', improvements_description: sp.improvements_description || '',
                                  });
                                }
                                const u = (field: string, val: string) => setSpEdit((prev: any) => ({ ...prev, [field]: val }));

                                return (
                                  <div style={{ marginTop: '12px', padding: '14px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>

                                    <div style={sectionStyle}>Property Address</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 0.5fr 1fr', gap: '6px' }}>
                                      <div><span style={labelStyle}>Street Address</span><input value={spEdit.property_address} onChange={e => u('property_address', e.target.value)} onBlur={() => saveField({ property_address: spEdit.property_address })} style={inputStyle} /></div>
                                      <div><span style={labelStyle}>City</span><input value={spEdit.property_city} onChange={e => u('property_city', e.target.value)} onBlur={() => saveField({ property_city: spEdit.property_city })} style={inputStyle} /></div>
                                      <div><span style={labelStyle}>State</span><input value={spEdit.property_state} onChange={e => u('property_state', e.target.value)} onBlur={() => saveField({ property_state: spEdit.property_state })} style={inputStyle} /></div>
                                      <div><span style={labelStyle}>Zip</span><input value={spEdit.property_zip} onChange={e => u('property_zip', e.target.value)} onBlur={() => saveField({ property_zip: spEdit.property_zip })} style={inputStyle} /></div>
                                    </div>

                                    <div style={sectionStyle}>Property Details</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: '6px' }}>
                                      <div><span style={labelStyle}>Beds</span><input type="number" value={spEdit.bedrooms} onChange={e => u('bedrooms', e.target.value)} onBlur={() => saveField({ bedrooms: Number(spEdit.bedrooms) || null })} style={inputStyle} /></div>
                                      <div><span style={labelStyle}>Baths</span><input type="number" step="0.5" value={spEdit.bathrooms} onChange={e => u('bathrooms', e.target.value)} onBlur={() => saveField({ bathrooms: Number(spEdit.bathrooms) || null })} style={inputStyle} /></div>
                                      <div><span style={labelStyle}>Sq Ft</span><input type="number" value={spEdit.square_footage} onChange={e => u('square_footage', e.target.value)} onBlur={() => saveField({ square_footage: Number(spEdit.square_footage) || null })} style={inputStyle} /></div>
                                      <div><span style={labelStyle}>Lot Sq Ft</span><input type="number" value={spEdit.lot_size_sqft} onChange={e => u('lot_size_sqft', e.target.value)} onBlur={() => saveField({ lot_size_sqft: Number(spEdit.lot_size_sqft) || null })} style={inputStyle} /></div>
                                      <div><span style={labelStyle}>Year Built</span><input type="number" value={spEdit.year_built} onChange={e => u('year_built', e.target.value)} onBlur={() => saveField({ year_built: Number(spEdit.year_built) || null })} style={inputStyle} /></div>
                                      <div><span style={labelStyle}>Garage</span><input type="number" value={spEdit.garage_spaces} onChange={e => u('garage_spaces', e.target.value)} onBlur={() => saveField({ garage_spaces: Number(spEdit.garage_spaces) || null })} style={inputStyle} /></div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginTop: '6px' }}>
                                      <div><span style={labelStyle}>Property Type</span><select defaultValue={sp.property_type || 'single_family'} onChange={async e => { await saveField({ property_type: e.target.value }); refreshProfile(); }} style={inputStyle}><option value="single_family">Single Family</option><option value="condo">Condo</option><option value="townhouse">Townhouse</option><option value="multi_family">Multi-Family</option><option value="land">Land</option><option value="manufactured">Manufactured</option></select></div>
                                      <div><span style={labelStyle}>Pool</span><select defaultValue={sp.pool ? '1' : '0'} onChange={async e => { await saveField({ pool: e.target.value === '1' }); refreshProfile(); }} style={inputStyle}><option value="0">No</option><option value="1">Yes</option></select></div>
                                      <div><span style={labelStyle}>Condition</span><select defaultValue={sp.property_condition || ''} onChange={async e => { await saveField({ property_condition: e.target.value }); refreshProfile(); }} style={inputStyle}><option value="">Not assessed</option><option value="excellent">Excellent</option><option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option><option value="needs_renovation">Needs Renovation</option></select></div>
                                    </div>

                                    <div style={sectionStyle}>Seller Motivation</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '6px' }}>
                                      <div><span style={labelStyle}>Motivation</span><select defaultValue={sp.motivation_level || 'medium'} onChange={async e => { await saveField({ motivation_level: e.target.value }); refreshProfile(); }} style={inputStyle}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option></select></div>
                                      <div><span style={labelStyle}>Stage</span><select defaultValue={sp.seller_stage || 'considering'} onChange={async e => { await saveField({ seller_stage: e.target.value }); refreshProfile(); }} style={inputStyle}><option value="considering">Considering</option><option value="researching">Researching</option><option value="preparing">Preparing</option><option value="ready_to_list">Ready to List</option><option value="listing">Listing</option><option value="showing">Showing</option><option value="reviewing_offers">Reviewing Offers</option><option value="under_contract">Under Contract</option><option value="closed">Closed</option></select></div>
                                      <div><span style={labelStyle}>Timeframe</span><input value={spEdit.selling_timeframe} onChange={e => u('selling_timeframe', e.target.value)} onBlur={() => saveField({ selling_timeframe: spEdit.selling_timeframe })} placeholder="3-6 months" style={inputStyle} /></div>
                                      <div><span style={labelStyle}>Also Buying?</span><select defaultValue={sp.is_also_buyer ? '1' : '0'} onChange={async e => { await saveField({ is_also_buyer: e.target.value === '1' }); refreshProfile(); }} style={inputStyle}><option value="0">No</option><option value="1">Yes</option></select></div>
                                    </div>
                                    <div style={{ marginTop: '6px' }}>
                                      <span style={labelStyle}>Reason for Selling</span>
                                      <input value={spEdit.reason_for_selling} onChange={e => u('reason_for_selling', e.target.value)} onBlur={() => saveField({ reason_for_selling: spEdit.reason_for_selling })} placeholder="Relocating, downsizing, upgrading..." style={inputStyle} />
                                    </div>

                                    <div style={sectionStyle}>Pricing</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '6px' }}>
                                      <div><span style={labelStyle}>Estimated Value</span><input value={spEdit.estimated_value} onChange={e => u('estimated_value', e.target.value)} onBlur={() => saveField({ estimated_value: Number(spEdit.estimated_value) || null })} placeholder="$450,000" style={inputStyle} /></div>
                                      <div><span style={labelStyle}>Desired Price</span><input value={spEdit.desired_price} onChange={e => u('desired_price', e.target.value)} onBlur={() => saveField({ desired_price: Number(spEdit.desired_price) || null })} placeholder="$460,000" style={inputStyle} /></div>
                                      <div><span style={labelStyle}>Minimum Price</span><input value={spEdit.minimum_price} onChange={e => u('minimum_price', e.target.value)} onBlur={() => saveField({ minimum_price: Number(spEdit.minimum_price) || null })} placeholder="$430,000" style={inputStyle} /></div>
                                      <div><span style={labelStyle}>Mortgage Balance</span><input value={spEdit.mortgage_balance} onChange={e => u('mortgage_balance', e.target.value)} onBlur={() => saveField({ mortgage_balance: Number(spEdit.mortgage_balance) || null })} placeholder="$280,000" style={inputStyle} /></div>
                                    </div>
                                    <div style={{ marginTop: '6px' }}>
                                      <span style={labelStyle}>Pricing Strategy</span>
                                      <select defaultValue={sp.pricing_strategy || ''} onChange={async e => { await saveField({ pricing_strategy: e.target.value }); refreshProfile(); }} style={inputStyle}><option value="">Not set</option><option value="aggressive">Aggressive (below market)</option><option value="market_value">At Market Value</option><option value="aspirational">Aspirational (above market)</option><option value="auction">Auction</option></select>
                                    </div>

                                    <div style={sectionStyle}>Improvements</div>
                                    <div>
                                      <span style={labelStyle}>Recent Improvements</span>
                                      <textarea value={spEdit.improvements_description} onChange={e => u('improvements_description', e.target.value)} onBlur={() => saveField({ improvements_description: spEdit.improvements_description })} placeholder="New roof 2023, kitchen remodel, new HVAC..." rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                                    </div>

                                    <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Button size="sm"
                                        onClick={async () => {
                                          try {
                                            await saveField({
                                              property_address: spEdit.property_address, property_city: spEdit.property_city,
                                              property_state: spEdit.property_state, property_zip: spEdit.property_zip,
                                              bedrooms: Number(spEdit.bedrooms) || null, bathrooms: Number(spEdit.bathrooms) || null,
                                              square_footage: Number(spEdit.square_footage) || null, lot_size_sqft: Number(spEdit.lot_size_sqft) || null,
                                              year_built: Number(spEdit.year_built) || null, garage_spaces: Number(spEdit.garage_spaces) || null,
                                              selling_timeframe: spEdit.selling_timeframe, reason_for_selling: spEdit.reason_for_selling,
                                              estimated_value: Number(spEdit.estimated_value) || null, desired_price: Number(spEdit.desired_price) || null,
                                              minimum_price: Number(spEdit.minimum_price) || null, mortgage_balance: Number(spEdit.mortgage_balance) || null,
                                              improvements_description: spEdit.improvements_description,
                                            });
                                            await refreshProfile();
                                            setAlert({ type: 'success', message: 'Seller profile saved!' });
                                            setEditingSellerProfile(false);
                                          } catch { setAlert({ type: 'danger', message: 'Failed to save' }); }
                                        }}
                                        style={{ background: colors.gold, border: 'none', fontWeight: 700, padding: '8px 24px' }}
                                      >
                                        Save Changes
                                      </Button>
                                      <span style={{ fontSize: '10px', color: '#888' }}>Dropdowns save immediately</span>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div style={{ background: 'white', borderRadius: '10px', padding: '16px', border: '1px solid #e9ecef', height: '100%' }}>
                              <h6 style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '12px' }}>
                                <FontAwesomeIcon icon={faChartLine} className="me-2" style={{ color: colors.blue }} />Market Snapshot
                              </h6>
                              {cma ? (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                                  <div><span style={{ color: '#888', fontSize: '11px', display: 'block' }}>Price Range</span><span style={{ fontWeight: 600 }}>{priceRange.low && priceRange.high ? `$${Number(priceRange.low).toLocaleString()} — $${Number(priceRange.high).toLocaleString()}` : '—'}</span></div>
                                  <div><span style={{ color: '#888', fontSize: '11px', display: 'block' }}>Avg Days on Market</span><span style={{ fontWeight: 600 }}>{avgDom > 0 ? avgDom : '—'}</span></div>
                                  <div><span style={{ color: '#888', fontSize: '11px', display: 'block' }}>Market Trend</span><Badge bg={trend === 'hot' ? 'danger' : trend === 'cooling' ? 'warning' : 'success'}>{trend}</Badge></div>
                                  <div><span style={{ color: '#888', fontSize: '11px', display: 'block' }}>Recent Sales (6mo)</span><span style={{ fontWeight: 600 }}>{compCount > 0 ? compCount : '—'}</span></div>
                                </div>
                              ) : (
                                <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
                                  <FontAwesomeIcon icon={faChartLine} size="2x" style={{ opacity: 0.3, marginBottom: '8px' }} />
                                  <p style={{ fontSize: '13px', margin: 0 }}>Run a CMA to see market data</p>
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>

                        {/* Action Buttons */}
                        <div style={{ background: 'white', borderRadius: '10px', padding: '16px', border: '1px solid #e9ecef' }}>
                          <div className="d-flex gap-2 flex-wrap">
                            {(cma?.id || cma?.report_id) && (
                              <>
                                <Button size="sm" variant="outline-dark"
                                  onClick={() => window.open(`${baseUrl}/smart-cma-workflow?reportId=${cma.report_id || cma.id}&step=2`, '_blank')}
                                >
                                  <FontAwesomeIcon icon={faEdit} className="me-1" /> Review / Edit Report
                                </Button>
                                <Button size="sm" variant="outline-primary"
                                  onClick={() => window.open(`${baseUrl}/cma-report/${cma.report_id || cma.id}?agentId=${selected?.id}`, '_blank')}
                                >
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" /> Seller View
                                </Button>
                                <Button size="sm" variant="outline-success"
                                  onClick={() => {
                                    const link = `${baseUrl}/cma-report/${cma.report_id || cma.id}?agentId=${selected?.id}`;
                                    navigator.clipboard.writeText(link);
                                    setAlert({ type: 'success', message: 'Seller report link copied to clipboard!' });
                                  }}
                                >
                                  <FontAwesomeIcon icon={faCopy} className="me-1" /> Share with Seller
                                </Button>
                              </>
                            )}
                            <Button size="sm"
                              onClick={handleGenerateSellerUpdate}
                              disabled={loadingDraft}
                              style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                            >
                              {loadingDraft ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faEnvelope} className="me-1" /> Draft Market Update</>}
                            </Button>
                            <Button size="sm" variant="outline-dark"
                              onClick={() => setEditingSellerProfile(!editingSellerProfile)}
                            >
                              <FontAwesomeIcon icon={faEdit} className="me-1" /> {editingSellerProfile ? 'Cancel Edit' : 'Edit Profile'}
                            </Button>
                          </div>
                        </div>

                        {/* AI Narration — from seller CMA report */}
                        {(() => {
                          const pd = typeof cma?.predictionData === 'string' ? JSON.parse(cma.predictionData) : cma?.predictionData;
                          const narr = pd?.narrative;
                          if (narr?.full || narr?.summary) return (
                            <div style={{ background: 'white', borderRadius: '10px', padding: '16px', border: '1px solid #e9ecef', marginTop: '12px' }}>
                              <h6 style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '12px' }}>
                                <FontAwesomeIcon icon={faBrain} className="me-2" style={{ color: colors.gold }} />
                                AI Market Analysis
                              </h6>
                              <p style={{ fontSize: '14px', color: '#333', lineHeight: 1.7, margin: 0 }}>
                                {narr.full || narr.summary}
                              </p>
                              {narr.key_factors && narr.key_factors.length > 0 && (
                                <div style={{ marginTop: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {narr.key_factors.map((f: string, idx: number) => (
                                    <span key={idx} style={{ fontSize: '11px', background: '#f0f4f8', color: colors.navy, padding: '4px 10px', borderRadius: '12px', border: '1px solid #e0e6ed' }}>
                                      {f}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {narr.fh_passed && (
                                <div style={{ marginTop: '8px', fontSize: '10px', color: '#27ae60' }}>
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                  Fair Housing compliant
                                </div>
                              )}
                            </div>
                          );
                          return null;
                        })()}

                        {/* CMA Report Details — shows when comps exist */}
                        {cma && comps.length > 0 && (
                          <div style={{ background: 'white', borderRadius: '10px', padding: '16px', border: '1px solid #e9ecef', marginTop: '12px' }}>
                            <h6 style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '12px' }}>
                              <FontAwesomeIcon icon={faHome} className="me-2" style={{ color: colors.gold }} />
                              Comparable Sales
                              <small className="text-muted ms-2">({comps.length} comps)</small>
                            </h6>
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              {comps.slice(0, 15).map((comp: any, i: number) => (
                                  <div key={i} style={{
                                    padding: '10px 12px', marginBottom: '6px', borderRadius: '8px',
                                    background: i % 2 === 0 ? '#f8f9fa' : '#ffffff',
                                    border: '1px solid #eee', fontSize: '13px'
                                  }}>
                                    <div className="d-flex justify-content-between align-items-start">
                                      <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, color: '#333' }}>{comp.address || `Comparable ${i + 1}`}</div>
                                        <div style={{ color: '#888', fontSize: '12px', marginTop: '2px' }}>
                                          {comp.bedrooms || '?'}BR/{comp.bathrooms || '?'}BA
                                          {comp.living_area ? ` | ${Number(comp.living_area).toLocaleString()} sqft` : ''}
                                          {comp.price_per_sqft ? ` | $${Math.round(comp.price_per_sqft)}/sqft` : ''}
                                          {comp.close_date ? ` | Sold ${new Date(comp.close_date).toLocaleDateString()}` : ''}
                                          {comp.distance_miles ? ` | ${Number(comp.distance_miles).toFixed(1)} mi` : ''}
                                          {comp.pool ? ' | Pool' : ''}
                                        </div>
                                      </div>
                                      <div style={{ textAlign: 'right', minWidth: '110px' }}>
                                        <div style={{ fontWeight: 700, color: colors.navy, fontSize: '15px' }}>
                                          {comp.close_price ? `$${Number(comp.close_price).toLocaleString()}` : '—'}
                                        </div>
                                        <div className="d-flex gap-1 justify-content-end mt-1">
                                          {comp.classification && (
                                            <Badge bg={comp.classification === 'renovated' ? 'success' : 'secondary'} style={{ fontSize: '9px' }}>
                                              {comp.classification}
                                            </Badge>
                                          )}
                                          {comp.days_on_market && (
                                            <Badge bg="light" text="dark" style={{ fontSize: '9px', border: '1px solid #ddd' }}>
                                              {comp.days_on_market} DOM
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* CMA Report History */}
                        {allCMAReports.length > 1 && (
                          <div style={{ background: 'white', borderRadius: '10px', padding: '16px', border: '1px solid #e9ecef', marginTop: '12px' }}>
                            <h6 style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '12px' }}>
                              <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: colors.gold }} />
                              CMA Report History ({allCMAReports.length})
                            </h6>
                            {allCMAReports.map((report: any, i: number) => {
                              const reportDate = new Date(report.created_on || report.createdAt);
                              const reportValue = Number(report.pricing_recommendations?.estimated_value || report.predicted_value || 0);
                              const reportConf = Number(report.pricing_recommendations?.confidence_score || report.confidence_score || 0);
                              const isSelected = report.id === sellerCMA?.id;

                              return (
                                <div key={report.id || i}
                                  onClick={() => setSellerCMA(report)}
                                  style={{
                                    padding: '10px 14px', marginBottom: '6px', borderRadius: '8px', cursor: 'pointer',
                                    background: isSelected ? '#fff8e1' : '#f8f9fa',
                                    border: `1px solid ${isSelected ? '#ffe082' : '#eee'}`,
                                    transition: 'all 0.15s'
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <div style={{ fontWeight: isSelected ? 700 : 500, color: '#333', fontSize: '13px' }}>
                                        {isSelected && <FontAwesomeIcon icon={faCheckCircle} className="me-1" style={{ color: colors.gold, fontSize: '11px' }} />}
                                        {reportDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        {i === 0 && <Badge bg="info" className="ms-2" style={{ fontSize: '9px' }}>Latest</Badge>}
                                      </div>
                                      <div style={{ fontSize: '11px', color: '#888' }}>
                                        {report.source || 'Smart CMA'}
                                        {reportConf > 0 && ` | ${Math.round(reportConf * 100)}% confidence`}
                                      </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <div style={{ fontWeight: 700, color: colors.navy, fontSize: '14px' }}>
                                        {reportValue > 0 ? `$${Math.round(reportValue).toLocaleString()}` : '—'}
                                      </div>
                                      <div className="d-flex gap-1 justify-content-end">
                                        <Button size="sm" variant="link" style={{ fontSize: '11px', padding: 0 }}
                                          onClick={(e) => { e.stopPropagation(); window.open(`${baseUrl}/smart-cma-workflow?reportId=${report.report_id || report.id}&step=2`, '_blank'); }}
                                        >Review</Button>
                                        <Button size="sm" variant="link" style={{ fontSize: '11px', padding: 0 }}
                                          onClick={(e) => { e.stopPropagation(); window.open(`${baseUrl}/cma-report/${report.report_id || report.id}?agentId=${selected?.id}`, '_blank'); }}
                                        >Share</Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e9ecef' }}>
                        {!showSellerForm ? (
                          <div style={{ textAlign: 'center', padding: '16px' }}>
                            <FontAwesomeIcon icon={faDollarSign} size="3x" style={{ color: colors.gold, opacity: 0.3, marginBottom: '16px' }} />
                            <h6 style={{ fontWeight: 600, color: '#555' }}>No seller profile for {getClientName(selected)}</h6>
                            <p className="text-muted" style={{ fontSize: '13px' }}>
                              Create a seller profile to track their property, run CMA reports, and automate market updates.
                            </p>
                            <Button size="sm" style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                              onClick={() => setShowSellerForm(true)}
                            >
                              <FontAwesomeIcon icon={faEdit} className="me-1" /> Create Seller Profile
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <h6 style={{ fontWeight: 700, color: colors.navy, marginBottom: '16px' }}>
                              <FontAwesomeIcon icon={faDollarSign} className="me-2" style={{ color: colors.gold }} />
                              New Seller Profile — {getClientName(selected)}
                            </h6>
                            <Row className="g-3">
                              <Col md={12}>
                                <Form.Group>
                                  <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Property Address *</Form.Label>
                                  <Form.Control size="sm" placeholder="123 E Main St" value={sellerFormData.property_address}
                                    onChange={e => setSellerFormData(p => ({ ...p, property_address: e.target.value }))} />
                                </Form.Group>
                              </Col>
                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>City</Form.Label>
                                  <Form.Control size="sm" placeholder="Phoenix" value={sellerFormData.property_city}
                                    onChange={e => setSellerFormData(p => ({ ...p, property_city: e.target.value }))} />
                                </Form.Group>
                              </Col>
                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>State</Form.Label>
                                  <Form.Control size="sm" value={sellerFormData.property_state}
                                    onChange={e => setSellerFormData(p => ({ ...p, property_state: e.target.value }))} />
                                </Form.Group>
                              </Col>
                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Zip</Form.Label>
                                  <Form.Control size="sm" placeholder="85281" value={sellerFormData.property_zip}
                                    onChange={e => setSellerFormData(p => ({ ...p, property_zip: e.target.value }))} />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Selling Timeframe</Form.Label>
                                  <Form.Select size="sm" value={sellerFormData.selling_timeframe}
                                    onChange={e => setSellerFormData(p => ({ ...p, selling_timeframe: e.target.value }))}>
                                    <option value="">Select...</option>
                                    <option value="ASAP">ASAP</option>
                                    <option value="1-3 months">1-3 months</option>
                                    <option value="3-6 months">3-6 months</option>
                                    <option value="6-12 months">6-12 months</option>
                                    <option value="Just exploring">Just exploring</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Reason for Selling</Form.Label>
                                  <Form.Select size="sm" value={sellerFormData.reason_for_selling}
                                    onChange={e => setSellerFormData(p => ({ ...p, reason_for_selling: e.target.value }))}>
                                    <option value="">Select...</option>
                                    <option value="Relocating">Relocating</option>
                                    <option value="Downsizing">Downsizing</option>
                                    <option value="Upgrading">Upgrading</option>
                                    <option value="Estate/Probate">Estate/Probate</option>
                                    <option value="Divorce">Divorce</option>
                                    <option value="Financial">Financial</option>
                                    <option value="Investment">Investment property</option>
                                    <option value="Other">Other</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Estimated Value</Form.Label>
                                  <Form.Control size="sm" type="number" placeholder="450000" value={sellerFormData.estimated_value}
                                    onChange={e => setSellerFormData(p => ({ ...p, estimated_value: e.target.value }))} />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group>
                                  <Form.Label style={{ fontSize: '12px', fontWeight: 600 }}>Desired Price</Form.Label>
                                  <Form.Control size="sm" type="number" placeholder="475000" value={sellerFormData.desired_price}
                                    onChange={e => setSellerFormData(p => ({ ...p, desired_price: e.target.value }))} />
                                </Form.Group>
                              </Col>
                            </Row>
                            <div className="d-flex gap-2 mt-3">
                              <Button size="sm" disabled={savingSellerProfile || !sellerFormData.property_address}
                                style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                                onClick={handleCreateSellerProfile}>
                                {savingSellerProfile ? <><Spinner size="sm" className="me-1" />Saving...</> : <><FontAwesomeIcon icon={faCheck} className="me-1" /> Save Seller Profile</>}
                              </Button>
                              <Button size="sm" variant="outline-secondary" onClick={() => setShowSellerForm(false)}>Cancel</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Offer Analyzer Tab */}
              {activeTab === 'offer-analyzer' && isSponsorPlan && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '40px', border: '1px solid #e9ecef', textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faChartLine} size="3x" style={{ color: colors.gold, opacity: 0.4, marginBottom: '16px' }} />
                  <h5 style={{ fontWeight: 700, color: colors.navy }}>MLS Authorization Required</h5>
                  <p style={{ color: '#666', fontSize: '14px', maxWidth: '450px', margin: '0 auto 12px' }}>
                    The Offer Analyzer uses live MLS comparable sales data which requires an active agent subscription with broker MLS authorization per ARMLS Content License Agreement.
                  </p>
                  <p style={{ color: '#888', fontSize: '12px' }}>Your sponsored agents will have full access to the Offer Analyzer when they connect their MLS account.</p>
                </div>
              )}
              {activeTab === 'offer-analyzer' && !isSponsorPlan && !hasMLSAccess && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '40px', border: '1px solid #e9ecef', textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faChartLine} size="3x" style={{ color: colors.gold, opacity: 0.4, marginBottom: '16px' }} />
                  <h5 style={{ fontWeight: 700, color: colors.navy }}>Connect Your MLS for Access</h5>
                  <p style={{ color: '#666', fontSize: '14px', maxWidth: '450px', margin: '0 auto 16px' }}>
                    The Offer Analyzer uses live MLS comparable sales data. Connect your ARMLS credentials to unlock comp analysis, value predictions, and offer ranges.
                  </p>
                  <Button onClick={() => window.location.href = '/agent-portal/dashboard/settings'} style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, padding: '12px 32px' }}>
                    Connect MLS in Settings
                  </Button>
                </div>
              )}
              {activeTab === 'offer-analyzer' && !isSponsorPlan && hasMLSAccess && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e9ecef' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <div>
                      <h5 style={{ color: colors.navy, fontWeight: 700, marginBottom: '4px' }}>
                        <FontAwesomeIcon icon={faChartLine} className="me-2" style={{ color: '#3498db' }} />
                        {(selected as any)?._cmaMode === 'selling' ? 'Listing Analyzer' : 'Offer Analyzer'}
                      </h5>
                      <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>
                        {(selected as any)?._cmaMode === 'selling'
                          ? 'Analyze comps and get a recommended listing price for your seller.'
                          : 'Enter an MLS ID or property address to analyze comps, predict value, and get an offer range.'}
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f0f2f5', borderRadius: '8px', padding: '4px' }}>
                      <button
                        onClick={() => { const s = { ...selected } as any; s._cmaMode = 'buying'; setSelected(s); }}
                        style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 700, borderRadius: '6px', border: 'none', cursor: 'pointer',
                          background: (selected as any)?._cmaMode !== 'selling' ? colors.navy : 'transparent',
                          color: (selected as any)?._cmaMode !== 'selling' ? 'white' : colors.navy }}>
                        Buying
                      </button>
                      <button
                        onClick={() => { const s = { ...selected } as any; s._cmaMode = 'selling'; setSelected(s); }}
                        style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 700, borderRadius: '6px', border: 'none', cursor: 'pointer',
                          background: (selected as any)?._cmaMode === 'selling' ? colors.gold : 'transparent',
                          color: (selected as any)?._cmaMode === 'selling' ? 'white' : colors.navy }}>
                        Selling
                      </button>
                    </div>
                  </div>

                  {/* MLS ID + Address inputs */}
                  <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr auto', gap: '10px', marginBottom: '20px', position: 'relative' }}>
                    <input
                      placeholder="MLS# (optional)"
                      value={buyerCMAMlsId}
                      onChange={e => setBuyerCMAMlsId(e.target.value)}
                      style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '12px 16px', fontSize: '14px' }}
                      onKeyDown={e => { if (e.key === 'Enter' && !buyerCMALoading) { setAddressSuggestions([]); handleRunBuyerCMA(); } }}
                    />
                    <div style={{ position: 'relative' }}>
                      <input
                        placeholder="Or type a property address..."
                        value={buyerCMAAddress}
                        onChange={e => handleAddressSearch(e.target.value)}
                        style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: '8px', padding: '12px 16px', fontSize: '14px' }}
                        onKeyDown={e => { if (e.key === 'Enter' && !buyerCMALoading) { setAddressSuggestions([]); handleRunBuyerCMA(); } }}
                      />
                      {addressSuggestions.length > 0 && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                          background: '#fff', border: '1px solid #dee2e6', borderRadius: '0 0 8px 8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto',
                        }}>
                          {addressSuggestions.map((s: any, i: number) => (
                            <div key={i} onClick={() => { setBuyerCMAAddress(s.place_name); setAddressSuggestions([]); }}
                              style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '13px', borderBottom: i < addressSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                              onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                              {s.place_name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button onClick={() => { setAddressSuggestions([]); handleRunBuyerCMA(); }} disabled={buyerCMALoading || (!buyerCMAAddress.trim() && !buyerCMAMlsId.trim())}
                      style={{ background: '#3498db', border: 'none', fontSize: '14px', padding: '12px 24px', whiteSpace: 'nowrap' }}>
                      {buyerCMALoading ? <><Spinner size="sm" className="me-1" />Analyzing...</> : <><FontAwesomeIcon icon={faChartLine} className="me-1" />Analyze</>}
                    </Button>
                  </div>

                  {/* Results */}
                  {buyerCMAResult?.analysis_results && (() => {
                    const ar = buyerCMAResult.analysis_results;
                    const predicted = ar.xgboost_prediction?.predicted_price;
                    const confidence = ar.enhanced_confidence || ar.xgboost_prediction?.confidence || 0;
                    const comps = ar.comparables || [];
                    const stats = ar.statistics || {};
                    // Calculate median from comps if stats doesn't have it
                    const compPrices = comps.map((c: any) => Number(c.close_price || c.list_price || 0)).filter((p: number) => p > 0).sort((a: number, b: number) => a - b);
                    const medianPrice = stats.median_price || (compPrices.length > 0 ? compPrices[Math.floor(compPrices.length / 2)] : null);
                    const compPpsf = comps.map((c: any) => { const p = Number(c.close_price || 0); const s = Number(c.living_area || 0); return s > 0 && p > 0 ? p / s : 0; }).filter((v: number) => v > 0);
                    const medianPpsf = stats.median_ppsf || stats.avg_price_per_sqft || (compPpsf.length > 0 ? Math.round(compPpsf.reduce((a: number, b: number) => a + b, 0) / compPpsf.length) : null);
                    const offerLow = predicted ? Math.round(predicted * 0.93) : null;
                    const offerMid = predicted ? Math.round(predicted * 0.96) : null;
                    const offerHigh = predicted ? Math.round(predicted * 0.99) : null;
                    const fmt = (v: any) => v ? `$${Number(v).toLocaleString()}` : '—';

                    return (
                      <div>
                        {/* Property header */}
                        <div style={{ background: colors.navy, borderRadius: '10px', padding: '20px', marginBottom: '20px', color: '#fff' }}>
                          <div style={{ fontSize: '11px', color: colors.gold2, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Property Analysis</div>
                          <div style={{ fontSize: '18px', fontWeight: 700 }}>{ar.property_address || buyerCMAAddress}</div>
                          <div style={{ fontSize: '12px', color: colors.muted, marginTop: '4px' }}>
                            Based on {comps.length} comparable sales • AI-Powered Analysis
                          </div>
                        </div>

                        {/* Price Range — adapts to Buying vs Selling mode */}
                        <div style={{ background: (selected as any)?._cmaMode === 'selling' ? '#fef9e7' : '#e8f4fd', borderRadius: '10px', padding: '24px', marginBottom: '20px', border: `1px solid ${(selected as any)?._cmaMode === 'selling' ? '#f0d78c' : '#b8daff'}` }}>
                          <div style={{ fontSize: '13px', color: (selected as any)?._cmaMode === 'selling' ? '#7d6608' : '#1a5276', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px', textAlign: 'center' }}>
                            {(selected as any)?._cmaMode === 'selling' ? 'Recommended Listing Range' : 'Recommended Offer Range'}
                          </div>
                          <div className="d-flex justify-content-around text-center">
                            <div>
                              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{(selected as any)?._cmaMode === 'selling' ? 'Conservative' : 'Opening Offer'}</div>
                              <div style={{ fontSize: '28px', fontWeight: 700, color: '#27ae60' }}>{fmt(offerLow)}</div>
                              <div style={{ fontSize: '11px', color: '#999' }}>{(selected as any)?._cmaMode === 'selling' ? 'Quick sale pricing' : '93% of market value'}</div>
                            </div>
                            <div style={{ borderLeft: `1px solid ${(selected as any)?._cmaMode === 'selling' ? '#f0d78c' : '#b8daff'}`, paddingLeft: '24px' }}>
                              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{(selected as any)?._cmaMode === 'selling' ? 'Market Value' : 'Target Price'}</div>
                              <div style={{ fontSize: '28px', fontWeight: 700, color: (selected as any)?._cmaMode === 'selling' ? colors.gold : '#2980b9' }}>{fmt(offerMid)}</div>
                              <div style={{ fontSize: '11px', color: '#999' }}>{(selected as any)?._cmaMode === 'selling' ? 'AI-powered valuation' : '96% of market value'}</div>
                            </div>
                            <div style={{ borderLeft: `1px solid ${(selected as any)?._cmaMode === 'selling' ? '#f0d78c' : '#b8daff'}`, paddingLeft: '24px' }}>
                              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{(selected as any)?._cmaMode === 'selling' ? 'Aspirational' : 'Max Offer'}</div>
                              <div style={{ fontSize: '28px', fontWeight: 700, color: '#e74c3c' }}>{fmt(offerHigh)}</div>
                              <div style={{ fontSize: '11px', color: '#999' }}>{(selected as any)?._cmaMode === 'selling' ? 'Premium positioning' : '99% of market value'}</div>
                            </div>
                          </div>
                        </div>

                        {/* Market Stats */}
                        <div className="d-flex gap-3 mb-4">
                          <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #e9ecef' }}>
                            <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>AI Predicted Value</div>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: colors.navy }}>{fmt(predicted)}</div>
                          </div>
                          <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #e9ecef' }}>
                            <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Median Comp Price</div>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: colors.navy }}>{fmt(medianPrice)}</div>
                          </div>
                          <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #e9ecef' }}>
                            <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Price per SqFt</div>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: colors.navy }}>{medianPpsf ? `$${Math.round(Number(medianPpsf))}` : '—'}</div>
                          </div>
                          <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #e9ecef' }}>
                            <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Comps Analyzed</div>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: colors.navy }}>{comps.length}</div>
                          </div>
                        </div>

                        {/* AI Narration — generated async after CMA */}
                        {cmaNarration ? (
                          <div style={{ background: 'white', borderRadius: '10px', padding: '16px', border: '1px solid #e9ecef', marginBottom: '12px' }}>
                            <h6 style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '12px' }}>
                              <FontAwesomeIcon icon={faBrain} className="me-2" style={{ color: colors.gold }} />
                              AI Market Analysis
                            </h6>
                            <p style={{ fontSize: '14px', color: '#333', lineHeight: 1.7, margin: 0 }}>
                              {cmaNarration.full || cmaNarration.summary}
                            </p>
                            {cmaNarration.key_factors && cmaNarration.key_factors.length > 0 && (
                              <div style={{ marginTop: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {cmaNarration.key_factors.map((f: string, i: number) => (
                                  <span key={i} style={{ fontSize: '11px', background: '#f0f4f8', color: colors.navy, padding: '4px 10px', borderRadius: '12px', border: '1px solid #e0e6ed' }}>
                                    {f}
                                  </span>
                                ))}
                              </div>
                            )}
                            {cmaNarration.fh_passed && (
                              <div style={{ marginTop: '8px', fontSize: '10px', color: '#27ae60' }}>
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                Fair Housing compliant
                              </div>
                            )}
                          </div>
                        ) : buyerCMAResult?.report_id ? (
                          <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '12px 16px', border: '1px solid #e9ecef', marginBottom: '12px', fontSize: '12px', color: '#888' }}>
                            <FontAwesomeIcon icon={faBrain} className="me-2" style={{ color: colors.gold }} />
                            AI narrative generating...
                          </div>
                        ) : null}

                        {/* Comparable Sales — clean inline cards */}
                        {comps.length > 0 && (
                          <div style={{ background: 'white', borderRadius: '10px', padding: '16px', border: '1px solid #e9ecef' }}>
                            <h6 style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '12px' }}>
                              <FontAwesomeIcon icon={faHome} className="me-2" style={{ color: colors.gold }} />
                              Comparable Sales
                              <small className="text-muted ms-2">({comps.length} comps)</small>
                            </h6>
                            <div style={{ maxHeight: '520px', overflowY: 'auto' }}>
                              {comps.slice(0, 15).map((comp: any, i: number) => {
                                const compPrice = comp.close_price || comp.list_price || 0;
                                const compSqft = comp.living_area ? Number(comp.living_area) : 0;
                                const compPpsf = comp.price_per_sqft ? Math.round(comp.price_per_sqft) : (compPrice && compSqft ? Math.round(compPrice / compSqft) : 0);
                                const imgs = compImages[i] || comp.images || [];
                                const compImg = imgs[0] || null;
                                return (
                                  <div key={i} style={{
                                    display: 'flex', gap: '12px', padding: '10px',
                                    borderBottom: i < comps.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    transition: 'background 0.15s',
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#fafaf5'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                  >
                                    {/* Thumbnail with mini carousel */}
                                    <div style={{
                                      width: '120px', height: '80px', flexShrink: 0,
                                      borderRadius: '8px', overflow: 'hidden', background: '#f0f0f0',
                                      position: 'relative',
                                    }}>
                                      {compImg ? (
                                        <>
                                          <img src={compImg} alt={comp.unparsed_address || 'Comp'}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                          />
                                          {imgs.length > 1 && (
                                            <div style={{ position: 'absolute', bottom: '3px', right: '4px', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '9px', padding: '1px 5px', borderRadius: '3px' }}>
                                              1/{imgs.length}
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: '10px' }}>
                                          {compImages[i] === undefined && comp.unparsed_address ? '...' : 'No Photo'}
                                        </div>
                                      )}
                                    </div>

                                    {/* Details */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                        <div style={{ fontWeight: 700, fontSize: '15px', color: colors.navy }}>
                                          {compPrice ? `$${Number(compPrice).toLocaleString()}` : '—'}
                                        </div>
                                        {comp.close_date && (
                                          <div style={{ fontSize: '10px', color: '#27ae60', fontWeight: 600 }}>
                                            Sold {new Date(comp.close_date).toLocaleDateString()}
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ fontSize: '12px', color: '#555', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {comp.unparsed_address || comp.address || `Comparable ${i + 1}`}
                                      </div>
                                      <div style={{ display: 'flex', gap: '8px', marginTop: '4px', fontSize: '11px', fontWeight: 600, color: colors.navy }}>
                                        {comp.bedrooms ? <span>{comp.bedrooms}bd</span> : null}
                                        {comp.bathrooms ? <span>{comp.bathrooms}ba</span> : null}
                                        {compSqft > 0 && <span>{compSqft.toLocaleString()}sf</span>}
                                        {compPpsf > 0 && <span style={{ color: '#888' }}>${compPpsf}/sf</span>}
                                        {comp.distance_miles && <span style={{ color: '#888' }}>{Number(comp.distance_miles).toFixed(1)}mi</span>}
                                      </div>
                                      {(comp.days_on_market || comp.classification) && (
                                        <div style={{ display: 'flex', gap: '6px', marginTop: '3px' }}>
                                          {comp.days_on_market && (
                                            <span style={{ fontSize: '9px', background: '#f0f0f0', color: '#666', padding: '1px 6px', borderRadius: '3px' }}>
                                              {comp.days_on_market} DOM
                                            </span>
                                          )}
                                          {comp.classification && (
                                            <span style={{ fontSize: '9px', background: comp.classification === 'renovated' ? '#e8f5e9' : '#f0f0f0', color: comp.classification === 'renovated' ? '#2e7d32' : '#666', padding: '1px 6px', borderRadius: '3px' }}>
                                              {comp.classification}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {comps.length === 0 && (
                          <div style={{ background: '#fff3cd', borderRadius: '8px', padding: '16px', border: '1px solid #ffc107', fontSize: '13px', color: '#856404', marginTop: '12px' }}>
                            No comparable sales found nearby. The AI prediction is based on property features only. Consider expanding the search or checking MLS directly.
                          </div>
                        )}

                        {/* Report Actions + History */}
                        {buyerCMAResult?.report_id && (() => {
                          const base = window.location.origin; // stay on current origin so the auth session carries (was cross-domain login bounce)
                          return (
                            <div style={{ background: 'white', borderRadius: '10px', padding: '16px', border: '1px solid #e9ecef', marginTop: '12px' }}>
                              <h6 style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '12px' }}>
                                <FontAwesomeIcon icon={faChartLine} className="me-2" style={{ color: colors.gold }} />
                                CMA Report
                              </h6>
                              <div className="d-flex gap-2 flex-wrap">
                                <Button size="sm" variant="outline-dark"
                                  onClick={() => window.open(`${base}/smart-cma-workflow?reportId=${buyerCMAResult.report_id}&step=2`, '_blank')}>
                                  <FontAwesomeIcon icon={faEdit} className="me-1" /> Review / Edit Report
                                </Button>
                                <Button size="sm" variant="outline-primary"
                                  onClick={() => window.open(`${base}/cma-report/${buyerCMAResult.report_id}?agentId=${selected?.id}`, '_blank')}>
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" /> Client View
                                </Button>
                                <Button size="sm" variant="outline-success"
                                  onClick={() => {
                                    const agentUserId = (() => { try { return JSON.parse(atob((localStorage.getItem('auth_token') || '').split('.')[1])).id; } catch { return ''; } })();
                                    navigator.clipboard.writeText(`${base}/cma-report/${buyerCMAResult.report_id}?agentId=${agentUserId}`);
                                    setAlert({ type: 'success', message: 'Offer report link copied!' });
                                  }}>
                                  <FontAwesomeIcon icon={faCopy} className="me-1" /> Share with Buyer
                                </Button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  {/* Report History */}
                  {buyerCMAReports.length > 1 && (
                    <div style={{ background: 'white', borderRadius: '10px', padding: '16px', border: '1px solid #e9ecef', marginTop: '12px' }}>
                      <h6 style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '12px' }}>
                        <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: colors.gold }} />
                        CMA Report History ({buyerCMAReports.length})
                      </h6>
                      {buyerCMAReports.map((report: any, i: number) => {
                        const rDate = new Date(report.created_on || report.createdAt);
                        const rPd = typeof report.propertyData === 'string' ? JSON.parse(report.propertyData) : (report.propertyData || {});
                        const rAddr = rPd.unparsed_address || rPd.original_address || rPd.address || report.title?.replace('Smart CMA - ', '') || '';
                        const rValue = Number(report.pricing_recommendations?.estimated_value || report.predicted_value || report.predictionData?.predicted_value || 0);
                        const rComps = Number(report.comparableCount || 0);
                        const isSelected = (report.report_id || report.id) === buyerCMAResult?.report_id;
                        return (
                          <div key={report.id} onClick={() => loadReportIntoBuyerCMA(report)}
                            style={{
                              padding: '10px 12px', marginBottom: '4px', borderRadius: '6px', cursor: 'pointer',
                              background: isSelected ? '#e8f4fd' : '#f8f9fa', border: isSelected ? '1px solid #3498db' : '1px solid #eee',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px',
                            }}
                            onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#eef'; }}
                            onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = '#f8f9fa'; }}
                          >
                            <div>
                              <div style={{ fontWeight: 600, color: '#333' }}>{rAddr || 'Property Analysis'}</div>
                              <div style={{ color: '#888', fontSize: '11px' }}>{rDate.toLocaleDateString()} {rDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 700, color: colors.navy }}>{rValue > 0 ? `$${rValue.toLocaleString()}` : '—'}</div>
                              <div style={{ fontSize: '10px', color: '#999' }}>{rComps} comps</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* AI Coach - Offer Strategy (Chat + Draft) */}
                  {buyerCMAResult?.analysis_results && (() => {
                    const ar = buyerCMAResult.analysis_results;
                    const predicted = ar.xgboost_prediction?.predicted_price || 0;
                    const compCount = (ar.comparables || []).length;
                    return (
                      <div style={{ marginTop: '20px', background: '#f8f9fc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px' }}>
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, #1a2b4a, #2c3e6d)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                              <FontAwesomeIcon icon={faRobot} style={{ color: '#d4a94d', fontSize: '13px' }} />
                            </div>
                            <span style={{ fontWeight: 700, fontSize: '13px', color: colors.navy }}>AI Coach — Offer Strategy</span>
                          </div>
                          {offerCoachChat.length > 0 && (
                            <button onClick={() => setOfferCoachChat([])} style={{ fontSize: '11px', color: '#999', background: 'none', border: 'none', cursor: 'pointer' }}>Clear Chat</button>
                          )}
                        </div>

                        {/* Initial prompt (only show if no chat yet) */}
                        {offerCoachChat.length === 0 && !offerCoachDraft && (
                          <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.6', marginBottom: '12px', background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                            <div style={{ marginBottom: '8px' }}>
                              You analyzed <strong>{buyerCMAAddress || 'this property'}</strong> — <strong>{compCount}</strong> comparable sales, predicted value <strong>${Math.round(predicted).toLocaleString()}</strong>.
                            </div>
                            <div style={{ color: '#666' }}>
                              What do you think about the comps? I can help you discuss the data, refine your strategy, or prepare a draft email for your buyer when you're ready.
                            </div>
                          </div>
                        )}

                        {/* Legal Disclaimer */}
                        <div style={{ fontSize: '10px', color: '#999', lineHeight: '1.5', padding: '8px 10px', background: '#fafafa', borderRadius: '4px', borderLeft: '2px solid #ddd', marginBottom: '12px' }}>
                          <strong>Disclaimer:</strong> AI-generated recommendations are based on available comparable sales data and predictive modeling. This is not an appraisal, BPO, or substitute for professional valuation. Data may be incomplete, outdated, or inaccurate. The licensed agent is solely responsible for all advice provided to clients and final offer decisions. Market conditions change rapidly — verify all data before presenting to clients.
                        </div>

                        {/* Chat history */}
                        {offerCoachChat.length > 0 && (
                          <div style={{ maxHeight: '250px', overflowY: 'auto', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {offerCoachChat.map((msg, i) => (
                              <div key={i} style={{
                                padding: '10px 12px', borderRadius: '8px', fontSize: '12px', lineHeight: '1.6',
                                ...(msg.role === 'agent'
                                  ? { background: colors.navy, color: 'white', marginLeft: '20%', textAlign: 'right' as const }
                                  : { background: 'white', border: '1px solid #e2e8f0', marginRight: '10%' })
                              }}>
                                {msg.role === 'coach' && <div style={{ fontSize: '10px', fontWeight: 700, color: colors.gold, marginBottom: '4px' }}>AI Coach</div>}
                                {msg.text}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Quick action buttons */}
                        {!offerCoachDraft && (
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                            <button
                              onClick={() => handleOfferCoachAsk('What is the closest comparable to this property and why?', 'chat')}
                              disabled={offerCoachLoading}
                              style={{ padding: '5px 12px', fontSize: '11px', fontWeight: 500, borderRadius: '14px', border: '1px solid #ddd', background: 'white', color: '#555', cursor: 'pointer' }}
                            >
                              Closest comp?
                            </button>
                            <button
                              onClick={() => handleOfferCoachAsk('Based on DOM and market activity, should we offer below asking or at asking price?', 'chat')}
                              disabled={offerCoachLoading}
                              style={{ padding: '5px 12px', fontSize: '11px', fontWeight: 500, borderRadius: '14px', border: '1px solid #ddd', background: 'white', color: '#555', cursor: 'pointer' }}
                            >
                              Offer strategy?
                            </button>
                            <button
                              onClick={() => handleOfferCoachAsk('Prepare a buyer offer strategy email with the closest comps and recommended price range', 'draft')}
                              disabled={offerCoachLoading}
                              style={{ padding: '5px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '14px', border: '1px solid ' + colors.navy, background: 'white', color: colors.navy, cursor: 'pointer' }}
                            >
                              Draft Offer Email
                            </button>
                          </div>
                        )}

                        {/* Chat input */}
                        {!offerCoachDraft && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                              type="text"
                              value={offerCoachMessage}
                              onChange={e => setOfferCoachMessage(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter' && offerCoachMessage.trim()) handleOfferCoachAsk(undefined, 'chat'); }}
                              placeholder="Ask about comps, strategy, buyer profile..."
                              style={{ flex: 1, padding: '8px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #ddd', outline: 'none' }}
                              disabled={offerCoachLoading}
                            />
                            <button
                              onClick={() => handleOfferCoachAsk(undefined, 'chat')}
                              disabled={offerCoachLoading || !offerCoachMessage.trim()}
                              style={{ padding: '8px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: 'none', background: colors.navy, color: 'white', cursor: 'pointer', opacity: offerCoachLoading || !offerCoachMessage.trim() ? 0.5 : 1 }}
                            >
                              {offerCoachLoading ? '...' : 'Ask'}
                            </button>
                          </div>
                        )}

                        {/* Loading */}
                        {offerCoachLoading && (
                          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#888' }}>
                            <Spinner animation="border" size="sm" /> Thinking...
                          </div>
                        )}

                        {/* Editable Draft Email */}
                        {offerCoachDraft && (
                          <div style={{ marginTop: '12px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: colors.navy, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Offer Strategy Email — Edit & Send
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                              <label style={{ fontSize: '11px', fontWeight: 600, color: '#666', display: 'block', marginBottom: '3px' }}>Subject</label>
                              <input
                                type="text"
                                value={offerDraftSubject}
                                onChange={e => setOfferDraftSubject(e.target.value)}
                                style={{ width: '100%', padding: '7px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', border: '1px solid #ddd', outline: 'none' }}
                              />
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                              <label style={{ fontSize: '11px', fontWeight: 600, color: '#666', display: 'block', marginBottom: '3px' }}>Body</label>
                              <textarea
                                value={offerDraftBody}
                                onChange={e => setOfferDraftBody(e.target.value)}
                                rows={10}
                                style={{ width: '100%', padding: '10px', fontSize: '12px', lineHeight: '1.6', borderRadius: '4px', border: '1px solid #ddd', outline: 'none', resize: 'vertical' }}
                              />
                            </div>
                            {/* Send From selector */}
                            <div style={{ marginBottom: '10px' }}>
                              <label style={{ fontSize: '11px', fontWeight: 600, color: '#666', display: 'block', marginBottom: '3px' }}>Send From</label>
                              {connectedAccounts.length > 0 ? (
                                <select
                                  value={selectedFromAccount}
                                  onChange={e => setSelectedFromAccount(e.target.value)}
                                  style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ddd', outline: 'none', minWidth: '200px' }}
                                >
                                  {connectedAccounts.map((acc: any) => (
                                    <option key={acc.id} value={String(acc.id)}>{acc.email_address || acc.email}</option>
                                  ))}
                                </select>
                              ) : (
                                <div style={{ fontSize: '11px', color: '#e74c3c' }}>No connected email accounts. Connect one in Settings.</div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                onClick={async () => {
                                  if (!offerCoachDraft.queueId) return;
                                  if (!selectedFromAccount) { setAlert({ type: 'danger', message: 'Select an email account to send from' }); return; }
                                  try {
                                    await axiosInstance.post(`/ai-coach/queue/${offerCoachDraft.queueId}/send`, {
                                      editedSubject: offerDraftSubject,
                                      editedBody: offerDraftBody,
                                      fromAccountId: selectedFromAccount,
                                    });
                                    setAlert({ type: 'success', message: 'Offer strategy email sent to client!' });
                                    setOfferCoachDraft(null);
                                    setOfferDraftSubject('');
                                    setOfferDraftBody('');
                                  } catch { setAlert({ type: 'danger', message: 'Failed to send — check connected email account' }); }
                                }}
                                style={{ padding: '7px 16px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#27ae60', color: 'white', cursor: 'pointer' }}
                              >
                                Send to Client
                              </button>
                              <button
                                onClick={() => { setOfferCoachDraft(null); setOfferDraftSubject(''); setOfferDraftBody(''); }}
                                style={{ padding: '7px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: '1px solid #ddd', background: 'white', color: '#666', cursor: 'pointer' }}
                              >
                                Dismiss
                              </button>
                              <button
                                onClick={() => handleOfferCoachAsk('Rewrite this email with more emphasis on the comparable sales data', 'draft')}
                                disabled={offerCoachLoading}
                                style={{ padding: '7px 14px', fontSize: '12px', fontWeight: 500, borderRadius: '6px', border: '1px solid #ddd', background: 'white', color: '#555', cursor: 'pointer' }}
                              >
                                Regenerate
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {!buyerCMAResult && !buyerCMALoading && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                      <FontAwesomeIcon icon={faChartLine} style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.2 }} />
                      <div style={{ fontSize: '14px' }}>Enter a property address above to run the offer analysis</div>
                    </div>
                  )}
                </div>
              )}

              {/* Activity Tab */}
              {activeTab === 'activity' && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>

                  {/* Customer Journey Pipeline */}
                  <div style={{ marginBottom: 20 }}>
                    <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px', fontSize: 13 }}>
                      {isSponsorPlan ? 'Loan Pipeline' : 'Customer Journey'}
                    </h6>
                    {(() => {
                      const currentStage = clientDetail?.ai_context?.journeyStage || 'new_contact';
                      const stages = isSponsorPlan ? [
                        { key: 'new_contact', label: 'New Lead' },
                        { key: 'hot_lead', label: 'Hot Lead' },
                        { key: 'active_buyer', label: 'Application' },
                        { key: 'pre_qualified', label: 'Pre-Qual' },
                        { key: 'under_contract', label: 'Under Contract' },
                        { key: 'disclosures', label: 'Disclosures' },
                        { key: 'initial_approval', label: 'Approval' },
                        { key: 'closing_docs', label: 'Closing' },
                        { key: 'past_buyer', label: 'Funded' },
                      ] : [
                        { key: 'new_contact', label: 'New' },
                        { key: 'active_buyer', label: 'Active Buyer' },
                        { key: 'active_seller', label: 'Active Seller' },
                        { key: 'under_contract', label: 'Under Contract' },
                        { key: 'past_buyer', label: 'Past Buyer' },
                        { key: 'past_seller', label: 'Past Seller' },
                        { key: 'sphere_of_influence', label: 'Sphere' },
                      ];
                      const currentIdx = stages.findIndex(s => s.key === currentStage);
                      return (
                        <div style={{ display: 'flex', gap: 0, position: 'relative' }}>
                          {stages.map((stage, i) => {
                            const isActive = stage.key === currentStage;
                            const isPast = i < currentIdx;
                            return (
                              <div key={stage.key} style={{ flex: 1, textAlign: 'center', cursor: 'pointer', position: 'relative' }}
                                onClick={async () => {
                                  try {
                                    const leadId = selected?.lead_id || selected?.id;
                                    await axiosInstance.patch(`/agents/clients/${leadId}/stage`, { stage: stage.key });
                                    toast.success(`Stage → ${stage.label}`);
                                    if (selected) { const r = await agentPortalService.getClientProfile(selected.id); if (r.success) setClientDetail(r.data); }
                                  } catch { toast.error('Failed'); }
                                }}>
                                <div style={{
                                  height: 6, borderRadius: 3,
                                  background: isActive ? colors.gold : isPast ? '#27ae60' : '#e0e0e0',
                                  marginBottom: 4,
                                }} />
                                <div style={{
                                  fontSize: 9, fontWeight: isActive ? 700 : 500,
                                  color: isActive ? colors.navy : isPast ? '#27ae60' : '#999',
                                }}>{stage.label}</div>
                                {isActive && <div style={{ fontSize: 7, color: colors.gold, marginTop: 1 }}>●</div>}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  {/* ─── Buyer Interest Summary ─── */}
                  {activityData?.buyerInterest && (() => {
                    const bi = activityData.buyerInterest;
                    const prefs = bi.preferences || {};
                    const hasPrefData = prefs.bedrooms || prefs.budgetMax || (prefs.cities && prefs.cities.length > 0);
                    return (
                      <div style={{ marginBottom: '20px', padding: '16px', background: '#FFFDF5', borderRadius: '10px', border: '1px solid #F0E6C8' }}>
                        <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '12px', fontSize: 13 }}>
                          <FontAwesomeIcon icon={faSearch} className="me-2" style={{ color: colors.gold }} />Buyer Interest Summary
                        </h6>

                        {/* Quick Stats Row */}
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '14px', flexWrap: 'wrap' }}>
                          {bi.chatStats?.totalMessages > 0 && (
                            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: '8px', padding: '8px 14px', textAlign: 'center' }}>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: colors.navy }}>{bi.chatStats.totalMessages}</div>
                              <div style={{ fontSize: '10px', color: '#888' }}>Chat Messages</div>
                            </div>
                          )}
                          {bi.savedCount > 0 && (
                            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: '8px', padding: '8px 14px', textAlign: 'center' }}>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: '#e74c3c' }}>{bi.savedCount}</div>
                              <div style={{ fontSize: '10px', color: '#888' }}>Saved Homes</div>
                            </div>
                          )}
                          {bi.likedCount > 0 && (
                            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: '8px', padding: '8px 14px', textAlign: 'center' }}>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: '#27ae60' }}>{bi.likedCount}</div>
                              <div style={{ fontSize: '10px', color: '#888' }}>Liked</div>
                            </div>
                          )}
                          {bi.viewCount > 0 && (
                            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: '8px', padding: '8px 14px', textAlign: 'center' }}>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: '#3498db' }}>{bi.viewCount}</div>
                              <div style={{ fontSize: '10px', color: '#888' }}>Pages Viewed</div>
                            </div>
                          )}
                          {bi.preApproved && (
                            <div style={{ background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '8px', padding: '8px 14px', textAlign: 'center' }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: '#155724' }}>✓ Pre-Approved</div>
                            </div>
                          )}
                        </div>

                        {/* Preferences */}
                        {hasPrefData && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, marginBottom: '6px' }}>LOOKING FOR</div>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {prefs.bedrooms && <span style={{ background: '#e8f0fe', color: '#1a73e8', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px' }}>{prefs.bedrooms} Bed</span>}
                              {prefs.bathrooms && <span style={{ background: '#e8f0fe', color: '#1a73e8', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px' }}>{prefs.bathrooms} Bath</span>}
                              {prefs.budgetMax && <span style={{ background: '#e6f4ea', color: '#137333', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px' }}>Up to ${Number(prefs.budgetMax).toLocaleString()}</span>}
                              {prefs.budgetMin && prefs.budgetMax && <span style={{ background: '#e6f4ea', color: '#137333', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px' }}>${Number(prefs.budgetMin).toLocaleString()} - ${Number(prefs.budgetMax).toLocaleString()}</span>}
                              {prefs.propertyType && <span style={{ background: '#fce8e6', color: '#c5221f', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px' }}>{prefs.propertyType.replace(/_/g, ' ')}</span>}
                              {prefs.cities?.map((c: string) => <span key={c} style={{ background: '#f3e8fd', color: '#7627bb', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px' }}>{c}</span>)}
                            </div>
                          </div>
                        )}

                        {/* Timeframe & Motivation */}
                        {(bi.timeframe || bi.motivation) && (
                          <div style={{ marginBottom: '12px' }}>
                            {bi.timeframe && bi.timeframe !== 'Not specified' && (
                              <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>
                                <strong>Timeframe:</strong> {bi.timeframe}
                              </div>
                            )}
                            {bi.motivation && (
                              <div style={{ fontSize: '12px', color: '#555' }}>
                                <strong>Notes:</strong> {bi.motivation}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Recent Chat Queries */}
                        {bi.recentQueries?.length > 0 && (
                          <div>
                            <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, marginBottom: '6px' }}>RECENT SEARCHES</div>
                            {bi.recentQueries.slice(0, 5).map((q: any, i: number) => (
                              <div key={i} style={{ padding: '4px 0', fontSize: '12px', borderBottom: '1px solid #f5f0e0' }}>
                                <span style={{ color: '#333' }}>"{q.query}"</span>
                                <span style={{ color: '#bbb', fontSize: '10px', marginLeft: '8px' }}>{new Date(q.date).toLocaleDateString()}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Last active */}
                        {bi.chatStats?.lastChat && (
                          <div style={{ fontSize: '10px', color: '#aaa', marginTop: '10px', textAlign: 'right' }}>
                            Last chat: {new Date(bi.chatStats.lastChat).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '16px' }}>
                    <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: colors.gold }} />Activity Timeline
                  </h6>

                  {activityLoading ? (
                    <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                  ) : (
                    <>
                      {/* Search History */}
                      {activityData?.searches?.length > 0 && (
                        <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #eee' }}>
                          <div style={{ fontSize: '12px', color: '#888', fontWeight: 600, marginBottom: '8px' }}>
                            <FontAwesomeIcon icon={faSearch} className="me-1" style={{ color: colors.green }} />AI Search History
                          </div>
                          {activityData.searches.map((s: any) => (
                            <div key={s.id} style={{ padding: '6px 0', fontSize: '13px', borderBottom: '1px solid #f5f5f5' }}>
                              <span style={{ color: '#333' }}>"{s.search_query}"</span>
                              <span style={{ color: '#999', fontSize: '11px', marginLeft: '8px' }}>{new Date(s.created_at).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Email History */}
                      {activityData?.emails?.length > 0 && (
                        <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #eee' }}>
                          <div style={{ fontSize: '12px', color: '#888', fontWeight: 600, marginBottom: '8px' }}>
                            <FontAwesomeIcon icon={faEnvelope} className="me-1" style={{ color: colors.blue }} />Email History
                          </div>
                          {activityData.emails.map((em: any) => (
                            <div key={em.id} style={{ padding: '6px 0', fontSize: '13px', borderBottom: '1px solid #f5f5f5' }}>
                              <span style={{ fontWeight: 500, color: '#333' }}>{em.subject}</span>
                              <span style={{ color: '#999', fontSize: '11px', marginLeft: '8px' }}>{new Date(em.sent_at).toLocaleString()}</span>
                              <span style={{ color: colors.green, fontSize: '11px', marginLeft: '8px' }}>{em.status}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Lead Activities */}
                      {activityData?.activities?.length > 0 ? (
                        activityData.activities.map((act: any) => {
                          const typeIcons: Record<string, { icon: any; color: string }> = {
                            profile_update: { icon: faEdit, color: colors.gold },
                            email: { icon: faEnvelope, color: colors.blue },
                            call: { icon: faPhone, color: colors.green },
                            meeting: { icon: faCalendarAlt, color: '#8e44ad' },
                            note: { icon: faStickyNote, color: colors.orange },
                            // Buyer portal activity types
                            buyer_search: { icon: faSearch, color: '#0078d4' },
                            buyer_save: { icon: faHeart, color: '#e74c3c' },
                            buyer_view: { icon: faEye, color: '#3498db' },
                            portal_visit: { icon: faGlobe, color: '#27ae60' },
                            showing_request: { icon: faCalendarAlt, color: '#e67e22' },
                            lead_capture: { icon: faUserPlus, color: colors.gold },
                            contact_request: { icon: faPhone, color: '#27ae60' },
                          };
                          const info = typeIcons[act.activity_type] || { icon: faCheckCircle, color: colors.muted };

                          return (
                            <div key={act.id} className="d-flex gap-3 align-items-start" style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FontAwesomeIcon icon={info.icon} style={{ color: info.color, fontSize: '11px' }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div className="d-flex justify-content-between">
                                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>{act.subject || act.activity_type.replace(/_/g, ' ')}</div>
                                  <span style={{ fontSize: '11px', color: '#999' }}>{new Date(act.activity_date).toLocaleString()}</span>
                                </div>
                                {act.description && <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', maxHeight: '80px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{act.description.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 300)}</div>}
                                {act.outcome && <div style={{ fontSize: '11px', color: colors.green, marginTop: '2px' }}>Outcome: {act.outcome}</div>}
                                {act.activity_subtype && <span style={{ fontSize: '10px', background: '#f0f0f0', color: '#888', padding: '1px 6px', borderRadius: '3px', marginTop: '4px', display: 'inline-block' }}>{act.activity_subtype}</span>}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        !activityData?.searches?.length && !activityData?.emails?.length && (
                          <p style={{ color: '#999', textAlign: 'center', padding: '20px 0', fontSize: '13px' }}>
                            No activity recorded yet. Activities will appear as you update profiles, send emails, and run AI searches.
                          </p>
                        )
                      )}
                    </>
                  )}
                </div>
              )}

              {/* AI Coach Tab */}
              {activeTab === 'ai-coach' && !hasAIPro && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '40px', border: '1px solid #e9ecef', textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faRobot} size="3x" style={{ color: colors.gold, opacity: 0.4, marginBottom: '16px' }} />
                  <h5 style={{ fontWeight: 700, color: colors.navy }}>Upgrade to AI Pro</h5>
                  <p style={{ color: '#666', fontSize: '14px', maxWidth: '450px', margin: '0 auto 8px' }}>
                    Let AI analyze your leads, draft personalized follow-ups, and build 30-day action plans — all from your own email.
                  </p>
                  <ul style={{ textAlign: 'left', maxWidth: '350px', margin: '0 auto 20px', fontSize: '13px', color: '#555', listStyle: 'none', padding: 0 }}>
                    {['AI Lead Coach — recommends the best next action', 'Instant response to new leads — even at 2 AM', '30-day automated action plans per lead', 'AI-drafted follow-ups you review & send', 'Monthly seller CMA auto-updates'].map((f, i) => (
                      <li key={i} style={{ padding: '4px 0' }}><span style={{ color: colors.gold, marginRight: '8px' }}>✓</span>{f}</li>
                    ))}
                  </ul>
                  <Button onClick={() => navigate('/pricing')} style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, padding: '12px 32px' }}>
                    Upgrade to AI Pro — $185/mo
                  </Button>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>Current plan: {userPlan.replace(/-/g, ' ')}</div>
                </div>
              )}
              {activeTab === 'ai-coach' && hasAIPro && (
                <div>
                  {/* Analyze Button */}
                  <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginBottom: '16px' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h6 style={{ color: colors.navy, fontWeight: 700, margin: 0 }}>
                          <FontAwesomeIcon icon={faRobot} className="me-2" style={{ color: colors.gold }} />
                          AI Lead Coach — {getClientName(selected)}
                        </h6>
                        <small className="text-muted">Jasper analyzes this lead and recommends the best next action</small>
                      </div>
                      <Button
                        onClick={handleGetRecommendation}
                        disabled={loadingRecommendation}
                        style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                      >
                        {loadingRecommendation ? (
                          <><Spinner size="sm" className="me-1" /> Analyzing...</>
                        ) : (
                          <><FontAwesomeIcon icon={faRobot} className="me-1" /> Analyze Lead</>
                        )}
                      </Button>
                    </div>

                    {/* Recommendation */}
                    {aiRecommendation && (
                      <div className="p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}>
                        <div className="d-flex align-items-center mb-2">
                          <Badge bg={aiRecommendation.urgency === 'high' ? 'danger' : aiRecommendation.urgency === 'medium' ? 'warning' : 'secondary'} className="me-2">
                            {aiRecommendation.urgency} priority
                          </Badge>
                          <Badge bg="info">{aiRecommendation.action?.replace(/_/g, ' ')}</Badge>
                          {aiRecommendation.leadType && (
                            <Badge bg={aiRecommendation.leadType === 'seller' ? 'warning' : 'primary'} className="ms-2">{aiRecommendation.leadType}</Badge>
                          )}
                        </div>
                        <p style={{ fontSize: '14px', color: '#333', margin: '8px 0', lineHeight: 1.6 }}>
                          {aiRecommendation.reason}
                        </p>
                        {aiRecommendation.metrics && (
                          <small className="text-muted">
                            Last contact: {aiRecommendation.metrics.daysSinceLastContact} days ago
                            {' | '}Emails sent: {aiRecommendation.metrics.outboundCount}
                            {' | '}Emails received: {aiRecommendation.metrics.inboundCount}
                          </small>
                        )}

                        {/* Action Buttons based on recommendation */}
                        <div className="mt-3 d-flex gap-2 flex-wrap">
                          {(aiRecommendation.action === 'draft_email' || aiRecommendation.action === 'send_listing') && (
                            <Button size="sm"
                              onClick={() => handleGenerateCoachDraft(aiRecommendation.reason)}
                              disabled={loadingDraft}
                              style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                            >
                              {loadingDraft ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faEnvelope} className="me-1" /> Draft Follow-Up Email</>}
                            </Button>
                          )}
                          {aiRecommendation.action === 'run_cma' && (
                            <Button size="sm" variant="outline-success"
                              onClick={() => navigate(`/agent-portal/client/${selected?.id}#seller-ai`)}
                            >
                              <FontAwesomeIcon icon={faChartLine} className="me-1" /> Run CMA Report
                            </Button>
                          )}
                          {aiRecommendation.action === 'run_cma' && (
                            <Button size="sm"
                              onClick={handleGenerateSellerUpdate}
                              disabled={loadingDraft}
                              style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                            >
                              {loadingDraft ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faEnvelope} className="me-1" /> Draft Market Update</>}
                            </Button>
                          )}
                          {aiRecommendation.action === 'schedule_call' && (
                            <Button size="sm" variant="outline-primary">
                              <FontAwesomeIcon icon={faPhone} className="me-1" /> Schedule Call
                            </Button>
                          )}
                          {aiRecommendation.action === 'update_stage' && (
                            <Button size="sm" variant="outline-info">
                              <FontAwesomeIcon icon={faEdit} className="me-1" /> Update to: {aiRecommendation.suggested_stage}
                            </Button>
                          )}
                          {/* Always show draft email as an option */}
                          {aiRecommendation.action !== 'draft_email' && aiRecommendation.action !== 'no_action' && (
                            <Button size="sm" variant="outline-secondary"
                              onClick={() => handleGenerateCoachDraft()}
                              disabled={loadingDraft}
                            >
                              <FontAwesomeIcon icon={faEnvelope} className="me-1" /> Draft Email Instead
                            </Button>
                          )}

                          {/* Schedule automation button */}
                          <Button size="sm" variant="outline-dark"
                            onClick={async () => {
                              try {
                                await axiosInstance.post('/ai-coach/schedule', {
                                  leadId: selected?.id,
                                  action: `coach_${aiRecommendation.action}`,
                                  priority: aiRecommendation.urgency,
                                  daysFromNow: 0,
                                  reason: aiRecommendation.reason
                                });
                                setAlert({ type: 'success', message: 'Automation scheduled — will run shortly' });
                                if (selected) fetchAiHistory(selected.id);
                              } catch (err) {
                                setAlert({ type: 'danger', message: 'Failed to schedule' });
                              }
                            }}
                          >
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" /> Schedule Automation
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Quick Actions — always available for seller leads */}
                    {aiRecommendation?.leadType === 'seller' && (
                      <div style={{ marginTop: '12px', padding: '12px', background: '#f0f4f8', borderRadius: '8px', border: '1px solid #d6dce4' }}>
                        <small style={{ fontWeight: 600, color: '#555', display: 'block', marginBottom: '8px' }}>Seller Quick Actions:</small>
                        <div className="d-flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline-success"
                            onClick={() => {
                              handleGenerateSellerUpdate();
                            }}
                            disabled={loadingDraft}
                          >
                            {loadingDraft ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faChartLine} className="me-1" /> Draft Market Update</>}
                          </Button>
                          <Button size="sm" variant="outline-primary"
                            onClick={async () => {
                              try {
                                setAlert({ type: 'info', message: 'Scheduling CMA + market update...' });
                                await axiosInstance.post('/ai-coach/schedule', {
                                  leadId: selected?.id,
                                  action: 'coach_cma_update',
                                  priority: 'medium',
                                  daysFromNow: 0,
                                  reason: 'Agent-triggered CMA refresh + market update'
                                });
                                setAlert({ type: 'success', message: 'CMA scheduled — will auto-run and draft market update' });
                                if (selected) fetchAiHistory(selected.id);
                              } catch (err) {
                                setAlert({ type: 'danger', message: 'Failed to schedule CMA' });
                              }
                            }}
                          >
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" /> Run CMA & Auto-Draft
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Draft Preview / Edit / Send */}
                  {aiDraft && (
                    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '2px solid #C9952A', marginBottom: '16px' }}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 style={{ color: colors.navy, fontWeight: 700, margin: 0 }}>
                          <FontAwesomeIcon icon={faEnvelope} className="me-2" style={{ color: colors.gold }} />
                          AI Draft — Ready for Review
                        </h6>
                        <div className="d-flex gap-2">
                          <Button size="sm" variant={editingAIDraft ? 'primary' : 'outline-primary'}
                            onClick={() => setEditingAIDraft(!editingAIDraft)}
                          >
                            <FontAwesomeIcon icon={faEdit} className="me-1" /> {editingAIDraft ? 'Preview' : 'Edit'}
                          </Button>
                        </div>
                      </div>

                      {/* Send from selector */}
                      {connectedAccounts.length > 0 && (
                        <div className="mb-3">
                          <label style={{ fontSize: '12px', color: '#666', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Send from:</label>
                          <select
                            value={selectedFromAccount}
                            onChange={e => setSelectedFromAccount(e.target.value)}
                            style={{ width: '100%', border: '1px solid #ddd', borderRadius: '8px', padding: '8px 12px', fontSize: '14px', background: '#f8f9fa' }}
                          >
                            {connectedAccounts.map((acc: any) => (
                              <option key={acc.id} value={String(acc.id)}>
                                {acc.email} ({acc.provider === 'gmail' || acc.provider === 'google' ? 'Gmail' : 'Outlook'})
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {editingAIDraft ? (
                        <div className="mb-3">
                          <input
                            value={aiEditSubject}
                            onChange={e => setAiEditSubject(e.target.value)}
                            className="form-control mb-2"
                            placeholder="Subject"
                            style={{ fontWeight: 600 }}
                          />
                          <textarea
                            value={aiEditBody}
                            onChange={e => setAiEditBody(e.target.value)}
                            className="form-control"
                            rows={8}
                            style={{ fontSize: '14px', lineHeight: 1.7 }}
                          />
                        </div>
                      ) : (
                        <div className="mb-3 p-3 rounded" style={{ background: '#fafaf8', border: '1px solid #e9ecef' }}>
                          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#333', fontSize: '15px' }}>
                            Subject: {aiDraft.subject}
                          </div>
                          <div style={{ fontSize: '14px', color: '#555', whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                            {aiDraft.body}
                          </div>
                        </div>
                      )}

                      {/* Compliance badge */}
                      {aiDraft.compliance && (
                        <div className="mb-3 d-flex align-items-center gap-2">
                          <Badge bg={aiDraft.compliance.aiAuditPassed !== false
                            ? (aiDraft.compliance.autoCleaned ? 'warning' : 'success')
                            : 'danger'
                          }>
                            <FontAwesomeIcon icon={aiDraft.compliance.aiAuditPassed !== false ? faCheckCircle : faExclamationTriangle} className="me-1" />
                            AI Audit: {aiDraft.compliance.aiAuditPassed !== false
                              ? (aiDraft.compliance.autoCleaned ? 'Auto-Cleaned' : 'Passed')
                              : 'Review Required'}
                          </Badge>
                          <small style={{ color: '#888' }}>
                            Compliance Score: {aiDraft.compliance.score}%
                          </small>
                          {aiDraft.compliance.violations?.length > 0 && (
                            <small className="text-warning">
                              {aiDraft.compliance.violations.length} item(s) reviewed
                            </small>
                          )}
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="d-flex gap-2">
                        <Button
                          onClick={handleSendCoachDraft}
                          disabled={sendingAIDraft}
                          style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600, padding: '10px 24px' }}
                        >
                          {sendingAIDraft ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Approve & Send</>}
                        </Button>
                        <Button variant="outline-secondary" onClick={() => { setAiDraft(null); setEditingAIDraft(false); }}>
                          Dismiss
                        </Button>
                      </div>

                      {/* Schedule Send */}
                      <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#888', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                          <FontAwesomeIcon icon={faClock} /> Schedule Send (Optional)
                        </label>
                        <div className="d-flex gap-2 align-items-center">
                          <input
                            type="datetime-local"
                            value={(aiDraft as any)?._scheduleDate || ''}
                            onChange={e => setAiDraft((prev: any) => prev ? { ...prev, _scheduleDate: e.target.value } : prev)}
                            style={{ padding: '7px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, color: colors.navy }}
                          />
                          <span style={{ fontSize: 11, color: '#aaa' }}>Leave blank to send immediately</span>
                          {(aiDraft as any)?._scheduleDate && (
                            <Button
                              size="sm"
                              variant="outline-dark"
                              disabled={sendingAIDraft}
                              onClick={async () => {
                                if (!aiDraft?.queueId) return;
                                setSendingAIDraft(true);
                                try {
                                  await axiosInstance.post(`/ai-coach/queue/${aiDraft.queueId}/send`, {
                                    fromAccountId: selectedFromAccount || undefined,
                                    editedSubject: editingAIDraft ? aiEditSubject : undefined,
                                    editedBody: editingAIDraft ? aiEditBody : undefined,
                                    scheduledFor: new Date((aiDraft as any)._scheduleDate).toISOString(),
                                  });
                                  toast.success(`Scheduled for ${new Date((aiDraft as any)._scheduleDate).toLocaleString()}`);
                                  setAiDraft(null); setEditingAIDraft(false);
                                } catch { toast.error('Failed to schedule'); }
                                setSendingAIDraft(false);
                              }}
                              style={{ whiteSpace: 'nowrap', fontWeight: 600 }}
                            >
                              <FontAwesomeIcon icon={faClock} className="me-1" />
                              {sendingAIDraft ? 'Scheduling...' : `Schedule for ${new Date((aiDraft as any)._scheduleDate).toLocaleDateString()}`}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Regenerate with instruction */}
                      <div className="d-flex gap-2 mt-3 align-items-end">
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: 10, fontWeight: 600, color: '#888', display: 'block', marginBottom: 3 }}>
                            Want something different? Tell AI what to change:
                          </label>
                          <input
                            type="text"
                            placeholder='e.g. "make it shorter" or "more urgent tone" or "mention their pre-approval"'
                            value={(aiDraft as any)?._regenInstruction || ''}
                            onChange={e => setAiDraft((prev: any) => prev ? { ...prev, _regenInstruction: e.target.value } : prev)}
                            onKeyDown={e => { if (e.key === 'Enter' && !loadingDraft) handleGenerateCoachDraft((aiDraft as any)?._regenInstruction || undefined); }}
                            style={{ width: '100%', padding: '8px 12px', fontSize: 13, borderRadius: 6, border: '1px solid #ddd', outline: 'none' }}
                          />
                        </div>
                        <Button
                          variant="outline-dark"
                          size="sm"
                          disabled={loadingDraft}
                          onClick={() => handleGenerateCoachDraft((aiDraft as any)?._regenInstruction || undefined)}
                          style={{ whiteSpace: 'nowrap', fontWeight: 600, padding: '8px 16px' }}
                        >
                          {loadingDraft ? <Spinner size="sm" /> : 'Regenerate'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Upcoming Scheduled Automations */}
                  {upcomingTasks.length > 0 && (
                    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginBottom: '16px' }}>
                      <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '16px' }}>
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2" style={{ color: colors.blue }} />
                        Upcoming Automations ({upcomingTasks.length})
                      </h6>
                      {upcomingTasks.map((task: any) => {
                        const taskData = typeof task.task_data === 'string' ? JSON.parse(task.task_data) : task.task_data || {};
                        const scheduledDate = new Date(task.scheduled_for);
                        const isOverdue = scheduledDate <= new Date();
                        const isPast = scheduledDate < new Date(Date.now() - 60000);

                        return (
                          <div key={task.task_id} style={{
                            padding: '14px 16px', marginBottom: '8px', borderRadius: '8px',
                            background: isOverdue ? '#fff3e0' : '#f8f9fa',
                            border: `1px solid ${isOverdue ? '#ffe0b2' : '#e9ecef'}`,
                          }}>
                            <div className="d-flex justify-content-between align-items-start">
                              <div style={{ flex: 1 }}>
                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                  <Badge bg={task.priority === 'urgent' ? 'danger' : task.priority === 'high' ? 'warning' : 'secondary'} style={{ fontSize: '10px' }}>
                                    {task.priority}
                                  </Badge>
                                  <Badge bg="info" style={{ fontSize: '10px' }}>
                                    {(task.action || '').replace(/^coach_/, '').replace(/_/g, ' ')}
                                  </Badge>
                                  {isOverdue && <Badge bg="danger" style={{ fontSize: '10px' }}>OVERDUE</Badge>}
                                  {task.requires_approval && <Badge bg="outline-warning" text="dark" style={{ fontSize: '10px', border: '1px solid #ffc107' }}>Needs Approval</Badge>}
                                </div>
                                <div style={{ fontSize: '13px', color: '#333', fontWeight: 600 }}>
                                  {taskData.reason || task.action?.replace(/_/g, ' ')}
                                </div>
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                  {isOverdue ? 'Ready to run' : `Scheduled: ${scheduledDate.toLocaleDateString()} ${scheduledDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                                  {task.automation_level && <span className="ms-2">| Mode: {task.automation_level}</span>}
                                </div>
                              </div>
                              {cancellingId !== task.task_id && reschedulingId !== task.task_id && (
                                <div className="d-flex gap-1 flex-wrap" style={{ minWidth: '140px', justifyContent: 'flex-end' }}>
                                  <Button size="sm" style={{ fontSize: '10px', padding: '3px 10px', background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                                    onClick={() => handleApproveTask(task.task_id)}
                                  >
                                    Approve
                                  </Button>
                                  <Button size="sm" variant="outline-secondary" style={{ fontSize: '10px', padding: '3px 8px' }}
                                    onClick={() => { setReschedulingId(task.task_id); setRescheduleDays('3'); }}
                                  >
                                    Reschedule
                                  </Button>
                                  <Button size="sm" variant="outline-danger" style={{ fontSize: '10px', padding: '3px 8px' }}
                                    onClick={() => { setCancellingId(task.task_id); setCancelReason(''); }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              )}
                            </div>

                            {/* Inline Reschedule */}
                            {reschedulingId === task.task_id && (
                              <div style={{ marginTop: '10px', padding: '10px', background: '#e3f2fd', borderRadius: '6px', border: '1px solid #bbdefb' }}>
                                <div className="d-flex align-items-center gap-2">
                                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#1565c0', whiteSpace: 'nowrap' }}>Reschedule to</label>
                                  <select value={rescheduleDays} onChange={e => setRescheduleDays(e.target.value)}
                                    style={{ border: '1px solid #bbdefb', borderRadius: '4px', padding: '4px 8px', fontSize: '12px' }}>
                                    <option value="1">Tomorrow</option>
                                    <option value="3">3 days</option>
                                    <option value="7">1 week</option>
                                    <option value="14">2 weeks</option>
                                    <option value="30">1 month</option>
                                  </select>
                                  <Button size="sm" variant="primary" style={{ fontSize: '11px', padding: '3px 10px' }}
                                    onClick={() => handleRescheduleTask(task.task_id)}>Save</Button>
                                  <Button size="sm" variant="outline-secondary" style={{ fontSize: '11px', padding: '3px 8px' }}
                                    onClick={() => setReschedulingId(null)}>Cancel</Button>
                                </div>
                              </div>
                            )}

                            {/* Inline Cancel */}
                            {cancellingId === task.task_id && (
                              <div style={{ marginTop: '10px', padding: '10px', background: '#fce4ec', borderRadius: '6px', border: '1px solid #ef9a9a' }}>
                                <input value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                                  placeholder="Reason (optional)"
                                  style={{ width: '100%', border: '1px solid #ef9a9a', borderRadius: '4px', padding: '6px 10px', fontSize: '12px', marginBottom: '8px' }}
                                  autoFocus />
                                <div className="d-flex gap-2">
                                  <Button size="sm" variant="danger" style={{ fontSize: '11px', padding: '3px 10px' }}
                                    onClick={() => handleCancelTask(task.task_id)}>Confirm Cancel</Button>
                                  <Button size="sm" variant="outline-secondary" style={{ fontSize: '11px', padding: '3px 8px' }}
                                    onClick={() => setCancellingId(null)}>Nevermind</Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* No recommendation yet */}
                  {!aiRecommendation && !loadingRecommendation && aiHistory.length === 0 && upcomingTasks.length === 0 && (
                    <div style={{ background: 'white', borderRadius: '12px', padding: '40px', border: '1px solid #e9ecef', textAlign: 'center' }}>
                      <FontAwesomeIcon icon={faRobot} size="3x" style={{ color: '#C9952A', opacity: 0.4 }} className="mb-3" />
                      <h6 style={{ fontWeight: 600, color: '#555' }}>Click "Analyze Lead" to get started</h6>
                      <p className="text-muted" style={{ fontSize: '13px' }}>
                        Jasper will review {getClientName(selected)}'s profile, email history, and market data to recommend the best next action.
                      </p>
                    </div>
                  )}

                  {/* Recommendation History */}
                  {aiHistory.length > 0 && (
                    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>
                      <h6 style={{ color: colors.navy, fontWeight: 700, marginBottom: '16px' }}>
                        <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: colors.gold }} />
                        AI Coach History
                      </h6>
                      {aiHistory.map((item: any) => {
                        const ctx = typeof item.context === 'string' ? JSON.parse(item.context) : item.context;
                        const rec = ctx?.recommendation;
                        const isSent = item.status === 'sent';
                        const isDismissed = item.status === 'dismissed';
                        const isPending = item.status === 'pending';

                        return (
                          <div key={item.id} style={{
                            padding: '12px 16px', marginBottom: '8px', borderRadius: '8px',
                            background: isSent ? '#f0f9f0' : isDismissed ? '#f5f5f5' : '#fff9f0',
                            border: `1px solid ${isSent ? '#c8e6c9' : isDismissed ? '#e0e0e0' : '#ffe0b2'}`
                          }}>
                            <div className="d-flex justify-content-between align-items-start">
                              <div style={{ flex: 1 }}>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <Badge bg={isSent ? 'success' : isDismissed ? 'secondary' : 'warning'} style={{ fontSize: '10px' }}>
                                    {isSent ? 'COMPLETED' : isDismissed ? 'DISMISSED' : 'PENDING'}
                                  </Badge>
                                  {rec?.urgency && (
                                    <Badge bg={rec.urgency === 'high' ? 'danger' : rec.urgency === 'medium' ? 'warning' : 'secondary'} style={{ fontSize: '10px' }}>
                                      {rec.urgency}
                                    </Badge>
                                  )}
                                  {rec?.action && (
                                    <Badge bg="info" style={{ fontSize: '10px' }}>{rec.action.replace(/_/g, ' ')}</Badge>
                                  )}
                                </div>
                                <div style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>
                                  {item.subject || 'AI Recommendation'}
                                </div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                  {item.body || rec?.reason || ''}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', minWidth: '100px' }}>
                                <small className="text-muted d-block mb-1">{new Date(item.created_at).toLocaleDateString()}</small>
                                {isPending && completingId !== item.id && (
                                  <div className="d-flex gap-1 justify-content-end flex-wrap">
                                    {/* Action-specific buttons based on recommendation type */}
                                    {item.action_type === 'coach_cma_ready' && ctx?.report_url ? (
                                      <Button size="sm" variant="success" style={{ fontSize: '10px', padding: '2px 8px' }}
                                        onClick={() => navigate(ctx.report_url)}
                                      ><FontAwesomeIcon icon={faChartLine} className="me-1" />View CMA Report</Button>
                                    ) : rec?.action === 'run_cma' || rec?.action === 'setup_monthly_cma' ? (
                                      <Button size="sm" variant="outline-success" style={{ fontSize: '10px', padding: '2px 8px' }}
                                        onClick={() => {
                                          if (selected) navigate(`/agent-portal/client/${selected.id}#seller-ai`);
                                        }}
                                      ><FontAwesomeIcon icon={faChartLine} className="me-1" />Run CMA</Button>
                                    ) : rec?.action === 'schedule_call' ? (
                                      <Button size="sm" variant="outline-primary" style={{ fontSize: '10px', padding: '2px 8px' }}
                                        onClick={() => { setCompletingId(item.id); setCompleteNotes(''); }}
                                      ><FontAwesomeIcon icon={faPhone} className="me-1" />Log Call</Button>
                                    ) : rec?.action === 'refi_analysis' || rec?.action === 'equity_review' ? (
                                      <Button size="sm" variant="outline-info" style={{ fontSize: '10px', padding: '2px 8px' }}
                                        onClick={() => {
                                          setActiveTab('property');
                                          if (selected) setSearchParams({ id: selected.id, tab: 'property' });
                                        }}
                                      ><FontAwesomeIcon icon={faHome} className="me-1" />{rec.action === 'refi_analysis' ? 'Refi Analysis' : 'Equity Review'}</Button>
                                    ) : item.body && item.body.length > 50 ? (
                                      <Button size="sm" variant="outline-primary" style={{ fontSize: '10px', padding: '2px 8px' }}
                                        onClick={() => {
                                          setAiDraft({ ...item, queueId: item.id });
                                          setAiEditSubject(item.subject || '');
                                          setAiEditBody(item.body || '');
                                        }}
                                      ><FontAwesomeIcon icon={faEnvelope} className="me-1" />Review Email</Button>
                                    ) : null}
                                    <Button size="sm" variant="outline-success" style={{ fontSize: '10px', padding: '2px 8px' }}
                                      onClick={() => { setCompletingId(item.id); setCompleteNotes(''); }}
                                    >Complete</Button>
                                    <Button size="sm" variant="outline-secondary" style={{ fontSize: '10px', padding: '2px 8px' }}
                                      onClick={() => handleDismissDraft(item.id).then(() => selected && fetchAiHistory(selected.id))}
                                    >Dismiss</Button>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Inline Complete Form */}
                            {completingId === item.id && (
                              <div style={{ marginTop: '12px', padding: '12px', background: '#f0f9f0', borderRadius: '8px', border: '1px solid #c8e6c9' }}>
                                <label style={{ fontSize: '12px', fontWeight: 600, color: '#2e7d32', display: 'block', marginBottom: '6px' }}>
                                  Summary notes (what happened?)
                                </label>
                                <textarea
                                  value={completeNotes}
                                  onChange={e => setCompleteNotes(e.target.value)}
                                  placeholder="e.g., Called Mike — agreed to list at $445k. Wants to start mid-May after kitchen update."
                                  rows={3}
                                  style={{ width: '100%', border: '1px solid #c8e6c9', borderRadius: '6px', padding: '10px', fontSize: '13px', resize: 'vertical', outline: 'none' }}
                                  autoFocus
                                />
                                <div className="d-flex gap-2 mt-2">
                                  <Button size="sm" variant="success" style={{ fontSize: '12px', fontWeight: 600 }}
                                    onClick={() => handleCompleteRecommendation(item.id)}
                                  >
                                    <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Save & Complete
                                  </Button>
                                  <Button size="sm" variant="outline-secondary" style={{ fontSize: '12px' }}
                                    onClick={() => { setCompletingId(null); setCompleteNotes(''); }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* AI Disclaimer */}
                  <div style={{ marginTop: '20px', padding: '12px 16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                    <small style={{ color: '#888', fontSize: '11px', lineHeight: 1.6 }}>
                      <strong>Disclosure:</strong> AI-generated recommendations may contain errors. Please review all recommendations carefully and ensure they accurately represent you, your business, and your client relationships before approving any actions. You are solely responsible for all communications sent to clients. AI is a tool to assist your business — not a replacement for professional judgment. All recommendations are audited for Fair Housing Act, ADRE, and NAR Code of Ethics compliance.
                    </small>
                  </div>
                </div>
              )}

              {/* Automations Tab */}
              {activeTab === 'automations' && !hasAIPro && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '40px', border: '1px solid #e9ecef', textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faCalendarAlt} size="3x" style={{ color: colors.gold, opacity: 0.4, marginBottom: '16px' }} />
                  <h5 style={{ fontWeight: 700, color: colors.navy }}>Upgrade to AI Pro</h5>
                  <p style={{ color: '#666', fontSize: '14px', maxWidth: '400px', margin: '0 auto 20px' }}>
                    Automate your workflow with AI-powered action plans, scheduled follow-ups, and CMA auto-updates.
                  </p>
                  <Button onClick={() => navigate('/pricing')} style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, padding: '12px 32px' }}>
                    Upgrade to AI Pro — $185/mo
                  </Button>
                </div>
              )}
              {activeTab === 'automations' && hasAIPro && (
                <div>
                  <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginBottom: '16px' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h6 style={{ color: colors.navy, fontWeight: 700, margin: 0 }}>
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-2" style={{ color: colors.blue }} />
                          Automations — {getClientName(selected)}
                        </h6>
                        <small className="text-muted">AI-recommended action plan and scheduled follow-ups for this lead</small>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        {allAutomations.length > 0 && <Badge bg="primary" style={{ fontSize: '12px', padding: '6px 12px' }}>{allAutomations.length} tasks</Badge>}
                        <Button size="sm"
                          onClick={handleGenerateActionPlan}
                          disabled={generatingPlan}
                          style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                        >
                          {generatingPlan ? <><Spinner size="sm" className="me-1" /> Planning...</> : <><FontAwesomeIcon icon={faRobot} className="me-1" /> Generate Action Plan</>}
                        </Button>
                      </div>
                    </div>

                    {allAutomations.length === 0 && !generatingPlan && (
                      <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <FontAwesomeIcon icon={faCalendarAlt} size="3x" style={{ color: '#C9952A', opacity: 0.4, marginBottom: '16px' }} />
                        <h6 style={{ fontWeight: 600, color: '#555' }}>No automations for {getClientName(selected)} yet</h6>
                        <p className="text-muted" style={{ fontSize: '13px', marginBottom: '16px' }}>
                          Click "Generate Action Plan" to have Jasper create a personalized 30-day action plan for this lead.
                        </p>
                        <Button
                          onClick={handleGenerateActionPlan}
                          disabled={generatingPlan}
                          style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600 }}
                        >
                          <FontAwesomeIcon icon={faRobot} className="me-1" /> Generate 30-Day Action Plan
                        </Button>
                      </div>
                    )}

                    {allAutomations.map((task: any) => {
                      const taskData = typeof task.task_data === 'string' ? JSON.parse(task.task_data) : task.task_data || {};
                      const isOverdue = new Date(task.scheduled_for) <= new Date();
                      const isThisLead = task.lead_id === selected?.id || task.lead_id === selected?.id?.substring(0, 26);

                      return (
                        <div key={task.task_id} style={{
                          padding: '16px', marginBottom: '10px', borderRadius: '10px',
                          background: isThisLead ? '#fff8e1' : '#f8f9fa',
                          border: `1px solid ${isThisLead ? '#ffe082' : '#e9ecef'}`,
                        }}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div style={{ flex: 1 }}>
                              <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                <span style={{ fontWeight: 700, fontSize: '14px', color: colors.navy }}>
                                  {task.lead_first_name} {task.lead_last_name || ''}
                                </span>
                                <Badge bg={task.priority === 'urgent' ? 'danger' : task.priority === 'high' ? 'warning' : 'secondary'} style={{ fontSize: '10px' }}>
                                  {task.priority}
                                </Badge>
                                <Badge bg="info" style={{ fontSize: '10px' }}>
                                  {(task.action || '').replace(/^coach_/, '').replace(/_/g, ' ')}
                                </Badge>
                                {isOverdue && <Badge bg="danger" style={{ fontSize: '10px' }}>READY</Badge>}
                                {isThisLead && <Badge bg="warning" text="dark" style={{ fontSize: '10px' }}>CURRENT LEAD</Badge>}
                              </div>

                              <div style={{ fontSize: '13px', color: '#444', lineHeight: 1.6, marginBottom: '8px' }}>
                                {taskData.reason || ''}
                              </div>

                              <div style={{ fontSize: '11px', color: '#999' }}>
                                <FontAwesomeIcon icon={faEnvelope} className="me-1" />{task.lead_email}
                                <span className="ms-3"><FontAwesomeIcon icon={faCalendarAlt} className="me-1" />{isOverdue ? 'Ready to run now' : new Date(task.scheduled_for).toLocaleString()}</span>
                                <span className="ms-3">Mode: {task.automation_level}</span>
                              </div>
                            </div>
                          </div>

                          {/* Action buttons */}
                          {cancellingId !== task.task_id && reschedulingId !== task.task_id && (
                            <div className="d-flex gap-2 mt-3">
                              <Button size="sm"
                                style={{ background: 'linear-gradient(135deg, #C9952A 0%, #E8B44A 100%)', border: 'none', fontWeight: 600, fontSize: '12px' }}
                                onClick={async () => {
                                  try {
                                    await axiosInstance.post(`/ai-coach/upcoming/${task.task_id}/approve`);
                                    setAlert({ type: 'success', message: `Approved: ${(task.action || '').replace(/_/g, ' ')} for ${task.lead_first_name}` });
                                    fetchAllAutomations();
                                  } catch (err) { setAlert({ type: 'danger', message: 'Failed' }); }
                                }}
                              >
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Approve & Run
                              </Button>
                              <Button size="sm" variant="outline-primary" style={{ fontSize: '12px' }}
                                onClick={() => { setReschedulingId(task.task_id); setRescheduleDays('3'); }}
                              >
                                Reschedule
                              </Button>
                              <Button size="sm" variant="outline-danger" style={{ fontSize: '12px' }}
                                onClick={() => { setCancellingId(task.task_id); setCancelReason(''); }}
                              >
                                Cancel
                              </Button>
                            </div>
                          )}

                          {/* Inline Reschedule */}
                          {reschedulingId === task.task_id && (
                            <div style={{ marginTop: '10px', padding: '12px', background: '#e3f2fd', borderRadius: '8px', border: '1px solid #bbdefb' }}>
                              <div className="d-flex align-items-center gap-2">
                                <label style={{ fontSize: '12px', fontWeight: 600, color: '#1565c0', whiteSpace: 'nowrap' }}>Reschedule to:</label>
                                <select value={rescheduleDays} onChange={e => setRescheduleDays(e.target.value)}
                                  style={{ border: '1px solid #bbdefb', borderRadius: '6px', padding: '6px 10px', fontSize: '13px' }}>
                                  <option value="1">Tomorrow</option>
                                  <option value="3">3 days</option>
                                  <option value="7">1 week</option>
                                  <option value="14">2 weeks</option>
                                  <option value="30">1 month</option>
                                </select>
                                <Button size="sm" variant="primary" style={{ fontSize: '12px' }}
                                  onClick={() => { handleRescheduleTask(task.task_id); fetchAllAutomations(); }}>Save</Button>
                                <Button size="sm" variant="outline-secondary" style={{ fontSize: '12px' }}
                                  onClick={() => setReschedulingId(null)}>Cancel</Button>
                              </div>
                            </div>
                          )}

                          {/* Inline Cancel */}
                          {cancellingId === task.task_id && (
                            <div style={{ marginTop: '10px', padding: '12px', background: '#fce4ec', borderRadius: '8px', border: '1px solid #ef9a9a' }}>
                              <input value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                                placeholder="Reason for cancelling (optional)"
                                style={{ width: '100%', border: '1px solid #ef9a9a', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', marginBottom: '10px' }}
                                autoFocus />
                              <div className="d-flex gap-2">
                                <Button size="sm" variant="danger" style={{ fontSize: '12px' }}
                                  onClick={() => { handleCancelTask(task.task_id); fetchAllAutomations(); }}>Confirm Cancel</Button>
                                <Button size="sm" variant="outline-secondary" style={{ fontSize: '12px' }}
                                  onClick={() => setCancellingId(null)}>Nevermind</Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* KOPS-576: Property Details Tab */}
              {activeTab === 'property' && selected && (() => {
                // Load property data on tab open
                const leadId = selected?.lead_id || selected?.id;
                if (leadId && propertyLoaded !== leadId && !propertyLoading) {
                  setPropertyLoading(true);
                  setPropertyLoaded(leadId);
                  (async () => {
                    try {
                      const { data: snapData } = await axiosInstance.get(`/county-deals/property-snapshots/${leadId}`);
                      if (snapData.success && snapData.properties?.length > 0) {
                        setPropertyData(snapData.properties);
                      } else {
                        const { data: liveData } = await axiosInstance.get(`/county-deals/property-lookup/${leadId}`);
                        if (liveData.success) setPropertyData(liveData.properties || []);
                        else setPropertyData([]);
                      }
                    } catch { setPropertyData([]); }
                    setPropertyLoading(false);
                  })();
                }

                const ALERT_COLORS: Record<string, { bg: string; color: string }> = {
                  danger: { bg: '#fce4ec', color: '#c62828' },
                  info: { bg: '#e3f2fd', color: '#1565c0' },
                  neutral: { bg: '#f5f5f5', color: '#666' },
                };

                return (
                  <div>
                    {/* Header */}
                    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <h6 style={{ fontWeight: 700, color: colors.navy, margin: 0 }}>
                          <FontAwesomeIcon icon={faHome} style={{ marginRight: 8, color: colors.gold }} />
                          Property Intelligence
                          {propertyData.length > 0 && <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 8 }}>({propertyData.length} propert{propertyData.length === 1 ? 'y' : 'ies'} via County Records)</span>}
                        </h6>
                        <button
                          onClick={async () => {
                            setPropertyLoading(true);
                            setPropertyData([]);
                            try {
                              const { data } = await axiosInstance.get(`/county-deals/property-lookup/${leadId}`);
                              if (data.success) setPropertyData(data.properties || []);
                            } catch {}
                            setPropertyLoading(false);
                          }}
                          disabled={propertyLoading}
                          style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: `1px solid ${colors.gold}`, background: 'white', color: colors.navy, cursor: 'pointer' }}
                        >
                          <FontAwesomeIcon icon={faSyncAlt} spin={propertyLoading} style={{ marginRight: 4 }} />
                          {propertyLoading ? 'Scanning...' : 'Refresh County Data'}
                        </button>
                      </div>

                      {propertyLoading && <div style={{ textAlign: 'center', padding: 40, color: '#888' }}><Spinner animation="border" size="sm" /> Searching county records...</div>}

                      {!propertyLoading && propertyData.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                          <FontAwesomeIcon icon={faHome} style={{ fontSize: 32, opacity: 0.3, marginBottom: 12 }} />
                          <div style={{ fontSize: 13 }}>No property records found in county database.</div>
                          <div style={{ fontSize: 11, marginTop: 4 }}>Click "Refresh County Data" to scan by owner name.</div>
                        </div>
                      )}

                      {/* Property Cards */}
                      {propertyData.map((prop: any, i: number) => {
                        const leadLast = (selected?.lead_last_name || selected?.last_name || '').toUpperCase().trim();
                        const countyOwner = (prop.ownership || '').toUpperCase();
                        const ownershipMatch = leadLast && leadLast.length > 2 && countyOwner.includes(leadLast);
                        const propertySold = !ownershipMatch && countyOwner.length > 2 && leadLast.length > 2;

                        return (
                          <div key={i} style={{ padding: 16, marginBottom: 12, borderRadius: 10, border: propertySold ? '2px solid #e74c3c' : '1px solid #e9ecef', background: propertySold ? '#fff5f5' : '#fafbfc' }}>
                            {/* Ownership Change Warning */}
                            {propertySold && (
                              <div style={{ background: '#fce4ec', borderRadius: 6, padding: '10px 12px', marginBottom: 12, border: '1px solid #f8bbd0' }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#c62828', marginBottom: 4 }}>
                                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 4 }} />
                                  Ownership Changed — Property May Have Been Sold
                                </div>
                                <div style={{ fontSize: 11, color: '#555' }}>
                                  County now shows owner: <strong>{prop.ownership}</strong>.
                                  {selected?.lead_first_name} {selected?.lead_last_name} is no longer on record.
                                </div>
                              </div>
                            )}

                            {/* Property Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 15, fontWeight: 700, color: propertySold ? '#999' : colors.navy, textDecoration: propertySold ? 'line-through' : 'none' }}>
                                  {prop.fullAddress || prop.property_address || prop.address}
                                </div>
                                <div style={{ fontSize: 12, color: '#666', marginTop: 3 }}>
                                  {prop.propertyType || prop.property_type || ''}
                                  {prop.sqft ? ` · ${Number(prop.sqft).toLocaleString()} sqft` : ''}
                                  {prop.bedrooms ? ` · ${prop.bedrooms}bd/${prop.bathrooms || '?'}ba` : ''}
                                  {prop.yearBuilt || prop.year_built ? ` · Built ${prop.yearBuilt || prop.year_built}` : ''}
                                  {prop.pool ? ' · Pool' : ''}
                                  {prop.stories ? ` · ${prop.stories} story` : ''}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
                                {(prop.fullCashValue || prop.assessed_value) && (
                                  <div>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: colors.navy }}>${Number(prop.fullCashValue || prop.assessed_value).toLocaleString()}</div>
                                    <div style={{ fontSize: 10, color: '#999' }}>County Assessed Value</div>
                                  </div>
                                )}
                                {prop.mlPredictedValue && (
                                  <div style={{ marginTop: 4 }}>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#27ae60' }}>${Number(prop.mlPredictedValue).toLocaleString()}</div>
                                    <div style={{ fontSize: 10, color: '#999' }}>AI Estimated Value</div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Deed & Ownership Details */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 14, padding: 12, background: '#fff', borderRadius: 8, border: '1px solid #eee' }}>
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 2 }}>Owner</div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>{prop.ownership || 'Unknown'}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 2 }}>Deed Type</div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>{prop.deedTypeLabel || prop.deed_type || prop.deedType || 'N/A'}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 2 }}>Deed Date</div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>{prop.deedDate || prop.deed_date || 'N/A'}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 2 }}>Ownership</div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>{prop.ownershipYears || prop.ownership_years ? `${prop.ownershipYears || prop.ownership_years} years` : 'N/A'}</div>
                              </div>
                              {prop.mailingAddress && (
                                <div style={{ gridColumn: 'span 2' }}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 2 }}>Mailing Address</div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: (prop.isAbsentee || prop.is_absentee) ? '#e74c3c' : colors.navy }}>
                                    {prop.mailingAddress || prop.mailing_address}
                                    {(prop.isAbsentee || prop.is_absentee) && ' (Absentee)'}
                                  </div>
                                </div>
                              )}
                              {prop.salePrice && (
                                <div>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 2 }}>Last Sale Price</div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: '#27ae60' }}>${Number(prop.salePrice).toLocaleString()}</div>
                                </div>
                              )}
                              {prop.apn && (
                                <div>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 2 }}>APN</div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>{prop.apn}</div>
                                </div>
                              )}
                            </div>

                            {/* Alert Badges */}
                            {(prop.alerts || []).length > 0 && (
                              <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {prop.alerts.map((a: any, j: number) => {
                                  const c = ALERT_COLORS[a.type] || ALERT_COLORS.neutral;
                                  return (
                                    <span key={j} style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: c.bg, color: c.color }}>
                                      {a.label}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

            </div>
          )}
        </div>
      </div>
      </>
      )}

      {/* KOPS-654: Upload Multiple Documents modal — the onBulkUpload trigger previously opened nothing. */}
      <Modal className="deal-theme" show={showUploadModal} onHide={() => { if (!bulkUploading) { setShowUploadModal(false); setBulkFiles([]); } }} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '16px' }}>Upload Multiple Documents</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Select one or more files</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              disabled={bulkUploading}
              onChange={(e: any) => setBulkFiles(Array.from(e.target.files || []))}
            />
            <Form.Text muted style={{ fontSize: 11 }}>PDF, image, or Word files. They'll be added to this deal.</Form.Text>
          </Form.Group>
          {bulkFiles.length > 0 && (
            <ul style={{ marginTop: 12, marginBottom: 0, fontSize: 12, color: '#555', paddingLeft: 18 }}>
              {bulkFiles.map((f, i) => <li key={i}>{f.name}</li>)}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" disabled={bulkUploading} onClick={() => { setShowUploadModal(false); setBulkFiles([]); }}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" disabled={bulkUploading || !bulkFiles.length} onClick={handleBulkUploadSubmit}>
            {bulkUploading ? 'Uploading…' : `Upload ${bulkFiles.length || ''} Document${bulkFiles.length === 1 ? '' : 's'}`}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

// KOPS-577: View Deal link component — checks if a client has a linked deal
const ViewDealLink: React.FC<{
  leadId: string;
  isSponsor: boolean;
  onNavigate: (mode: string) => void;
}> = ({ leadId, isSponsor, onNavigate }) => {
  const [dealInfo, setDealInfo] = useState<{ id: string; dealName: string; stage: string; pipelineType: string } | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (checked) return;
    setChecked(true);
    (async () => {
      try {
        const { data } = await axiosInstance.get(`/agent/deals/by-lead/${leadId}`);
        if (data?.success && data.deal) {
          setDealInfo(data.deal);
        }
      } catch {}
    })();
  }, [leadId, checked]);

  if (!dealInfo) return null;

  const boardMode = dealInfo.pipelineType === 'lender' ? 'pipeline' : 'deal-board';
  const stageColor = dealInfo.stage === 'closed' || dealInfo.stage === 'past_buyer' ? '#27ae60'
    : dealInfo.stage === 'under_contract' ? '#27ae60'
    : dealInfo.stage === 'offer_submitted' || dealInfo.stage === 'pre_qualified' ? '#3498db'
    : '#e67e22';

  return (
    <div className="d-flex align-items-center gap-2 mt-2">
      <FontAwesomeIcon icon={faExternalLinkAlt} style={{ color: colors.gold, fontSize: 11 }} />
      <span style={{ fontSize: 11, fontWeight: 600, color: '#888' }}>Deal:</span>
      <button
        onClick={() => onNavigate(boardMode)}
        style={{
          fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 6,
          border: `1px solid ${stageColor}`, background: `${stageColor}15`,
          color: stageColor, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: 9 }} />
        {dealInfo.dealName} — {dealInfo.stage?.replace(/_/g, ' ')}
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// KOPS-662: Deal Automations Tab — shows task timeline per deal
// ─────────────────────────────────────────────────────────────────────

// Each group has a triggerStage — tasks only become actionable when deal reaches that stage.
// Groups before the current stage show as completed/active. Future groups show as locked.
interface AutoGroup {
  category: string; label: string; icon: any; color: string;
  triggerStage: string; // which deal board stage activates this group
  tasks: { name: string; daysOffset: number; type: 'email' | 'task' | 'notification'; auto?: boolean }[];
}

// Stage ordering for comparison
const STAGE_ORDER = ['new_lead', 'contacted', 'showing_meeting', 'offer_submitted', 'pre_contract', 'earnest_money', 'under_contract', 'inspection', 'appraisal', 'loan_approval', 'closing', 'broker_review', 'completed', 'closed'];

function isStageReached(currentStage: string, targetStage: string): boolean {
  const currentIdx = STAGE_ORDER.indexOf(currentStage);
  const targetIdx = STAGE_ORDER.indexOf(targetStage);
  if (currentIdx === -1 || targetIdx === -1) return false;
  return currentIdx >= targetIdx;
}

const AUTOMATION_TEMPLATES: Record<string, AutoGroup[]> = {
  purchase: [
    {
      category: 'pre_contract', label: 'Pre-Contract — Getting Started', icon: faUser, color: '#e74c3c',
      triggerStage: 'new_lead',
      tasks: [
        { name: 'Send welcome email to buyer', daysOffset: 0, type: 'email', auto: true },
        { name: 'Notify agent: new lead', daysOffset: 0, type: 'notification', auto: true },
        { name: 'Verify buyer pre-approval status', daysOffset: 1, type: 'task' },
        { name: 'Send agency disclosure to client', daysOffset: 2, type: 'email' },
        { name: 'Set up MLS search alerts for buyer criteria', daysOffset: 2, type: 'task' },
        { name: 'Schedule property showings', daysOffset: 3, type: 'task' },
        { name: 'Discuss offer strategy with buyer', daysOffset: 5, type: 'task' },
      ],
    },
    {
      category: 'under_contract', label: 'Under Contract — File Opened', icon: faFileContract, color: '#3498db',
      triggerStage: 'under_contract',
      tasks: [
        { name: 'Send executed contract to all parties', daysOffset: 0, type: 'email', auto: true },
        { name: 'Verify earnest money deposit received', daysOffset: 1, type: 'task' },
        { name: 'Create transaction timeline', daysOffset: 0, type: 'task', auto: true },
        { name: 'Open escrow with title company', daysOffset: 1, type: 'task' },
        { name: 'Request pre-approval letter from lender', daysOffset: 1, type: 'task' },
      ],
    },
    {
      category: 'inspection', label: 'Inspection Period', icon: faSearch, color: '#f39c12',
      triggerStage: 'inspection',
      tasks: [
        { name: 'Send inspection recommendations to client', daysOffset: 0, type: 'email' },
        { name: 'Schedule home inspection', daysOffset: 1, type: 'task' },
        { name: 'Attend / review inspection report', daysOffset: 3, type: 'task' },
        { name: 'Send BINSR to listing agent', daysOffset: 4, type: 'email' },
        { name: 'Negotiate inspection repairs', daysOffset: 5, type: 'task' },
        { name: 'Inspection resolution confirmation', daysOffset: 7, type: 'notification', auto: true },
      ],
    },
    {
      category: 'appraisal', label: 'Appraisal', icon: faHome, color: '#9b59b6',
      triggerStage: 'appraisal',
      tasks: [
        { name: 'Appraisal ordered notification', daysOffset: 0, type: 'notification', auto: true },
        { name: 'Track appraisal scheduling', daysOffset: 2, type: 'task' },
        { name: 'Review appraisal report', daysOffset: 7, type: 'task' },
        { name: 'Address appraisal issues (if any)', daysOffset: 8, type: 'task' },
      ],
    },
    {
      category: 'financing', label: 'Loan Approval', icon: faUniversity, color: '#2980b9',
      triggerStage: 'loan_approval',
      tasks: [
        { name: 'Confirm loan application submitted', daysOffset: 0, type: 'task' },
        { name: 'Track underwriting progress', daysOffset: 3, type: 'task' },
        { name: 'Loan approval confirmation', daysOffset: 5, type: 'notification' },
        { name: 'Review closing disclosure', daysOffset: 7, type: 'task' },
        { name: 'Confirm funds for closing', daysOffset: 8, type: 'task' },
      ],
    },
    {
      category: 'closing', label: 'Closing & Post-Close', icon: faTrophy, color: '#27ae60',
      triggerStage: 'closing',
      tasks: [
        { name: 'Schedule final walkthrough', daysOffset: 0, type: 'task' },
        { name: 'Send closing checklist to client', daysOffset: 0, type: 'email' },
        { name: 'Verify utility transfers', daysOffset: 1, type: 'task' },
        { name: 'Confirm homeowners insurance', daysOffset: 0, type: 'task' },
        { name: 'Review settlement statement', daysOffset: 1, type: 'task' },
        { name: 'Send congratulations email', daysOffset: 2, type: 'email', auto: true },
        { name: 'Enroll in monthly CMA auto-updates', daysOffset: 2, type: 'task', auto: true },
      ],
    },
  ],
  listing: [
    {
      category: 'listing_setup', label: 'Listing Setup', icon: faFileContract, color: '#e74c3c',
      triggerStage: 'new_lead',
      tasks: [
        { name: 'Send welcome email to seller', daysOffset: 0, type: 'email', auto: true },
        { name: 'Notify agent: new listing file', daysOffset: 0, type: 'notification', auto: true },
        { name: 'Request seller disclosure (SPDS)', daysOffset: 2, type: 'email' },
        { name: 'Request lead-based paint disclosure', daysOffset: 2, type: 'email' },
        { name: 'Photography authorization', daysOffset: 3, type: 'task' },
        { name: 'MLS input sheet review', daysOffset: 3, type: 'task' },
      ],
    },
    {
      category: 'active_listing', label: 'Active Listing', icon: faHome, color: '#f39c12',
      triggerStage: 'showing_meeting',
      tasks: [
        { name: 'MLS listing confirmation', daysOffset: 0, type: 'notification' },
        { name: 'Weekly showing feedback summary', daysOffset: 7, type: 'email' },
        { name: 'Market update to seller (bi-weekly)', daysOffset: 14, type: 'email' },
        { name: 'Price adjustment review', daysOffset: 21, type: 'task' },
      ],
    },
    {
      category: 'under_contract', label: 'Under Contract & Closing', icon: faTrophy, color: '#27ae60',
      triggerStage: 'under_contract',
      tasks: [
        { name: 'Offer received — notify seller', daysOffset: 0, type: 'notification', auto: true },
        { name: 'Counter-offer tracking', daysOffset: 1, type: 'task' },
        { name: 'Inspection coordination', daysOffset: 5, type: 'task' },
        { name: 'Appraisal coordination', daysOffset: 10, type: 'task' },
        { name: 'Closing document preparation', daysOffset: 25, type: 'task' },
        { name: 'Send congratulations email', daysOffset: 30, type: 'email', auto: true },
        { name: 'Enroll in monthly CMA auto-updates', daysOffset: 30, type: 'task', auto: true },
      ],
    },
  ],
};

// Pre-written email templates for automation tasks
const EMAIL_TEMPLATES: Record<string, { subject: string; body: string }> = {
  'Send welcome email to buyer': {
    subject: 'Welcome — Your Home Search with {{agentName}}',
    body: `Hi {{clientName}},\n\nWelcome! I'm excited to help you find your perfect home.\n\nI've set up a personalized search portal for you where you can:\n• Browse homes with AI-powered search\n• Save your favorite properties\n• Get instant home valuations\n• Schedule showings directly\n\nAccess your portal anytime:\n{{portalUrl}}\n\nLet me know if you have any questions. I'm here to help every step of the way.\n\nBest,\n{{agentName}}`,
  },
  'Send agency disclosure to client': {
    subject: 'Agency Disclosure & Buyer Advisory — Please Review',
    body: `Hi {{clientName}},\n\nAs we begin working together, Arizona law requires that I provide you with the following disclosures:\n\n1. Agency Disclosure & Election — explains our working relationship\n2. Buyer Advisory — important information about the home buying process\n3. Wire Fraud Warning — protect yourself from wire transfer scams\n4. Fair Housing Disclosure — your rights under Fair Housing law\n5. AI Disclosure — how our AI tools assist in your home search\n\nThese documents are available in your portal for your records. Please review them at your convenience.\n\nIf you have any questions about these documents, I'm happy to walk through them with you.\n\nBest,\n{{agentName}}`,
  },
  'Send executed contract to all parties': {
    subject: 'Executed Purchase Contract — {{propertyAddress}}',
    body: `Hi {{clientName}},\n\nGreat news — the purchase contract for {{propertyAddress}} has been fully executed!\n\nHere are the key dates from your contract:\n• Inspection period: 10 days from execution\n• Appraisal contingency: [date]\n• Loan contingency: [date]\n• Projected closing: [date]\n\nThe executed contract is available in your portal. Our transaction coordinator will be managing the file and keeping you updated at every step.\n\nCongratulations on this exciting step!\n\nBest,\n{{agentName}}`,
  },
  'Send inspection recommendations to client': {
    subject: 'Home Inspection — Recommended Inspectors for {{propertyAddress}}',
    body: `Hi {{clientName}},\n\nNow that we're under contract, the next step is scheduling your home inspection. I strongly recommend getting an inspection within the first 5 days of your inspection period.\n\nHere are three inspectors I recommend:\n\n1. [Inspector Name] — $[price] — ⭐ [rating]\n2. [Inspector Name] — $[price] — ⭐ [rating]\n3. [Inspector Name] — $[price] — ⭐ [rating]\n\nWould you like to be present during the inspection? If so, what times work best for you?\n\nYou can also select your preferred inspector and schedule directly through your portal.\n\nBest,\n{{agentName}}`,
  },
  'Send BINSR to listing agent': {
    subject: 'Buyer Inspection Notice — {{propertyAddress}}',
    body: `Hi,\n\nPlease find attached the Buyer's Inspection Notice and Seller's Response (BINSR) for the property at {{propertyAddress}}.\n\nThe buyer has identified the following items from the inspection report that require attention. Please review and respond at your earliest convenience.\n\nThank you,\n{{agentName}}`,
  },
  'Send closing checklist to client': {
    subject: 'Closing Checklist — {{propertyAddress}}',
    body: `Hi {{clientName}},\n\nWe're approaching closing day! Here's what you need to prepare:\n\n✅ Bring valid government-issued photo ID\n✅ Confirm funds are ready for wire transfer (DO NOT wire without verbal confirmation from title)\n✅ Review the settlement statement in your portal\n✅ Confirm homeowner's insurance is bound\n✅ Schedule utility transfers for closing day\n✅ Final walkthrough is scheduled for [date/time]\n\nThe title company address is: [address]\nClosing time: [time]\n\nAlmost there — congratulations!\n\nBest,\n{{agentName}}`,
  },
  'Send congratulations email': {
    subject: 'Congratulations on Your New Home! 🏠',
    body: `Hi {{clientName}},\n\nCongratulations — you are officially a homeowner!\n\nIt's been a pleasure working with you on this journey. Your new home at {{propertyAddress}} is a great choice.\n\nA few things to know:\n• I'll be sending you monthly market updates for your neighborhood\n• Your portal will show your home's value over time\n• If you ever need anything — referrals for contractors, questions about the area — I'm always here\n\nWelcome home!\n\nBest,\n{{agentName}}`,
  },
  'Send welcome email to seller': {
    subject: 'Welcome — Let\'s Get Your Home Sold',
    body: `Hi {{clientName}},\n\nThank you for choosing to work with me to sell your home at {{propertyAddress}}.\n\nI've set up your portal where you can:\n• Track your home's current market value\n• See comparable sales in your neighborhood\n• Monitor showing activity and feedback\n• Access all transaction documents\n\nNext steps:\n1. I'll need the Seller Property Disclosure Statement (SPDS)\n2. We'll schedule photography\n3. Review and finalize the listing price\n\nI'll be in touch shortly to get everything started.\n\nBest,\n{{agentName}}`,
  },
  'Request seller disclosure (SPDS)': {
    subject: 'Seller Disclosure (SPDS) Needed — {{propertyAddress}}',
    body: `Hi {{clientName}},\n\nAs we prepare to list your home, Arizona law requires a Seller Property Disclosure Statement (SPDS). This form discloses any known material facts about the property.\n\nI've uploaded the blank SPDS to your portal. Please complete it and return it to me at your earliest convenience — ideally before we go active on MLS.\n\nIf you have any questions about what to disclose, I'm happy to walk through it with you.\n\nBest,\n{{agentName}}`,
  },
};

const DealAutomationsTab: React.FC<{
  dealId?: string; dealType?: string; stage?: string;
  parties?: any[]; propertyAddress?: string;
  brokerageId?: string; uploadedDocs?: any[];
}> = ({ dealId, dealType, stage, parties = [], propertyAddress = '', brokerageId, uploadedDocs = [] }) => {
  // Resolve agent info from localStorage + JWT token
  const [agentInfo, setAgentInfo] = useState({ name: '', slug: '', email: '' });
  useEffect(() => {
    try {
      // Try user_info, user, and JWT token for name/email
      const u = JSON.parse(localStorage.getItem('user_info') || '{}');
      const u2 = JSON.parse(localStorage.getItem('user') || '{}');
      let name = `${u.user_first_name || u2.user_first_name || ''} ${u.user_last_name || u2.user_last_name || ''}`.trim();
      let email = u.user_email || u2.user_email || u.email || u2.email || '';
      // Fallback to JWT
      if (!name) {
        try {
          const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || '';
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            name = payload.name || name;
            email = email || payload.email || '';
          }
        } catch {}
      }
      // Fetch portal slug from API
      const userId = u.id || u2.id;
      if (userId) {
        axiosInstance.get('/portal-settings/my-slug').then(({ data }) => {
          setAgentInfo({ name: name || 'Your Agent', slug: data.slug || '', email });
        }).catch(() => {
          setAgentInfo({ name: name || 'Your Agent', slug: '', email });
        });
      } else {
        setAgentInfo({ name: name || 'Your Agent', slug: '', email });
      }
    } catch {
      setAgentInfo({ name: 'Your Agent', slug: '', email: '' });
    }
  }, []);

  // Get ALL client parties (buyers/sellers) — show even without email for TO display
  const clientParties = parties.filter(p => ['buyer', 'seller', 'client'].includes(p.party_type));
  const clientPartiesWithEmail = clientParties.filter(p => p.email);
  // Resolve best client name — prefer party name, fall back to deal client_name
  const clientName = (() => {
    // Try parties first — find a real name
    for (const p of parties) {
      if (!['buyer', 'seller', 'client'].includes(p.party_type)) continue;
      const n = p.name || p.full_name || '';
      if (n && n !== 'New Deal' && n !== 'Unknown Client' && n !== 'Unknown') return n;
      if (p.first_name) return `${p.first_name} ${p.last_name || ''}`.trim();
    }
    // Fall back to deal detail client_name or deal_name
    return '[Client Name]';
  })();
  const portalUrl = agentInfo.slug ? `https://www.aisearchportal.com/${agentInfo.slug}` : 'https://www.aisearchportal.com';
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState<string | null>(null);
  const [knowledgeItems, setKnowledgeItems] = useState<any[]>([]);
  const [knowledgeScore, setKnowledgeScore] = useState<number | null>(null);

  // Inline email compose state
  const [composing, setComposing] = useState<string | null>(null);
  const [composeTo, setComposeTo] = useState('');
  const [composeCc, setComposeCc] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeSending, setComposeSending] = useState(false);

  useEffect(() => {
    if (!dealId) { setLoading(false); return; }
    axiosInstance.get(`/deal-workflow/deals/${dealId}/tasks/pending`)
      .then(({ data }) => { if (data.success) setTasks(data.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
    // Load required docs from broker checklist (same source as Documents tab)
    if (brokerageId) {
      axiosInstance.get(`/broker-checklist/${brokerageId}/documents`, {
        params: { transaction_type: dealType || 'purchase' },
      }).then(({ data }) => {
        const requiredDocs = data?.documents || data?.checklist || (Array.isArray(data) ? data : []);
        // Compare required docs against uploaded docs to build knowledge items
        const uploadedNames = uploadedDocs.map((d: any) => (d.filename || d.original_filename || d.name || '').toLowerCase());
        const uploadedTypes = new Set(uploadedDocs.map((d: any) => (d.document_type || d.type || '').toLowerCase()));

        const items = requiredDocs.map((doc: any) => {
          const docName = (doc.name || doc.document_name || '').toLowerCase();
          const docType = (doc.document_type || doc.type || '').toLowerCase();
          // Check if any uploaded doc matches by name or type
          const isUploaded = uploadedNames.some((n: string) => n.includes(docName) || docName.includes(n.replace(/\.[^.]+$/, '')))
            || uploadedTypes.has(docType)
            || (doc.uploaded_filename); // broker-checklist may already flag matched docs

          return {
            document_type: doc.name || doc.document_name || docType,
            priority: doc.priority || doc.priority_level || 'medium',
            status: doc.status === 'exempt' ? 'complete' : isUploaded || doc.uploaded_filename ? 'needs_review' : 'missing',
            uploaded: isUploaded || !!doc.uploaded_filename,
          };
        });

        setKnowledgeItems(items);
        const complete = items.filter((i: any) => i.status !== 'missing').length;
        setKnowledgeScore(items.length > 0 ? Math.round((complete / items.length) * 100) : 0);
      }).catch(() => {});
    }
  }, [dealId, brokerageId, dealType, uploadedDocs?.length]);

  const type = (dealType === 'listing' || dealType === 'seller') ? 'listing' : 'purchase';
  const template = AUTOMATION_TEMPLATES[type] || AUTOMATION_TEMPLATES.purchase;
  const currentStage = stage || 'new_lead';

  const isCompleted = (taskName: string) => tasks.some(t => t.task_name === taskName && t.status === 'completed');
  const isPending = (taskName: string) => tasks.some(t => t.task_name === taskName && t.status === 'pending');

  // Open email composer with pre-filled template — replace placeholders with real deal data
  const openComposer = (taskName: string) => {
    const tmpl = EMAIL_TEMPLATES[taskName];
    const replacePlaceholders = (text: string) => text
      .replace(/\{\{clientName\}\}/g, clientName)
      .replace(/\{\{agentName\}\}/g, agentInfo.name)
      .replace(/\{\{propertyAddress\}\}/g, propertyAddress || 'the property')
      .replace(/\{\{portalUrl\}\}/g, portalUrl);

    setComposing(taskName);
    setComposeTo(clientPartiesWithEmail.map(p => p.email).join(', '));
    setComposeCc('');
    setComposeSubject(replacePlaceholders(tmpl?.subject || taskName));
    setComposeBody(replacePlaceholders(tmpl?.body || `Draft email for: ${taskName}\n\nEdit this message before sending.`));
  };

  // Send the composed email + mark task complete
  const handleSendEmail = async () => {
    if (!composing || !dealId) return;
    setComposeSending(true);
    try {
      // Send email using editable TO/CC fields
      const toEmails = composeTo.split(',').map(e => e.trim()).filter(Boolean);
      const ccEmails = composeCc.split(',').map(e => e.trim()).filter(Boolean);
      if (toEmails.length > 0) {
        await axiosInstance.post('/ai-coach/send-deal-automation', {
          dealId,
          taskName: composing,
          subject: composeSubject,
          body: composeBody,
          toEmails,
          ccEmails,
          agentName: agentInfo.name,
          agentEmail: agentInfo.email,
        }).catch(() => {
          console.warn('[AUTOMATIONS] Send endpoint not available — marking task complete');
        });
      }

      // Mark task complete
      const match = tasks.find(t => t.task_name === composing);
      if (match) {
        await axiosInstance.patch(`/deal-workflow/deals/${dealId}/tasks/${match.id}`, { status: 'completed' });
      }
      toast.success(`Email sent: ${composing}`);
      setComposing(null);
      // Refresh tasks
      const { data } = await axiosInstance.get(`/deal-workflow/deals/${dealId}/tasks/pending`);
      if (data.success) setTasks(data.data || []);
    } catch {
      toast.error('Failed to send email');
    } finally {
      setComposeSending(false);
    }
  };

  const handleMarkDone = async (taskName: string) => {
    setSending(taskName);
    try {
      const match = tasks.find(t => t.task_name === taskName);
      if (match) {
        await axiosInstance.patch(`/deal-workflow/deals/${dealId}/tasks/${match.id}`, { status: 'completed' });
      }
      toast.success(`${taskName} — done`);
      const { data } = await axiosInstance.get(`/deal-workflow/deals/${dealId}/tasks/pending`);
      if (data.success) setTasks(data.data || []);
    } catch {
      toast.error('Failed to process automation');
    } finally {
      setSending(null);
    }
  };

  return (
    <div style={{ background: 'white', border: '1px solid #dee2e6', borderLeft: `4px solid ${colors.gold}`, borderRadius: '10px', padding: '18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: colors.navy }}>
            <FontAwesomeIcon icon={faBolt} style={{ marginRight: '6px', color: colors.gold }} />
            Deal Automations
          </div>
          <div style={{ fontSize: '11px', color: colors.muted, marginTop: '2px' }}>
            {type === 'listing' ? 'Listing' : 'Buyer'} workflow — current stage: <strong>{currentStage.replace(/_/g, ' ')}</strong>
          </div>
        </div>
        {loading && <Spinner animation="border" size="sm" style={{ color: colors.gold }} />}
      </div>

      {/* ── KOPS-680: Action Required — from deal_knowledge checkpoint ── */}
      {knowledgeItems.filter(k => k.status === 'missing').length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {/* Score bar */}
          {knowledgeScore !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1, background: '#e9ecef', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                <div style={{
                  width: `${knowledgeScore}%`, height: '100%', borderRadius: 4,
                  background: knowledgeScore >= 80 ? '#27ae60' : knowledgeScore >= 50 ? '#f39c12' : '#e74c3c',
                }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: knowledgeScore >= 80 ? '#27ae60' : knowledgeScore >= 50 ? '#f39c12' : '#e74c3c' }}>
                {knowledgeScore}%
              </span>
            </div>
          )}
          <div style={{ fontSize: 11, fontWeight: 700, color: '#e74c3c', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 6 }} />
            Action Required — {knowledgeItems.filter(k => k.status === 'missing').length} missing documents
          </div>
          {knowledgeItems
            .filter(k => k.status === 'missing')
            .sort((a: any, b: any) => {
              const p: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
              return (p[a.priority] ?? 4) - (p[b.priority] ?? 4);
            })
            .slice(0, 8)
            .map((k: any, i: number) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', marginBottom: 4, borderRadius: 6,
                background: k.priority === 'critical' ? '#fef2f2' : '#fffbeb',
                border: `1px solid ${k.priority === 'critical' ? '#fecaca' : '#fef3c7'}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
                    background: k.priority === 'critical' ? '#e74c3c' : k.priority === 'high' ? '#f39c12' : '#3498db',
                    color: 'white', textTransform: 'uppercase',
                  }}>{k.priority}</span>
                  <span style={{ fontSize: 12, color: colors.navy, fontWeight: 500 }}>
                    {k.document_type.replace(/_/g, ' ')}
                  </span>
                </div>
                <span style={{ fontSize: 10, color: '#999' }}>Upload required</span>
              </div>
            ))}
          {knowledgeItems.filter(k => k.status === 'missing').length > 8 && (
            <div style={{ fontSize: 10, color: '#999', textAlign: 'center', marginTop: 4 }}>
              +{knowledgeItems.filter(k => k.status === 'missing').length - 8} more items
            </div>
          )}
        </div>
      )}

      {/* ── Inline Email Composer (top of tab) ── */}
      {composing && (
        <div style={{
          background: '#fff', borderRadius: '10px', padding: '20px', marginBottom: '16px',
          border: `1px solid #dee2e6`, borderLeft: `4px solid ${colors.gold}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: colors.navy }}>
              <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '6px', color: colors.gold }} />
              {composing}
            </div>
            <button onClick={() => setComposing(null)} style={{ background: 'transparent', border: 'none', color: '#999', cursor: 'pointer', fontSize: '16px' }}>✕</button>
          </div>

          {/* TO field — editable, pre-filled from deal parties */}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '10px', color: colors.navy, fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>To</label>
            <input
              value={composeTo}
              onChange={e => setComposeTo(e.target.value)}
              placeholder="recipient@email.com"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #dee2e6',
                background: '#fff', color: colors.navy, fontSize: '13px', outline: 'none', cursor: 'text',
              }}
              onFocus={e => { e.currentTarget.style.borderColor = colors.gold; e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.gold}33`; }}
              onBlur={e => { e.currentTarget.style.borderColor = '#dee2e6'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          </div>

          {/* CC field */}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '10px', color: colors.navy, fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Cc <span style={{ fontWeight: 400, color: '#999' }}>(optional)</span></label>
            <input
              value={composeCc}
              onChange={e => setComposeCc(e.target.value)}
              placeholder="cc@email.com, another@email.com"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #dee2e6',
                background: '#fff', color: colors.navy, fontSize: '13px', outline: 'none', cursor: 'text',
              }}
              onFocus={e => { e.currentTarget.style.borderColor = colors.gold; e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.gold}33`; }}
              onBlur={e => { e.currentTarget.style.borderColor = '#dee2e6'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          </div>

          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '10px', color: colors.navy, fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Subject</label>
            <input
              value={composeSubject}
              onChange={e => setComposeSubject(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #dee2e6',
                background: '#fff', color: colors.navy, fontSize: '13px', fontWeight: 600, outline: 'none',
                cursor: 'text',
              }}
              onFocus={e => { e.currentTarget.style.borderColor = colors.gold; e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.gold}33`; }}
              onBlur={e => { e.currentTarget.style.borderColor = '#dee2e6'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '10px', color: colors.navy, fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Message</label>
            <textarea
              value={composeBody}
              onChange={e => setComposeBody(e.target.value)}
              rows={12}
              style={{
                width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #dee2e6',
                background: '#fff', color: '#333', fontSize: '13px', lineHeight: 1.7,
                outline: 'none', resize: 'vertical', fontFamily: 'inherit',
                cursor: 'text',
              }}
              onFocus={e => { e.currentTarget.style.borderColor = colors.gold; e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.gold}33`; }}
              onBlur={e => { e.currentTarget.style.borderColor = '#dee2e6'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={handleSendEmail}
              disabled={composeSending || !composeSubject.trim()}
              style={{
                background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`,
                color: colors.navy, border: 'none', borderRadius: '8px', padding: '10px 24px',
                fontSize: '13px', fontWeight: 700, cursor: 'pointer', opacity: composeSending ? 0.6 : 1,
              }}
            >
              <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '6px' }} />
              {composeSending ? 'Sending…' : 'Approve & Send'}
            </button>
            <button onClick={() => setComposing(null)} style={{
              background: 'transparent', color: '#666', border: '1px solid #dee2e6',
              borderRadius: '8px', padding: '10px 16px', fontSize: '13px', cursor: 'pointer',
            }}>
              Cancel
            </button>
            <span style={{ fontSize: '10px', color: '#999', marginLeft: 'auto' }}>
              Review and edit before sending
            </span>
          </div>
        </div>
      )}

      {template.map((group) => {
        const unlocked = isStageReached(currentStage, group.triggerStage);
        const isCurrentGroup = unlocked && !template.some(g => g.category !== group.category && isStageReached(currentStage, g.triggerStage) && STAGE_ORDER.indexOf(g.triggerStage) > STAGE_ORDER.indexOf(group.triggerStage));

        return (
          <div key={group.category} style={{ marginBottom: '20px', opacity: unlocked ? 1 : 0.5 }}>
            {/* Group header */}
            <div style={{
              fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.5px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px',
              color: unlocked ? group.color : '#999',
            }}>
              <FontAwesomeIcon icon={unlocked ? group.icon : faClock} />
              {group.label}
              {!unlocked && (
                <span style={{
                  fontSize: '9px', fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                  background: '#f0f0f0', color: '#999', marginLeft: 4,
                }}>
                  🔒 Unlocks at {group.triggerStage.replace(/_/g, ' ')}
                </span>
              )}
              {unlocked && !isCurrentGroup && (
                <span style={{
                  fontSize: '9px', fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                  background: '#f0fdf4', color: '#27ae60', marginLeft: 4,
                }}>
                  ✓ Stage reached
                </span>
              )}
            </div>

            {/* Locked state — collapsed */}
            {!unlocked ? (
              <div style={{
                padding: '10px 14px', borderRadius: '6px', background: '#f8f9fb',
                border: '1px dashed #ddd', fontSize: '11px', color: '#999',
              }}>
                {group.tasks.length} automation{group.tasks.length !== 1 ? 's' : ''} will activate when deal reaches this stage
              </div>
            ) : (
              /* Unlocked — show tasks */
              group.tasks.map((task, i) => {
                const completed = isCompleted(task.name);
                const pending = isPending(task.name);
                const isSending = sending === task.name;

                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
                    marginBottom: '4px', borderRadius: '6px',
                    background: completed ? '#f0fdf4' : pending ? '#fffdf5' : '#f8f9fb',
                    border: `1px solid ${completed ? '#bbf7d0' : pending ? '#fef3c7' : '#e8eef6'}`,
                  }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                      background: completed ? '#27ae60' : pending ? `${colors.gold}20` : '#e8eef6',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {completed ? (
                        <FontAwesomeIcon icon={faCheck} style={{ color: '#fff', fontSize: 10 }} />
                      ) : pending ? (
                        <FontAwesomeIcon icon={faClock} style={{ color: colors.gold, fontSize: 10 }} />
                      ) : (
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ccc' }} />
                      )}
                    </div>

                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '12px', color: completed ? '#555' : colors.navy, fontWeight: 500,
                        textDecoration: completed ? 'line-through' : 'none',
                      }}>
                        {task.name}
                      </div>
                      <div style={{ fontSize: '10px', color: '#999', marginTop: '1px', display: 'flex', gap: '8px' }}>
                        {task.auto && <span style={{ color: colors.gold, fontWeight: 600 }}>AUTO</span>}
                        <span>{task.type === 'email' ? '📧 Email' : task.type === 'notification' ? '🔔 Notification' : '📋 Task'}</span>
                        <span>Day {task.daysOffset}</span>
                      </div>
                    </div>

                    {!completed && task.type === 'email' && (
                      <button onClick={() => openComposer(task.name)}
                        style={{ background: colors.navy, color: colors.gold, border: 'none', borderRadius: '6px', padding: '4px 12px', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>
                        Review
                      </button>
                    )}
                    {!completed && task.type === 'task' && (
                      <button onClick={() => handleMarkDone(task.name)} disabled={sending === task.name}
                        style={{ background: 'transparent', color: colors.navy, border: '1px solid #dee2e6', borderRadius: '6px', padding: '4px 12px', fontSize: '10px', fontWeight: 600, cursor: 'pointer' }}>
                        {sending === task.name ? '...' : 'Done'}
                      </button>
                    )}
                    {completed && (
                      <span style={{ fontSize: '10px', color: '#27ae60', fontWeight: 600 }}>✓ Complete</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        );
      })}

      <div style={{ textAlign: 'center', padding: '12px 0', borderTop: '1px solid #e8eef6', marginTop: '8px' }}>
        <span style={{ fontSize: '10px', color: '#999' }}>
          AUTO tasks trigger automatically. 🔒 Locked groups unlock when the deal advances to that stage.
        </span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// KOPS-680: Deal Coach Tab — RAG-grounded deal intelligence
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// KOPS-680: Deal Brief — AI-generated summary at top of Overview tab
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// KOPS-683: Client Portal Invite — send/resend magic link to client
// ---------------------------------------------------------------------------

const ClientPortalInvite: React.FC<{ dealId: string; parties: any[]; clientName?: string; dealType?: string }> = ({ dealId, parties, clientName, dealType }) => {
  const [sending, setSending] = useState(false);
  const [sentEmails, setSentEmails] = useState<string[]>([]);

  // Only show invite for OUR client — the party we represent
  // Listing/seller deal → we represent the seller
  // Purchase/buyer deal → we represent the buyer
  const ourPartyTypes = (dealType === 'listing' || dealType === 'seller')
    ? ['seller', 'landlord']
    : ['buyer', 'tenant', 'client'];

  const clientParties = parties.filter(p =>
    ourPartyTypes.includes(p.party_type) && p.email
  );

  if (clientParties.length === 0) return null;

  const handleSend = async (email: string, name: string) => {
    setSending(true);
    try {
      await axiosInstance.post(`/transactions/${dealId}/send-client-portal-invite`, {
        client_email: email,
        client_name: name || 'Client',
      });
      setSentEmails(prev => [...prev, email]);
      toast.success(`Portal invite sent to ${email}`);
    } catch {
      toast.error(`Failed to send invite to ${email}`);
    }
    setSending(false);
  };

  return (
    <div style={{
      background: 'white', border: '1px solid #dee2e6', borderLeft: '4px solid #27ae60',
      borderRadius: '10px', padding: '12px 18px', marginBottom: '12px',
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: colors.navy, marginBottom: 8 }}>
        📧 Client Deal Portal
      </div>
      {clientParties.map((p: any) => {
        const alreadySent = sentEmails.includes(p.email);
        return (
          <div key={p.email} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
            <div style={{ fontSize: 11, color: colors.muted }}>
              {p.name || 'Client'} — {p.email}
            </div>
            <button
              onClick={() => handleSend(p.email, p.name)}
              disabled={sending || alreadySent}
              style={{
                padding: '4px 12px', border: 'none', borderRadius: 4,
                background: alreadySent ? '#27ae60' : colors.gold, color: alreadySent ? 'white' : colors.navy,
                fontSize: 10, fontWeight: 700, cursor: sending ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', opacity: sending ? 0.6 : 1,
              }}
            >
              {alreadySent ? '✓ Sent' : 'Send Invite'}
            </button>
          </div>
        );
      })}
    </div>
  );
};

const DealBrief: React.FC<{ dealId: string }> = ({ dealId }) => {
  // Load from localStorage instantly, then refresh from API in background
  const cacheKey = `deal_brief_${dealId}`;
  const ackCacheKey = `deal_brief_ack_${dealId}`;

  const [brief, setBrief] = useState<any>(() => {
    try { const c = localStorage.getItem(cacheKey); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [loading, setLoading] = useState(!brief);
  const [acknowledged, setAcknowledged] = useState(() => {
    try { const a = localStorage.getItem(ackCacheKey); return a ? JSON.parse(a).ack : false; } catch { return false; }
  });
  const [acknowledgedDate, setAcknowledgedDate] = useState<string | null>(() => {
    try { const a = localStorage.getItem(ackCacheKey); return a ? JSON.parse(a).date : null; } catch { return null; }
  });
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!dealId) return;
    // Fetch brief + check if already acknowledged (background refresh)
    Promise.all([
      axiosInstance.get(`/deal-coach/${dealId}/brief`),
      axiosInstance.get(`/transactions/${dealId}/activity?type=brief_acknowledged&limit=1`).catch(() => null),
    ]).then(([briefRes, ackRes]) => {
      if (briefRes.data?.success) {
        setBrief(briefRes.data);
        try { localStorage.setItem(cacheKey, JSON.stringify(briefRes.data)); } catch {}
      }
      // If there's a prior acknowledgment AND the brief hasn't been regenerated since, mark as acknowledged
      const lastAck = ackRes?.data?.activities?.[0];
      if (lastAck && briefRes.data?.cached) {
        const ackTime = new Date(lastAck.created_at).getTime();
        const briefAgeMs = (briefRes.data.cacheAge || 0) * 60 * 60 * 1000;
        const briefGeneratedAt = Date.now() - briefAgeMs;
        if (ackTime > briefGeneratedAt) {
          setAcknowledged(true);
          setAcknowledgedDate(new Date(lastAck.created_at).toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }));
          try { localStorage.setItem(ackCacheKey, JSON.stringify({ ack: true, date: new Date(lastAck.created_at).toLocaleDateString() })); } catch {}
        }
      } else {
        setAcknowledged(false);
        setAcknowledgedDate(null);
        try { localStorage.removeItem(ackCacheKey); } catch {}
      }
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [dealId]);

  if (!brief?.brief) {
    return loading ? (
      <div style={{
        background: 'white', border: '1px solid #dee2e6', borderLeft: `4px solid ${colors.gold}`,
        borderRadius: '10px', padding: '14px 18px', marginBottom: '12px',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${colors.gold}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
        <span style={{ fontSize: 12, color: colors.muted }}>Preparing your deal brief...</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    ) : null;
  }

  const formatBrief = (text: string) => {
    return text
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/^### (.+)$/gm, '<div style="margin:10px 0 4px;font-size:12px;font-weight:700;color:#0F1E35;">$1</div>')
      .replace(/^## (.+)$/gm, '<div style="margin:12px 0 6px;font-size:13px;font-weight:700;color:#0F1E35;">$1</div>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/^[•\-]\s+(.+)$/gm, '<div style="margin:2px 0;padding-left:14px;font-size:12px;">• $1</div>')
      .replace(/^(\d+)\.\s+(.+)$/gm, '<div style="margin:2px 0;padding-left:14px;font-size:12px;">$1. $2</div>')
      .replace(/✅/g, '<span style="color:#27ae60">✅</span>')
      .replace(/❌/g, '<span style="color:#e74c3c">❌</span>')
      .replace(/⚠️/g, '<span style="color:#f39c12">⚠️</span>')
      .replace(/💡/g, '💡')
      .replace(/\n\n/g, '<br/>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div style={{
      background: 'white', border: '1px solid #dee2e6', borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '10px', padding: '16px 18px', marginBottom: '12px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon icon={faRobot} style={{ color: colors.gold, fontSize: 14 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.5px' }}>AI Deal Brief</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Score badge */}
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
            background: (brief.score ?? 0) >= 80 ? '#27ae6020' : (brief.score ?? 0) >= 50 ? '#f39c1220' : '#e74c3c20',
            color: (brief.score ?? 0) >= 80 ? '#27ae60' : (brief.score ?? 0) >= 50 ? '#f39c12' : '#e74c3c',
          }}>
            {brief.score}% complete
          </span>
          <span style={{ fontSize: 10, color: colors.muted }}>Day {brief.daysOpen}</span>
          <button
            onClick={async () => {
              setRefreshing(true);
              try {
                localStorage.removeItem(cacheKey);
                localStorage.removeItem(ackCacheKey);
                const { data } = await axiosInstance.get(`/deal-coach/${dealId}/brief?refresh=true`);
                if (data.success) {
                  setBrief(data);
                  try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch {}
                  setAcknowledged(false);
                  setAcknowledgedDate(null);
                }
              } catch {}
              setRefreshing(false);
            }}
            disabled={refreshing}
            style={{
              padding: '2px 8px', borderRadius: 4, border: `1px solid ${colors.gold}`,
              background: 'transparent', color: colors.gold, fontSize: 10, fontWeight: 600,
              cursor: refreshing ? 'wait' : 'pointer', opacity: refreshing ? 0.6 : 1,
            }}
          >
            <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} style={{ marginRight: 4, fontSize: 9 }} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Brief content */}
      <div
        dangerouslySetInnerHTML={{ __html: formatBrief(brief.brief) }}
        style={{ fontSize: 12, lineHeight: 1.7, color: '#333' }}
      />

      {/* Acknowledgement */}
      {!acknowledged ? (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: colors.muted }}>Please acknowledge you have reviewed this brief</span>
          <button
            onClick={async () => {
              const dateStr = new Date().toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
              setAcknowledged(true);
              setAcknowledgedDate(dateStr);
              try { localStorage.setItem(ackCacheKey, JSON.stringify({ ack: true, date: dateStr })); } catch {}
              try {
                await axiosInstance.post(`/transactions/${dealId}/activity`, {
                  activity_type: 'brief_acknowledged',
                  description: `Agent acknowledged AI Deal Brief (${brief.score}% compliance, Day ${brief.daysOpen})`,
                });
              } catch {}
            }}
            style={{
              padding: '6px 16px', border: 'none', borderRadius: 5,
              background: colors.gold, color: colors.navy,
              fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            I Acknowledge
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: '#27ae60', fontSize: 13 }}>✓</span>
          <span style={{ fontSize: 10, color: '#27ae60', fontWeight: 600 }}>Acknowledged {acknowledgedDate || new Date().toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
        </div>
      )}
    </div>
  );
};

const DealCoachTab: React.FC<{ dealId: string; transaction: any }> = ({ dealId, transaction }) => {
  const [messages, setMessages] = useState<{ role: string; text: string }[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkpoint, setCheckpoint] = useState<any>(null);
  const [loadingCheckpoint, setLoadingCheckpoint] = useState(true);
  const chatEndRef = React.useRef<HTMLDivElement>(null);

  // Load checkpoint on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get(`/deal-coach/${dealId}/checkpoint`);
        if (res.data?.success) setCheckpoint(res.data);
      } catch {}
      setLoadingCheckpoint(false);
    })();
  }, [dealId]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text?: string) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      const res = await axiosInstance.post(`/deal-coach/${dealId}/chat`, {
        message: msg,
        conversationHistory: messages.slice(-8),
      });
      if (res.data?.response) {
        setMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, I couldn\'t process that. Please try again.' }]);
    }
    setLoading(false);
  };

  const formatMarkdown = (text: string) => {
    return text
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/^### (.+)$/gm, '<h4 style="margin:10px 0 4px;font-size:12px;color:#0F1E35;">$1</h4>')
      .replace(/^## (.+)$/gm, '<h3 style="margin:12px 0 6px;font-size:13px;font-weight:700;color:#0F1E35;">$1</h3>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/^[•\-]\s+(.+)$/gm, '<li style="margin:2px 0;margin-left:14px;font-size:12px;">$1</li>')
      .replace(/^(\d+)\.\s+(.+)$/gm, '<li style="margin:2px 0;margin-left:14px;font-size:12px;">$2</li>')
      .replace(/`([^`]+)`/g, '<code style="background:#f0f0f0;padding:1px 4px;border-radius:2px;font-size:11px;">$1</code>')
      .replace(/\n\n/g, '<br/><br/>')
      .replace(/<\/li>\n(?!<li)/g, '</li>')
      .replace(/\n/g, '<br/>');
  };

  const cp = checkpoint?.checkpoint;
  const alerts = checkpoint?.alerts || [];

  return (
    <div style={{ background: 'white', border: '1px solid #dee2e6', borderLeft: `4px solid ${colors.gold}`, borderRadius: '10px', padding: '0', overflow: 'hidden' }}>

      {/* Checkpoint Score Bar */}
      {!loadingCheckpoint && cp && (
        <div style={{ padding: '12px 18px', borderBottom: '1px solid #eee', background: '#fafbfc' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Deal Compliance
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: cp.score >= 80 ? '#27ae60' : cp.score >= 50 ? '#f39c12' : '#e74c3c' }}>
              {cp.score}%
            </span>
          </div>
          <div style={{ background: '#e9ecef', borderRadius: 4, height: 6, overflow: 'hidden' }}>
            <div style={{
              width: `${cp.score}%`, height: '100%', borderRadius: 4,
              background: cp.score >= 80 ? '#27ae60' : cp.score >= 50 ? '#f39c12' : '#e74c3c',
              transition: 'width 0.5s',
            }} />
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 10, color: '#888' }}>
            <span>✅ {cp.complete} complete</span>
            <span>⏳ {cp.needs_review} pending</span>
            <span>❌ {cp.missing} missing</span>
          </div>
          {alerts.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {alerts.slice(0, 3).map((a: string, i: number) => (
                <div key={i} style={{ fontSize: 10, color: '#e74c3c', padding: '2px 0' }}>⚠️ {a}</div>
              ))}
              {alerts.length > 3 && <div style={{ fontSize: 10, color: '#999' }}>+{alerts.length - 3} more alerts</div>}
            </div>
          )}
        </div>
      )}

      {/* Chat Messages */}
      <div style={{ height: 360, overflowY: 'auto', padding: '12px 18px' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            <FontAwesomeIcon icon={faRobot} style={{ fontSize: 28, color: colors.gold, marginBottom: 10 }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: colors.navy, marginBottom: 6 }}>AI Deal Coach</div>
            <div style={{ fontSize: 11, color: '#888', maxWidth: 350, margin: '0 auto', lineHeight: 1.5, marginBottom: 16 }}>
              Ask about your deal — next steps, compliance requirements, deadlines, or document questions.
              Grounded in the AAR Purchase Contract and Arizona law.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {[
                'What should I do next with this deal?',
                'What documents am I missing?',
                'What are the inspection period deadlines?',
                'When is the BINSR due?',
              ].map((q, i) => (
                <button key={i} onClick={() => sendMessage(q)} style={{
                  background: '#f8f9fa', border: '1px solid #eee', borderRadius: 6, padding: '8px 10px',
                  textAlign: 'left', cursor: 'pointer', fontSize: 11, color: colors.navy, fontFamily: 'inherit',
                }}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: 8,
          }}>
            <div style={{
              maxWidth: '85%', padding: '10px 14px', borderRadius: 10,
              background: m.role === 'user' ? colors.navy : '#f4f5f7',
              color: m.role === 'user' ? 'white' : colors.navy,
              fontSize: 12, lineHeight: 1.6,
            }}>
              {m.role === 'user' ? m.text : (
                <div dangerouslySetInnerHTML={{ __html: formatMarkdown(m.text) }} />
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', gap: 4, padding: '8px 0' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 6, height: 6, borderRadius: '50%', background: colors.gold,
                animation: `pulse 1s ease-in-out ${i * 0.15}s infinite`,
              }} />
            ))}
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '10px 18px', borderTop: '1px solid #eee', display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendMessage()}
          placeholder="Ask about this deal..."
          disabled={loading}
          style={{
            flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6,
            fontSize: 12, fontFamily: 'inherit', color: colors.navy, outline: 'none',
          }}
        />
        <button
          onClick={() => sendMessage()}
          disabled={loading || !input.trim()}
          style={{
            padding: '8px 16px', border: 'none', borderRadius: 6,
            background: colors.gold, color: colors.navy, fontSize: 12,
            fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading || !input.trim() ? 0.5 : 1,
          }}
        >
          Send
        </button>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

export default AgentClientHub;
